import React, { useState, useEffect, useRef } from 'react';
import {
  Check, ArrowLeft, ArrowRight, RefreshCw, Heart, Sparkles,
  Play, Star, X, Users, User, Eye, Home, BookOpen, ClipboardList,
  Gift, Copy, Calendar, Edit3, History, Search, Inbox, Video, Lightbulb,
  Plus, Link, AlertCircle, Send, Film, MessageCircle, ChevronDown, HeartHandshake
} from 'lucide-react';
import {
  DndContext,
  DragOverlay,
  MouseSensor,
  TouchSensor,
  KeyboardSensor,
  useSensor,
  useSensors,
  closestCorners,
  DragStartEvent,
  DragEndEvent,
} from '@dnd-kit/core';
import { SortableContext, verticalListSortingStrategy, useSortable, arrayMove } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { BackButton, Card, PrimaryButton, TagBadge, VideoModal, Modal, EmptyState, VideoPlayer } from '@/components/ui';
import { DragDropCalendarView } from '@/components/DragDropCalendarView';
import { ScheduleActivityModal } from '@/components/ScheduleActivityModal';
import { UpcomingActivities } from '@/components/UpcomingActivities';
import { ClipSidebar } from '@/components/ClipSidebar';
import { ClipDragPreview } from '@/components/ClipDragPreview';
import { ActivityDragPreview } from '@/components/ActivityDragPreview';
import { FloatingChat } from '@/components/FloatingChat';
import { SubmissionReviewModal } from '@/components/SubmissionReviewModal';
import { CoupleSetupModal } from '@/components/CoupleSetupModal';
import { CATEGORIES, TAGS, RATING_OPTIONS, COLORS } from '@/data';
import { TAG_TUTORIAL_DEFINITIONS, getTutorialByTagId } from '@/data/tag-tutorials';
import { HIS_SIZE_SCALE, HIS_GIRTH_SCALE, HIS_CUM_SCALE, HIS_CONSISTENCY_SCALE, HIS_TASTE_SCALE, HIS_PRECUM_SCALE } from '@/data/profile-scales';
import { generateReviewerCode, generateCoupleCode, generateId, now, getRedGifsWatchUrl, validateClipUrl, normalizeRedGifsUrl } from '@/lib/utils';
import { getCoupleByCode, saveCoupleByCode } from '@/lib/storage';
import { d1Get, d1List, d1Upsert, d1Delete } from '@/lib/d1-storage';
import { ClipThumb } from '@/components/ClipThumb';
import { STATUS_CHIPS, VERDICTS, ageLabel, genderLabel, isDecided, reviewerLabel, verdictTally } from '@/lib/submissions';
import { getClipCategoryIds } from '@/types';
import type { Clip, Reviewer, CoupleRecommendation, CouplePreference, ScheduledActivity, ActivityActor, CoupleData, CoupleSubmission, CoupleRequest, SubmittedClip, SubmittedClipReview, Creator, ClipView, WishlistItem, CoupleQuestion, TagTutorial, ReviewerFollowUp, CoupleFollowUp, SuggestedEdit, PickForUsRequest, CoupleMessage, ClipSuggestionRequest, ActivityFollowUp } from '@/types';
import { CopyLinkButton, reviewerLoginLink } from '@/components/CopyLinkButton';

// Fun pre-generated codenames for partners - spicy & playful
const PARTNER_CODENAMES = [
  '🔥 Firecracker', '😈 My Trouble', '🍑 Peachy', '💋 Sugar Lips', '🌶️ Spicy One',
  '🦊 Foxy', '🍯 Honey Pot', '👅 Tasty', '💦 Wet Dream', '🎭 Bad Influence',
  '🌙 Night Owl', '🐆 Wild Thing', '💥 Dynamite', '🍭 Sweet Cheeks', '⚡ Sparky'
];

interface ReviewerViewProps {
  reviewers: Reviewer[];
  saveReviewers: (reviewers: Reviewer[]) => Promise<void>;
  dateNiteClips: Clip[];
  saveDateNiteClips: (clips: Clip[]) => Promise<void>;
  currentReviewer: Reviewer | null;
  setCurrentReviewer: (reviewer: Reviewer | null) => void;
  onBack: () => void;
  loadData: () => void;
  // Creator clip submissions
  submittedClips: SubmittedClip[];
  saveSubmittedClips: (clips: SubmittedClip[]) => Promise<void>;
  creators: Creator[];
  // Clip view tracking
  clipViews: ClipView[];
  saveClipViews: (views: ClipView[]) => Promise<void>;
  // Couple wishlist
  wishlist: WishlistItem[];
  saveWishlist: (items: WishlistItem[], coupleId?: string) => Promise<void>;
  // Couple questions (from reviewers)
  coupleQuestions: CoupleQuestion[];
  saveCoupleQuestions: (questions: CoupleQuestion[]) => Promise<void>;
  // Tag education tutorials
  tagTutorials: TagTutorial[];
  saveTagTutorials: (tutorials: TagTutorial[]) => Promise<void>;
  // Admin follow-up questions for reviewers
  reviewerFollowUps: ReviewerFollowUp[];
  saveReviewerFollowUps: (followUps: ReviewerFollowUp[]) => Promise<void>;
  // Suggested clip edits (pending admin approval)
  suggestedEdits: SuggestedEdit[];
  saveSuggestedEdits: (edits: SuggestedEdit[]) => Promise<void>;
  // Couple follow-ups (notifications for couples)
  coupleFollowUps: CoupleFollowUp[];
  saveCoupleFollowUps: (followUps: CoupleFollowUp[]) => Promise<void>;
  // Pick for us requests from couples
  pickForUsRequests: PickForUsRequest[];
  savePickForUsRequests: (requests: PickForUsRequest[]) => Promise<void>;
  // Clip suggestions from admin (for reviewing)
  clipSuggestions: ClipSuggestionRequest[];
  saveClipSuggestions: (suggestions: ClipSuggestionRequest[]) => Promise<void>;
  // Navigate to full DateNite couple experience
  onSwitchToCoupleMode?: (coupleId: string) => void;
}

// Full reviewer form type
interface ReviewerForm {
  // Section A: Background
  gender: string;
  ageRange: string;
  relationshipStatus: string;
  sexuallyActive: string;
  watchesAdultContent: string;
  adultContentThemes: string;
  adultContentExample: string;
  whatMakesGoodContent: string;
  whatFeelsFake: string;
  preferredStyle: string;
  familiarWithProducts: string;
  relevantExperience: string;
  whyInterested: string;
  // Section B: Load Boost
  enjoysVolumeContent: string;
  // Male specific
  triedLoadBoost: string;
  loadBoostExperience: string;
  partnerTypicalVolume: string;
  partnerVolumeLikeDislike: string;
  // Female specific
  wantPartnerLoadBoost: string;
  enjoysSwallowing: string;
  swallowingDescription: string;
  wouldEnjoyBetterTaste: string;
  enjoysFacials: string;
  volumeMakesDifference: string;
  // Section C: Soaking Wet
  // Female specific
  canSquirt: string;
  squirtScenarioDescription: string;
  wantToSquirtMore: string;
  usedArousalProducts: string;
  // Wetness & orgasm questions (female)
  orgasmFromSex: string;
  typicalWetness: number;
  wetnessNotes: string;
  wantToBeWetter: string;
  // Male specific
  enjoysSquirtingContent: string;
  wantMoreWithPartner: string;
  partnerUsedArousalProducts: string;
  // General
  additionalNotes: string;
  // Couple integration
  partnerName: string;
  // Section D: Sexual Experience Details
  lastSexualActivity: string;
  facialExperience: string;
  oralExperience: string;
  analExperience: string;
  favoriteSexualMemory: string;
  fantasiesNotTried: string;
  biggestTurnOns: string;
  dealBreakers: string;
  // Section E: Couple Experience Interest
  interestedInCoupleExperience: string;
  wouldSubmitClips: string;
  comfortWithAnonymousFeedback: string;
  interestedInScheduledActivities: string;
  interestedInCuratedExperiences: string;
  partnerSizePreference: string;
  partnerSizeDescription: string;
  wetnessImportance: string;
  openToNewThings: string;
  coupleExperienceNotes: string;
}

const initialForm: ReviewerForm = {
  gender: '',
  ageRange: '',
  relationshipStatus: '',
  sexuallyActive: '',
  watchesAdultContent: '',
  adultContentThemes: '',
  adultContentExample: '',
  whatMakesGoodContent: '',
  whatFeelsFake: '',
  preferredStyle: '',
  familiarWithProducts: '',
  relevantExperience: '',
  whyInterested: '',
  enjoysVolumeContent: '',
  triedLoadBoost: '',
  loadBoostExperience: '',
  partnerTypicalVolume: '',
  partnerVolumeLikeDislike: '',
  wantPartnerLoadBoost: '',
  enjoysSwallowing: '',
  swallowingDescription: '',
  wouldEnjoyBetterTaste: '',
  enjoysFacials: '',
  volumeMakesDifference: '',
  canSquirt: '',
  squirtScenarioDescription: '',
  wantToSquirtMore: '',
  usedArousalProducts: '',
  orgasmFromSex: '',
  typicalWetness: 0,
  wetnessNotes: '',
  wantToBeWetter: '',
  enjoysSquirtingContent: '',
  wantMoreWithPartner: '',
  partnerUsedArousalProducts: '',
  additionalNotes: '',
  partnerName: '',
  lastSexualActivity: '',
  facialExperience: '',
  oralExperience: '',
  analExperience: '',
  favoriteSexualMemory: '',
  fantasiesNotTried: '',
  biggestTurnOns: '',
  dealBreakers: '',
  interestedInCoupleExperience: '',
  wouldSubmitClips: '',
  comfortWithAnonymousFeedback: '',
  interestedInScheduledActivities: '',
  interestedInCuratedExperiences: '',
  partnerSizePreference: '',
  partnerSizeDescription: '',
  wetnessImportance: '',
  openToNewThings: '',
  coupleExperienceNotes: ''
};

// Sortable rank item for category rankings drag-to-reorder
const SortableRankItem: React.FC<{
  id: string;
  rank: number;
  title: string;
  rating?: string;
  isChampion: boolean;
}> = ({ id, rank, title, rating, isChampion }) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id });
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };
  return (
    <div ref={setNodeRef} style={style} {...attributes} {...listeners}
      className={`flex items-center gap-3 px-3 py-2 rounded-lg cursor-grab active:cursor-grabbing ${
        isChampion ? 'bg-amber-500/10 border border-amber-500/30' : 'bg-zinc-800 border border-zinc-700'
      }`}
    >
      <span className={`text-sm font-bold w-6 text-center ${isChampion ? 'text-amber-400' : 'text-zinc-500'}`}>
        {rank}
      </span>
      <p className="text-white text-sm flex-1 truncate">{title}</p>
      {rating && <span className="text-zinc-500 text-xs shrink-0">{rating}</span>}
      {isChampion && <span className="text-amber-400 text-xs shrink-0">Champion</span>}
    </div>
  );
};

export const ReviewerView: React.FC<ReviewerViewProps> = ({
  reviewers,
  saveReviewers,
  dateNiteClips,
  saveDateNiteClips,
  currentReviewer,
  setCurrentReviewer,
  onBack,
  loadData,
  submittedClips,
  saveSubmittedClips,
  creators,
  clipViews,
  saveClipViews,
  wishlist,
  saveWishlist,
  coupleQuestions,
  saveCoupleQuestions,
  tagTutorials,
  saveTagTutorials,
  reviewerFollowUps,
  saveReviewerFollowUps,
  suggestedEdits,
  saveSuggestedEdits,
  coupleFollowUps,
  saveCoupleFollowUps,
  pickForUsRequests,
  savePickForUsRequests,
  clipSuggestions,
  saveClipSuggestions,
  onSwitchToCoupleMode
}) => {
  const [step, setStep] = useState(1);
  const [code, setCode] = useState('');
  const [coupleCode, setCoupleCode] = useState('');
  const [showCode, setShowCode] = useState(false);
  const [tab, setTab] = useState<'datenite' | 'insights' | 'mypicks' | 'calendar' | 'couple' | 'inbox' | 'submissions'>('submissions');
  const [form, setForm] = useState<ReviewerForm>(initialForm);
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [tagFilter, setTagFilter] = useState<string[]>([]);
  const [hideReviewed, setHideReviewed] = useState(true); // Hide already-reviewed clips by default
  const [reviewingClip, setReviewingClip] = useState<Clip | null>(null);
  const [clipReview, setClipReview] = useState({ rating: '', comment: '', cumLevel: 3, cumConsistency: 3, wetnessLevel: 3, squirtLevel: 0, suggestedTags: [] as string[], dateNightNotes: '' });
  const [showOwnCoupleDetails, setShowOwnCoupleDetails] = useState(false);
  const [showPreviouslyReviewedActivity, setShowPreviouslyReviewedActivity] = useState(false);
  const [showPendingEdits, setShowPendingEdits] = useState(false);
  const [showEditNotifications, setShowEditNotifications] = useState(false);
  const [showClipsToReview, setShowClipsToReview] = useState(true);
  const [_activityReviewTick, setActivityReviewTick] = useState(0); // Force re-render when marking activities reviewed
  const [showTryAtHomeFeedback, setShowTryAtHomeFeedback] = useState(false);
  const [tryAtHomeFeedback, setTryAtHomeFeedback] = useState({
  rating: '',
  feedback: '',
  tookMedia: '' as '' | 'yes' | 'no',
  willingToShare: '' as '' | 'yes' | 'maybe' | 'no',
  mediaUrl: '',
  comparisonToClip: ''
});
  const [recommendations, setRecommendations] = useState<CoupleRecommendation[]>([]);
  const [showRecommendations, setShowRecommendations] = useState(false);
  const [codeCopied, setCodeCopied] = useState(false);
  const [useCodename, setUseCodename] = useState(false);
  const [selectedCodename, setSelectedCodename] = useState('');
  const [couplePreferences, setCouplePreferences] = useState<CouplePreference[]>([]);
  const [sampleClips, setSampleClips] = useState<Clip[]>([]);
  const [currentSampleIndex, setCurrentSampleIndex] = useState(0);
  const [selectedToTry, setSelectedToTry] = useState<string[]>([]); // clip IDs they want to try
  const [showChallengeVideo, setShowChallengeVideo] = useState(false);
  const [watchingClip, setWatchingClip] = useState<Clip | null>(null); // For video modal

  // Clip editing state
  const [_isEditingClip, setIsEditingClip] = useState(false); // Setter still used for cleanup
  const [editingClipData, setEditingClipData] = useState<{
    title: string;
    description: string;
    tags: string[];
    categoryIds: string[]; // Multi-category support
    link: string; // Added for URL editing
    reason: string; // Why they're suggesting this change
  }>({ title: '', description: '', tags: [], categoryIds: [], link: '', reason: '' });

  // Add new clip state
  const [showAddClipModal, setShowAddClipModal] = useState(false);
  const [newClipUrlError, setNewClipUrlError] = useState<string | null>(null);

  // Couple linking state (for reviewers to link to any couple)
  const [showLinkCoupleModal, setShowLinkCoupleModal] = useState(false);
  const [linkCoupleCode, setLinkCoupleCode] = useState('');
  const [linkCoupleError, setLinkCoupleError] = useState('');
  const [showUnlinkConfirm, setShowUnlinkConfirm] = useState(false);

  // Calendar scheduling state
  const [scheduledActivities, setScheduledActivities] = useState<ScheduledActivity[]>([]);
  const [showScheduleModal, setShowScheduleModal] = useState(false);
  const [selectedCalendarDate, setSelectedCalendarDate] = useState<Date | undefined>();
  const [rescheduleActivityId, setRescheduleActivityId] = useState<string | null>(null);

  // Drag-drop state for calendar
  const [activeClip, setActiveClip] = useState<Clip | null>(null);
  const [activeActivity, setActiveActivity] = useState<{ activity: ScheduledActivity; clip: Clip | undefined } | null>(null);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);

  // Own couple data (reviewer's personal Date Night profile)
  const [linkedCoupleData, setLinkedCoupleData] = useState<CoupleData | null>(null);
  // Managed couple data (external couple this reviewer coaches/manages - separate from own)
  const [managedCoupleData, setManagedCoupleData] = useState<CoupleData | null>(null);
  // Tab state for managed couple card: inbox | activity | profile | calendar
  const [managedCoupleTab, setManagedCoupleTab] = useState<'inbox' | 'activity' | 'profile' | 'calendar' | 'submissions'>('inbox');
  // Sub-tab state for inbox: action | chat | qa | requests
  const [inboxSubTab, setInboxSubTab] = useState<'action' | 'chat' | 'qa' | 'requests'>('action');
  // Scheduled activities for managed couple (separate from reviewer's own calendar)
  const [managedCoupleScheduledActivities, setManagedCoupleScheduledActivities] = useState<ScheduledActivity[]>([]);
  // Managed couple calendar UI state
  const [showManagedScheduleModal, setShowManagedScheduleModal] = useState(false);
  const [selectedManagedCalendarDate, setSelectedManagedCalendarDate] = useState<Date | undefined>();
  const [managedRescheduleActivityId, setManagedRescheduleActivityId] = useState<string | null>(null);
  const [managedSidebarCollapsed, setManagedSidebarCollapsed] = useState(false);
  const [coupleSubmissions, setCoupleSubmissions] = useState<CoupleSubmission[]>([]);
  const [coupleRequests, setCoupleRequests] = useState<CoupleRequest[]>([]);
  // Content request form state (reviewer -> couple)
  const [showContentRequestForm, setShowContentRequestForm] = useState(false);
  const [contentRequest, setContentRequest] = useState({ description: '', angle: '', finish: '', type: 'solo' as 'solo' | 'couple' });
  const [respondingToRequestId, setRespondingToRequestId] = useState<string | null>(null);
  const [requestResponseNote, setRequestResponseNote] = useState('');
  // Activity follow-ups for managed couple (contains detailed reflections)
  const [managedCoupleFollowUps, setManagedCoupleFollowUps] = useState<ActivityFollowUp[]>([]);
  // Submitted clips for managed couple (for suggested questions)
  const [managedCoupleSubmittedClips, setManagedCoupleSubmittedClips] = useState<any[]>([]);

  // Question confirmation popup (preview/edit before sending to couple)
  const [pendingQuestion, setPendingQuestion] = useState<{
    question: string;
    category: string;
    context?: string;
  } | null>(null);

  // Pick-for-us confirmation modal state
  const [pendingPick, setPendingPick] = useState<{
    requestId: string;
    clipId: string;
    clipTitle: string;
    whyPicked: string;
    followUpQuestion: string;
    scheduledDate: string; // ISO date string YYYY-MM-DD
  } | null>(null);

  // Tag tutorial state
  const [expandedTutorialTag, setExpandedTutorialTag] = useState<string | null>(null);

  // Previously answered Q&A toggle
  const [showPreviouslyAnswered, setShowPreviouslyAnswered] = useState(false);

  // Clip suggestion review modal state
  const [reviewingSuggestionClip, setReviewingSuggestionClip] = useState<{
    suggestionId: string;
    clipItem: { id: string; url: string; title: string; description?: string };
  } | null>(null);
  const [suggestionClipRating, setSuggestionClipRating] = useState(0);
  const [suggestionClipComment, setSuggestionClipComment] = useState('');
  const [suggestionClipInterested, setSuggestionClipInterested] = useState<boolean | null>(null);

  // Couple onboarding wizard (shown when first linking to a couple)
  const [showCoupleOnboardingWizard, setShowCoupleOnboardingWizard] = useState(false);
  const [onboardingWizardStep, setOnboardingWizardStep] = useState(0);
  const [onboardingCoupleData, setOnboardingCoupleData] = useState<CoupleData | null>(null);
  const [wizardPreviewClip, setWizardPreviewClip] = useState<Clip | null>(null);

  // Clip search state (for recommending clips to couple)
  const [clipSearchTerm, setClipSearchTerm] = useState('');
  const [clipCategoryFilter, setClipCategoryFilter] = useState('all');

  // Creator clip submission review state
  const [submissionStatusFilter, setSubmissionStatusFilter] = useState<'needs-my-review' | 'reviewed' | 'all'>('needs-my-review');
  const [submissionWelcomeDismissed, setSubmissionWelcomeDismissed] = useState(() => {
    try {
      return localStorage.getItem('vb-submissions-welcome-dismissed') === '1';
    } catch {
      return false;
    }
  });
  const [reviewingSubmission, setReviewingSubmission] = useState<SubmittedClip | null>(null);
  const [submissionReviewNotice, setSubmissionReviewNotice] = useState<{
    clipId: string;
    clipTitle: string;
    otherReviewCount: number;
    type: 'review' | 'reflection';
    requiresReflection?: boolean;
  } | null>(null);

  // Pleasure Playlist state
  const [playlistMode, setPlaylistMode] = useState(false);
  const [showPlaylistReaction, setShowPlaylistReaction] = useState(false);
  const playlistSaveTimer = useRef<ReturnType<typeof setTimeout>>();

  // Category Champions state
  const [showChampions, setShowChampions] = useState(true);
  const [viewingRankings, setViewingRankings] = useState<string | null>(null); // categoryId
  const [matchupReasonPrompt, setMatchupReasonPrompt] = useState<{ categoryId: string; winnerId: string; loserId: string } | null>(null);
  const [matchupReasonText, setMatchupReasonText] = useState('');

  // Couple setup modal state
  const [showCoupleSetupModal, setShowCoupleSetupModal] = useState(false);

  // Mode switch confirmation
  const [showModeConfirm, setShowModeConfirm] = useState(false);

  // Generate sample clips from different categories when entering Step 6
  const generateSampleClips = () => {
    const shuffledCategories = [...CATEGORIES].sort(() => Math.random() - 0.5).slice(0, 5);
    const samples: Clip[] = [];
    for (const cat of shuffledCategories) {
      const catClips = dateNiteClips.filter(c => c.categoryId === cat.id);
      if (catClips.length > 0) {
        const randomClip = catClips[Math.floor(Math.random() * catClips.length)];
        if (randomClip) {
          samples.push(randomClip);
        }
      }
    }
    setSampleClips(samples);
    setCurrentSampleIndex(0);
  };

  const rateSampleClip = (rating: 'try' | 'maybe' | 'skip') => {
    const clip = sampleClips[currentSampleIndex];
    if (!clip) return;

    const pref: CouplePreference = {
      clipId: clip.id,
      categoryId: clip.categoryId,
      clipTitle: clip.title,
      rating,
      ratedAt: now()
    };
    setCouplePreferences(prev => [...prev, pref]);

    if (currentSampleIndex < sampleClips.length - 1) {
      setCurrentSampleIndex(prev => prev + 1);
    } else {
      // Done with this round
      setCurrentSampleIndex(sampleClips.length); // Signal completion
    }
  };

  const loadMoreSamples = () => {
    generateSampleClips();
  };

  // Helper to render text with clickable links and in-app clip buttons
  // Smart parsing: detects [WATCH_CLIP:id:title] markers AND quoted clip titles
  const renderTextWithLinks = (text: string, onWatchClip?: (clipId: string) => void) => {
    // First, handle [WATCH_CLIP:id:title] markers
    const clipMarkerRegex = /\[WATCH_CLIP:([^:]+):([^\]]+)\]/g;
    const urlRegex = /(https?:\/\/[^\s]+)/g;

    // Smart parsing: find quoted clip titles that match actual clips
    const findClipByTitle = (title: string): Clip | undefined => {
      const normalizedTitle = title.toLowerCase().trim();
      return dateNiteClips.find(c => c.title.toLowerCase() === normalizedTitle);
    };

    // Check for quoted titles like "clip title" that match actual clips
    const quotedTitleRegex = /"([^"]+)"/g;

    // Replace quoted titles with markers if they match actual clips
    let processedText = text;
    let match;
    const quotedMatches: { original: string; clipId: string; title: string }[] = [];
    while ((match = quotedTitleRegex.exec(text)) !== null) {
      const quotedTitle = match[1];
      if (!quotedTitle) continue;
      const matchedClip = findClipByTitle(quotedTitle);
      if (matchedClip && !text.includes(`[WATCH_CLIP:${matchedClip.id}:`)) {
        // Don't duplicate if there's already a marker for this clip
        quotedMatches.push({ original: `"${quotedTitle}"`, clipId: matchedClip.id, title: matchedClip.title });
      }
    }

    // Add watch buttons for quoted clip titles found (append after the quote)
    for (const qm of quotedMatches) {
      // Replace the quote with a marker to be rendered as a button
      processedText = processedText.replace(
        qm.original,
        `"${qm.title}" [WATCH_CLIP:${qm.clipId}:${qm.title}]`
      );
    }

    // Split by clip markers
    const clipParts = processedText.split(clipMarkerRegex);

    return clipParts.map((part, i) => {
      // Every 3rd element starting from 1 is clipId, every 3rd from 2 is title
      if (i % 3 === 1) {
        // This is a clipId
        const clipId = part;
        const clipTitle = clipParts[i + 1] || 'Watch Clip';
        return (
          <button
            key={`clip-${i}`}
            onClick={async (e) => {
              e.stopPropagation();
              // Find the clip and track the view
              const clip = dateNiteClips.find(c => c.id === clipId);
              if (clip) {
                // Track the view
                await trackClipView(clip, 'datenite');
                // Show the clip
                if (onWatchClip) {
                  onWatchClip(clipId);
                } else {
                  setWatchingClip(clip);
                }
              }
            }}
            className="inline-flex items-center gap-1 px-3 py-1.5 bg-pink-500/20 hover:bg-pink-500/30 text-pink-400 rounded-lg text-sm font-medium transition-colors my-1"
          >
            <Play className="w-4 h-4" /> Watch: {clipTitle}
          </button>
        );
      }
      if (i % 3 === 2) {
        // This is the title part, already handled above
        return null;
      }

      // Regular text - check for URLs
      const urlParts = part.split(urlRegex);
      return urlParts.map((urlPart, j) => {
        if (urlRegex.test(urlPart)) {
          urlRegex.lastIndex = 0;
          return (
            <a
              key={`${i}-${j}`}
              href={urlPart}
              target="_blank"
              rel="noopener noreferrer"
              className="text-pink-400 hover:text-pink-300 underline break-all"
              onClick={(e) => e.stopPropagation()}
            >
              {urlPart}
            </a>
          );
        }
        return <span key={`${i}-${j}`}>{urlPart}</span>;
      });
    });
  };

  // Restore couple link from localStorage backup (if global storage lost it)
  useEffect(() => {
    const restoreCoupleLink = async () => {
      if (currentReviewer && !currentReviewer.coupleId) {
        const backupCoupleId = localStorage.getItem(`vb-reviewer-couple:${currentReviewer.id}`);
        if (backupCoupleId) {
          // Restore the couple link
          const updatedReviewer = { ...currentReviewer, coupleId: backupCoupleId };
          const updated = reviewers.map(r => r.id === currentReviewer.id ? updatedReviewer : r);
          await saveReviewers(updated);
          setCurrentReviewer(updatedReviewer);
        }
      }
    };
    restoreCoupleLink();
  }, [currentReviewer?.id]);

  // Load recommendations when reviewer has a coupleId
  useEffect(() => {
    const loadRecommendations = async () => {
      if (currentReviewer?.coupleId) {
        try {
          const recs = await d1List<CoupleRecommendation>('couple_recommendations', { coupleId: currentReviewer.coupleId });
          setRecommendations(recs);
        } catch {
          setRecommendations([]);
        }
      }
    };
    loadRecommendations();
  }, [currentReviewer?.coupleId]);

  // Load own couple data (reviewer's personal Date Night profile)
  useEffect(() => {
    const loadLinkedCoupleData = async () => {
      if (currentReviewer?.coupleId) {
        const data = await getCoupleByCode(currentReviewer.coupleId);
        setLinkedCoupleData(data);
      } else {
        setLinkedCoupleData(null);
      }
    };
    loadLinkedCoupleData();
  }, [currentReviewer?.coupleId]);

  // Load managed couple data (external couple this reviewer coaches - separate from own profile)
  useEffect(() => {
    const loadManagedCoupleData = async () => {
      if (currentReviewer?.managedCoupleId) {
        const data = await getCoupleByCode(currentReviewer.managedCoupleId);
        setManagedCoupleData(data);
        // Load submitted clips for managed couple with reviews attached
        const managedClips = await d1List<SubmittedClip>('submitted_clips', { creatorId: currentReviewer.managedCoupleId });
        const allSubReviews = await d1List<SubmittedClipReview & { submittedClipId: string }>('submitted_clip_reviews');
        const clipIds = new Set(managedClips.map(c => c.id));
        const relevantReviews = allSubReviews.filter(r => clipIds.has(r.submittedClipId));
        const reviewsByClip = new Map<string, SubmittedClipReview[]>();
        for (const r of relevantReviews) {
          reviewsByClip.set(r.submittedClipId, [...(reviewsByClip.get(r.submittedClipId) || []), r]);
        }
        const clipsWithReviews = managedClips.map(c => ({ ...c, reviews: reviewsByClip.get(c.id) || [] }));
        setManagedCoupleSubmittedClips(clipsWithReviews as any[]);
      } else {
        setManagedCoupleData(null);
        setManagedCoupleSubmittedClips([]);
      }
    };
    loadManagedCoupleData();
  }, [currentReviewer?.managedCoupleId]);

  // Load activity follow-ups for managed couple (detailed reflections on tried clips)
  useEffect(() => {
    const loadManagedCoupleFollowUps = async () => {
      if (currentReviewer?.managedCoupleId) {
        try {
          const followUps = await d1List<ActivityFollowUp>('activity_followups', { coupleId: currentReviewer.managedCoupleId });
          // Only include completed follow-ups with reflections
          setManagedCoupleFollowUps(followUps.filter(f => f.didComplete && (f.hisReflection || f.herReflection)));
        } catch {
          setManagedCoupleFollowUps([]);
        }
      } else {
        setManagedCoupleFollowUps([]);
      }
    };
    loadManagedCoupleFollowUps();
  }, [currentReviewer?.managedCoupleId, managedCoupleTab]);

  // Load managed couple's scheduled activities (for their calendar view)
  useEffect(() => {
    const loadManagedCoupleSchedule = async () => {
      if (currentReviewer?.managedCoupleId) {
        try {
          const activities = await d1List<ScheduledActivity>('scheduled_activities', { coupleId: currentReviewer.managedCoupleId });
          setManagedCoupleScheduledActivities(activities);
        } catch {
          setManagedCoupleScheduledActivities([]);
        }
      } else {
        setManagedCoupleScheduledActivities([]);
      }
    };
    loadManagedCoupleSchedule();
  }, [currentReviewer?.managedCoupleId, managedCoupleTab]);

  // Load couple submissions (from managed couple, not own profile)
  useEffect(() => {
    const loadCoupleSubmissions = async () => {
      if (currentReviewer?.managedCoupleId) {
        try {
          const subs = await d1List<CoupleSubmission>('couple_submissions', { coupleId: currentReviewer.managedCoupleId });
          setCoupleSubmissions(subs);
        } catch {
          setCoupleSubmissions([]);
        }
      } else {
        setCoupleSubmissions([]);
      }
    };
    loadCoupleSubmissions();
  }, [currentReviewer?.managedCoupleId, tab]);

  // Mark submission as reviewed (per-reviewer tracking)
  const markSubmissionReviewed = async (submissionId: string) => {
    if (!currentReviewer?.managedCoupleId) return;
    const submission = coupleSubmissions.find(s => s.id === submissionId);
    if (!submission) return;
    const existingIds = submission.reviewedByIds || (submission.reviewedBy ? [submission.reviewedBy] : []);
    const reviewedByIds = existingIds.includes(currentReviewer.id) ? existingIds : [...existingIds, currentReviewer.id];
    const updatedItem = { ...submission, status: 'reviewed' as const, reviewedAt: now(), reviewedBy: currentReviewer.id, reviewedByIds };
    await d1Upsert('couple_submissions', updatedItem);
    setCoupleSubmissions(coupleSubmissions.map(s => s.id === submissionId ? updatedItem : s));
  };

  // Load couple requests (from managed couple, not own profile)
  useEffect(() => {
    const loadCoupleRequests = async () => {
      if (currentReviewer?.managedCoupleId) {
        try {
          const reqs = await d1List<CoupleRequest>('couple_requests', { coupleId: currentReviewer.managedCoupleId });
          setCoupleRequests(reqs);
        } catch {
          setCoupleRequests([]);
        }
      } else {
        setCoupleRequests([]);
      }
    };
    loadCoupleRequests();
  }, [currentReviewer?.managedCoupleId, tab]);

  // Helper: check if a review belongs to the current reviewer (handles id vs code mismatch)
  const isMyReview = (reviewerId: string) =>
    reviewerId === currentReviewer?.id || reviewerId === currentReviewer?.code;

  const getSubmissionReviews = (submission: SubmittedClip): SubmittedClipReview[] => {
    const reviews = [...(submission.reviews || [])];
    if (submission.review && !reviews.some(r => r.reviewerId === submission.review!.reviewerId)) {
      reviews.push(submission.review);
    }
    return reviews;
  };

  const getMySubmissionReview = (submission: SubmittedClip) =>
    getSubmissionReviews(submission).find(r => isMyReview(r.reviewerId));

  const getOtherSubmissionReviews = (submission: SubmittedClip) =>
    getSubmissionReviews(submission).filter(r => !isMyReview(r.reviewerId));

  const hasMySubmissionReview = (submission: SubmittedClip) =>
    !!getMySubmissionReview(submission);

  // No review yet, or a draft saved without a verdict
  const needsMySubmissionReview = (submission: SubmittedClip) =>
    !isDecided(submission) && (!hasMySubmissionReview(submission) || !getMySubmissionReview(submission)?.verdict);

  const needsMySubmissionReflection = (submission: SubmittedClip) => {
    const myReview = getMySubmissionReview(submission);
    return !!myReview?.verdict
      && !myReview.postRevealChangedMind
      && getOtherSubmissionReviews(submission).length > 0
      && !isDecided(submission);
  };

  const needsMySubmissionAction = (submission: SubmittedClip) =>
    needsMySubmissionReview(submission) || needsMySubmissionReflection(submission);

  // Mark request as fulfilled (with optional response note)
  const markRequestFulfilled = async (requestId: string, responseNote?: string) => {
    if (!currentReviewer?.managedCoupleId) return;
    const request = coupleRequests.find(r => r.id === requestId);
    if (!request) return;
    const updatedItem = { ...request, status: 'fulfilled' as const, fulfilledAt: now(), fulfilledBy: currentReviewer.id, ...(responseNote && { responseNote }) };
    await d1Upsert('couple_requests', updatedItem);
    setCoupleRequests(coupleRequests.map(r => r.id === requestId ? updatedItem : r));
  };

  // Decline a content request (with optional response note)
  const declineRequest = async (requestId: string, responseNote?: string) => {
    if (!currentReviewer?.managedCoupleId) return;
    const request = coupleRequests.find(r => r.id === requestId);
    if (!request) return;
    const updatedItem = { ...request, status: 'declined' as const, fulfilledAt: now(), fulfilledBy: currentReviewer.id, ...(responseNote && { responseNote }) };
    await d1Upsert('couple_requests', updatedItem);
    setCoupleRequests(coupleRequests.map(r => r.id === requestId ? updatedItem : r));
  };

  // Load scheduled activities when reviewer logs in (now keyed by coupleId)
  useEffect(() => {
    const loadScheduledActivities = async () => {
      if (currentReviewer?.coupleId) {
        try {
          const activities = await d1List<ScheduledActivity>('scheduled_activities', { coupleId: currentReviewer.coupleId });
          setScheduledActivities(activities);
        } catch {
          setScheduledActivities([]);
        }
      } else {
        setScheduledActivities([]);
      }
    };
    loadScheduledActivities();
  }, [currentReviewer?.coupleId, currentReviewer?.id]);

  // Save scheduled activities (keyed by coupleId for shared access)
  const saveScheduledActivities = async (activities: ScheduledActivity[]) => {
    if (!currentReviewer?.coupleId) return;
    const activitiesWithCoupleId = activities.map(a => ({
      ...a,
      coupleId: a.coupleId || currentReviewer.coupleId,
    }));
    const prevIds = new Set(scheduledActivities.map(a => a.id));
    const nextIds = new Set(activitiesWithCoupleId.map(a => a.id));
    for (const a of activitiesWithCoupleId) {
      const prev = scheduledActivities.find(p => p.id === a.id);
      if (!prev || JSON.stringify(prev) !== JSON.stringify(a)) {
        await d1Upsert('scheduled_activities', a);
      }
    }
    for (const id of prevIds) {
      if (!nextIds.has(id)) {
        await d1Delete('scheduled_activities', id);
      }
    }
    setScheduledActivities(activitiesWithCoupleId);
  };

  // Get context for activity creation
  const getReviewerContext = (): ActivityActor => ({
    type: 'reviewer',
    id: currentReviewer?.id || '',
    name: currentReviewer?.partnerName ? `Reviewer (${currentReviewer.partnerName})` : 'Reviewer',
  });

  // Search clips for recommendation
  const searchedClips = dateNiteClips.filter(clip => {
    // Category filter
    if (clipCategoryFilter !== 'all' && clip.categoryId !== clipCategoryFilter) return false;
    // Search term filter
    if (clipSearchTerm.trim()) {
      const term = clipSearchTerm.toLowerCase();
      const matchesTitle = clip.title.toLowerCase().includes(term);
      const matchesDescription = clip.description?.toLowerCase().includes(term);
      const matchesTags = clip.tags?.some(t => t.toLowerCase().includes(term));
      if (!matchesTitle && !matchesDescription && !matchesTags) return false;
    }
    return true;
  });

  // Handle confirming a pick-for-us selection
  const handleConfirmPick = async () => {
    if (!pendingPick || !managedCoupleData?.coupleId || !currentReviewer || !pendingPick.scheduledDate) return;

    // 1. Create the scheduled activity for the managed couple
    const newActivity: ScheduledActivity = {
      id: generateId('sched'),
      coupleId: managedCoupleData.coupleId,
      clipId: pendingPick.clipId,
      scheduledDate: pendingPick.scheduledDate,
      createdAt: now(),
      createdBy: {
        type: 'reviewer',
        id: currentReviewer.id,
        name: currentReviewer.name
      },
      notes: pendingPick.whyPicked || undefined
    };

    // Save activity to the managed couple's schedule
    await d1Upsert('scheduled_activities', newActivity);

    // 2. Update the pick request status with activity link
    const updatedRequests = pickForUsRequests.map(r =>
      r.id === pendingPick.requestId
        ? {
            ...r,
            status: 'scheduled' as const,
            pickedClipId: pendingPick.clipId,
            pickedAt: now(),
            reviewerNote: pendingPick.whyPicked || undefined,
            scheduledActivityId: newActivity.id
          }
        : r
    );
    await savePickForUsRequests(updatedRequests);

    // 3. Send chat message to notify couple
    // Format the date nicely
    const schedDate = new Date(pendingPick.scheduledDate + 'T12:00:00');
    const dateStr = schedDate.toLocaleDateString('en-US', { weekday: 'long', month: 'short', day: 'numeric' });

    // Build the message
    let messageText = `🎯 I picked "${pendingPick.clipTitle}" from your suggestions!\n\n📅 Scheduled for ${dateStr}`;
    if (pendingPick.whyPicked) {
      messageText += `\n\n💭 ${pendingPick.whyPicked}`;
    }
    if (pendingPick.followUpQuestion) {
      messageText += `\n\n❓ After you try it: ${pendingPick.followUpQuestion}`;
    }

    const newMessage: CoupleMessage = {
      id: generateId('msg'),
      coupleId: managedCoupleData.coupleId,
      fromType: 'system',
      fromName: currentReviewer.name || 'Your Reviewer',
      message: messageText,
      createdAt: now(),
      seenByCouple: false,
      seenByReviewer: true,
      clipId: pendingPick.clipId,
      systemType: 'pick-for-us'
    };

    await d1Upsert('couple_messages', newMessage);

    // 4. Close modal
    setPendingPick(null);
  };

  // Handle scheduling a new activity
  const handleScheduleActivity = async (activity: ScheduledActivity) => {
    // Add createdBy context if not already set
    const activityWithCreator: ScheduledActivity = {
      ...activity,
      coupleId: activity.coupleId || currentReviewer?.coupleId,
      createdBy: activity.createdBy || getReviewerContext(),
    };
    // If rescheduling, remove the old activity first
    let baseActivities = scheduledActivities;
    if (rescheduleActivityId) {
      baseActivities = scheduledActivities.filter(a => a.id !== rescheduleActivityId);
      setRescheduleActivityId(null);
    }
    const updated = [...baseActivities, activityWithCreator];
    await saveScheduledActivities(updated);
    setShowScheduleModal(false);
    setSelectedCalendarDate(undefined);
  };

  // --- Managed couple calendar handlers ---
  const saveManagedCoupleScheduledActivities = async (activities: ScheduledActivity[]) => {
    if (!managedCoupleData?.coupleId) return;
    const activitiesWithCoupleId = activities.map(a => ({
      ...a,
      coupleId: a.coupleId || managedCoupleData.coupleId,
    }));
    const prevIds = new Set(managedCoupleScheduledActivities.map(a => a.id));
    const nextIds = new Set(activitiesWithCoupleId.map(a => a.id));
    for (const a of activitiesWithCoupleId) {
      const prev = managedCoupleScheduledActivities.find(p => p.id === a.id);
      if (!prev || JSON.stringify(prev) !== JSON.stringify(a)) {
        await d1Upsert('scheduled_activities', a);
      }
    }
    for (const id of prevIds) {
      if (!nextIds.has(id)) {
        await d1Delete('scheduled_activities', id);
      }
    }
    setManagedCoupleScheduledActivities(activitiesWithCoupleId);
  };

  const handleManagedScheduleActivity = async (activity: ScheduledActivity) => {
    const activityWithCreator: ScheduledActivity = {
      ...activity,
      coupleId: activity.coupleId || managedCoupleData?.coupleId,
      createdBy: activity.createdBy || getReviewerContext(),
    };
    let baseActivities = managedCoupleScheduledActivities;
    if (managedRescheduleActivityId) {
      baseActivities = managedCoupleScheduledActivities.filter(a => a.id !== managedRescheduleActivityId);
      setManagedRescheduleActivityId(null);
    }
    await saveManagedCoupleScheduledActivities([...baseActivities, activityWithCreator]);
    setShowManagedScheduleModal(false);
    setSelectedManagedCalendarDate(undefined);
  };

  const handleManagedMarkComplete = async (activityId: string, note?: string, userClipUrl?: string) => {
    const updated = managedCoupleScheduledActivities.map(a =>
      a.id === activityId ? { ...a, completedAt: now(), notes: note || a.notes, userClipUrl: userClipUrl || a.userClipUrl, modifiedBy: getReviewerContext(), modifiedAt: now() } : a
    );
    await saveManagedCoupleScheduledActivities(updated);
  };

  const handleManagedDeleteActivity = async (activityId: string) => {
    await saveManagedCoupleScheduledActivities(managedCoupleScheduledActivities.filter(a => a.id !== activityId));
  };

  const handleManagedRescheduleActivity = (activityId: string) => {
    setManagedRescheduleActivityId(activityId);
    setShowManagedScheduleModal(true);
  };

  const handleManagedDragStart = (event: DragStartEvent) => {
    const { active } = event;
    if (active.data.current?.type === 'clip') {
      setActiveClip(active.data.current.clip);
      setActiveActivity(null);
    } else if (active.data.current?.type === 'activity') {
      setActiveActivity({ activity: active.data.current.activity, clip: active.data.current.clip });
      setActiveClip(null);
    }
  };

  const handleManagedDragEnd = async (event: DragEndEvent) => {
    const { active, over } = event;
    setActiveClip(null);
    setActiveActivity(null);
    if (!over) return;
    if (over.data.current?.type === 'calendar-cell') {
      const targetDate = over.data.current.date as string;
      if (!targetDate) return;
      if (active.data.current?.type === 'clip') {
        const clipId = active.id as string;
        const activity: ScheduledActivity = {
          id: generateId('sched'),
          clipId,
          scheduledDate: targetDate,
          createdAt: now(),
          createdBy: getReviewerContext(),
        };
        await saveManagedCoupleScheduledActivities([...managedCoupleScheduledActivities, activity]);
      } else if (active.data.current?.type === 'activity') {
        const activityId = active.data.current.activity.id;
        const currentActivity = managedCoupleScheduledActivities.find(a => a.id === activityId);
        if (currentActivity && currentActivity.scheduledDate !== targetDate) {
          const updated = managedCoupleScheduledActivities.map(a =>
            a.id === activityId ? { ...a, scheduledDate: targetDate, modifiedBy: getReviewerContext(), modifiedAt: now() } : a
          );
          await saveManagedCoupleScheduledActivities(updated);
        }
      }
    }
  };

  // Handle marking activity as complete with optional note and user clip URL
  const handleMarkActivityComplete = async (activityId: string, note?: string, userClipUrl?: string) => {
    const updated = scheduledActivities.map(a =>
      a.id === activityId ? {
        ...a,
        completedAt: now(),
        notes: note || a.notes, // Add or keep existing note
        userClipUrl: userClipUrl || a.userClipUrl, // Add user-submitted clip URL
        modifiedBy: getReviewerContext(),
        modifiedAt: now()
      } : a
    );
    await saveScheduledActivities(updated);

    // Also add to homework history
    const activity = scheduledActivities.find(a => a.id === activityId);
    if (activity && currentReviewer) {
      const historyEntry = {
        clipId: activity.clipId,
        assignedAt: activity.scheduledDate,
        completedAt: now(),
        rating: undefined,
        feedback: undefined,
      };
      const updatedReviewer = {
        ...currentReviewer,
        homeworkHistory: [...(currentReviewer.homeworkHistory || []), historyEntry],
      };
      const updated = reviewers.map(r => r.id === currentReviewer.id ? updatedReviewer : r);
      await saveReviewers(updated);
      setCurrentReviewer(updatedReviewer);
    }
  };

  // Handle deleting activity
  const handleDeleteActivity = async (activityId: string) => {
    const updated = scheduledActivities.filter(a => a.id !== activityId);
    await saveScheduledActivities(updated);
  };

  // Handle reschedule
  const handleRescheduleActivity = (activityId: string) => {
    setRescheduleActivityId(activityId);
    setShowScheduleModal(true);
  };

  // Handle saving submission review — directly upsert to D1 (no more array sync)
  const handleSaveSubmissionReview = async (updatedSubmission: SubmittedClip): Promise<SubmittedClip | null> => {
    try {
      // 1. Update the submission row from its latest saved state, so a stale local copy can't
      //    undo the admin's decision or library link. Reviewers only own gender, products
      //    taking and their own questions; a first review moves Submitted → Under Review.
      const { reviews } = updatedSubmission;
      const local = submittedClips.find(s => s.id === updatedSubmission.id);
      const latest = await d1Get<SubmittedClip>('submitted_clips', updatedSubmission.id).catch(() => null);
      const base: SubmittedClip = latest ?? { ...updatedSubmission, review: local?.review, reviews: undefined };
      const submissionRow: SubmittedClip = {
        ...base,
        gender: updatedSubmission.gender,
        productsTaking: updatedSubmission.productsTaking,
        reviewerQuestions: [
          ...(base.reviewerQuestions || []).filter(q => !isMyReview(q.reviewerId)),
          ...(updatedSubmission.reviewerQuestions || []).filter(q => isMyReview(q.reviewerId)),
        ],
        status: base.status === 'Submitted' ? 'Under Review' : base.status,
      };
      const { reviews: _unusedReviews, ...submissionFields } = submissionRow;
      await d1Upsert('submitted_clips', submissionFields as Record<string, unknown>);

      // 2. Upsert this reviewer's review directly to submitted_clip_reviews
      const myReview = (reviews || []).find(r => isMyReview(r.reviewerId));
      if (myReview) {
        // Delete any existing review by this reviewer for this clip, then insert
        const existing = await d1List<{ id: number }>('submitted_clip_reviews', {
          submittedClipId: updatedSubmission.id,
          reviewerId: myReview.reviewerId,
        });
        for (const e of existing) {
          await d1Delete('submitted_clip_reviews', e.id);
        }
        await d1Upsert('submitted_clip_reviews', {
          ...myReview,
          submittedClipId: updatedSubmission.id,
        });
      }

      // Couple uploads also live in couple_submissions (same id); that's what the couple sees
      if (myReview?.verdict && currentReviewer && (updatedSubmission.gender === 'couple' || updatedSubmission.creatorId.startsWith('VBC-'))) {
        try {
          const coupleCopy = await d1Get<CoupleSubmission>('couple_submissions', updatedSubmission.id);
          if (coupleCopy) {
            const ids = coupleCopy.reviewedByIds || (coupleCopy.reviewedBy ? [coupleCopy.reviewedBy] : []);
            await d1Upsert('couple_submissions', {
              ...coupleCopy,
              status: coupleCopy.status === 'pending' ? 'reviewed' : coupleCopy.status,
              reviewedAt: now(),
              reviewedBy: currentReviewer.id,
              reviewedByIds: ids.includes(currentReviewer.id) ? ids : [...ids, currentReviewer.id],
            });
          }
        } catch (err) {
          console.error('Failed to update couple submission status:', err);
        }
      }

      // 3. Update local state — refresh from D1 to get reviews attached
      const allReviews = await d1List<SubmittedClipReview & { submittedClipId: string }>('submitted_clip_reviews', { submittedClipId: updatedSubmission.id });
      const fullSubmission = { ...submissionRow, reviews: allReviews };
      const savedReview = (reviews || []).find(r => isMyReview(r.reviewerId));
      const isReflectionSave = !!savedReview?.postRevealChangedMind;
      const otherReviewCount = allReviews.filter(r => !isMyReview(r.reviewerId)).length;
      // Drafts (no verdict yet) get no notice; the card itself says "Add your verdict"
      if (savedReview?.verdict) {
        setSubmissionReviewNotice({
          clipId: updatedSubmission.id,
          clipTitle: updatedSubmission.clipTitle,
          otherReviewCount,
          type: isReflectionSave ? 'reflection' : 'review',
          requiresReflection: !isReflectionSave && otherReviewCount > 0,
        });
      }
      // Update via saveSubmittedClips to sync parent state in App.tsx
      const updatedList = submittedClips.map(s => s.id === updatedSubmission.id ? fullSubmission : s);
      await saveSubmittedClips(updatedList);
      // Also update managed couple clips if applicable
      if (updatedSubmission.creatorId === currentReviewer?.managedCoupleId) {
        setManagedCoupleSubmittedClips(prev => prev.map((s: any) => s.id === updatedSubmission.id ? fullSubmission : s));
      }
      setReviewingSubmission(fullSubmission);
      return fullSubmission;
    } catch (err) {
      console.error('Failed to save submission review:', err);
      return null;
    }
  };

  // DnD sensors configuration
  const sensors = useSensors(
    useSensor(MouseSensor, {
      activationConstraint: { distance: 10 },
    }),
    useSensor(TouchSensor, {
      activationConstraint: { delay: 200, tolerance: 5 },
    }),
    useSensor(KeyboardSensor)
  );

  // Handle drag start
  const handleDragStart = (event: DragStartEvent) => {
    const { active } = event;
    if (active.data.current?.type === 'clip') {
      setActiveClip(active.data.current.clip);
      setActiveActivity(null);
    } else if (active.data.current?.type === 'activity') {
      setActiveActivity({
        activity: active.data.current.activity,
        clip: active.data.current.clip,
      });
      setActiveClip(null);
    }
  };

  // Handle drag end - create activity on valid drop or reschedule existing
  const handleDragEnd = async (event: DragEndEvent) => {
    const { active, over } = event;
    setActiveClip(null);
    setActiveActivity(null);

    if (!over) return; // Dropped outside

    // Check if dropped on a calendar cell
    if (over.data.current?.type === 'calendar-cell') {
      const targetDate = over.data.current.date as string;
      if (!targetDate) return;

      // Check if dragging a clip (new activity) or an existing activity (reschedule)
      if (active.data.current?.type === 'clip') {
        // Creating new activity from clip
        const clipId = active.id as string;
        const activity: ScheduledActivity = {
          id: generateId('sched'),
          clipId,
          scheduledDate: targetDate,
          createdAt: now(),
          createdBy: getReviewerContext(),
        };
        const updated = [...scheduledActivities, activity];
        await saveScheduledActivities(updated);
      } else if (active.data.current?.type === 'activity') {
        // Rescheduling existing activity
        const activityId = active.data.current.activity.id;
        const currentActivity = scheduledActivities.find(a => a.id === activityId);

        // Don't update if dropped on the same date
        if (currentActivity && currentActivity.scheduledDate !== targetDate) {
          const updated = scheduledActivities.map(a =>
            a.id === activityId
              ? {
                  ...a,
                  scheduledDate: targetDate,
                  modifiedBy: getReviewerContext(),
                  modifiedAt: now(),
                }
              : a
          );
          await saveScheduledActivities(updated);
        }
      }
    }
  };

  const copyCode = async (codeToCopy: string) => {
    await navigator.clipboard.writeText(codeToCopy);
    setCodeCopied(true);
    setTimeout(() => setCodeCopied(false), 2000);
  };

  const CUM_LEVELS = [
    { value: 1, emoji: '💧', label: 'Drip', desc: 'Just a taste', color: 'from-blue-400 to-blue-500' },
    { value: 2, emoji: '💦', label: 'Splash', desc: 'Getting wet!', color: 'from-cyan-400 to-blue-500' },
    { value: 3, emoji: '🚿', label: 'Firehose', desc: 'Steady stream!', color: 'from-teal-400 to-cyan-500' },
    { value: 4, emoji: '🌊', label: 'Flood', desc: 'Holy moly!', color: 'from-emerald-400 to-teal-500' },
    { value: 5, emoji: '🌊', label: 'TSUNAMI', desc: 'LEGENDARY!!!', color: 'from-orange-400 to-red-500' },
  ];

  // Female-focused categories
  const FEMALE_CATEGORY_IDS = ['female-masturbation', 'cunnilingus', 'fingering', 'her-pov', 'fpov-bj'];

  const WETNESS_LEVELS = [
    { value: 1, emoji: '💧', label: 'Light', desc: 'Light moisture', color: 'from-pink-300 to-pink-400' },
    { value: 2, emoji: '💦', label: 'Wet', desc: 'Noticeably wet', color: 'from-pink-400 to-rose-400' },
    { value: 3, emoji: '🌊', label: 'Very Wet', desc: 'Very wet!', color: 'from-rose-400 to-pink-500' },
    { value: 4, emoji: '🫧', label: 'Dripping', desc: 'Dripping wet!', color: 'from-fuchsia-400 to-pink-500' },
    { value: 5, emoji: '🌧️', label: 'Soaking', desc: 'Soaking wet!!!', color: 'from-purple-400 to-fuchsia-500' },
  ];

  const SQUIRT_LEVELS = [
    { value: 0, emoji: '🚫', label: 'None', desc: 'No squirting', color: 'from-zinc-500 to-zinc-600' },
    { value: 1, emoji: '💧', label: 'Light', desc: 'Light squirt', color: 'from-violet-300 to-violet-400' },
    { value: 2, emoji: '💦', label: 'Medium', desc: 'Nice squirt', color: 'from-violet-400 to-purple-400' },
    { value: 3, emoji: '🚿', label: 'Strong', desc: 'Strong squirt!', color: 'from-purple-400 to-violet-500' },
    { value: 4, emoji: '🌊', label: 'Fountain', desc: 'Fountain!', color: 'from-fuchsia-400 to-purple-500' },
    { value: 5, emoji: '💥', label: 'Gushing', desc: 'GUSHING!!!', color: 'from-pink-400 to-fuchsia-500' },
  ];

  // Helper booleans for gender-specific questions
  const isFemale = form.gender === 'Female';
  const isMale = form.gender === 'Male';
  const watchesContent = form.watchesAdultContent === 'Yes regularly' || form.watchesAdultContent === 'Sometimes';

  const register = async () => {
    const newCode = generateReviewerCode();
    const partnerDisplayName = useCodename ? selectedCodename : form.partnerName.trim();
    const newCoupleCode = partnerDisplayName ? generateCoupleCode() : undefined;

    // If they selected a clip to try, set it as their first assignment
    const firstClipId = selectedToTry[0];
    const firstChallenge = firstClipId ? {
      clipId: firstClipId,
      assignedAt: now()
    } : undefined;

    // Build reviewer object, spreading form first then overriding specific fields
    const reviewer: Reviewer = {
      ...form,
      id: generateId('Reviewer'),
      code: newCode,
      registeredAt: now(),
      // Couple integration
      coupleId: newCoupleCode,
      partnerName: partnerDisplayName || undefined,
      couplePreferences: couplePreferences.length > 0 ? couplePreferences : undefined,
      tryAtHome: firstChallenge,
      homeworkHistory: [],
      // Map to legacy field for backwards compatibility
      watchesContent: form.watchesAdultContent === 'Yes regularly' ? 'Regularly'
        : form.watchesAdultContent === 'Sometimes' ? 'Sometimes'
        : 'Rarely'
    };
    await saveReviewers([reviewer, ...reviewers]);
    setCurrentReviewer(reviewer);
    setCode(newCode);
    setCoupleCode(newCoupleCode || '');
    setShowCode(true);
  };

  const togglePick = async (clip: Clip) => {
    if (!currentReviewer) return;
    const isPicked = clip.picks?.includes(currentReviewer.id);
    const updated = dateNiteClips.map(c => c.id === clip.id ? {
      ...c,
      picks: isPicked
        ? (c.picks || []).filter(p => p !== currentReviewer.id)
        : [...(c.picks || []), currentReviewer.id]
    } : c);
    await saveDateNiteClips(updated);
  };

  const assignTryAtHome = async (clip: Clip) => {
    if (!currentReviewer) return;
    const updated = reviewers.map(r => r.id === currentReviewer.id ? {
      ...r,
      tryAtHome: { clipId: clip.id, assignedAt: now() }
    } : r);
    await saveReviewers(updated);
    setCurrentReviewer({ ...currentReviewer, tryAtHome: { clipId: clip.id, assignedAt: now() } });
  };

  const completeTryAtHome = async () => {
    if (!currentReviewer || !currentReviewer.tryAtHome) return;

    // Create homework history entry with media capture data
    const historyEntry = {
      clipId: currentReviewer.tryAtHome.clipId,
      assignedAt: currentReviewer.tryAtHome.assignedAt,
      completedAt: now(),
      rating: tryAtHomeFeedback.rating,
      feedback: tryAtHomeFeedback.feedback,
      tookMedia: tryAtHomeFeedback.tookMedia || undefined,
      willingToShare: tryAtHomeFeedback.willingToShare || undefined,
      mediaUrl: tryAtHomeFeedback.mediaUrl || undefined,
      comparisonToClip: tryAtHomeFeedback.comparisonToClip || undefined
    };

    const updated = reviewers.map(r => r.id === currentReviewer.id ? {
      ...r,
      tryAtHome: undefined,
      homeworkHistory: [...(r.homeworkHistory || []), historyEntry]
    } : r);
    await saveReviewers(updated);
    setCurrentReviewer({
      ...currentReviewer,
      tryAtHome: undefined,
      homeworkHistory: [...(currentReviewer.homeworkHistory || []), historyEntry]
    });
    setShowTryAtHomeFeedback(false);
    setTryAtHomeFeedback({ rating: '', feedback: '', tookMedia: '', willingToShare: '', mediaUrl: '', comparisonToClip: '' });
  };

  const tryAtHomeClip = currentReviewer?.tryAtHome
    ? dateNiteClips.find(c => c.id === currentReviewer.tryAtHome?.clipId)
    : null;

  const submitClipReview = async () => {
    if (!reviewingClip || !clipReview.rating || !currentReviewer) return;
    const isFemaleCategory = FEMALE_CATEGORY_IDS.includes(reviewingClip.categoryId);
    const review = {
      reviewerId: currentReviewer.id,
      rating: clipReview.rating,
      comment: clipReview.comment,
      date: now(),
      // Include appropriate metrics based on category type
      ...(isFemaleCategory ? {
        wetnessLevel: clipReview.wetnessLevel,
        squirtLevel: clipReview.squirtLevel
      } : {
        cumLevel: clipReview.cumLevel,
        cumConsistency: clipReview.cumConsistency
      })
    };
    const updated = dateNiteClips.map(c => {
      if (c.id !== reviewingClip.id) return c;
      // Merge existing tags with new suggestions (no duplicates)
      const existingTags = c.tags || [];
      const newTags = clipReview.suggestedTags.filter(t => !existingTags.includes(t));
      return {
        ...c,
        reviews: [...(c.reviews || []), review],
        tags: [...existingTags, ...newTags]
      };
    });
    await saveDateNiteClips(updated);
    setReviewingClip(null);
    setClipReview({ rating: '', comment: '', cumLevel: 3, cumConsistency: 3, wetnessLevel: 3, squirtLevel: 0, suggestedTags: [], dateNightNotes: '' });
  };

  // Save clip edits - creates a suggestion for admin approval instead of direct save
  const saveClipEdits = async () => {
    if (!reviewingClip || !currentReviewer) return;

    // Check for existing pending suggestion from this reviewer for this clip
    const existingPending = suggestedEdits.find(
      e => e.clipId === reviewingClip.id &&
           e.reviewerId === currentReviewer.id &&
           e.status === 'pending'
    );
    if (existingPending) {
      alert('You already have a pending edit suggestion for this clip. Please wait for it to be reviewed.');
      return;
    }

    // Validate URL if changed
    if (editingClipData.link && editingClipData.link !== reviewingClip.link) {
      const urlError = validateClipUrl(editingClipData.link);
      if (urlError) {
        setNewClipUrlError(urlError);
        return;
      }
    }

    const normalizedLink = editingClipData.link ? normalizeRedGifsUrl(editingClipData.link) : reviewingClip.link;

    // Split categories into primary + additional
    const [primaryCategory, ...additionalCategories] = editingClipData.categoryIds;

    // Build suggested changes - only include fields that changed
    const suggestedChanges: SuggestedEdit['suggestedChanges'] = {};
    if (editingClipData.title !== reviewingClip.title) suggestedChanges.title = editingClipData.title;
    if (editingClipData.description !== (reviewingClip.description || '')) suggestedChanges.description = editingClipData.description;
    if (JSON.stringify(editingClipData.tags) !== JSON.stringify(reviewingClip.tags || [])) suggestedChanges.tags = editingClipData.tags;
    if (primaryCategory !== reviewingClip.categoryId) suggestedChanges.categoryId = primaryCategory;
    if (JSON.stringify(additionalCategories) !== JSON.stringify(reviewingClip.additionalCategoryIds || [])) {
      suggestedChanges.additionalCategoryIds = additionalCategories.length > 0 ? additionalCategories : undefined;
    }
    if (normalizedLink !== reviewingClip.link) suggestedChanges.link = normalizedLink;

    // If no changes, just close
    if (Object.keys(suggestedChanges).length === 0) {
      setIsEditingClip(false);
      setNewClipUrlError(null);
      return;
    }

    // Create the suggestion
    const suggestion: SuggestedEdit = {
      id: generateId('sug'),
      clipId: reviewingClip.id,
      reviewerId: currentReviewer.id,
      reviewerName: currentReviewer.code,
      submittedAt: now(),
      status: 'pending',
      suggestedChanges,
      ...(editingClipData.reason.trim() && { reason: editingClipData.reason.trim() }),
    };

    await saveSuggestedEdits([...suggestedEdits, suggestion]);
    setIsEditingClip(false);
    setNewClipUrlError(null);
    alert('Edit suggestion submitted for review!');
  };

  // === PLEASURE PLAYLIST HELPERS ===

  // Generate playlist from reviewer's highest-rated clips
  const generatePlaylist = () => {
    if (!currentReviewer) return;
    const myReviews: { clip: Clip; rating: string }[] = [];
    for (const clip of dateNiteClips) {
      const review = clip.reviews?.find(r => isMyReview(r.reviewerId));
      if (review) myReviews.push({ clip, rating: review.rating });
    }

    // Split by rating tier
    const mustTry = myReviews.filter(r => r.rating === '🔥 Must Try');
    const good = myReviews.filter(r => r.rating === '👍 Good');

    // Shuffle within each tier
    const shuffle = (arr: { clip: Clip; rating: string }[]) => {
      const a = [...arr];
      for (let i = a.length - 1; i > 0; i--) {
        const j = Math.floor(Math.random() * (i + 1));
        [a[i], a[j]] = [a[j]!, a[i]!];
      }
      return a;
    };

    const orderedClips = [...shuffle(mustTry), ...shuffle(good)].map(r => r.clip.id);

    const updatedReviewer = {
      ...currentReviewer,
      pleasurePlaylist: {
        clips: orderedClips,
        enjoyed: {},
        skipped: [],
        savedForLater: [],
        generatedAt: now(),
      },
    };
    setCurrentReviewer(updatedReviewer);
    const updatedReviewers = reviewers.map(r => r.id === currentReviewer.id ? updatedReviewer : r);
    saveReviewers(updatedReviewers);
    setPlaylistMode(true);
  };

  // Get valid playlist clips (filter out deleted clips)
  const getPlaylistClips = () => {
    const playlist = currentReviewer?.pleasurePlaylist;
    if (!playlist) return [];
    return (playlist.clips || []).filter(id => dateNiteClips.some(c => c.id === id));
  };

  // Get next unplayed clip index in playlist
  const getNextPlaylistIndex = (validClips: string[], playlist: Exclude<Reviewer['pleasurePlaylist'], undefined>) => {
    return validClips.findIndex(id =>
      !playlist.enjoyed[id] && !playlist.skipped.includes(id)
    );
  };

  // Save playlist action (debounced)
  const savePlaylistAction = (updatedPlaylist: Exclude<Reviewer['pleasurePlaylist'], undefined>) => {
    if (!currentReviewer) return;
    const updatedReviewer = { ...currentReviewer, pleasurePlaylist: updatedPlaylist };
    setCurrentReviewer(updatedReviewer);

    if (playlistSaveTimer.current) clearTimeout(playlistSaveTimer.current);
    playlistSaveTimer.current = setTimeout(() => {
      const updatedReviewers = reviewers.map(r => r.id === currentReviewer.id ? updatedReviewer : r);
      saveReviewers(updatedReviewers);
    }, 300);
  };

  const handlePlaylistEnjoy = (clipId: string, reaction?: string) => {
    const playlist = currentReviewer?.pleasurePlaylist;
    if (!playlist) return;
    const updatedPlaylist = {
      ...playlist,
      enjoyed: { ...playlist.enjoyed, [clipId]: { enjoyedAt: now(), reaction } },
    };
    savePlaylistAction(updatedPlaylist);
    setShowPlaylistReaction(false);
  };

  const handlePlaylistSkip = (clipId: string) => {
    const playlist = currentReviewer?.pleasurePlaylist;
    if (!playlist) return;
    const updatedPlaylist = {
      ...playlist,
      skipped: [...playlist.skipped, clipId],
    };
    savePlaylistAction(updatedPlaylist);
  };

  const handlePlaylistSaveForLater = (clipId: string) => {
    const playlist = currentReviewer?.pleasurePlaylist;
    if (!playlist) return;
    const updatedPlaylist = {
      ...playlist,
      savedForLater: playlist.savedForLater.includes(clipId)
        ? playlist.savedForLater
        : [...playlist.savedForLater, clipId],
    };
    savePlaylistAction(updatedPlaylist);
  };

  // === CATEGORY CHAMPIONS HELPERS ===

  // Initialize champions for a category (shuffle clips, set first matchup)
  const initCategoryChampions = (categoryId: string) => {
    if (!currentReviewer) return;
    const catClips = dateNiteClips.filter(c => {
      const inCategory = c.categoryId === categoryId || c.additionalCategoryIds?.includes(categoryId);
      return inCategory && c.reviews?.some(r => isMyReview(r.reviewerId));
    });
    if (catClips.length < 2) return;

    // Shuffle clips
    const shuffled = [...catClips];
    for (let i = shuffled.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [shuffled[i], shuffled[j]] = [shuffled[j]!, shuffled[i]!];
    }

    const champions = currentReviewer.categoryChampions || {
      matchups: [], champions: {}, pendingOrder: {}, currentKing: {}, rankings: {}, seenClips: {},
    };
    const allClipIds = shuffled.map(c => c.id);
    const updatedChampions = {
      ...champions,
      pendingOrder: { ...champions.pendingOrder, [categoryId]: shuffled.slice(1).map(c => c.id) },
      currentKing: { ...champions.currentKing, [categoryId]: shuffled[0]!.id },
      seenClips: { ...(champions.seenClips || {}), [categoryId]: allClipIds },
    };

    const updatedReviewer = { ...currentReviewer, categoryChampions: updatedChampions };
    setCurrentReviewer(updatedReviewer);
    saveReviewers(reviewers.map(r => r.id === currentReviewer.id ? updatedReviewer : r));

    // Return the first matchup pair
    return { king: shuffled[0], challenger: shuffled[1] };
  };

  // Handle a champion matchup decision
  const handleChampionPick = (categoryId: string, winnerId: string, loserId: string, reason?: string) => {
    if (!currentReviewer) return;
    const champions = currentReviewer.categoryChampions;
    if (!champions) return;

    const pending = champions.pendingOrder[categoryId] || [];
    const matchup = { categoryId, winnerId, loserId, decidedAt: now(), ...(reason ? { reason } : {}) };
    const remaining = pending.slice(1);

    if (remaining.length === 0) {
      // No more challengers — winner is champion! Build ranking from matchup history
      const catMatchups = [...champions.matchups, matchup].filter(m => m.categoryId === categoryId);
      // Build ranking: champion first, then losers in reverse order (last loser = 2nd place)
      const ranked = [winnerId];
      const losers = catMatchups.map(m => m.loserId).reverse();
      losers.forEach(id => { if (!ranked.includes(id)) ranked.push(id); });
      // Add any remaining seen clips not in matchups
      const seen = champions.seenClips?.[categoryId] || [];
      seen.forEach(id => { if (!ranked.includes(id)) ranked.push(id); });

      const updatedChampions = {
        ...champions,
        matchups: [...champions.matchups, matchup],
        champions: { ...champions.champions, [categoryId]: winnerId },
        currentKing: { ...champions.currentKing, [categoryId]: winnerId },
        rankings: { ...(champions.rankings || {}), [categoryId]: ranked },
      };
      const updatedReviewer = { ...currentReviewer, categoryChampions: updatedChampions };
      setCurrentReviewer(updatedReviewer);
      saveReviewers(reviewers.map(r => r.id === currentReviewer.id ? updatedReviewer : r));
    } else {
      // Advance: winner becomes king, next in remaining becomes challenger
      const updatedChampions = {
        ...champions,
        matchups: [...champions.matchups, matchup],
        currentKing: { ...champions.currentKing, [categoryId]: winnerId },
        pendingOrder: { ...champions.pendingOrder, [categoryId]: remaining },
      };
      const updatedReviewer = { ...currentReviewer, categoryChampions: updatedChampions };
      setCurrentReviewer(updatedReviewer);
      saveReviewers(reviewers.map(r => r.id === currentReviewer.id ? updatedReviewer : r));
    }
  };

  // Save updated rankings after drag reorder
  const saveRankings = (categoryId: string, newOrder: string[]) => {
    if (!currentReviewer?.categoryChampions) return;
    const updatedChampions = {
      ...currentReviewer.categoryChampions,
      rankings: { ...(currentReviewer.categoryChampions.rankings || {}), [categoryId]: newOrder },
      // Update champion to match new #1
      champions: { ...currentReviewer.categoryChampions.champions, [categoryId]: newOrder[0]! },
    };
    const updatedReviewer = { ...currentReviewer, categoryChampions: updatedChampions };
    setCurrentReviewer(updatedReviewer);
    saveReviewers(reviewers.map(r => r.id === currentReviewer.id ? updatedReviewer : r));
  };

  // Get next matchup for any category (includes new challenger detection)
  const getNextMatchup = () => {
    const champions = currentReviewer?.categoryChampions;

    // First: check crowned categories for new challengers
    if (champions) {
      for (const [catId, championId] of Object.entries(champions.champions)) {
        const seen = champions.seenClips?.[catId] || [];
        const catClips = dateNiteClips.filter(c => {
          const inCat = c.categoryId === catId || c.additionalCategoryIds?.includes(catId);
          return inCat && c.reviews?.some(r => isMyReview(r.reviewerId));
        });
        const newClips = catClips.filter(c => !seen.includes(c.id));
        if (newClips.length > 0) {
          // New challenger! Set up: champion vs first new clip
          const cat = CATEGORIES.find(c => c.id === catId);
          const champion = dateNiteClips.find(c => c.id === championId);
          if (cat && champion && newClips[0]) {
            // Add remaining new clips to pending, track all as seen
            const remaining = newClips.slice(1).map(c => c.id);
            const updatedChampions = {
              ...champions,
              currentKing: { ...champions.currentKing, [catId]: championId },
              pendingOrder: { ...champions.pendingOrder, [catId]: remaining },
              // Remove from crowned while being challenged
              champions: { ...champions.champions },
              seenClips: { ...(champions.seenClips || {}), [catId]: [...seen, ...newClips.map(c => c.id)] },
            };
            delete updatedChampions.champions[catId];
            const updatedReviewer = { ...currentReviewer, categoryChampions: updatedChampions };
            setCurrentReviewer(updatedReviewer);
            saveReviewers(reviewers.map(r => r.id === currentReviewer!.id ? updatedReviewer : r));
            return { category: cat, clipA: champion, clipB: newClips[0], isDefense: true };
          }
        }
      }
    }

    // Then: find categories with 2+ reviewed clips that don't have a champion yet
    const eligibleCategories = CATEGORIES
      .map(cat => {
        const catClips = dateNiteClips.filter(c => {
          const inCat = c.categoryId === cat.id || c.additionalCategoryIds?.includes(cat.id);
          return inCat && c.reviews?.some(r => isMyReview(r.reviewerId));
        });
        return { ...cat, reviewedCount: catClips.length };
      })
      .filter(cat => cat.reviewedCount >= 2 && !champions?.champions[cat.id])
      .sort((a, b) => b.reviewedCount - a.reviewedCount);

    if (eligibleCategories.length === 0) return null;

    const nextCat = eligibleCategories[0]!;
    const kingId = champions?.currentKing[nextCat.id];
    const pending = champions?.pendingOrder[nextCat.id];

    if (!kingId || !pending || pending.length === 0) {
      // No state yet, or stuck with empty pending (recovery) — re-initialize
      const result = initCategoryChampions(nextCat.id);
      if (!result) return null;
      return { category: nextCat, clipA: result.king, clipB: result.challenger };
    }

    const king = dateNiteClips.find(c => c.id === kingId);
    const challengerId = pending[0];
    const challenger = challengerId ? dateNiteClips.find(c => c.id === challengerId) : null;

    if (!king || !challenger) return null;
    return { category: nextCat, clipA: king, clipB: challenger };
  };

  // Track clip view
  const trackClipView = async (clip: Clip, source: 'datenite' | 'submissions' = 'datenite') => {
    if (!currentReviewer) return;
    const view: ClipView = {
      clipId: clip.id,
      viewerId: currentReviewer.id,
      viewerName: currentReviewer.partnerName || currentReviewer.id,
      viewedAt: now(),
      source
    };
    await saveClipViews([...clipViews, view]);
  };

  // Add clip to wishlist (Want to Try)
  const addToWishlist = async (clip: Clip, notes?: string) => {
    if (!currentReviewer?.coupleId) return;

    // Check if already in wishlist
    if (wishlist.some(w => w.clipId === clip.id && w.coupleId === currentReviewer.coupleId)) {
      return; // Already exists
    }

    const item: WishlistItem = {
      id: `wish-${Date.now()}`,
      coupleId: currentReviewer.coupleId,
      clipId: clip.id,
      clipTitle: clip.title,
      addedBy: currentReviewer.partnerName || currentReviewer.id,
      addedAt: now(),
      status: 'want-to-try',
      notes: notes?.trim() || undefined
    };
    await saveWishlist([...wishlist, item], currentReviewer.coupleId);
  };

  // Check if clip is in wishlist
  const isInWishlist = (clipId: string) => {
    return wishlist.some(w => w.clipId === clipId && w.coupleId === currentReviewer?.coupleId && w.status === 'want-to-try');
  };

  // Add a new clip
  const addNewClip = async () => {
    // Validate required fields
    if (!editingClipData.title.trim()) {
      setNewClipUrlError('Title is required');
      return;
    }
    if (editingClipData.categoryIds.length === 0) {
      setNewClipUrlError('Please select at least one category');
      return;
    }

    // Validate URL
    const urlError = validateClipUrl(editingClipData.link);
    if (urlError) {
      setNewClipUrlError(urlError);
      return;
    }

    const normalizedLink = normalizeRedGifsUrl(editingClipData.link);

    // Split categories into primary + additional
    const [primaryCategory, ...additionalCategories] = editingClipData.categoryIds;
    if (!primaryCategory) return; // Guard - should never happen due to validation above

    const newClip: Clip = {
      id: generateId('clip'),
      title: editingClipData.title.trim(),
      description: editingClipData.description.trim(),
      categoryId: primaryCategory,
      additionalCategoryIds: additionalCategories.length > 0 ? additionalCategories : undefined,
      link: normalizedLink,
      tags: editingClipData.tags,
      rating: '',
      notesForHim: '',
      notesForHer: '',
      reviews: [],
      picks: []
    };

    await saveDateNiteClips([...dateNiteClips, newClip]);
    setShowAddClipModal(false);
    setEditingClipData({ title: '', description: '', tags: [], categoryIds: [], link: '', reason: '' });
    setNewClipUrlError(null);
  };

  // Toggle tag in editing mode
  const toggleEditTag = (tagId: string) => {
    setEditingClipData(prev => ({
      ...prev,
      tags: prev.tags.includes(tagId)
        ? prev.tags.filter(t => t !== tagId)
        : [...prev.tags, tagId]
    }));
  };

  // Link to a couple by code
  const linkToCouple = async () => {
    const code = linkCoupleCode.trim().toUpperCase();
    if (!code) {
      setLinkCoupleError('Please enter a couple code');
      return;
    }

    // Don't allow linking to your own couple profile
    if (currentReviewer?.coupleId === code) {
      setLinkCoupleError('This is your own couple profile. Use "Enter Date Night" to access it.');
      return;
    }

    try {
      // Try to find the couple in D1
      const coupleData = await getCoupleByCode(code);
      if (coupleData) {

        // Valid couple found - update reviewer with managed coupleId (external couple they coach)
        if (currentReviewer) {
          const updatedReviewer = { ...currentReviewer, managedCoupleId: code };
          const updated = reviewers.map(r => r.id === currentReviewer.id ? updatedReviewer : r);
          await saveReviewers(updated);
          setCurrentReviewer(updatedReviewer);

          // Backup to localStorage for persistence
          localStorage.setItem(`vb-reviewer-managed-couple:${currentReviewer.id}`, code);

          // Update the couple's data with reviewer info so they know who's managing them
          const reviewerDisplayName = currentReviewer.name || currentReviewer.partnerName || `Reviewer ${currentReviewer.id.slice(-4)}`;
          const updatedCoupleData: CoupleData = {
            ...coupleData,
            managedByReviewerId: currentReviewer.id,
            managedByReviewerName: reviewerDisplayName,
            managedByAcceptedAt: now()
          };
          await saveCoupleByCode(updatedCoupleData);

          // Create a notification for the couple in their inbox
          const notification: CoupleFollowUp = {
            id: `notify-${Date.now()}`,
            coupleId: code,
            question: `🎉 ${reviewerDisplayName} is now your reviewer! They'll help you discover new content, answer questions, and make your date nights amazing. Say hi in the chat!`,
            sentAt: now(),
            status: 'pending'
          };
          const existingFollowUps = coupleFollowUps || [];
          await saveCoupleFollowUps([...existingFollowUps, notification]);
        }

        setShowLinkCoupleModal(false);
        setLinkCoupleCode('');
        setLinkCoupleError('');

        // Load couple requests for the wizard
        try {
          const reqs = await d1List<CoupleRequest>('couple_requests', { coupleId: code });
          setCoupleRequests(reqs);
        } catch {
          setCoupleRequests([]);
        }

        // Show the onboarding wizard for the managed couple
        setOnboardingCoupleData(coupleData);
        setOnboardingWizardStep(0);
        setShowCoupleOnboardingWizard(true);

        // Load managed couple data (with updated reviewer info)
        const displayName = currentReviewer?.name || currentReviewer?.partnerName || `Reviewer ${currentReviewer?.id.slice(-4)}`;
        setManagedCoupleData({
          ...coupleData,
          managedByReviewerId: currentReviewer?.id,
          managedByReviewerName: displayName,
          managedByAcceptedAt: now()
        });
      } else {
        // Also check if it's a reviewer's coupleId (they created a couple profile)
        const reviewerWithCode = reviewers.find(r => r.coupleId === code);
        if (reviewerWithCode) {
          if (currentReviewer) {
            const updatedReviewer = { ...currentReviewer, managedCoupleId: code };
            const updated = reviewers.map(r => r.id === currentReviewer.id ? updatedReviewer : r);
            await saveReviewers(updated);
            setCurrentReviewer(updatedReviewer);
          }

          setShowLinkCoupleModal(false);
          setLinkCoupleCode('');
          setLinkCoupleError('');
        } else {
          setLinkCoupleError(`Couple code "${code}" not found`);
        }
      }
    } catch {
      setLinkCoupleError('Error looking up couple code');
    }
  };

  // Unlink from managed couple (external couple being coached)
  const unlinkFromCouple = async () => {
    if (!currentReviewer) return;

    // Also remove the reviewer info from the couple's data
    if (managedCoupleData) {
      const updatedCoupleData: CoupleData = {
        ...managedCoupleData,
        managedByReviewerId: undefined,
        managedByReviewerName: undefined,
        managedByAcceptedAt: undefined
      };
      await saveCoupleByCode(updatedCoupleData);
    }

    // Remove managedCoupleId from reviewer (keeps their own coupleId intact)
    const updatedReviewer = { ...currentReviewer, managedCoupleId: undefined };
    const updated = reviewers.map(r => r.id === currentReviewer.id ? updatedReviewer : r);
    await saveReviewers(updated);
    setCurrentReviewer(updatedReviewer);

    // Clear localStorage backup
    localStorage.removeItem(`vb-reviewer-managed-couple:${currentReviewer.id}`);

    // Clear managed couple data
    setManagedCoupleData(null);
    setCoupleRequests([]);
    setShowUnlinkConfirm(false);
  };

  const getAverageRating = (clip: Clip): string | null => {
    if (!clip.reviews?.length) return null;
    const scores: Record<string, number> = { '🔥 Must Try': 4, '👍 Good': 3, '🤔 Maybe': 2, '⏭️ Skip': 1 };
    const avg = (clip.reviews || []).reduce((sum, r) => sum + (scores[r.rating] || 0), 0) / (clip.reviews || []).length;
    if (avg >= 3.5) return '🔥';
    if (avg >= 2.5) return '👍';
    if (avg >= 1.5) return '🤔';
    return '⏭️';
  };

  const filteredDateNiteClips = dateNiteClips.filter(clip => {
    if (categoryFilter !== 'all' && clip.categoryId !== categoryFilter) return false;
    if (tagFilter.length > 0 && !tagFilter.some(tag => clip.tags?.includes(tag))) return false;
    // Hide clips already reviewed by this reviewer (if toggle is on)
    if (hideReviewed && currentReviewer && clip.reviews?.some(r => isMyReview(r.reviewerId))) return false;
    return true;
  });

  // Get only tags that are actually used in clips (filter out empty tags)
  const usedTagIds = new Set(dateNiteClips.flatMap(clip => clip.tags || []));
  const usedTags = TAGS.filter(tag => usedTagIds.has(tag.id));

  // Validation helpers
  const canProceedStep2 = form.gender && form.ageRange && form.relationshipStatus
    && form.watchesAdultContent && form.familiarWithProducts
    && (!watchesContent || form.whatMakesGoodContent);

  const canProceedStep3 = !!form.enjoysVolumeContent;

  const canProceedStep4 = (isFemale && form.canSquirt) || (isMale && form.enjoysSquirtingContent) || (!isFemale && !isMale);

  // Show code screen after registration
  if (showCode) return (
    <div className="min-h-screen bg-zinc-900 flex items-center justify-center p-6">
      <Card title="You're Set!" centered>
        <div
          className="w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4"
          style={{ backgroundColor: COLORS.lime }}
        >
          <Check className="w-8 h-8 text-zinc-900" />
        </div>

        {/* Reviewer Code */}
        <div className="mb-6">
          <p className="text-zinc-400 text-sm mb-2">Your Reviewer Code (to return later):</p>
          <div className="bg-zinc-900 rounded-xl p-4 border-2 border-dashed border-zinc-600">
            <p className="text-2xl font-mono font-bold tracking-wider" style={{ color: COLORS.lime }}>{code}</p>
          </div>
          <div className="mt-2 flex items-center justify-center gap-2">
            <CopyLinkButton url={reviewerLoginLink(code)} label="Copy my login link" />
            <span className="text-zinc-500 text-xs">Bookmark it to skip the code next time</span>
          </div>
        </div>

        {/* Couple Code */}
        {coupleCode && (
          <div className="mb-6">
            <p className="text-zinc-400 text-sm mb-2">Your Couple Code (share with friends!):</p>
            <div className="bg-pink-500/10 border-2 border-pink-500/30 rounded-xl p-4">
              <p className="text-2xl font-mono font-bold tracking-wider text-pink-400">{coupleCode}</p>
              <p className="text-zinc-500 text-xs mt-2">Friends can use this to recommend clips for you & {useCodename ? selectedCodename : form.partnerName}</p>
            </div>
          </div>
        )}

        <PrimaryButton onClick={() => setShowCode(false)} className="w-full">Continue to Dashboard</PrimaryButton>
      </Card>
    </div>
  );

  // Check if we have a partner (for showing step 6)
  const hasPartner = useCodename ? !!selectedCodename : !!form.partnerName.trim();
  const totalSteps = hasPartner ? 7 : 6;

  // Onboarding flow (5-6 steps)
  if (!currentReviewer) return (
    <div className="min-h-screen bg-zinc-900 p-6">
      <div className="max-w-lg mx-auto">
        <BackButton onClick={onBack} />

        {/* Stats banner */}
        {step === 1 && (
          <div className="flex gap-4 my-6">
            <div className="flex-1 bg-zinc-800 rounded-xl p-4 text-center border border-zinc-700">
              <p className="text-2xl font-bold" style={{ color: COLORS.lime }}>{reviewers.length + 127}+</p>
              <p className="text-zinc-400 text-sm">reviewers in network</p>
            </div>
          </div>
        )}

        {/* Step indicator */}
        <div className="flex items-center gap-1 mb-8">
          {Array.from({ length: totalSteps }, (_, i) => i + 1).map(s => (
            <div key={s} className="flex-1 flex items-center">
              <div
                className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-semibold ${step >= s ? 'text-zinc-900' : 'bg-zinc-800 text-zinc-500'}`}
                style={step >= s ? { backgroundColor: COLORS.lime } : {}}
              >
                {step > s ? <Check className="w-4 h-4" /> : s}
              </div>
              {s < totalSteps && <div className={`flex-1 h-1 mx-1 rounded ${step > s ? 'bg-lime-500' : 'bg-zinc-800'}`} />}
            </div>
          ))}
        </div>

        {/* Step 1: Guidelines */}
        {step === 1 && (
          <div className="bg-zinc-800 rounded-2xl p-6 border border-zinc-700">
            <h2 className="text-2xl font-bold text-white mb-6 flex items-center gap-3">
              <BookOpen className="w-6 h-6" style={{ color: COLORS.lime }} /> Reviewer Guidelines
            </h2>
            <div className="space-y-6 text-sm mb-8">
              <div className="bg-green-900/20 border border-green-800/50 rounded-xl p-4">
                <h4 className="font-semibold text-green-400 mb-2">✓ Trusted Creator Network</h4>
                <p className="text-xs text-zinc-400">All content comes from our verified, trusted creator network. No concerns about legitimacy - focus purely on content quality and fit.</p>
              </div>
              <div>
                <h3 className="font-semibold text-white mb-2">Your Role</h3>
                <p className="text-zinc-400">Review explicit content from creators demonstrating VB Health product results. Ensure quality, compliance, and profile each clip for categorisation.</p>
              </div>
              <div>
                <h3 className="font-semibold text-white mb-2">What to Check</h3>
                <ul className="space-y-2 text-zinc-300">
                  {['On-Brand – Authentic, matches VB Health tone', 'Shows Outcome – Demonstrates the product results (not the packaging)', 'Claims Compliant – No medical claims'].map((t, i) => (
                    <li key={i} className="flex items-start gap-2">
                      <Check className="w-4 h-4 mt-0.5" style={{ color: COLORS.lime }} />
                      <span>{t}</span>
                    </li>
                  ))}
                </ul>
              </div>

              {/* Privacy Note */}
              <div className="bg-amber-500/10 border border-amber-500/30 rounded-xl p-4">
                <h4 className="font-semibold text-amber-400 mb-2">🔒 A Note on Privacy</h4>
                <p className="text-xs text-zinc-400">Some clips intentionally hide faces - many creators love being naughty but prefer to keep their identities private. This is totally normal and doesn't affect quality!</p>
              </div>

              <div className="bg-cyan-900/20 border border-cyan-800/50 rounded-xl p-4">
                <h4 className="font-semibold text-cyan-400 mb-2">📊 Clip Profiling</h4>
                <p className="text-xs text-zinc-400">For each clip, you'll categorise details like consistency type, finish location, and standout qualities to help us organise content.</p>
              </div>
              <div className="bg-red-900/20 border border-red-800/50 rounded-xl p-4">
                <h4 className="font-semibold text-red-400 mb-2">🚫 Auto-Reject</h4>
                <p className="text-xs text-zinc-400">Medical claims • Fake results • Competitor products • Anyone underage • Non-consensual</p>
              </div>
            </div>
            <button
              onClick={() => setStep(2)}
              className="w-full flex items-center justify-center gap-2 text-zinc-900 px-5 py-3 rounded-xl font-semibold"
              style={{ backgroundColor: COLORS.lime }}
            >
              I Understand <ArrowRight className="w-4 h-4" />
            </button>
          </div>
        )}

        {/* Step 2: About You (Background) */}
        {step === 2 && (
          <div className="bg-zinc-800 rounded-2xl p-6 border border-zinc-700">
            <h2 className="text-2xl font-bold text-white mb-2 flex items-center gap-3">
              <ClipboardList className="w-6 h-6" style={{ color: COLORS.lime }} /> About You
            </h2>
            <p className="text-zinc-400 text-sm mb-6">Help us understand your perspective</p>
            <div className="space-y-5">
              <div>
                <label className="block text-sm font-medium text-zinc-300 mb-2">How do you identify? *</label>
                <select
                  value={form.gender}
                  onChange={e => setForm({ ...form, gender: e.target.value })}
                  className="w-full bg-zinc-700 border border-zinc-600 rounded-xl px-4 py-3 text-white"
                >
                  <option value="">Select...</option>
                  <option value="Male">Male</option>
                  <option value="Female">Female</option>
                  <option value="Non-binary">Non-binary</option>
                </select>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-zinc-300 mb-2">Age range *</label>
                  <select
                    value={form.ageRange}
                    onChange={e => setForm({ ...form, ageRange: e.target.value })}
                    className="w-full bg-zinc-700 border border-zinc-600 rounded-xl px-4 py-3 text-white"
                  >
                    <option value="">Select...</option>
                    <option value="18-24">18-24</option>
                    <option value="25-34">25-34</option>
                    <option value="35-44">35-44</option>
                    <option value="45-54">45-54</option>
                    <option value="55+">55+</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-zinc-300 mb-2">Relationship status *</label>
                  <select
                    value={form.relationshipStatus}
                    onChange={e => setForm({ ...form, relationshipStatus: e.target.value })}
                    className="w-full bg-zinc-700 border border-zinc-600 rounded-xl px-4 py-3 text-white"
                  >
                    <option value="">Select...</option>
                    <option value="Single">Single</option>
                    <option value="Dating">Dating</option>
                    <option value="Long-term partner">Long-term partner</option>
                    <option value="Married">Married</option>
                    <option value="It's complicated">It's complicated</option>
                    </select>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-zinc-300 mb-2">How sexually active are you currently?</label>
                <select
                  value={form.sexuallyActive}
                  onChange={e => setForm({ ...form, sexuallyActive: e.target.value })}
                  className="w-full bg-zinc-700 border border-zinc-600 rounded-xl px-4 py-3 text-white"
                >
                  <option value="">Select...</option>
                  <option value="Very active">Very active</option>
                  <option value="Fairly active">Fairly active</option>
                  <option value="Occasionally">Occasionally</option>
                  <option value="Not currently">Not currently</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-zinc-300 mb-2">Do you watch adult content? *</label>
                <select
                  value={form.watchesAdultContent}
                  onChange={e => setForm({ ...form, watchesAdultContent: e.target.value })}
                  className="w-full bg-zinc-700 border border-zinc-600 rounded-xl px-4 py-3 text-white"
                >
                  <option value="">Select...</option>
                  <option value="Yes regularly">Yes, regularly</option>
                  <option value="Sometimes">Sometimes</option>
                  <option value="Rarely">Rarely</option>
                  <option value="No">No</option>
                </select>
              </div>

              {watchesContent && (
                <>
                  <div>
                    <label className="block text-sm font-medium text-zinc-300 mb-2">What themes or styles do you typically enjoy?</label>
                    <textarea
                      value={form.adultContentThemes}
                      onChange={e => setForm({ ...form, adultContentThemes: e.target.value })}
                      placeholder="e.g. Amateur, POV, passionate, specific categories..."
                      className="w-full bg-zinc-700 border border-zinc-600 rounded-xl px-4 py-3 text-white placeholder-zinc-500 h-20 resize-none"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-zinc-300 mb-2">Would you share an example link of content you enjoy? <span className="text-zinc-500">(optional)</span></label>
                    <input
                      type="url"
                      value={form.adultContentExample}
                      onChange={e => setForm({ ...form, adultContentExample: e.target.value })}
                      placeholder="https://..."
                      className="w-full bg-zinc-700 border border-zinc-600 rounded-xl px-4 py-3 text-white placeholder-zinc-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-zinc-300 mb-2">What makes adult content "good" to you? *</label>
                    <textarea
                      value={form.whatMakesGoodContent}
                      onChange={e => setForm({ ...form, whatMakesGoodContent: e.target.value })}
                      placeholder="e.g. Authenticity, chemistry, production quality, genuine reactions..."
                      className="w-full bg-zinc-700 border border-zinc-600 rounded-xl px-4 py-3 text-white placeholder-zinc-500 h-20 resize-none"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-zinc-300 mb-2">What turns you off or feels fake?</label>
                    <textarea
                      value={form.whatFeelsFake}
                      onChange={e => setForm({ ...form, whatFeelsFake: e.target.value })}
                      placeholder="e.g. Over-acting, bad angles, clearly scripted moments..."
                      className="w-full bg-zinc-700 border border-zinc-600 rounded-xl px-4 py-3 text-white placeholder-zinc-500 h-20 resize-none"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-zinc-300 mb-2">Do you prefer amateur/authentic or professional/polished content?</label>
                    <select
                      value={form.preferredStyle}
                      onChange={e => setForm({ ...form, preferredStyle: e.target.value })}
                      className="w-full bg-zinc-700 border border-zinc-600 rounded-xl px-4 py-3 text-white"
                    >
                      <option value="">Select...</option>
                      <option value="Strongly prefer amateur/authentic">Strongly prefer amateur/authentic</option>
                      <option value="Lean towards amateur">Lean towards amateur</option>
                      <option value="No strong preference">No strong preference</option>
                      <option value="Lean towards professional">Lean towards professional</option>
                      <option value="Strongly prefer professional/polished">Strongly prefer professional/polished</option>
                    </select>
                  </div>
                </>
              )}

              <div>
                <label className="block text-sm font-medium text-zinc-300 mb-2">Familiar with VB Health products? *</label>
                <select
                  value={form.familiarWithProducts}
                  onChange={e => setForm({ ...form, familiarWithProducts: e.target.value })}
                  className="w-full bg-zinc-700 border border-zinc-600 rounded-xl px-4 py-3 text-white"
                >
                  <option value="">Select...</option>
                  <option value="Yes - know them well">Yes - know them well</option>
                  <option value="Somewhat">Somewhat</option>
                  <option value="No - new to these">No - new to these</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-zinc-300 mb-2">Any relevant experience?</label>
                <select
                  value={form.relevantExperience}
                  onChange={e => setForm({ ...form, relevantExperience: e.target.value })}
                  className="w-full bg-zinc-700 border border-zinc-600 rounded-xl px-4 py-3 text-white"
                >
                  <option value="">Select...</option>
                  <option value="Content moderation">Content moderation</option>
                  <option value="Social media">Social media</option>
                  <option value="Adult content industry">Adult content industry</option>
                  <option value="Marketing">Marketing</option>
                  <option value="None">None</option>
                  <option value="Other">Other</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-zinc-300 mb-2">Why are you interested in being a reviewer?</label>
                <textarea
                  value={form.whyInterested}
                  onChange={e => setForm({ ...form, whyInterested: e.target.value })}
                  placeholder="e.g. Curious about the products, enjoy reviewing content, want to help..."
                  className="w-full bg-zinc-700 border border-zinc-600 rounded-xl px-4 py-3 text-white placeholder-zinc-500 h-20 resize-none"
                />
              </div>
            </div>
            <div className="flex gap-3 mt-6">
              <button onClick={() => setStep(1)} className="flex items-center gap-2 bg-zinc-700 text-zinc-300 px-5 py-3 rounded-xl">
                <ArrowLeft className="w-4 h-4" /> Back
              </button>
              <button
                onClick={() => setStep(3)}
                disabled={!canProceedStep2}
                className="flex-1 flex items-center justify-center gap-2 text-zinc-900 px-5 py-3 rounded-xl font-semibold disabled:opacity-50"
                style={{ backgroundColor: COLORS.lime }}
              >
                Continue <ArrowRight className="w-4 h-4" />
              </button>
            </div>
          </div>
        )}

        {/* Step 3: Load Boost */}
        {step === 3 && (
          <div className="bg-zinc-800 rounded-2xl p-6 border border-zinc-700">
            <div className="flex items-center gap-3 mb-2">
              <div className="w-10 h-10 rounded-xl bg-blue-900/50 flex items-center justify-center">
                <span className="text-lg">💦</span>
              </div>
              <h2 className="text-2xl font-bold text-white">Load Boost</h2>
            </div>
            <p className="text-zinc-400 text-sm mb-6">This product increases ejaculate volume. Your perspective helps us review content effectively.</p>

            <div className="space-y-5">
              <div>
                <label className="block text-sm font-medium text-zinc-300 mb-2">Do you enjoy content featuring impressive finishes with lots of volume? *</label>
                <select
                  value={form.enjoysVolumeContent}
                  onChange={e => setForm({ ...form, enjoysVolumeContent: e.target.value })}
                  className="w-full bg-zinc-700 border border-zinc-600 rounded-xl px-4 py-3 text-white"
                >
                  <option value="">Select...</option>
                  <option value="Yes, definitely">Yes, definitely</option>
                  <option value="Sometimes">Sometimes</option>
                  <option value="Not particularly">Not particularly</option>
                </select>
              </div>

              {isMale && (
                <>
                  <div>
                    <label className="block text-sm font-medium text-zinc-300 mb-2">Have you tried Load Boost or similar volume products?</label>
                    <select
                      value={form.triedLoadBoost}
                      onChange={e => setForm({ ...form, triedLoadBoost: e.target.value })}
                      className="w-full bg-zinc-700 border border-zinc-600 rounded-xl px-4 py-3 text-white"
                    >
                      <option value="">Select...</option>
                      <option value="Yes - Load Boost specifically">Yes - Load Boost specifically</option>
                      <option value="Yes - similar products">Yes - similar products</option>
                      <option value="No but interested">No, but I'd be interested</option>
                      <option value="No">No</option>
                    </select>
                  </div>
                  {(form.triedLoadBoost === 'Yes - Load Boost specifically' || form.triedLoadBoost === 'Yes - similar products') && (
                    <div>
                      <label className="block text-sm font-medium text-zinc-300 mb-2">What was your experience?</label>
                      <textarea
                        value={form.loadBoostExperience}
                        onChange={e => setForm({ ...form, loadBoostExperience: e.target.value })}
                        placeholder="Did you notice a difference? How significant?"
                        className="w-full bg-zinc-700 border border-zinc-600 rounded-xl px-4 py-3 text-white placeholder-zinc-500 h-20 resize-none"
                      />
                    </div>
                  )}
                  <div>
                    <label className="block text-sm font-medium text-zinc-300 mb-2">What does your partner typically experience when you finish? (volume-wise)</label>
                    <select
                      value={form.partnerTypicalVolume}
                      onChange={e => setForm({ ...form, partnerTypicalVolume: e.target.value })}
                      className="w-full bg-zinc-700 border border-zinc-600 rounded-xl px-4 py-3 text-white"
                    >
                      <option value="">Select...</option>
                      <option value="A lot">A lot</option>
                      <option value="Average amount">Average amount</option>
                      <option value="Not much">Not much</option>
                      <option value="Varies">Varies</option>
                      <option value="Not sure / no partner">Not sure / no partner currently</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-zinc-300 mb-2">What do you or your partner like or dislike about it?</label>
                    <textarea
                      value={form.partnerVolumeLikeDislike}
                      onChange={e => setForm({ ...form, partnerVolumeLikeDislike: e.target.value })}
                      placeholder="e.g. They enjoy the visual, wish there was more, cleanup is an issue..."
                      className="w-full bg-zinc-700 border border-zinc-600 rounded-xl px-4 py-3 text-white placeholder-zinc-500 h-20 resize-none"
                    />
                  </div>
                </>
              )}

              {isFemale && (
                <>
                  <div>
                    <label className="block text-sm font-medium text-zinc-300 mb-2">Would you want your partner to experience the effects of Load Boost?</label>
                    <select
                      value={form.wantPartnerLoadBoost}
                      onChange={e => setForm({ ...form, wantPartnerLoadBoost: e.target.value })}
                      className="w-full bg-zinc-700 border border-zinc-600 rounded-xl px-4 py-3 text-white"
                    >
                      <option value="">Select...</option>
                      <option value="Yes, definitely">Yes, definitely</option>
                      <option value="Maybe, curious">Maybe, I'm curious</option>
                      <option value="Not particularly">Not particularly</option>
                        </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-zinc-300 mb-2">When your partner finishes, do you enjoy swallowing?</label>
                    <select
                      value={form.enjoysSwallowing}
                      onChange={e => setForm({ ...form, enjoysSwallowing: e.target.value })}
                      className="w-full bg-zinc-700 border border-zinc-600 rounded-xl px-4 py-3 text-white"
                    >
                      <option value="">Select...</option>
                      <option value="Yes, usually">Yes, usually</option>
                      <option value="Sometimes">Sometimes</option>
                      <option value="Not typically">Not typically</option>
                        </select>
                  </div>

                  {(form.enjoysSwallowing === 'Yes, usually' || form.enjoysSwallowing === 'Sometimes') && (
                    <div>
                      <label className="block text-sm font-medium text-zinc-300 mb-2">
                        Tell us more about your experience with swallowing
                      </label>
                      <textarea
                        value={form.swallowingDescription}
                        onChange={e => setForm({ ...form, swallowingDescription: e.target.value })}
                        placeholder="What do you enjoy about it? Any memorable experiences?"
                        rows={3}
                        className="w-full bg-zinc-700 border border-zinc-600 rounded-xl px-4 py-3 text-white resize-none"
                      />
                    </div>
                  )}

                  {form.enjoysSwallowing && (
                    <div>
                      <label className="block text-sm font-medium text-zinc-300 mb-2">Would you enjoy it more if it tasted better?</label>
                      <select
                        value={form.wouldEnjoyBetterTaste}
                        onChange={e => setForm({ ...form, wouldEnjoyBetterTaste: e.target.value })}
                        className="w-full bg-zinc-700 border border-zinc-600 rounded-xl px-4 py-3 text-white"
                      >
                        <option value="">Select...</option>
                        <option value="Yes, definitely">Yes, definitely</option>
                        <option value="Maybe">Maybe</option>
                        <option value="Taste doesn't matter to me">Taste doesn't really matter to me</option>
                        <option value="It already tastes fine">It already tastes fine to me</option>
                      </select>
                    </div>
                  )}

                  <div>
                    <label className="block text-sm font-medium text-zinc-300 mb-2">Do you enjoy receiving facials?</label>
                    <select
                      value={form.enjoysFacials}
                      onChange={e => setForm({ ...form, enjoysFacials: e.target.value })}
                      className="w-full bg-zinc-700 border border-zinc-600 rounded-xl px-4 py-3 text-white"
                    >
                      <option value="">Select...</option>
                      <option value="Yes">Yes</option>
                      <option value="Sometimes">Sometimes</option>
                      <option value="Not typically">Not typically</option>
                        </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-zinc-300 mb-2">Does the amount/volume make a difference to your enjoyment?</label>
                    <select
                      value={form.volumeMakesDifference}
                      onChange={e => setForm({ ...form, volumeMakesDifference: e.target.value })}
                      className="w-full bg-zinc-700 border border-zinc-600 rounded-xl px-4 py-3 text-white"
                    >
                      <option value="">Select...</option>
                      <option value="Yes, definitely">Yes, definitely</option>
                      <option value="Somewhat">Somewhat</option>
                      <option value="Not particularly">Not particularly</option>
                        </select>
                  </div>
                </>
              )}
            </div>
            <div className="flex gap-3 mt-6">
              <button onClick={() => setStep(2)} className="flex items-center gap-2 bg-zinc-700 text-zinc-300 px-5 py-3 rounded-xl">
                <ArrowLeft className="w-4 h-4" /> Back
              </button>
              <button
                onClick={() => setStep(4)}
                disabled={!canProceedStep3}
                className="flex-1 flex items-center justify-center gap-2 text-zinc-900 px-5 py-3 rounded-xl font-semibold disabled:opacity-50"
                style={{ backgroundColor: COLORS.lime }}
              >
                Continue <ArrowRight className="w-4 h-4" />
              </button>
            </div>
          </div>
        )}

        {/* Step 4: Soaking Wet */}
        {step === 4 && (
          <div className="bg-zinc-800 rounded-2xl p-6 border border-zinc-700">
            <div className="flex items-center gap-3 mb-2">
              <div className="w-10 h-10 rounded-xl bg-pink-900/50 flex items-center justify-center">
                <span className="text-lg">💧</span>
              </div>
              <h2 className="text-2xl font-bold text-white">Soaking Wet</h2>
            </div>
            <p className="text-zinc-400 text-sm mb-6">This product enhances arousal and natural wetness/squirting. Your perspective helps us review content effectively.</p>

            <div className="space-y-5">
              {isFemale && (
                <>
                  <div>
                    <label className="block text-sm font-medium text-zinc-300 mb-2">Are you able to squirt? *</label>
                    <select
                      value={form.canSquirt}
                      onChange={e => setForm({ ...form, canSquirt: e.target.value })}
                      className="w-full bg-zinc-700 border border-zinc-600 rounded-xl px-4 py-3 text-white"
                    >
                      <option value="">Select...</option>
                      <option value="Yes, easily">Yes, easily</option>
                      <option value="Sometimes">Sometimes</option>
                      <option value="Not yet but would like to">Not yet, but I'd like to</option>
                      <option value="Not interested">Not interested</option>
                        </select>
                  </div>

                  {(form.canSquirt === 'Yes, easily' || form.canSquirt === 'Sometimes') && (
                    <div>
                      <label className="block text-sm font-medium text-zinc-300 mb-2">Can you describe what typically triggers it or the scenario?</label>
                      <textarea
                        value={form.squirtScenarioDescription}
                        onChange={e => setForm({ ...form, squirtScenarioDescription: e.target.value })}
                        placeholder="e.g. Certain positions, stimulation type, build-up required, with a partner vs solo..."
                        className="w-full bg-zinc-700 border border-zinc-600 rounded-xl px-4 py-3 text-white placeholder-zinc-500 h-24 resize-none"
                      />
                    </div>
                  )}

                  <div>
                    <label className="block text-sm font-medium text-zinc-300 mb-2">Would you like to squirt more easily or more often?</label>
                    <select
                      value={form.wantToSquirtMore}
                      onChange={e => setForm({ ...form, wantToSquirtMore: e.target.value })}
                      className="w-full bg-zinc-700 border border-zinc-600 rounded-xl px-4 py-3 text-white"
                    >
                      <option value="">Select...</option>
                      <option value="Yes, definitely">Yes, definitely</option>
                      <option value="Maybe, curious">Maybe, I'm curious</option>
                      <option value="Happy as is">Happy as is</option>
                      <option value="Not interested">Not interested</option>
                        </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-zinc-300 mb-2">Have you used arousal or wetness-enhancing products before?</label>
                    <select
                      value={form.usedArousalProducts}
                      onChange={e => setForm({ ...form, usedArousalProducts: e.target.value })}
                      className="w-full bg-zinc-700 border border-zinc-600 rounded-xl px-4 py-3 text-white"
                    >
                      <option value="">Select...</option>
                      <option value="Yes - Soaking Wet">Yes - Soaking Wet specifically</option>
                      <option value="Yes - other products">Yes - other products</option>
                      <option value="No but interested">No, but I'd be interested</option>
                      <option value="No">No</option>
                    </select>
                  </div>

                  {/* Wetness & Orgasm Questions */}
                  <div className="pt-4 border-t border-zinc-700">
                    <p className="text-pink-400 text-sm font-medium mb-4">💦 Wetness & Orgasm</p>

                    <div className="space-y-5">
                      <div>
                        <label className="block text-sm font-medium text-zinc-300 mb-2">Do you typically orgasm from sex?</label>
                        <select
                          value={form.orgasmFromSex}
                          onChange={e => setForm({ ...form, orgasmFromSex: e.target.value })}
                          className="w-full bg-zinc-700 border border-zinc-600 rounded-xl px-4 py-3 text-white"
                        >
                          <option value="">Select...</option>
                          <option value="Always">Always or almost always</option>
                          <option value="Usually">Usually (more often than not)</option>
                          <option value="Sometimes">Sometimes (about half the time)</option>
                          <option value="Rarely">Rarely</option>
                          <option value="Never">Never / not yet</option>
                                </select>
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-zinc-300 mb-2">How wet do you typically get during arousal?</label>
                        <div className="flex items-center gap-3">
                          <span className="text-zinc-500 text-sm">Dry</span>
                          <div className="flex-1 flex gap-2">
                            {[1, 2, 3, 4, 5].map(level => (
                              <button
                                key={level}
                                type="button"
                                onClick={() => setForm({ ...form, typicalWetness: level })}
                                className={`flex-1 py-3 rounded-lg text-lg transition-all ${
                                  form.typicalWetness === level
                                    ? 'bg-pink-500 text-white scale-105'
                                    : form.typicalWetness >= level
                                      ? 'bg-pink-500/30 text-pink-300'
                                      : 'bg-zinc-700 text-zinc-500 hover:bg-zinc-600'
                                }`}
                              >
                                {'💧'.repeat(level)}
                              </button>
                            ))}
                          </div>
                          <span className="text-zinc-500 text-sm">Very wet</span>
                        </div>
                      </div>

                      {form.typicalWetness > 0 && form.typicalWetness <= 2 && (
                        <div>
                          <label className="block text-sm font-medium text-zinc-300 mb-2">Would you like to be wetter?</label>
                          <select
                            value={form.wantToBeWetter}
                            onChange={e => setForm({ ...form, wantToBeWetter: e.target.value })}
                            className="w-full bg-zinc-700 border border-zinc-600 rounded-xl px-4 py-3 text-white"
                          >
                            <option value="">Select...</option>
                            <option value="Yes, definitely">Yes, definitely</option>
                            <option value="Maybe, curious">Maybe, I'm curious</option>
                            <option value="Happy as is">Happy as is</option>
                            <option value="Not sure">Not sure</option>
                          </select>
                        </div>
                      )}

                      {form.typicalWetness >= 3 && (
                        <div>
                          <label className="block text-sm font-medium text-zinc-300 mb-2">Any notes about your wetness experience?</label>
                          <textarea
                            value={form.wetnessNotes}
                            onChange={e => setForm({ ...form, wetnessNotes: e.target.value })}
                            placeholder="e.g. What affects it, consistency, anything you'd like to share..."
                            className="w-full bg-zinc-700 border border-zinc-600 rounded-xl px-4 py-3 text-white placeholder-zinc-500 h-20 resize-none"
                          />
                        </div>
                      )}
                    </div>
                  </div>
                </>
              )}

              {isMale && (
                <>
                  <div>
                    <label className="block text-sm font-medium text-zinc-300 mb-2">Do you enjoy content featuring squirting or visible wetness? *</label>
                    <select
                      value={form.enjoysSquirtingContent}
                      onChange={e => setForm({ ...form, enjoysSquirtingContent: e.target.value })}
                      className="w-full bg-zinc-700 border border-zinc-600 rounded-xl px-4 py-3 text-white"
                    >
                      <option value="">Select...</option>
                      <option value="Yes, definitely">Yes, definitely</option>
                      <option value="Sometimes">Sometimes</option>
                      <option value="Not particularly">Not particularly</option>
                        </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-zinc-300 mb-2">Is this something you'd enjoy experiencing more with a partner?</label>
                    <select
                      value={form.wantMoreWithPartner}
                      onChange={e => setForm({ ...form, wantMoreWithPartner: e.target.value })}
                      className="w-full bg-zinc-700 border border-zinc-600 rounded-xl px-4 py-3 text-white"
                    >
                      <option value="">Select...</option>
                      <option value="Yes, definitely">Yes, definitely</option>
                      <option value="Maybe, curious">Maybe, I'm curious</option>
                      <option value="Not particularly">Not particularly</option>
                        </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-zinc-300 mb-2">Has a partner used arousal products before?</label>
                    <select
                      value={form.partnerUsedArousalProducts}
                      onChange={e => setForm({ ...form, partnerUsedArousalProducts: e.target.value })}
                      className="w-full bg-zinc-700 border border-zinc-600 rounded-xl px-4 py-3 text-white"
                    >
                      <option value="">Select...</option>
                      <option value="Yes - Soaking Wet">Yes - Soaking Wet specifically</option>
                      <option value="Yes - other products">Yes - other products</option>
                      <option value="Not that I know of">Not that I know of</option>
                      <option value="No">No</option>
                    </select>
                  </div>
                </>
              )}

              {!isMale && !isFemale && (
                <div className="bg-zinc-900 rounded-xl p-4 text-center">
                  <p className="text-zinc-400">Questions will be tailored based on your gender selection. You selected "{form.gender}".</p>
                  <p className="text-zinc-500 text-sm mt-2">Feel free to continue to the next step.</p>
                </div>
              )}
            </div>
            <div className="flex gap-3 mt-6">
              <button onClick={() => setStep(3)} className="flex items-center gap-2 bg-zinc-700 text-zinc-300 px-5 py-3 rounded-xl">
                <ArrowLeft className="w-4 h-4" /> Back
              </button>
              <button
                onClick={() => setStep(5)}
                disabled={!canProceedStep4}
                className="flex-1 flex items-center justify-center gap-2 text-zinc-900 px-5 py-3 rounded-xl font-semibold disabled:opacity-50"
                style={{ backgroundColor: COLORS.lime }}
              >
                Continue <ArrowRight className="w-4 h-4" />
              </button>
            </div>
          </div>
        )}

        {/* Step 5: Couple Experience Interest */}
        {step === 5 && (
          <div className="bg-zinc-800 rounded-2xl p-6 border border-zinc-700">
            <h2 className="text-2xl font-bold text-white mb-2 flex items-center gap-3">
              <HeartHandshake className="w-6 h-6 text-pink-400" /> Couple Experience
            </h2>
            <p className="text-zinc-400 text-sm mb-4">
              Beyond reviewing, we offer something unique for couples. Here's what's possible:
            </p>

            {/* What's possible pitch */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-6">
              <div className="bg-gradient-to-r from-pink-500/10 to-purple-500/10 border border-pink-500/30 rounded-xl p-3">
                <p className="text-pink-400 font-semibold text-sm mb-1">Anonymous Expert Feedback</p>
                <p className="text-zinc-400 text-xs">A reviewer who doesn't know you personally gives honest, constructive feedback on your intimate experiences - no judgement, just helpful insights.</p>
              </div>
              <div className="bg-gradient-to-r from-blue-500/10 to-cyan-500/10 border border-blue-500/30 rounded-xl p-3">
                <p className="text-blue-400 font-semibold text-sm mb-1">Curated Date Nights</p>
                <p className="text-zinc-400 text-xs">Based on your preferences, size, wetness, and what excites you both - we hand-pick activities and clips tailored specifically to your couple.</p>
              </div>
              <div className="bg-gradient-to-r from-green-500/10 to-emerald-500/10 border border-green-500/30 rounded-xl p-3">
                <p className="text-green-400 font-semibold text-sm mb-1">Scheduled Activities</p>
                <p className="text-zinc-400 text-xs">Get suggestions scheduled into your calendar - try new things on a regular cadence without the pressure of deciding what to do.</p>
              </div>
              <div className="bg-gradient-to-r from-amber-500/10 to-orange-500/10 border border-amber-500/30 rounded-xl p-3">
                <p className="text-amber-400 font-semibold text-sm mb-1">Private Chat with Reviewer</p>
                <p className="text-zinc-400 text-xs">Two-way chat with your assigned reviewer. Ask questions, share how things went, get tailored recommendations in real time.</p>
              </div>
            </div>

            <div className="space-y-5">
              <div>
                <label className="block text-sm font-medium text-zinc-300 mb-2">Does this sound interesting to you as a couple?</label>
                <select value={form.interestedInCoupleExperience} onChange={e => setForm({ ...form, interestedInCoupleExperience: e.target.value })} className="w-full bg-zinc-700 border border-zinc-600 rounded-xl px-4 py-3 text-white">
                  <option value="">Select...</option>
                  <option value="Yes, definitely">Yes, definitely - sign us up</option>
                  <option value="Curious">Curious - tell me more</option>
                  <option value="Maybe later">Maybe later</option>
                  <option value="Just reviewing">I'm just here to review, thanks</option>
                </select>
              </div>

              {(form.interestedInCoupleExperience === 'Yes, definitely' || form.interestedInCoupleExperience === 'Curious') && (
                <>
                  <div>
                    <label className="block text-sm font-medium text-zinc-300 mb-2">Would you be open to submitting clips of your experiences for anonymous feedback?</label>
                    <select value={form.wouldSubmitClips} onChange={e => setForm({ ...form, wouldSubmitClips: e.target.value })} className="w-full bg-zinc-700 border border-zinc-600 rounded-xl px-4 py-3 text-white">
                      <option value="">Select...</option>
                      <option value="yes">Yes - we'd love honest feedback</option>
                      <option value="maybe">Maybe - depends on how anonymous it is</option>
                      <option value="no">No - we'd prefer just receiving suggestions</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-zinc-300 mb-2">How comfortable are you with someone anonymously giving feedback on your intimate life?</label>
                    <select value={form.comfortWithAnonymousFeedback} onChange={e => setForm({ ...form, comfortWithAnonymousFeedback: e.target.value })} className="w-full bg-zinc-700 border border-zinc-600 rounded-xl px-4 py-3 text-white">
                      <option value="">Select...</option>
                      <option value="Very comfortable">Very comfortable - bring it on</option>
                      <option value="Somewhat comfortable">Somewhat comfortable - as long as it's constructive</option>
                      <option value="Nervous but open">Nervous but open to trying</option>
                      <option value="Not comfortable">Not comfortable with that</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-zinc-300 mb-2">Would you like activities scheduled into your calendar?</label>
                    <select value={form.interestedInScheduledActivities} onChange={e => setForm({ ...form, interestedInScheduledActivities: e.target.value })} className="w-full bg-zinc-700 border border-zinc-600 rounded-xl px-4 py-3 text-white">
                      <option value="">Select...</option>
                      <option value="Yes, weekly">Yes - weekly suggestions would be great</option>
                      <option value="Yes, occasionally">Yes - but not too often, maybe fortnightly</option>
                      <option value="On demand">Just when we ask for them</option>
                      <option value="No">No thanks</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-zinc-300 mb-2">Would you want experiences curated to your specific preferences?</label>
                    <p className="text-zinc-500 text-xs mb-2">e.g. based on his size, her wetness, what you've tried, what you fantasize about</p>
                    <select value={form.interestedInCuratedExperiences} onChange={e => setForm({ ...form, interestedInCuratedExperiences: e.target.value })} className="w-full bg-zinc-700 border border-zinc-600 rounded-xl px-4 py-3 text-white">
                      <option value="">Select...</option>
                      <option value="Yes, the more personal the better">Yes - the more personal the better</option>
                      <option value="Yes, but keep it tasteful">Yes - but keep the suggestions tasteful</option>
                      <option value="Generic is fine">Generic suggestions are fine</option>
                    </select>
                  </div>

                  {/* Preference details - shown if they want curated experiences */}
                  {form.interestedInCuratedExperiences && form.interestedInCuratedExperiences !== 'Generic is fine' && (
                    <div className="bg-gradient-to-r from-purple-500/10 to-pink-500/10 border border-purple-500/30 rounded-xl p-4 space-y-4">
                      <p className="text-purple-400 text-sm font-medium">Help us curate for you</p>

                      {isFemale && (
                        <div>
                          <label className="block text-sm font-medium text-zinc-300 mb-2">How important is your partner's size to your experience?</label>
                          <select value={form.partnerSizePreference} onChange={e => setForm({ ...form, partnerSizePreference: e.target.value })} className="w-full bg-zinc-700 border border-zinc-600 rounded-xl px-4 py-3 text-white">
                            <option value="">Select...</option>
                            <option value="Very important">Very important</option>
                            <option value="Somewhat matters">Somewhat matters</option>
                            <option value="Doesn't matter">Doesn't really matter</option>
                                    </select>
                        </div>
                      )}

                      {isMale && (
                        <div>
                          <label className="block text-sm font-medium text-zinc-300 mb-2">How would you describe your size?</label>
                          <select value={form.partnerSizeDescription} onChange={e => setForm({ ...form, partnerSizeDescription: e.target.value })} className="w-full bg-zinc-700 border border-zinc-600 rounded-xl px-4 py-3 text-white">
                            <option value="">Select...</option>
                            <option value="Small">On the smaller side</option>
                            <option value="Average">Average</option>
                            <option value="Above average">Above average</option>
                            <option value="Large">Large</option>
                                    </select>
                        </div>
                      )}

                      <div>
                        <label className="block text-sm font-medium text-zinc-300 mb-2">
                          {isFemale ? 'How important is wetness/arousal to your enjoyment?' : 'How important is her wetness to the experience for you?'}
                        </label>
                        <select value={form.wetnessImportance} onChange={e => setForm({ ...form, wetnessImportance: e.target.value })} className="w-full bg-zinc-700 border border-zinc-600 rounded-xl px-4 py-3 text-white">
                          <option value="">Select...</option>
                          <option value="Very important">Very important - the wetter the better</option>
                          <option value="Important">Important - it makes a real difference</option>
                          <option value="Nice but not essential">Nice but not essential</option>
                          <option value="Not something I think about">Not something I think about</option>
                        </select>
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-zinc-300 mb-2">How open are you both to trying completely new things?</label>
                        <select value={form.openToNewThings} onChange={e => setForm({ ...form, openToNewThings: e.target.value })} className="w-full bg-zinc-700 border border-zinc-600 rounded-xl px-4 py-3 text-white">
                          <option value="">Select...</option>
                          <option value="Very open - push our boundaries">Very open - push our boundaries</option>
                          <option value="Open but gradual">Open but take it gradual</option>
                          <option value="Stick to what we know mostly">Stick to what we know mostly</option>
                          <option value="Only tried and tested">Only tried and tested things</option>
                        </select>
                      </div>
                    </div>
                  )}

                  <div>
                    <label className="block text-sm font-medium text-zinc-300 mb-2">Anything else you'd want from a couple experience?</label>
                    <textarea value={form.coupleExperienceNotes} onChange={e => setForm({ ...form, coupleExperienceNotes: e.target.value })} placeholder="What would make this amazing for you? Any specific things you'd love help with..." className="w-full bg-zinc-700 border border-zinc-600 rounded-xl px-4 py-3 text-white placeholder-zinc-500 h-20 resize-none" />
                  </div>
                </>
              )}
            </div>

            <div className="flex gap-3 mt-6">
              <button onClick={() => setStep(4)} className="flex items-center gap-2 bg-zinc-700 text-zinc-300 px-5 py-3 rounded-xl">
                <ArrowLeft className="w-4 h-4" /> Back
              </button>
              <button
                onClick={() => setStep(6)}
                className="flex-1 flex items-center justify-center gap-2 text-zinc-900 px-5 py-3 rounded-xl font-semibold"
                style={{ backgroundColor: COLORS.lime }}
              >
                Continue <ArrowRight className="w-4 h-4" />
              </button>
            </div>
          </div>
        )}

        {/* Step 6: Partner & Final */}
        {step === 6 && (
          <div className="bg-zinc-800 rounded-2xl p-6 border border-zinc-700">
            <h2 className="text-2xl font-bold text-white mb-2 flex items-center gap-3">
              <Heart className="w-6 h-6 text-pink-400" /> Experience Date Night Too!
            </h2>
            <p className="text-zinc-400 text-sm mb-6">
              Don't just review - actually try things with your partner! Add them below to get a couple code.
            </p>

            <div className="space-y-5">
              {/* Partner Name/Codename Section */}
              <div className="bg-gradient-to-r from-pink-500/10 to-purple-500/10 border border-pink-500/30 rounded-xl p-4">
                <div className="flex items-center gap-3 mb-4">
                  <button
                    onClick={() => { setUseCodename(false); setSelectedCodename(''); }}
                    className={`flex-1 py-2 rounded-lg text-sm font-medium ${!useCodename ? 'bg-pink-500 text-white' : 'bg-zinc-700 text-zinc-400'}`}
                  >
                    Real Name
                  </button>
                  <button
                    onClick={() => { setUseCodename(true); setForm({ ...form, partnerName: '' }); }}
                    className={`flex-1 py-2 rounded-lg text-sm font-medium ${useCodename ? 'bg-pink-500 text-white' : 'bg-zinc-700 text-zinc-400'}`}
                  >
                    Fun Codename 😈
                  </button>
                </div>

                {!useCodename ? (
                  <div>
                    <label className="block text-sm font-medium text-zinc-300 mb-2">Partner's name <span className="text-zinc-500">(optional)</span></label>
                    <input
                      type="text"
                      value={form.partnerName}
                      onChange={e => setForm({ ...form, partnerName: e.target.value })}
                      placeholder="e.g. Mike, Sarah..."
                      className="w-full bg-zinc-700 border border-zinc-600 rounded-xl px-4 py-3 text-white placeholder-zinc-500"
                    />
                  </div>
                ) : (
                  <div>
                    <label className="block text-sm font-medium text-zinc-300 mb-2">Pick a codename for your partner</label>
                    <div className="grid grid-cols-2 gap-2">
                      {PARTNER_CODENAMES.map(name => (
                        <button
                          key={name}
                          onClick={() => setSelectedCodename(name)}
                          className={`px-3 py-2 rounded-lg text-sm ${selectedCodename === name ? 'bg-pink-500 text-white' : 'bg-zinc-700 text-zinc-300 hover:bg-zinc-600'}`}
                        >
                          {name}
                        </button>
                      ))}
                    </div>
                    <input
                      type="text"
                      value={selectedCodename.startsWith('💕') || selectedCodename.startsWith('🔥') ? '' : selectedCodename}
                      onChange={e => setSelectedCodename(e.target.value)}
                      placeholder="Or type your own..."
                      className="w-full bg-zinc-700 border border-zinc-600 rounded-xl px-4 py-3 text-white placeholder-zinc-500 mt-2"
                    />
                  </div>
                )}

                {hasPartner && (
                  <p className="text-pink-400 text-xs mt-3">
                    You'll get a couple code (VBC-XXXXXX) to share with friends who can recommend clips for you!
                  </p>
                )}
              </div>

              {/* Sexual Experience Details Section */}
              <div className="bg-gradient-to-r from-red-500/10 to-orange-500/10 border border-red-500/30 rounded-xl p-4">
                <h3 className="text-white font-semibold mb-3 flex items-center gap-2">
                  🔥 Sexual Experience Details <span className="text-zinc-500 text-sm font-normal">(helps personalize recommendations)</span>
                </h3>

                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-zinc-300 mb-2">Describe your last sexual activity</label>
                    <textarea
                      value={form.lastSexualActivity}
                      onChange={e => setForm({ ...form, lastSexualActivity: e.target.value })}
                      placeholder="What did you do? How was it? What did you enjoy most?"
                      className="w-full bg-zinc-700 border border-zinc-600 rounded-xl px-4 py-3 text-white placeholder-zinc-500 h-20 resize-none"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-zinc-300 mb-2">Have you had/given facials? Describe your experience</label>
                    <textarea
                      value={form.facialExperience}
                      onChange={e => setForm({ ...form, facialExperience: e.target.value })}
                      placeholder="Yes/No, how often, what do you think of them, any memorable experiences..."
                      className="w-full bg-zinc-700 border border-zinc-600 rounded-xl px-4 py-3 text-white placeholder-zinc-500 h-20 resize-none"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-zinc-300 mb-2">Describe your oral experiences</label>
                    <textarea
                      value={form.oralExperience}
                      onChange={e => setForm({ ...form, oralExperience: e.target.value })}
                      placeholder="Do you enjoy giving/receiving? Any preferences or memorable experiences..."
                      className="w-full bg-zinc-700 border border-zinc-600 rounded-xl px-4 py-3 text-white placeholder-zinc-500 h-20 resize-none"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-zinc-300 mb-2">Have you tried anal? Describe</label>
                    <textarea
                      value={form.analExperience}
                      onChange={e => setForm({ ...form, analExperience: e.target.value })}
                      placeholder="Yes/No, thoughts on it, any experiences you'd like to share..."
                      className="w-full bg-zinc-700 border border-zinc-600 rounded-xl px-4 py-3 text-white placeholder-zinc-500 h-20 resize-none"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-zinc-300 mb-2">Most memorable/favorite sexual experience</label>
                    <textarea
                      value={form.favoriteSexualMemory}
                      onChange={e => setForm({ ...form, favoriteSexualMemory: e.target.value })}
                      placeholder="Tell us about a time that really stands out..."
                      className="w-full bg-zinc-700 border border-zinc-600 rounded-xl px-4 py-3 text-white placeholder-zinc-500 h-20 resize-none"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-zinc-300 mb-2">Fantasies you haven't tried yet</label>
                    <textarea
                      value={form.fantasiesNotTried}
                      onChange={e => setForm({ ...form, fantasiesNotTried: e.target.value })}
                      placeholder="What would you like to explore? What's on your bucket list?"
                      className="w-full bg-zinc-700 border border-zinc-600 rounded-xl px-4 py-3 text-white placeholder-zinc-500 h-20 resize-none"
                    />
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-zinc-300 mb-2">Biggest turn-ons</label>
                      <textarea
                        value={form.biggestTurnOns}
                        onChange={e => setForm({ ...form, biggestTurnOns: e.target.value })}
                        placeholder="What really gets you going..."
                        className="w-full bg-zinc-700 border border-zinc-600 rounded-xl px-4 py-3 text-white placeholder-zinc-500 h-16 resize-none"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-zinc-300 mb-2">Deal breakers / Turn-offs</label>
                      <textarea
                        value={form.dealBreakers}
                        onChange={e => setForm({ ...form, dealBreakers: e.target.value })}
                        placeholder="What you absolutely don't like..."
                        className="w-full bg-zinc-700 border border-zinc-600 rounded-xl px-4 py-3 text-white placeholder-zinc-500 h-16 resize-none"
                      />
                    </div>
                  </div>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-zinc-300 mb-2">Additional notes <span className="text-zinc-500">(optional)</span></label>
                <textarea
                  value={form.additionalNotes}
                  onChange={e => setForm({ ...form, additionalNotes: e.target.value })}
                  placeholder="Any other context, preferences, or things you'd like to share..."
                  className="w-full bg-zinc-700 border border-zinc-600 rounded-xl px-4 py-3 text-white placeholder-zinc-500 h-20 resize-none"
                />
              </div>
            </div>

            <div className="bg-zinc-900 rounded-xl p-4 mt-6">
              <p className="text-xs text-zinc-500 mb-2">Summary</p>
              <div className="grid grid-cols-2 gap-2 text-sm">
                <p className="text-zinc-400">Gender: <span className="text-white">{form.gender}</span></p>
                <p className="text-zinc-400">Age: <span className="text-white">{form.ageRange}</span></p>
                <p className="text-zinc-400">Relationship: <span className="text-white">{form.relationshipStatus}</span></p>
                <p className="text-zinc-400">Watches content: <span className="text-white">{form.watchesAdultContent}</span></p>
                {hasPartner && (
                  <p className="text-zinc-400">Partner: <span className="text-pink-400">{useCodename ? selectedCodename : form.partnerName}</span></p>
                )}
              </div>
            </div>

            <div className="flex gap-3 mt-6">
              <button onClick={() => setStep(5)} className="flex items-center gap-2 bg-zinc-700 text-zinc-300 px-5 py-3 rounded-xl">
                <ArrowLeft className="w-4 h-4" /> Back
              </button>
              {hasPartner ? (
                <button
                  onClick={() => { generateSampleClips(); setStep(7); }}
                  className="flex-1 flex items-center justify-center gap-2 text-zinc-900 px-5 py-3 rounded-xl font-semibold"
                  style={{ backgroundColor: COLORS.lime }}
                >
                  Set Preferences <ArrowRight className="w-4 h-4" />
                </button>
              ) : (
                <button
                  onClick={register}
                  className="flex-1 flex items-center justify-center gap-2 text-zinc-900 px-5 py-3 rounded-xl font-semibold"
                  style={{ backgroundColor: COLORS.lime }}
                >
                  Start Reviewing <ArrowRight className="w-4 h-4" />
                </button>
              )}
            </div>
          </div>
        )}

        {/* Step 7: Couple Date Night Experience (only if has partner) */}
        {step === 7 && (
          <div className="bg-zinc-800 rounded-2xl p-6 border border-zinc-700">
            {currentSampleIndex < sampleClips.length ? (
              <>
                {/* Intro banner on first clip */}
                {currentSampleIndex === 0 && (
                  <div className="bg-gradient-to-r from-pink-500/20 to-purple-500/20 border border-pink-500/30 rounded-xl p-4 mb-6">
                    <div className="flex items-center gap-3 mb-2">
                      <Heart className="w-6 h-6 text-pink-400" />
                      <h3 className="text-lg font-bold text-white">Experience Date Night Together!</h3>
                    </div>
                    <p className="text-zinc-300 text-sm">
                      Before you start reviewing, let's find something fun for you & <span className="text-pink-400 font-medium">{useCodename ? selectedCodename : form.partnerName}</span> to try tonight!
                      We'll show you 5 different activities - tell us which ones excite you both.
                    </p>
                  </div>
                )}

                {/* Progress */}
                <div className="flex items-center justify-between mb-4">
                  <h2 className="text-xl font-bold text-white flex items-center gap-2">
                    <Sparkles className="w-5 h-5" style={{ color: COLORS.lime }} /> Would You Two Try This?
                  </h2>
                  <span className="text-zinc-400 text-sm font-medium">
                    {currentSampleIndex + 1} of {sampleClips.length}
                  </span>
                </div>

                {sampleClips[currentSampleIndex] && (() => {
                  const clip = sampleClips[currentSampleIndex];
                  const cat = CATEGORIES.find(c => c.id === clip.categoryId);

                  return (
                    <div className="mb-6">
                      <div className={`bg-gradient-to-r ${cat?.color} rounded-xl p-4 mb-4`}>
                        <div className="flex items-center gap-3">
                          <span className="text-4xl">{cat?.emoji}</span>
                          <div>
                            <p className="text-white/70 text-sm">{cat?.name}</p>
                            <p className="text-white font-bold text-lg">{clip.title}</p>
                          </div>
                        </div>
                      </div>

                      {/* Embedded video preview */}
                      <VideoPlayer url={clip.link} className="mb-4" />

                      <p className="text-zinc-300 text-sm mb-4">{clip.description}</p>
                    </div>
                  );
                })()}

                <p className="text-zinc-500 text-xs text-center mb-4">
                  Think about it together - would you two want to recreate this?
                </p>

                <div className="grid grid-cols-3 gap-3">
                  <button
                    onClick={() => rateSampleClip('try')}
                    className="py-4 rounded-xl text-center bg-green-500/20 border-2 border-green-500/50 text-green-400 hover:bg-green-500/30 transition-all hover:scale-105"
                  >
                    <span className="text-2xl block mb-1">🔥</span>
                    <span className="text-sm font-medium">We'd Try It!</span>
                  </button>
                  <button
                    onClick={() => rateSampleClip('maybe')}
                    className="py-4 rounded-xl text-center bg-yellow-500/20 border-2 border-yellow-500/50 text-yellow-400 hover:bg-yellow-500/30 transition-all hover:scale-105"
                  >
                    <span className="text-2xl block mb-1">🤔</span>
                    <span className="text-sm font-medium">Maybe...</span>
                  </button>
                  <button
                    onClick={() => rateSampleClip('skip')}
                    className="py-4 rounded-xl text-center bg-zinc-700 border-2 border-zinc-600 text-zinc-400 hover:bg-zinc-600 transition-all hover:scale-105"
                  >
                    <span className="text-2xl block mb-1">👋</span>
                    <span className="text-sm font-medium">Not For Us</span>
                  </button>
                </div>
              </>
            ) : (
              <>
                {/* Done rating - Pick what to try */}
                <div className="py-4">
                  <div className="text-center mb-6">
                    <div className="w-16 h-16 rounded-full bg-pink-500/20 flex items-center justify-center mx-auto mb-4">
                      <Heart className="w-8 h-8 text-pink-400" />
                    </div>
                    <h2 className="text-xl font-bold text-white mb-2">
                      {couplePreferences.filter(p => p.rating === 'try').length > 0
                        ? `Perfect! You Found ${couplePreferences.filter(p => p.rating === 'try').length} To Try!`
                        : 'All Done!'}
                    </h2>
                    <p className="text-zinc-400 text-sm">
                      {couplePreferences.filter(p => p.rating === 'try').length > 0
                        ? `Select which one you & ${useCodename ? selectedCodename : form.partnerName} want to try first:`
                        : "No worries - you can explore all clips once you're in!"}
                    </p>
                  </div>

                  {/* Selectable list of clips they liked */}
                  {couplePreferences.filter(p => p.rating === 'try').length > 0 && (
                    <div className="bg-zinc-900 rounded-xl p-4 mb-6">
                      <p className="text-xs text-zinc-500 mb-3">Tap to select your first challenge:</p>
                      <div className="space-y-2">
                        {couplePreferences.filter(p => p.rating === 'try').map((pref, i) => {
                          const isSelected = selectedToTry.includes(pref.clipId);
                          const cat = CATEGORIES.find(c => c.id === pref.categoryId);
                          return (
                            <button
                              key={i}
                              onClick={() => {
                                if (isSelected) {
                                  setSelectedToTry(prev => prev.filter(id => id !== pref.clipId));
                                } else {
                                  setSelectedToTry([pref.clipId]); // Single selection
                                }
                              }}
                              className={`w-full flex items-center gap-3 p-3 rounded-xl transition-all ${
                                isSelected
                                  ? 'bg-green-500/20 border-2 border-green-500'
                                  : 'bg-zinc-800 border-2 border-zinc-700 hover:border-zinc-600'
                              }`}
                            >
                              <span className="text-2xl">{cat?.emoji}</span>
                              <span className={`flex-1 text-left ${isSelected ? 'text-green-400 font-medium' : 'text-zinc-300'}`}>
                                {pref.clipTitle}
                              </span>
                              {isSelected && <Check className="w-5 h-5 text-green-400" />}
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  )}

                  {/* Maybe section */}
                  {couplePreferences.filter(p => p.rating === 'maybe').length > 0 && (
                    <div className="bg-zinc-900/50 rounded-xl p-4 mb-6">
                      <p className="text-xs text-zinc-500 mb-2">Maybes (come back to these later):</p>
                      <div className="flex flex-wrap gap-2">
                        {couplePreferences.filter(p => p.rating === 'maybe').map((pref, i) => (
                          <span key={i} className="px-3 py-1 bg-yellow-500/10 text-yellow-400 text-sm rounded-full">
                            {pref.clipTitle}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}

                  {couplePreferences.filter(p => p.rating === 'try').length === 0 && (
                    <button
                      onClick={loadMoreSamples}
                      className="w-full mb-3 flex items-center justify-center gap-2 bg-zinc-700 text-zinc-300 px-5 py-3 rounded-xl hover:bg-zinc-600"
                    >
                      <RefreshCw className="w-4 h-4" /> Show Me 5 More
                    </button>
                  )}

                  <button
                    onClick={register}
                    className="w-full flex items-center justify-center gap-2 text-zinc-900 px-5 py-3 rounded-xl font-semibold"
                    style={{ backgroundColor: COLORS.lime }}
                  >
                    {selectedToTry.length > 0 ? 'Set Challenge & Start Reviewing' : 'Start Reviewing'} <ArrowRight className="w-4 h-4" />
                  </button>
                </div>
              </>
            )}

            {currentSampleIndex < sampleClips.length && (
              <div className="flex gap-3 mt-6">
                <button
                  onClick={() => setStep(6)}
                  className="flex items-center gap-2 bg-zinc-700 text-zinc-300 px-5 py-3 rounded-xl"
                >
                  <ArrowLeft className="w-4 h-4" /> Back
                </button>
                <button
                  onClick={() => {
                    setCurrentSampleIndex(sampleClips.length); // Skip to end
                  }}
                  className="flex-1 text-zinc-400 hover:text-white text-sm"
                >
                  Skip for now
                </button>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );

  const unseenRecommendations = recommendations.filter(r => !r.seen);

  // =========================================
  // REVIEWER DASHBOARD (logged in reviewer)
  // =========================================
  return (
    <div className="min-h-screen bg-zinc-900 p-4 sm:p-6">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="bg-zinc-800 rounded-2xl p-5 mb-6 border border-zinc-700">
          <div className="flex flex-wrap justify-between items-center gap-3">
            <div className="flex items-center gap-4">
              <BackButton onClick={onBack} />
              <div>
                <h1 className="text-xl font-bold text-white">Reviewer Dashboard</h1>
                <p className="text-zinc-400 text-sm">{currentReviewer.id}</p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              {/* Mode Toggle */}
              <button
                onClick={async () => {
                  if (!currentReviewer.coupleId) {
                    // Show couple setup modal if no couple profile
                    setShowCoupleSetupModal(true);
                  } else if (currentReviewer.mode === 'couple') {
                    // Switching back to reviewer mode - no confirmation needed
                    const updatedReviewer: Reviewer = { ...currentReviewer, mode: 'reviewer' };
                    const updated = reviewers.map(r => r.id === currentReviewer.id ? updatedReviewer : r);
                    await saveReviewers(updated);
                    setCurrentReviewer(updatedReviewer);
                    setTab('datenite');
                  } else {
                    // Switching TO couple mode - show confirmation
                    setShowModeConfirm(true);
                  }
                }}
                className={`flex items-center gap-3 px-4 py-2 rounded-full font-semibold text-sm transition-all ${
                  currentReviewer.mode === 'couple'
                    ? 'bg-gradient-to-r from-pink-500 to-rose-500 text-white shadow-lg shadow-pink-500/30'
                    : currentReviewer.coupleId
                      ? 'bg-zinc-700 text-zinc-300 border border-zinc-600 hover:border-pink-500/50'
                      : 'bg-pink-500/10 text-pink-400 border border-pink-500/30 hover:bg-pink-500/20'
                }`}
              >
                {/* Toggle indicator */}
                <div className={`w-10 h-5 rounded-full relative transition-colors ${
                  currentReviewer.mode === 'couple' ? 'bg-white/30' : 'bg-zinc-600'
                }`}>
                  <div className={`absolute top-0.5 w-4 h-4 rounded-full transition-all ${
                    currentReviewer.mode === 'couple'
                      ? 'right-0.5 bg-white'
                      : 'left-0.5 bg-zinc-400'
                  }`} />
                </div>
                {currentReviewer.mode === 'couple' ? (
                  <>
                    <Heart className="w-4 h-4" />
                    Couple
                  </>
                ) : currentReviewer.coupleId ? (
                  <>
                    <ClipboardList className="w-4 h-4" />
                    Reviewer
                  </>
                ) : (
                  <>
                    <Heart className="w-4 h-4" />
                    Set Up Couple
                  </>
                )}
              </button>
              <button
                onClick={loadData}
                className="flex items-center gap-2 text-zinc-900 px-4 py-2 rounded-full font-semibold text-sm"
                style={{ backgroundColor: COLORS.lime }}
              >
                <RefreshCw className="w-4 h-4" />
              </button>
            </div>
          </div>

          {/* Couple Code Section */}
          {currentReviewer.coupleId && (
            <div className="mt-4 pt-4 border-t border-zinc-700">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <Heart className="w-5 h-5 text-pink-400" />
                  <div>
                    <p className="text-zinc-400 text-xs">Your Couple Code {currentReviewer.partnerName && `(You & ${currentReviewer.partnerName})`}</p>
                    <p className="text-pink-400 font-mono font-bold">{currentReviewer.coupleId}</p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => copyCode(currentReviewer.coupleId!)}
                    className="flex items-center gap-1 px-3 py-1.5 bg-pink-500/20 text-pink-400 rounded-lg text-sm hover:bg-pink-500/30"
                  >
                    <Copy className="w-3 h-3" />
                    {codeCopied ? 'Copied!' : 'Share'}
                  </button>
                  {(linkedCoupleData?.hisProfile || ((linkedCoupleData?.tried || []).length > 0)) && (
                    <button
                      onClick={() => setShowOwnCoupleDetails(!showOwnCoupleDetails)}
                      className="flex items-center gap-1 px-2 py-1.5 text-zinc-400 hover:text-zinc-200 text-xs transition-colors"
                    >
                      <ChevronDown className={`w-3.5 h-3.5 transition-transform ${showOwnCoupleDetails ? 'rotate-180' : ''}`} />
                      {showOwnCoupleDetails ? 'Hide' : 'Details'}
                    </button>
                  )}
                </div>
              </div>
              <p className="text-zinc-500 text-xs mt-2">Share this code with friends so they can recommend clips for your date nights!</p>

              {/* His Profile Display + Recently Tried (collapsible) */}
              {showOwnCoupleDetails && (<>
              {/* His Profile Display (if linked couple has one) */}
              {linkedCoupleData?.hisProfile && (linkedCoupleData.hisProfile.size || linkedCoupleData.hisProfile.girth || linkedCoupleData.hisProfile.cumIntensity || linkedCoupleData.hisProfile.cumConsistency || linkedCoupleData.hisProfile.cumTaste || linkedCoupleData.hisProfile.precumProduction) && (
                <div className="mt-4 bg-gradient-to-r from-blue-500/10 to-purple-500/10 rounded-lg p-3 border border-blue-500/20">
                  <p className="text-zinc-400 text-xs mb-2 flex items-center gap-1">
                    <span>🍆</span> His Stats
                  </p>
                  <div className="flex flex-wrap items-center gap-3 text-sm">
                    {linkedCoupleData.hisProfile.size && (
                      <span className="flex items-center gap-1">
                        <span className="text-lg">{HIS_SIZE_SCALE.find(s => s.value === linkedCoupleData.hisProfile?.size)?.emoji}</span>
                        <span className="text-zinc-400">{HIS_SIZE_SCALE.find(s => s.value === linkedCoupleData.hisProfile?.size)?.label}</span>
                      </span>
                    )}
                    {linkedCoupleData.hisProfile.girth && (
                      <span className="flex items-center gap-1">
                        <span className="text-lg">{HIS_GIRTH_SCALE.find(s => s.value === linkedCoupleData.hisProfile?.girth)?.emoji}</span>
                        <span className="text-zinc-400">{HIS_GIRTH_SCALE.find(s => s.value === linkedCoupleData.hisProfile?.girth)?.size}</span>
                      </span>
                    )}
                    {linkedCoupleData.hisProfile.cumIntensity && (
                      <span className="flex items-center gap-1">
                        <span className="text-lg">{HIS_CUM_SCALE.find(s => s.value === linkedCoupleData.hisProfile?.cumIntensity)?.emoji}</span>
                        <span className="text-zinc-400">{HIS_CUM_SCALE.find(s => s.value === linkedCoupleData.hisProfile?.cumIntensity)?.label}</span>
                      </span>
                    )}
                    {linkedCoupleData.hisProfile.cumConsistency && (
                      <span className="flex items-center gap-1">
                        <span className="text-lg">{HIS_CONSISTENCY_SCALE.find(s => s.value === linkedCoupleData.hisProfile?.cumConsistency)?.emoji}</span>
                        <span className="text-zinc-400">{HIS_CONSISTENCY_SCALE.find(s => s.value === linkedCoupleData.hisProfile?.cumConsistency)?.label}</span>
                      </span>
                    )}
                    {linkedCoupleData.hisProfile.cumTaste && (
                      <span className="flex items-center gap-1">
                        <span className="text-lg">{HIS_TASTE_SCALE.find(s => s.value === linkedCoupleData.hisProfile?.cumTaste)?.emoji}</span>
                        <span className="text-zinc-400">{HIS_TASTE_SCALE.find(s => s.value === linkedCoupleData.hisProfile?.cumTaste)?.label}</span>
                      </span>
                    )}
                    {linkedCoupleData.hisProfile.precumProduction && (
                      <span className="flex items-center gap-1">
                        <span className="text-lg">{HIS_PRECUM_SCALE.find(s => s.value === linkedCoupleData.hisProfile?.precumProduction)?.emoji}</span>
                        <span className="text-zinc-400">Pre: {HIS_PRECUM_SCALE.find(s => s.value === linkedCoupleData.hisProfile?.precumProduction)?.label}</span>
                      </span>
                    )}
                  </div>
                </div>
              )}

              {/* Recently Tried Activities */}
              {linkedCoupleData && (linkedCoupleData.tried || []).length > 0 && (
                <div className="mt-4 bg-zinc-800/50 rounded-lg p-3 border border-zinc-700">
                  <p className="text-zinc-400 text-xs mb-2 flex items-center gap-1">
                    <span>🕐</span> Recently Tried
                  </p>
                  <div className="space-y-2">
                    {(linkedCoupleData.tried || []).slice(-3).reverse().map((tried) => {
                      const clip = dateNiteClips.find(c => c.id === tried.clipId);
                      const category = clip ? CATEGORIES.find(c => c.id === clip.categoryId) : null;
                      const ratingInfo = tried.rating ? { hot: '🔥🔥🔥', good: '🔥🔥', meh: '🔥', nope: '❌' }[tried.rating] : '';
                      return (
                        <div
                          key={tried.clipId}
                          className={`text-sm ${clip ? 'cursor-pointer hover:bg-zinc-700/50 -mx-2 px-2 py-1 rounded-lg transition-colors group' : ''}`}
                          onClick={() => clip && setWatchingClip(clip)}
                        >
                          <div className="flex items-center gap-2">
                            <span className="text-lg">{category?.emoji}</span>
                            <span className="text-white flex items-center gap-1">
                              {clip?.title || 'Unknown'}
                              {clip && <Play className="w-3 h-3 text-zinc-500 group-hover:text-lime-400 transition-colors" />}
                            </span>
                            <span className="text-zinc-500 text-xs">
                              {new Date(tried.triedAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                            </span>
                            <span className="text-zinc-500">{ratingInfo}</span>
                          </div>
                          {tried.herThoughts && (
                            <p className="text-zinc-500 text-xs ml-7 mt-0.5">
                              "{tried.herThoughts}" - Her
                            </p>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
              </>)}
            </div>
          )}
        </div>

        {/* Recommendations Banner */}
        {unseenRecommendations.length > 0 && (
          <div
            className="bg-gradient-to-r from-purple-500/20 to-pink-500/20 border border-purple-500/30 rounded-xl p-4 mb-6 cursor-pointer hover:border-purple-500/50 transition-colors"
            onClick={() => setShowRecommendations(true)}
          >
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-gradient-to-r from-purple-500 to-pink-500 flex items-center justify-center">
                  <Gift className="w-5 h-5 text-white" />
                </div>
                <div>
                  <p className="text-white font-semibold">
                    {unseenRecommendations.length} New Recommendation{unseenRecommendations.length > 1 ? 's' : ''} from Friends!
                  </p>
                  <p className="text-zinc-400 text-sm">
                    {unseenRecommendations.map(r => r.fromName).join(', ')} sent you clips to try
                  </p>
                </div>
              </div>
              <ArrowRight className="w-5 h-5 text-purple-400" />
            </div>
          </div>
        )}

        <div className="flex gap-2 mb-4 overflow-x-auto pb-2 scrollbar-hide">
          {/* Couple Mode Tabs */}
          {currentReviewer?.mode === 'couple' ? (
            <>
              <button
                onClick={() => setTab('calendar')}
                className={`px-5 py-2.5 rounded-xl font-medium whitespace-nowrap ${tab === 'calendar' ? 'text-zinc-900' : 'bg-zinc-800 text-zinc-400'}`}
                style={tab === 'calendar' ? { backgroundColor: COLORS.lime } : {}}
              >
                <Calendar className="w-4 h-4 inline mr-2" /> Our Calendar
              </button>
              <button
                onClick={() => setTab('datenite')}
                className={`px-5 py-2.5 rounded-xl font-medium whitespace-nowrap ${tab === 'datenite' ? 'text-zinc-900' : 'bg-zinc-800 text-zinc-400'}`}
                style={tab === 'datenite' ? { backgroundColor: COLORS.lime } : {}}
              >
                <Heart className="w-4 h-4 inline mr-2" /> Browse Ideas
              </button>
              <button
                onClick={() => setTab('mypicks')}
                className={`px-5 py-2.5 rounded-xl font-medium whitespace-nowrap ${tab === 'mypicks' ? 'text-zinc-900' : 'bg-zinc-800 text-zinc-400'}`}
                style={tab === 'mypicks' ? { backgroundColor: COLORS.lime } : {}}
              >
                <Star className="w-4 h-4 inline mr-2" /> Our Wishlist ({wishlist.filter(w => w.coupleId === currentReviewer.coupleId && w.status === 'want-to-try').length})
              </button>
              <button
                onClick={() => setTab('couple')}
                className={`px-5 py-2.5 rounded-xl font-medium whitespace-nowrap ${tab === 'couple' ? 'text-zinc-900' : 'bg-zinc-800 text-zinc-400'}`}
                style={tab === 'couple' ? { backgroundColor: COLORS.lime } : {}}
              >
                <Users className="w-4 h-4 inline mr-2" /> Profile
              </button>
            </>
          ) : (
            <>
              {/* Reviewer Mode Tabs */}
              <button
                onClick={() => setTab('submissions')}
                className={`px-5 py-2.5 rounded-xl font-medium whitespace-nowrap relative ${tab === 'submissions' ? 'text-zinc-900' : 'bg-zinc-800 text-zinc-400'}`}
                style={tab === 'submissions' ? { backgroundColor: COLORS.lime } : {}}
              >
                <Video className="w-4 h-4 inline mr-2" /> Submissions
                {(() => {
                  const needsMyReview = submittedClips.filter(needsMySubmissionAction).length;
                  return needsMyReview > 0 ? (
                    <span className="absolute -top-1 -right-1 w-5 h-5 bg-orange-500 text-white text-xs rounded-full flex items-center justify-center">
                      {needsMyReview}
                    </span>
                  ) : null;
                })()}
              </button>
              <button
                onClick={() => setTab('datenite')}
                className={`px-5 py-2.5 rounded-xl font-medium whitespace-nowrap ${tab === 'datenite' ? 'text-zinc-900' : 'bg-zinc-800 text-zinc-400'}`}
                style={tab === 'datenite' ? { backgroundColor: COLORS.lime } : {}}
              >
                <ClipboardList className="w-4 h-4 inline mr-2" /> Review Queue
              </button>
              <button
                onClick={() => setTab('mypicks')}
                className={`px-5 py-2.5 rounded-xl font-medium whitespace-nowrap ${tab === 'mypicks' ? 'text-zinc-900' : 'bg-zinc-800 text-zinc-400'}`}
                style={tab === 'mypicks' ? { backgroundColor: COLORS.lime } : {}}
              >
                <Star className="w-4 h-4 inline mr-2" /> My Reviews ({dateNiteClips.filter(c => c.reviews?.some(r => isMyReview(r.reviewerId))).length})
              </button>
              {currentReviewer?.coupleId && (
                <button
                  onClick={() => setTab('calendar')}
                  className={`px-5 py-2.5 rounded-xl font-medium whitespace-nowrap ${tab === 'calendar' ? 'text-zinc-900' : 'bg-zinc-800 text-zinc-400'}`}
                  style={tab === 'calendar' ? { backgroundColor: COLORS.lime } : {}}
                >
                  <Calendar className="w-4 h-4 inline mr-2" /> Plan Dates
                </button>
              )}
              <button
                onClick={() => setTab('couple')}
                className={`px-5 py-2.5 rounded-xl font-medium whitespace-nowrap ${tab === 'couple' ? 'text-zinc-900' : 'bg-zinc-800 text-zinc-400'}`}
                style={tab === 'couple' ? { backgroundColor: COLORS.lime } : {}}
              >
                <Users className="w-4 h-4 inline mr-2" /> Manage Couple
                {!currentReviewer?.coupleId && <span className="ml-1 text-xs">(Link)</span>}
              </button>
              <button
                  onClick={() => setTab('inbox')}
                  className={`px-5 py-2.5 rounded-xl font-medium whitespace-nowrap relative ${tab === 'inbox' ? 'text-zinc-900' : 'bg-zinc-800 text-zinc-400'}`}
                  style={tab === 'inbox' ? { backgroundColor: COLORS.lime } : {}}
                >
                  <Inbox className="w-4 h-4 inline mr-2" /> Inbox
                  {(() => {
                    const couplePending = currentReviewer?.managedCoupleId ? (coupleSubmissions.filter(s => {
                      const reviewedByMe = s.reviewedByIds?.includes(currentReviewer.id) || (!s.reviewedByIds && s.reviewedBy === currentReviewer.id);
                      return !reviewedByMe && (s.status === 'pending' || s.status === 'reviewed');
                    }).length + coupleRequests.filter(r => r.status === 'pending').length) : 0;
                    const myTutorialProgress = tagTutorials.filter(t => t.reviewerId === currentReviewer?.id);
                    // Only count tutorials that have been assigned by admin and not completed
                    const unreadTutorials = myTutorialProgress.filter(t => t.assignedBy && t.status !== 'completed').length;
                    // Count follow-ups excluding edit notifications
                    const pendingFollowUps = reviewerFollowUps.filter(fu => fu.reviewerId === currentReviewer?.id && fu.status === 'pending' && !fu.editOutcome).length;
                    // Count unread edit notifications
                    const unreadEditNotifications = reviewerFollowUps.filter(fu => fu.reviewerId === currentReviewer?.id && fu.editOutcome && fu.status !== 'dismissed').length;
                    // Count pending clip suggestions for this reviewer
                    const pendingClipSuggestions = clipSuggestions.filter(cs => (cs.reviewerId === currentReviewer?.id || cs.reviewerId === currentReviewer?.code) && cs.status === 'pending').length;
                    const totalUnread = couplePending + unreadTutorials + pendingFollowUps + unreadEditNotifications + pendingClipSuggestions;
                    return totalUnread > 0 ? (
                      <span className="absolute -top-1 -right-1 w-5 h-5 bg-pink-500 text-white text-xs rounded-full flex items-center justify-center">
                        {totalUnread}
                      </span>
                    ) : null;
                  })()}
                </button>
            </>
          )}
        </div>

        {/* Try at Home Assignment Banner - only show on reviewer's own tabs, not on Manage Couple tab */}
        {tryAtHomeClip && (tab === 'datenite' || tab === 'calendar' || tab === 'mypicks') && (
          <div className="bg-gradient-to-r from-pink-500/20 via-orange-500/20 to-red-500/20 border-2 border-pink-500/50 rounded-xl p-5 mb-6 animate-pulse-slow">
            <div className="flex items-start gap-4">
              <div className="w-14 h-14 rounded-full bg-gradient-to-r from-pink-500 to-orange-500 flex items-center justify-center flex-shrink-0">
                <Heart className="w-7 h-7 text-white" />
              </div>
              <div className="flex-1">
                <h3 className="font-bold text-white text-lg flex items-center gap-2">
                  🔥 Tonight's Challenge{currentReviewer?.partnerName && ` for You & ${currentReviewer.partnerName}`}
                </h3>
                <p className="text-zinc-300 mt-1">
                  You picked this together - time to try it IRL! Let us know how it goes.
                </p>
                <div className="mt-3 bg-zinc-800/70 rounded-xl p-4">
                  <div className="flex items-center gap-3">
                    <span className="text-3xl">{CATEGORIES.find(c => c.id === tryAtHomeClip.categoryId)?.emoji}</span>
                    <div>
                      <p className="text-white font-semibold text-lg">{tryAtHomeClip.title}</p>
                      <p className="text-zinc-400 text-sm">{tryAtHomeClip.description}</p>
                    </div>
                  </div>
                </div>
                <div className="flex gap-3 mt-4">
                  <button
                    onClick={() => setShowChallengeVideo(true)}
                    className="flex-1 flex items-center justify-center gap-2 bg-zinc-700 text-white px-4 py-2.5 rounded-xl text-sm hover:bg-zinc-600 transition-colors"
                  >
                    <Play className="w-4 h-4" /> Watch Together
                  </button>
                  <button
                    onClick={() => setShowTryAtHomeFeedback(true)}
                    className="flex-1 flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl text-sm font-semibold text-zinc-900 transition-transform hover:scale-105"
                    style={{ backgroundColor: COLORS.lime }}
                  >
                    <Check className="w-4 h-4" /> We Did It!
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Incomplete Couple Profile Prompt - don't show on Manage Couple tab */}
        {tab !== 'couple' && currentReviewer?.coupleId && linkedCoupleData && (() => {
          const missingItems: string[] = [];

          // Check for missing preferences
          if (!linkedCoupleData.preferences || Object.keys(linkedCoupleData.preferences).length === 0) {
            missingItems.push('Category preferences');
          }

          // Check for missing his profile stats
          if (!linkedCoupleData.hisProfile || (!linkedCoupleData.hisProfile.size && !linkedCoupleData.hisProfile.cumIntensity)) {
            missingItems.push('His profile stats');
          }

          // Check for missing available days
          if (!linkedCoupleData.availableDays || linkedCoupleData.availableDays.length === 0) {
            missingItems.push('Available days for dates');
          }

          // Check for missing feedback
          if (!linkedCoupleData.wouldSubmitClips && !linkedCoupleData.productExcitement) {
            missingItems.push('Product feedback');
          }

          if (missingItems.length === 0) return null;

          return (
            <div className="bg-gradient-to-r from-purple-500/10 to-pink-500/10 border border-purple-500/30 rounded-xl p-4 mb-6">
              <div className="flex items-start gap-3">
                <div className="w-10 h-10 rounded-full bg-purple-500/20 flex items-center justify-center flex-shrink-0">
                  <AlertCircle className="w-5 h-5 text-purple-400" />
                </div>
                <div className="flex-1">
                  <h3 className="font-semibold text-white mb-1">Complete Your Couple Profile</h3>
                  <p className="text-zinc-400 text-sm mb-2">
                    Help us personalize your experience by filling in some missing info:
                  </p>
                  <div className="flex flex-wrap gap-2 mb-3">
                    {missingItems.map((item, i) => (
                      <span key={i} className="px-2 py-1 bg-purple-500/20 text-purple-300 text-xs rounded-full">
                        {item}
                      </span>
                    ))}
                  </div>
                  <button
                    onClick={() => {
                      // Navigate to DateNite view where onboarding will trigger
                      if (currentReviewer.coupleId && onSwitchToCoupleMode) {
                        onSwitchToCoupleMode(currentReviewer.coupleId);
                      }
                    }}
                    className="px-4 py-2 rounded-lg text-sm font-medium text-zinc-900"
                    style={{ backgroundColor: COLORS.lime }}
                  >
                    Complete Profile →
                  </button>
                </div>
              </div>
            </div>
          );
        })()}

        {/* === PLEASURE PLAYLIST + CATEGORY CHAMPIONS (show on datenite & mypicks) === */}
        {(tab === 'datenite' || tab === 'mypicks') && (() => {
          const myReviewedClipsForFeatures = dateNiteClips.filter(c => c.reviews?.some(r => isMyReview(r.reviewerId)));
          const playlist = currentReviewer.pleasurePlaylist;
          const validClips = getPlaylistClips();
          const enjoyedCount = playlist ? Object.keys(playlist.enjoyed).length : 0;
          const totalPlaylist = validClips.length;
          const highRatedCount = myReviewedClipsForFeatures.filter(c => {
            const review = c.reviews?.find(r => isMyReview(r.reviewerId));
            return review && (review.rating === '🔥 Must Try' || review.rating === '👍 Good');
          }).length;

          // Champions data
          const champions = currentReviewer.categoryChampions;
          const crownedCount = champions ? Object.keys(champions.champions).length : 0;
          const eligibleCategories = CATEGORIES.filter(cat => {
            const catClips = dateNiteClips.filter(c => {
              const inCat = c.categoryId === cat.id || c.additionalCategoryIds?.includes(cat.id);
              return inCat && c.reviews?.some(r => isMyReview(r.reviewerId));
            });
            return catClips.length >= 2;
          });
          const uncrownedCount = eligibleCategories.filter(cat => !champions?.champions[cat.id]).length;
          // Always call getNextMatchup when champions are shown - it handles both
          // uncrowned categories AND new challenger detection for crowned categories
          const matchup = showChampions ? getNextMatchup() : null;

          // Playlist player mode (full screen takeover)
          if (playlistMode && playlist && totalPlaylist > 0) {
            const nextIdx = getNextPlaylistIndex(validClips, playlist);
            const allDone = nextIdx < 0;
            const currentClipId = validClips[nextIdx >= 0 ? nextIdx : 0] ?? '';
            const currentClip = currentClipId ? dateNiteClips.find(c => c.id === currentClipId) : undefined;
            const cat = currentClip ? CATEGORIES.find(c => c.id === currentClip.categoryId) : null;
            const isSaved = currentClipId ? playlist.savedForLater.includes(currentClipId) : false;

            return (
              <div className="bg-zinc-900 rounded-2xl border border-purple-500/30 overflow-hidden mb-4">
                <div className="px-4 py-3 bg-gradient-to-r from-purple-500/20 to-pink-500/20 flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <button onClick={() => setPlaylistMode(false)} className="text-zinc-400 hover:text-white">
                      <ArrowLeft className="w-5 h-5" />
                    </button>
                    <span className="text-white font-medium">Your Playlist</span>
                  </div>
                  <span className="text-purple-300 text-sm">{enjoyedCount} of {totalPlaylist} enjoyed</span>
                </div>
                <div className="w-full bg-zinc-800 h-1">
                  <div className="h-1 bg-gradient-to-r from-purple-500 to-pink-500 transition-all" style={{ width: `${totalPlaylist > 0 ? (enjoyedCount / totalPlaylist) * 100 : 0}%` }} />
                </div>

                {allDone ? (
                  <div className="p-8 text-center">
                    <p className="text-4xl mb-4">🎉</p>
                    <h3 className="text-xl font-bold text-white mb-2">Playlist Complete!</h3>
                    <p className="text-zinc-400 mb-6">You've been through all {totalPlaylist} clips.</p>
                    <div className="flex gap-3 justify-center">
                      <button
                        onClick={() => {
                          const updatedPlaylist = { ...playlist, skipped: [] };
                          savePlaylistAction(updatedPlaylist);
                        }}
                        className="px-4 py-2 rounded-lg bg-purple-500/20 text-purple-300 hover:bg-purple-500/30 text-sm"
                      >
                        Replay Skipped
                      </button>
                      <button onClick={generatePlaylist} className="px-4 py-2 rounded-lg text-sm font-medium text-zinc-900" style={{ backgroundColor: COLORS.lime }}>
                        Regenerate Playlist
                      </button>
                      <button onClick={() => setPlaylistMode(false)} className="px-4 py-2 rounded-lg bg-zinc-700 text-zinc-300 text-sm">
                        Close
                      </button>
                    </div>
                  </div>
                ) : currentClip ? (
                  <>
                    <div className="px-4 py-2 flex items-center gap-2">
                      <span className="text-lg">{cat?.emoji}</span>
                      <span className="text-white font-medium text-sm">{currentClip.title}</span>
                      <span className="text-zinc-500 text-xs ml-auto">{cat?.name}</span>
                    </div>
                    <VideoPlayer url={currentClip.link} initialMuted={false} />
                    {showPlaylistReaction && (
                      <div className="px-4 py-3 bg-zinc-800/80 flex items-center justify-center gap-4">
                        <span className="text-zinc-400 text-sm mr-2">How was it?</span>
                        {['🔥', '💦', '😍', '🤤'].map(emoji => (
                          <button key={emoji} onClick={() => handlePlaylistEnjoy(currentClipId, emoji)} className="text-2xl hover:scale-125 transition-transform">
                            {emoji}
                          </button>
                        ))}
                        <button onClick={() => handlePlaylistEnjoy(currentClipId)} className="text-zinc-500 text-xs hover:text-zinc-300 ml-2">
                          Skip reaction
                        </button>
                      </div>
                    )}
                    {!showPlaylistReaction && (
                      <div className="p-4 flex gap-3">
                        <button onClick={() => setShowPlaylistReaction(true)} className="flex-1 flex items-center justify-center gap-2 px-4 py-3 rounded-xl font-medium text-sm bg-purple-500/20 text-purple-300 hover:bg-purple-500/30 border border-purple-500/30">
                          <Heart className="w-4 h-4" /> Enjoyed
                        </button>
                        <button onClick={() => handlePlaylistSkip(currentClipId)} className="flex-1 flex items-center justify-center gap-2 px-4 py-3 rounded-xl font-medium text-sm bg-zinc-700 text-zinc-300 hover:bg-zinc-600">
                          <ArrowRight className="w-4 h-4" /> Skip
                        </button>
                        <button onClick={() => handlePlaylistSaveForLater(currentClipId)} className={`px-4 py-3 rounded-xl text-sm ${isSaved ? 'bg-amber-500/20 text-amber-400 border border-amber-500/30' : 'bg-zinc-700 text-zinc-400 hover:bg-zinc-600'}`}>
                          <Star className="w-4 h-4" />
                        </button>
                      </div>
                    )}
                    {playlist.savedForLater.length > 0 && (
                      <div className="px-4 pb-3">
                        <p className="text-zinc-500 text-xs">{playlist.savedForLater.length} saved for later</p>
                      </div>
                    )}
                  </>
                ) : null}
              </div>
            );
          }

          return (
            <>
              {/* PLAYLIST BANNER */}
              {highRatedCount >= 5 && (
                <div
                  onClick={() => {
                    if (!currentReviewer.pleasurePlaylist) {
                      generatePlaylist();
                    } else {
                      setPlaylistMode(true);
                    }
                  }}
                  className="bg-gradient-to-r from-purple-500/20 to-pink-500/20 border border-purple-500/30 rounded-xl p-4 mb-4 cursor-pointer hover:border-purple-500/50 transition-colors"
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <span className="text-3xl">🎬</span>
                      <div>
                        <h3 className="text-white font-bold">Your Pleasure Playlist</h3>
                        <p className="text-purple-300 text-sm">
                          {playlist
                            ? `${enjoyedCount} of ${totalPlaylist} enjoyed — tap to continue`
                            : `We picked your ${highRatedCount} highest-rated clips just for you — sit back and enjoy`
                          }
                        </p>
                        {!playlist && (
                          <p className="text-zinc-500 text-xs mt-1">Your personal playlist based on clips you rated Must Try and Good</p>
                        )}
                      </div>
                    </div>
                    <Play className="w-6 h-6 text-purple-400" />
                  </div>
                </div>
              )}

              {/* CATEGORY CHAMPIONS */}
              {eligibleCategories.length > 0 && (
                <div className="bg-zinc-800 rounded-2xl border border-zinc-700 mb-4 overflow-hidden">
                  <button
                    onClick={() => setShowChampions(!showChampions)}
                    className="w-full px-4 py-3 flex items-center justify-between hover:bg-zinc-750 transition-colors"
                  >
                    <div className="flex items-center gap-3">
                      <span className="text-2xl">🏆</span>
                      <div className="text-left">
                        <h3 className="text-white font-bold text-sm">Pick Your Favorite</h3>
                        <p className="text-zinc-400 text-xs">
                          {crownedCount > 0 ? `${crownedCount} of ${eligibleCategories.length} categories decided` : 'Head-to-head matchups'}
                          {uncrownedCount > 0 && ` — ${uncrownedCount} left to decide`}
                        </p>
                      </div>
                    </div>
                    <ChevronDown className={`w-5 h-5 text-zinc-400 transition-transform ${showChampions ? 'rotate-180' : ''}`} />
                  </button>

                  {showChampions && (
                    <div className="px-4 pb-4">
                      {/* Explanation for first-timers */}
                      {crownedCount === 0 && (
                        <p className="text-zinc-500 text-xs mb-3">
                          We'll show you two clips from the same category — pick the one you prefer. The winner faces the next clip until one comes out on top. Crown a champion for each category at your own pace.
                        </p>
                      )}
                      {/* Active matchup with preview */}
                      {matchup && matchup.category && matchup.clipA && matchup.clipB && (
                        <div className="mb-4">
                          <p className="text-zinc-400 text-xs mb-3 flex items-center gap-2">
                            <span className="text-lg">{matchup.category.emoji}</span>
                            {matchup.category.name} — {(matchup as any).isDefense ? 'new challenger!' : 'which do you prefer?'}
                          </p>
                          {(matchup as any).isDefense && (
                            <p className="text-amber-400 text-xs mb-2">A new clip is challenging your champion. Defend or dethrone?</p>
                          )}
                          <div className="grid grid-cols-2 gap-3">
                            {[matchup.clipA, matchup.clipB].map(clip => {
                              const myReview = clip.reviews?.find(r => isMyReview(r.reviewerId));
                              return (
                                <div key={clip.id} className="bg-zinc-900 border border-zinc-600 rounded-xl overflow-hidden hover:border-amber-500/50 transition-all group">
                                  {/* Video preview thumbnail */}
                                  <div className="relative aspect-video bg-zinc-950">
                                    <VideoPlayer
                                      url={clip.link}
                                      title={clip.title}
                                      showThumbnail
                                      onClick={() => setWatchingClip(clip)}
                                    />
                                  </div>
                                  <div className="p-3">
                                    <p className="text-white text-sm font-medium mb-1 line-clamp-2">{clip.title}</p>
                                    {myReview && (
                                      <span className="text-xs text-zinc-500">Your rating: {myReview.rating}</span>
                                    )}
                                    <button
                                      onClick={() => {
                                        const otherId = clip.id === matchup.clipA!.id ? matchup.clipB!.id : matchup.clipA!.id;
                                        setMatchupReasonPrompt({ categoryId: matchup.category!.id, winnerId: clip.id, loserId: otherId });
                                        setMatchupReasonText('');
                                      }}
                                      className="w-full mt-2 px-3 py-2 rounded-lg text-sm font-medium bg-amber-500/20 text-amber-400 hover:bg-amber-500/30 border border-amber-500/30 transition-colors"
                                    >
                                      Pick this one
                                    </button>
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      )}

                      {/* Matchup reason prompt */}
                      {matchupReasonPrompt && (
                        <div className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4" onClick={() => {
                          handleChampionPick(matchupReasonPrompt.categoryId, matchupReasonPrompt.winnerId, matchupReasonPrompt.loserId);
                          setMatchupReasonPrompt(null);
                        }}>
                          <div className="bg-zinc-900 rounded-2xl border border-zinc-700 w-full max-w-sm p-5" onClick={e => e.stopPropagation()}>
                            <h3 className="text-white font-semibold mb-1">What made you pick this one?</h3>
                            <p className="text-zinc-500 text-xs mb-3">Quick note on why — or skip to move on</p>
                            <textarea
                              value={matchupReasonText}
                              onChange={e => setMatchupReasonText(e.target.value)}
                              placeholder="e.g. better angle, hotter reaction, bigger load..."
                              className="w-full bg-zinc-800 border border-zinc-600 rounded-lg p-3 text-white text-sm resize-none focus:outline-none focus:border-amber-500/50"
                              rows={3}
                              autoFocus
                            />
                            <div className="flex gap-2 mt-3">
                              <button
                                onClick={() => {
                                  handleChampionPick(matchupReasonPrompt.categoryId, matchupReasonPrompt.winnerId, matchupReasonPrompt.loserId);
                                  setMatchupReasonPrompt(null);
                                }}
                                className="flex-1 px-3 py-2 rounded-lg text-sm text-zinc-400 hover:text-white border border-zinc-700 hover:border-zinc-500 transition-colors"
                              >
                                Skip
                              </button>
                              <button
                                onClick={() => {
                                  handleChampionPick(matchupReasonPrompt.categoryId, matchupReasonPrompt.winnerId, matchupReasonPrompt.loserId, matchupReasonText.trim() || undefined);
                                  setMatchupReasonPrompt(null);
                                }}
                                className="flex-1 px-3 py-2 rounded-lg text-sm font-medium bg-amber-500/20 text-amber-400 hover:bg-amber-500/30 border border-amber-500/30 transition-colors"
                              >
                                Save & continue
                              </button>
                            </div>
                          </div>
                        </div>
                      )}

                      {/* Crowned champions grid — tap to see rankings */}
                      {crownedCount > 0 && (
                        <div>
                          <p className="text-amber-400 text-xs mb-2 font-medium uppercase tracking-wide">
                            Your Winners — tap to see full ranking
                          </p>
                          <div className="grid grid-cols-3 gap-2">
                            {Object.entries(champions?.champions || {}).map(([catId, clipId]) => {
                              const cat = CATEGORIES.find(c => c.id === catId);
                              const clip = dateNiteClips.find(c => c.id === clipId);
                              if (!cat || !clip) return null;
                              return (
                                <button
                                  key={catId}
                                  onClick={() => setViewingRankings(catId)}
                                  className="bg-zinc-900 rounded-lg p-2 border border-amber-500/20 text-center hover:border-amber-500/40 transition-colors"
                                >
                                  <span className="text-lg">{cat.emoji}</span>
                                  <p className="text-white text-xs font-medium line-clamp-1 mt-1">{clip.title}</p>
                                  <p className="text-amber-400/60 text-[10px]">{cat.name}</p>
                                </button>
                              );
                            })}
                          </div>
                        </div>
                      )}

                      {uncrownedCount === 0 && crownedCount > 0 && (
                        <p className="text-center text-zinc-500 text-xs mt-3">All categories decided!</p>
                      )}
                    </div>
                  )}
                </div>
              )}

              {/* Rankings modal for a category */}
              {viewingRankings && (() => {
                const cat = CATEGORIES.find(c => c.id === viewingRankings);
                const rankingIds = champions?.rankings?.[viewingRankings] || [];
                // Fallback: if no rankings stored yet, show champion + all reviewed clips
                const allCatClips = dateNiteClips.filter(c => {
                  const inCat = c.categoryId === viewingRankings || c.additionalCategoryIds?.includes(viewingRankings);
                  return inCat && c.reviews?.some(r => isMyReview(r.reviewerId));
                });
                const orderedIds = rankingIds.length > 0
                  ? rankingIds.filter(id => allCatClips.some(c => c.id === id))
                  : [champions?.champions[viewingRankings], ...allCatClips.filter(c => c.id !== champions?.champions[viewingRankings]).map(c => c.id)].filter(Boolean) as string[];

                return (
                  <div className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4" onClick={() => setViewingRankings(null)}>
                    <div className="bg-zinc-900 rounded-2xl border border-zinc-700 w-full max-w-md max-h-[80vh] overflow-hidden" onClick={e => e.stopPropagation()}>
                      <div className="px-4 py-3 bg-gradient-to-r from-amber-500/20 to-orange-500/20 flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <span className="text-xl">{cat?.emoji}</span>
                          <span className="text-white font-bold">{cat?.name} Rankings</span>
                        </div>
                        <button onClick={() => setViewingRankings(null)} className="text-zinc-400 hover:text-white">
                          <X className="w-5 h-5" />
                        </button>
                      </div>
                      <p className="text-zinc-500 text-xs px-4 pt-2">Drag to reorder your personal ranking</p>
                      <div className="p-4 overflow-y-auto max-h-[60vh]">
                        <DndContext
                          sensors={useSensors(
                            useSensor(MouseSensor, { activationConstraint: { distance: 5 } }),
                            useSensor(TouchSensor, { activationConstraint: { delay: 150, tolerance: 5 } })
                          )}
                          collisionDetection={closestCorners}
                          onDragEnd={(event: DragEndEvent) => {
                            const { active, over } = event;
                            if (over && active.id !== over.id) {
                              const oldIndex = orderedIds.indexOf(active.id as string);
                              const newIndex = orderedIds.indexOf(over.id as string);
                              const newOrder = arrayMove(orderedIds, oldIndex, newIndex);
                              saveRankings(viewingRankings, newOrder);
                            }
                          }}
                        >
                          <SortableContext items={orderedIds} strategy={verticalListSortingStrategy}>
                            <div className="space-y-2">
                              {orderedIds.map((clipId, idx) => {
                                const clip = dateNiteClips.find(c => c.id === clipId);
                                if (!clip) return null;
                                const review = clip.reviews?.find(r => isMyReview(r.reviewerId));
                                return (
                                  <SortableRankItem
                                    key={clipId}
                                    id={clipId}
                                    rank={idx + 1}
                                    title={clip.title}
                                    rating={review?.rating}
                                    isChampion={idx === 0}
                                  />
                                );
                              })}
                            </div>
                          </SortableContext>
                        </DndContext>
                      </div>
                    </div>
                  </div>
                );
              })()}
            </>
          );
        })()}

        {/* My Reviews Tab */}
        {tab === 'mypicks' && (() => {
          const myReviewedClips = dateNiteClips.filter(c => c.reviews?.some(r => isMyReview(r.reviewerId)));
          const myPicks = dateNiteClips.filter(c => c.picks?.includes(currentReviewer.id));

          return (
          <div>
            {/* Stats Header */}
            <div className="bg-zinc-800 rounded-xl p-4 mb-4 border border-zinc-700">
              <div className="flex items-center gap-6">
                <div className="text-center">
                  <p className="text-2xl font-bold" style={{ color: COLORS.lime }}>{myReviewedClips.length}</p>
                  <p className="text-xs text-zinc-500">Clips Reviewed</p>
                </div>
                <div className="text-center">
                  <p className="text-2xl font-bold text-amber-400">{myPicks.length}</p>
                  <p className="text-xs text-zinc-500">Picks for Couples</p>
                </div>
                <div className="text-center">
                  <p className="text-2xl font-bold text-purple-400">{CATEGORIES.filter(cat => myReviewedClips.some(c => c.categoryId === cat.id)).length}</p>
                  <p className="text-xs text-zinc-500">Categories Covered</p>
                </div>
              </div>
            </div>

            {/* === PICKS & REVIEWS GRID === */}
            {myReviewedClips.length === 0 ? (
              <div className="bg-zinc-800 rounded-xl p-12 text-center text-zinc-500 border border-zinc-700">
                <Star className="w-12 h-12 mx-auto mb-4 opacity-30" />
                <p className="mb-2">No reviews yet</p>
                <p className="text-sm">Head to the Review Queue to start reviewing clips!</p>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {dateNiteClips.filter(c => c.picks?.includes(currentReviewer.id)).map(clip => {
                  const cat = CATEGORIES.find(c => c.id === clip.categoryId);
                  return (
                    <div key={clip.id} className="bg-zinc-800 rounded-xl border border-amber-500/30 overflow-hidden">
                      <div className={`bg-gradient-to-r ${cat?.color} px-4 py-2`}>
                        <span className="text-white text-sm font-medium">{cat?.emoji} {cat?.name}</span>
                      </div>
                      <div className="p-4">
                        <h3 className="font-semibold text-white mb-1">{clip.title}</h3>
                        <div className="flex gap-2 mt-3">
                          <button
                            onClick={() => setWatchingClip(clip)}
                            className="flex-1 flex items-center justify-center gap-1 bg-zinc-700 text-white px-3 py-2 rounded-lg text-sm hover:bg-zinc-600"
                          >
                            <Eye className="w-4 h-4" /> Watch
                          </button>
                          <button
                            onClick={() => togglePick(clip)}
                            className="px-3 py-2 rounded-lg bg-red-500/20 text-red-400 hover:bg-red-500/30"
                          >
                            <X className="w-4 h-4" />
                          </button>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
          );
        })()}

        {/* Calendar/Plan Dates Tab */}
        {tab === 'calendar' && currentReviewer?.coupleId && (
          <DndContext
            sensors={sensors}
            collisionDetection={closestCorners}
            onDragStart={handleDragStart}
            onDragEnd={handleDragEnd}
          >
            <div className="space-y-6">
              {/* Full-width Activities Section (History + Scheduled) */}
              <div className="w-full">
                <div className="flex items-center justify-between mb-4">
                  <h2 className="text-lg font-bold text-white flex items-center gap-2">
                    <History className="w-5 h-5" style={{ color: COLORS.lime }} /> Activity Timeline
                  </h2>
                  <PrimaryButton
                    onClick={() => {
                      setSelectedCalendarDate(undefined);
                      setShowScheduleModal(true);
                    }}
                  >
                    <Calendar className="w-4 h-4" /> Schedule Activity
                  </PrimaryButton>
                </div>
                <UpcomingActivities
                  scheduledActivities={scheduledActivities}
                  clips={dateNiteClips}
                  onViewClip={clip => setWatchingClip(clip)}
                  onMarkComplete={handleMarkActivityComplete}
                  onReschedule={handleRescheduleActivity}
                  onDelete={handleDeleteActivity}
                  showCompleted
                  limit={10}
                />
              </div>

              {/* Clips Sidebar + Calendar Row */}
              <div className="flex gap-6">
                {/* Clip Sidebar for dragging to calendar */}
                <ClipSidebar
                  clips={dateNiteClips}
                  isCollapsed={sidebarCollapsed}
                  onToggleCollapse={() => setSidebarCollapsed(!sidebarCollapsed)}
                />

                {/* Calendar View */}
                <div className="flex-1">
                  <DragDropCalendarView
                    scheduledActivities={scheduledActivities}
                    clips={dateNiteClips}
                    onActivityClick={(activity) => {
                      const clip = dateNiteClips.find(c => c.id === activity.clipId);
                      if (clip) setWatchingClip(clip);
                    }}
                  />
                </div>
              </div>
            </div>

            {/* Drag Overlay for visual feedback while dragging */}
            <DragOverlay>
              {activeClip && <ClipDragPreview clip={activeClip} />}
              {activeActivity && activeActivity.clip && (
                <ActivityDragPreview clip={activeActivity.clip} />
              )}
            </DragOverlay>
          </DndContext>
        )}

        {/* Manage Couple Tab */}
        {tab === 'couple' && (
          <div className="space-y-6">
            {/* Link to External Couple Section (for managing someone else's couple) */}
            {!currentReviewer?.managedCoupleId && (
              <div className="bg-zinc-800 border border-zinc-700 rounded-2xl p-6">
                <div className="text-center">
                  <div className="w-16 h-16 rounded-full bg-purple-500/20 flex items-center justify-center mx-auto mb-4">
                    <Users className="w-8 h-8 text-purple-400" />
                  </div>
                  <h3 className="text-xl font-bold text-white mb-2">Manage a Couple</h3>
                  <p className="text-zinc-400 mb-6">Enter a couple's VBC code to coach their date nights, see their preferences, and recommend activities.</p>

                  <div className="max-w-sm mx-auto">
                    <input
                      type="text"
                      value={linkCoupleCode}
                      onChange={e => { setLinkCoupleCode(e.target.value.toUpperCase()); setLinkCoupleError(''); }}
                      onKeyDown={e => e.key === 'Enter' && linkToCouple()}
                      placeholder="VBC-XXXXXX"
                      className="w-full bg-zinc-700 border border-zinc-600 rounded-xl px-4 py-3 text-white font-mono text-center text-lg tracking-wider mb-4"
                    />
                    {linkCoupleError && <p className="text-red-400 text-sm mb-4">{linkCoupleError}</p>}
                    <PrimaryButton onClick={linkToCouple} className="w-full">
                      <Users className="w-4 h-4" /> Link to Couple
                    </PrimaryButton>
                  </div>
                </div>
              </div>
            )}

            {/* Managed Couple Profile & Insights (external couple being coached) */}
            {currentReviewer?.managedCoupleId && managedCoupleData && (
              <>
                {/* Managed Couple Header */}
                <div className="bg-zinc-800 rounded-2xl p-6 border border-zinc-700">
                  <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center gap-4">
                      <div className="w-14 h-14 rounded-full bg-gradient-to-r from-purple-500 to-indigo-500 flex items-center justify-center">
                        <Users className="w-7 h-7 text-white" />
                      </div>
                      <div>
                        <div className="flex items-center gap-2">
                          <h2 className="text-xl font-bold text-white">{managedCoupleData.hisName} & {managedCoupleData.herName}</h2>
                          <span className="px-2 py-0.5 bg-purple-500/20 text-purple-400 text-xs rounded-full border border-purple-500/30">
                            Managed
                          </span>
                        </div>
                        <p className="text-zinc-400 text-sm font-mono">{managedCoupleData.coupleId}</p>
                        {managedCoupleData.lastActivityAt && (
                          <p className="text-zinc-500 text-xs mt-1">
                            Last active: {new Date(managedCoupleData.lastActivityAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} at {new Date(managedCoupleData.lastActivityAt).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })}
                          </p>
                        )}
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => setShowLinkCoupleModal(true)}
                        className="text-zinc-400 hover:text-white text-sm"
                      >
                        Switch
                      </button>
                      <span className="text-zinc-600">|</span>
                      <button
                        onClick={() => setShowUnlinkConfirm(true)}
                        className="text-red-400 hover:text-red-300 text-sm"
                      >
                        Unlink
                      </button>
                    </div>
                  </div>

                  {/* Clip Feedback Nudge Banner */}
                  {(() => {
                    const pendingClipRequests = coupleRequests.filter(r =>
                      r.status === 'pending' &&
                      ((r.exampleUrl && r.exampleUrl.trim()) || (r.exampleUrls && r.exampleUrls.filter(u => u.trim()).length > 0))
                    );
                    if (pendingClipRequests.length === 0) return null;
                    return (
                      <div className="bg-amber-500/10 border border-amber-500/30 rounded-xl p-3 flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <Film className="w-5 h-5 text-amber-400 shrink-0" />
                          <div>
                            <p className="text-white text-sm font-medium">
                              {pendingClipRequests.length} clip suggestion{pendingClipRequests.length !== 1 ? 's' : ''} from {managedCoupleData.hisName} & {managedCoupleData.herName} need{pendingClipRequests.length === 1 ? 's' : ''} your feedback
                            </p>
                            <p className="text-zinc-400 text-xs">They shared clips they'd love your thoughts on</p>
                          </div>
                        </div>
                        <button
                          onClick={() => { setManagedCoupleTab('inbox'); setInboxSubTab('requests'); }}
                          className="px-3 py-1.5 bg-amber-500 text-zinc-900 text-sm rounded-lg hover:bg-amber-400 transition-colors font-medium shrink-0"
                        >
                          Review Now
                        </button>
                      </div>
                    );
                  })()}

                  {/* Tab Navigation for Managed Couple */}
                  {(() => {
                    const pendingPicksCount = pickForUsRequests.filter(r =>
                      r.coupleId === managedCoupleData.coupleId && r.status === 'pending'
                    ).length;
                    const pendingQuestionsCount = coupleQuestions.filter(q =>
                      q.coupleId === managedCoupleData.coupleId && q.status === 'pending'
                    ).length;
                    const pendingContentRequests = coupleRequests.filter(r => r.status === 'pending').length;
                    // Count NEW replies (answered within last 48 hours)
                    const newRepliesCount = coupleQuestions.filter(q =>
                      q.coupleId === managedCoupleData.coupleId &&
                      q.status === 'answered' &&
                      q.answeredAt &&
                      (Date.now() - new Date(q.answeredAt).getTime()) < 48 * 60 * 60 * 1000
                    ).length;
                    const totalPending = pendingPicksCount + pendingQuestionsCount + pendingContentRequests;
                    const upcomingActivities = managedCoupleScheduledActivities.filter(a => !a.completedAt).length;

                    // Count unreplied couple answers (CQs and CFUs the reviewer hasn't replied to yet)
                    const repliedCQIds = new Set(coupleQuestions.filter(cq => cq.parentId).map(cq => cq.parentId!));
                    const repliedCFUIds = new Set(coupleFollowUps.filter(cfu => cfu.parentId).map(cfu => cfu.parentId!));
                    const unrepliedCQCount = coupleQuestions.filter(q => q.coupleId === managedCoupleData.coupleId && q.status === 'answered' && !repliedCQIds.has(q.id)).length;
                    const unrepliedCFUCount = coupleFollowUps.filter(fu => fu.coupleId === managedCoupleData.coupleId && fu.status === 'answered' && !repliedCFUIds.has(fu.id)).length;
                    const unrepliedTotal = unrepliedCQCount + unrepliedCFUCount;

                    // Count unactioned activity items (follow-ups + tried clips without follow-ups)
                    const reviewedActivityKey = `vb-reviewed-activities:${currentReviewer?.id}`;
                    const reviewedActivityIdsRaw = localStorage.getItem(reviewedActivityKey);
                    const reviewedActivitySet = new Set<string>(reviewedActivityIdsRaw ? JSON.parse(reviewedActivityIdsRaw) : []);
                    const unactionedFollowUps = managedCoupleFollowUps.filter(f => !reviewedActivitySet.has(f.id)).length;
                    const triedWithoutFollowUps = (managedCoupleData?.tried || []).filter(t => !managedCoupleFollowUps.some(f => f.clipId === t.clipId) && !reviewedActivitySet.has(`tried-${t.clipId}`)).length;
                    const totalUnactionedActivity = unactionedFollowUps + triedWithoutFollowUps;

                    // Count managed couple submissions that need a blind review or post-review reflection
                    const unreviewedSubmissionsCount = managedCoupleSubmittedClips.filter(needsMySubmissionAction).length;

                    return (
                      <div className="flex gap-1 mb-4 border-b border-zinc-700 overflow-x-auto">
                        {(['inbox', 'submissions', 'activity', 'calendar', 'profile'] as const).map(tabName => (
                          <button
                            key={tabName}
                            onClick={() => setManagedCoupleTab(tabName)}
                            className={`px-4 py-2 text-sm font-medium capitalize transition-colors whitespace-nowrap ${
                              managedCoupleTab === tabName
                                ? 'text-white border-b-2 border-pink-500'
                                : 'text-zinc-400 hover:text-zinc-200'
                            }`}
                          >
                            {tabName}
                            {tabName === 'inbox' && totalPending > 0 && (
                              <span className="ml-2 px-1.5 py-0.5 bg-pink-500 text-white text-xs rounded-full">
                                {totalPending}
                              </span>
                            )}
                            {tabName === 'inbox' && newRepliesCount > 0 && (
                              <span className="ml-1 px-1.5 py-0.5 bg-lime-500 text-zinc-900 text-xs font-bold rounded-full animate-pulse">
                                {newRepliesCount} NEW
                              </span>
                            )}
                            {tabName === 'inbox' && unrepliedTotal > 0 && (
                              <span className="ml-1 px-1.5 py-0.5 bg-orange-500 text-white text-xs font-bold rounded-full">
                                {unrepliedTotal}
                              </span>
                            )}
                            {tabName === 'submissions' && unreviewedSubmissionsCount > 0 && (
                              <span className="ml-2 px-1.5 py-0.5 bg-orange-500 text-white text-xs rounded-full">
                                {unreviewedSubmissionsCount}
                              </span>
                            )}
                            {tabName === 'activity' && totalUnactionedActivity > 0 && (
                              <span className="ml-2 px-1.5 py-0.5 bg-amber-500 text-white text-xs rounded-full">
                                {totalUnactionedActivity}
                              </span>
                            )}
                            {tabName === 'calendar' && upcomingActivities > 0 && (
                              <span className="ml-2 px-1.5 py-0.5 bg-purple-500 text-white text-xs rounded-full">
                                {upcomingActivities}
                              </span>
                            )}
                          </button>
                        ))}
                      </div>
                    );
                  })()}

                  {/* INBOX TAB */}
                  {managedCoupleTab === 'inbox' && (
                    <>
                      {/* Precum Prompt Banner - show when precumProduction >= 3 and not dismissed */}
                      {(() => {
                        const precumLevel = managedCoupleData?.hisProfile?.precumProduction;
                        if (!precumLevel || precumLevel < 3) return null;

                        // Check if dismissed (stored on reviewer object)
                        const dismissedPrompts = currentReviewer?.dismissedPrecumPrompts || [];
                        if (dismissedPrompts.includes(managedCoupleData.coupleId)) return null;

                        // Check if precum question already asked
                        const precumQuestionExists = coupleQuestions.some(
                          q => q.coupleId === managedCoupleData?.coupleId &&
                               (q.question.toLowerCase().includes('precum') || q.category === 'precum')
                        );
                        if (precumQuestionExists) return null;

                        const precumLabel = HIS_PRECUM_SCALE.find(s => s.value === precumLevel)?.label || '';

                        return (
                          <div className="bg-gradient-to-r from-cyan-500/10 to-blue-500/10 border border-cyan-500/30 rounded-xl p-4 mb-4">
                            <div className="flex items-start justify-between gap-3">
                              <div className="flex-1">
                                <h4 className="text-cyan-400 font-semibold text-sm flex items-center gap-2">
                                  💧 Notable Precum Production
                                  <span className="text-xs px-2 py-0.5 bg-cyan-500/20 rounded-full">
                                    {precumLabel}
                                  </span>
                                </h4>
                                <p className="text-zinc-400 text-xs mt-1">
                                  {managedCoupleData.hisName} has significant precum. This could be a great conversation starter!
                                </p>
                                <div className="flex gap-2 mt-3">
                                  <button
                                    onClick={() => {
                                      setPendingQuestion({
                                        question: `Does ${managedCoupleData.herName} enjoy the precum? How do you incorporate it into foreplay?`,
                                        category: 'precum',
                                        context: 'Based on profile data'
                                      });
                                    }}
                                    className="px-3 py-1.5 bg-cyan-500/20 hover:bg-cyan-500/30 text-cyan-400 rounded-lg text-xs font-medium transition-colors"
                                  >
                                    Ask About This
                                  </button>
                                  <button
                                    onClick={async () => {
                                      if (!currentReviewer) return;
                                      const updatedReviewer = {
                                        ...currentReviewer,
                                        dismissedPrecumPrompts: [...(currentReviewer.dismissedPrecumPrompts || []), managedCoupleData.coupleId]
                                      };
                                      await saveReviewers(reviewers.map(r => r.id === currentReviewer.id ? updatedReviewer : r));
                                      setCurrentReviewer(updatedReviewer);
                                    }}
                                    className="px-3 py-1.5 text-zinc-500 hover:text-zinc-300 text-xs transition-colors"
                                  >
                                    Dismiss
                                  </button>
                                </div>
                              </div>
                              <button
                                onClick={async () => {
                                  if (!currentReviewer) return;
                                  const updatedReviewer = {
                                    ...currentReviewer,
                                    dismissedPrecumPrompts: [...(currentReviewer.dismissedPrecumPrompts || []), managedCoupleData.coupleId]
                                  };
                                  await saveReviewers(reviewers.map(r => r.id === currentReviewer.id ? updatedReviewer : r));
                                  setCurrentReviewer(updatedReviewer);
                                }}
                                className="text-zinc-500 hover:text-zinc-300"
                              >
                                <X className="w-4 h-4" />
                              </button>
                            </div>
                          </div>
                        );
                      })()}

                      {/* Sub-tab Navigation */}
                      {(() => {
                        // Compute sub-tab badge counts
                        const repliedCQIds = new Set(coupleQuestions.filter(cq => cq.parentId).map(cq => cq.parentId!));
                        const repliedCFUIds = new Set(coupleFollowUps.filter(cfu => cfu.parentId).map(cfu => cfu.parentId!));
                        const unrepliedCQCount = coupleQuestions.filter(q => q.coupleId === managedCoupleData.coupleId && q.status === 'answered' && !repliedCQIds.has(q.id)).length;
                        const unrepliedCFUCount = coupleFollowUps.filter(fu => fu.coupleId === managedCoupleData.coupleId && fu.status === 'answered' && !repliedCFUIds.has(fu.id)).length;
                        const unrepliedTotal = unrepliedCQCount + unrepliedCFUCount;
                        const pendingPicksCount = pickForUsRequests.filter(r => r.coupleId === managedCoupleData.coupleId && r.status === 'pending').length;
                        const actionRequiredCount = unrepliedTotal + pendingPicksCount;
                        const pendingContentRequests = coupleRequests.filter(r => r.status === 'pending').length;

                        return (
                          <div className="flex gap-1 mb-4 border-b border-zinc-600 overflow-x-auto">
                            {([
                              { id: 'action' as const, label: 'Action', count: actionRequiredCount },
                              { id: 'chat' as const, label: 'Chat', count: 0 },
                              { id: 'qa' as const, label: 'Q&A', count: 0 },
                              { id: 'requests' as const, label: 'Requests', count: pendingContentRequests },
                            ]).map(subtab => (
                              <button
                                key={subtab.id}
                                onClick={() => setInboxSubTab(subtab.id)}
                                className={`px-3 py-1.5 text-xs font-medium transition-colors whitespace-nowrap ${
                                  inboxSubTab === subtab.id
                                    ? 'text-white border-b-2 border-lime-500'
                                    : 'text-zinc-500 hover:text-zinc-300'
                                }`}
                              >
                                {subtab.label}
                                {subtab.count > 0 && (
                                  <span className="ml-1.5 px-1.5 py-0.5 bg-pink-500 text-white text-xs rounded-full">
                                    {subtab.count}
                                  </span>
                                )}
                              </button>
                            ))}
                          </div>
                        );
                      })()}

                      {/* ACTION SUB-TAB */}
                      {inboxSubTab === 'action' && (
                        <>
                      {/* Needs Your Reply - unreplied couple answers */}
                      {(() => {
                        const repliedCQIdsSet = new Set(coupleQuestions.filter(cq => cq.parentId).map(cq => cq.parentId!));
                        const repliedCFUIdsSet = new Set(coupleFollowUps.filter(cfu => cfu.parentId).map(cfu => cfu.parentId!));
                        const needsReplyCQ = coupleQuestions.filter(q => q.coupleId === managedCoupleData.coupleId && q.status === 'answered' && !repliedCQIdsSet.has(q.id));
                        const needsReplyCFU = coupleFollowUps.filter(fu => fu.coupleId === managedCoupleData.coupleId && fu.status === 'answered' && !repliedCFUIdsSet.has(fu.id));
                        const totalNeedsReply = needsReplyCQ.length + needsReplyCFU.length;
                        if (totalNeedsReply === 0) return null;

                        return (
                          <div className="bg-gradient-to-r from-orange-500/10 to-red-500/10 border border-orange-500/30 rounded-xl p-4 mb-4">
                            <h4 className="text-orange-400 font-semibold text-sm mb-1 flex items-center gap-2">
                              🔔 Needs Your Reply
                              <span className="px-2 py-0.5 bg-orange-500 text-white rounded-full text-xs font-bold">
                                {totalNeedsReply}
                              </span>
                            </h4>
                            <p className="text-zinc-400 text-xs mb-3">Tap reply to follow up on their answers</p>
                            <div className="space-y-3 max-h-64 overflow-y-auto">
                              {needsReplyCQ.map(q => (
                                <div key={q.id} className="bg-zinc-800/50 rounded-lg p-3 border-l-4 border-l-orange-400">
                                  <span className="text-xs px-1.5 py-0.5 bg-lime-500/20 text-lime-400 rounded mb-1 inline-block">Q&A</span>
                                  <p className="text-white text-sm mb-1">Q: {q.question}</p>
                                  <p className="text-lime-400 text-sm mb-2">A: {q.answer}</p>
                                  <button
                                    onClick={async () => {
                                      const followUp = prompt(`Reply to their answer:\n"${q.answer || ''}"\n\nYour follow-up question:`);
                                      if (followUp?.trim() && saveCoupleQuestions && currentReviewer) {
                                        const newQuestion: CoupleQuestion = {
                                          id: generateId('cq'),
                                          coupleId: managedCoupleData.coupleId,
                                          fromReviewerId: currentReviewer.id,
                                          fromReviewerName: currentReviewer.partnerName || 'Your Reviewer',
                                          question: followUp.trim(),
                                          sentAt: now(),
                                          status: 'pending',
                                          parentId: q.id
                                        };
                                        await saveCoupleQuestions([...coupleQuestions, newQuestion]);
                                      }
                                    }}
                                    className="text-orange-400 hover:text-orange-300 text-xs flex items-center gap-1"
                                  >
                                    <MessageCircle className="w-3 h-3" /> Reply
                                  </button>
                                </div>
                              ))}
                              {needsReplyCFU.map(fu => (
                                <div key={fu.id} className="bg-zinc-800/50 rounded-lg p-3 border-l-4 border-l-orange-400">
                                  <span className="text-xs px-1.5 py-0.5 bg-amber-500/20 text-amber-400 rounded mb-1 inline-block">AI Q&A</span>
                                  <p className="text-zinc-400 text-xs mb-1">Q: {fu.question}</p>
                                  <p className="text-white text-sm mb-2">{fu.answer}</p>
                                  <button
                                    onClick={() => {
                                      const followUpQuestion = prompt(`Reply to their answer about:\n"${fu.question}"\n\nYour follow-up question:`);
                                      if (followUpQuestion?.trim()) {
                                        const newFollowUp: CoupleFollowUp = {
                                          id: `cfu-${Date.now()}`,
                                          coupleId: managedCoupleData.coupleId,
                                          question: followUpQuestion.trim(),
                                          context: `Follow-up to: ${fu.question}`,
                                          sentAt: now(),
                                          status: 'pending',
                                          parentId: fu.id
                                        };
                                        saveCoupleFollowUps([...coupleFollowUps, newFollowUp]);
                                      }
                                    }}
                                    className="text-orange-400 hover:text-orange-300 text-xs flex items-center gap-1"
                                  >
                                    <MessageCircle className="w-3 h-3" /> Reply
                                  </button>
                                </div>
                              ))}
                            </div>
                          </div>
                        );
                      })()}

                      {/* Pick For Us Requests from this couple */}
                      {(() => {
                        const pendingPicks = pickForUsRequests.filter(r =>
                          r.coupleId === managedCoupleData.coupleId &&
                          r.status === 'pending'
                        );
                        if (pendingPicks.length === 0) return null;

                        return (
                          <div className="bg-gradient-to-r from-purple-500/10 to-pink-500/10 border border-purple-500/30 rounded-xl p-4 mb-4">
                        <h4 className="text-purple-400 font-semibold text-sm mb-3 flex items-center gap-2">
                          🎯 Pick For Us
                          <span className="px-2 py-0.5 bg-purple-500/20 rounded-full text-xs">
                            {pendingPicks.length} request{pendingPicks.length > 1 ? 's' : ''}
                          </span>
                        </h4>
                        <p className="text-zinc-400 text-xs mb-3">
                          {managedCoupleData.hisName} & {managedCoupleData.herName} want you to pick a clip for them!
                        </p>
                        {pendingPicks.map(req => (
                          <div key={req.id} className="bg-zinc-800/50 rounded-lg p-3 mb-2">
                            <p className="text-zinc-400 text-xs mb-2">
                              Submitted {new Date(req.submittedAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                            </p>
                            <div className="space-y-2">
                              {(req.items || []).map(item => {
                                const clip = dateNiteClips.find(c => c.id === item.clipId);
                                const cat = clip ? CATEGORIES.find(c => c.id === clip.categoryId) : null;
                                return (
                                  <div key={item.clipId} className="flex items-center justify-between bg-zinc-900/50 rounded px-3 py-2">
                                    <span className="text-white text-sm">
                                      {cat?.emoji} {item.clipTitle}
                                    </span>
                                    <button
                                      onClick={() => {
                                        // Open confirmation modal with next available date pre-selected
                                        const today = new Date();
                                        const availDays = managedCoupleData?.availableDays || [];
                                        let nextDate = '';
                                        // Find next available day in the next 14 days
                                        for (let i = 1; i <= 14; i++) {
                                          const d = new Date(today);
                                          d.setDate(today.getDate() + i);
                                          const dayOfWeek = d.getDay();
                                          if (availDays.length === 0 || availDays.includes(dayOfWeek)) {
                                            nextDate = d.toISOString().split('T')[0] || '';
                                            break;
                                          }
                                        }
                                        setPendingPick({
                                          requestId: req.id,
                                          clipId: item.clipId,
                                          clipTitle: item.clipTitle,
                                          whyPicked: '',
                                          followUpQuestion: '',
                                          scheduledDate: nextDate
                                        });
                                      }}
                                      className="px-3 py-1 bg-lime-500/20 hover:bg-lime-500/30 text-lime-400 rounded text-xs font-medium transition-colors"
                                    >
                                      Pick This
                                    </button>
                                  </div>
                                );
                              })}
                            </div>
                          </div>
                        ))}
                      </div>
                    );
                  })()}
                        </>
                      )}

                      {/* CHAT SUB-TAB */}
                      {inboxSubTab === 'chat' && (
                        <div className="bg-zinc-800/50 rounded-xl p-4 border border-zinc-700">
                          <h4 className="text-lime-400 font-semibold text-sm mb-3 flex items-center gap-2">
                            💬 Chat with {managedCoupleData.hisName} & {managedCoupleData.herName}
                          </h4>
                          <FloatingChat
                            coupleId={managedCoupleData.coupleId}
                            userType="reviewer"
                            userName={currentReviewer?.name || currentReviewer?.partnerName || 'Your Reviewer'}
                            partnerLabel={`${managedCoupleData.hisName} & ${managedCoupleData.herName}`}
                            clips={dateNiteClips}
                            accentColor="lime"
                            embedded={true}
                          />
                        </div>
                      )}

                      {/* Q&A SUB-TAB */}
                      {inboxSubTab === 'qa' && (
                        <>
                      {/* Recently Answered Questions - show couple's Q&A replies */}
                      {(() => {
                        const answeredQuestions = coupleQuestions.filter(
                          q => q.coupleId === managedCoupleData.coupleId && q.status === 'answered'
                        );
                        if (answeredQuestions.length === 0) return null;

                        // Sort by most recent answer first
                        const sortedAnswered = [...answeredQuestions].sort((a, b) =>
                          new Date(b.answeredAt || 0).getTime() - new Date(a.answeredAt || 0).getTime()
                        );

                        return (
                          <div className="bg-gradient-to-r from-lime-500/10 to-green-500/10 border border-lime-500/30 rounded-xl p-4 mb-4">
                            <h4 className="text-lime-400 font-semibold text-sm mb-3 flex items-center gap-2">
                              💬 Q&A Replies
                              <span className="px-2 py-0.5 bg-lime-500/20 rounded-full text-xs">
                                {answeredQuestions.length} answered
                              </span>
                            </h4>
                            <div className="space-y-3 max-h-64 overflow-y-auto">
                              {sortedAnswered.map(q => {
                                // Check if this is a nested reply (has parentId)
                                const parentQuestion = q.parentId
                                  ? coupleQuestions.find(pq => pq.id === q.parentId)
                                  : null;
                                // Check if answered within last 48 hours
                                const isRecent = q.answeredAt && (Date.now() - new Date(q.answeredAt).getTime()) < 48 * 60 * 60 * 1000;
                                const hasReply = coupleQuestions.some(cq => cq.parentId === q.id);

                                return (
                                  <div key={q.id} className={`rounded-lg p-3 ${isRecent ? 'bg-lime-500/10 border border-lime-500/30' : hasReply ? 'bg-zinc-800/30 opacity-75' : 'bg-zinc-800/50 border-l-4 border-l-orange-400'}`}>
                                    {hasReply && (
                                      <span className="inline-block px-2 py-0.5 bg-green-500/20 text-green-400 text-xs font-medium rounded mb-2">
                                        ✓ Replied
                                      </span>
                                    )}
                                    {isRecent && !hasReply && (
                                      <span className="inline-block px-2 py-0.5 bg-lime-500 text-zinc-900 text-xs font-bold rounded mb-2">
                                        NEW REPLY!
                                      </span>
                                    )}
                                    {parentQuestion && (
                                      <div className="mb-2 pl-2 border-l-2 border-zinc-600">
                                        <p className="text-zinc-500 text-xs">Replying to: {parentQuestion.question}</p>
                                      </div>
                                    )}
                                    <p className="text-white text-sm mb-1">Q: {q.question}</p>
                                    <p className="text-lime-400 text-sm">A: {q.answer}</p>
                                    <div className="flex items-center justify-between mt-2">
                                      <div className="flex items-center gap-2">
                                        {q.answeredBy && (
                                          <p className="text-zinc-500 text-xs">— answered by {q.answeredBy}</p>
                                        )}
                                        {q.answeredAt && (
                                          <p className="text-zinc-600 text-xs">
                                            {new Date(q.answeredAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                                          </p>
                                        )}
                                      </div>
                                      <button
                                        onClick={async () => {
                                          const followUp = prompt(`Reply to their answer:\n"${q.answer || ''}"\n\nYour follow-up question:`);
                                          if (followUp?.trim() && saveCoupleQuestions && currentReviewer) {
                                            const newQuestion: CoupleQuestion = {
                                              id: generateId('cq'),
                                              coupleId: managedCoupleData.coupleId,
                                              fromReviewerId: currentReviewer.id,
                                              fromReviewerName: currentReviewer.partnerName || 'Your Reviewer',
                                              question: followUp.trim(),
                                              sentAt: now(),
                                              status: 'pending',
                                              parentId: q.id
                                            };
                                            await saveCoupleQuestions([...coupleQuestions, newQuestion]);
                                          }
                                        }}
                                        className="text-lime-400 hover:text-lime-300 text-xs flex items-center gap-1"
                                      >
                                        <MessageCircle className="w-3 h-3" /> Reply
                                      </button>
                                    </div>
                                  </div>
                                );
                              })}
                            </div>
                          </div>
                        );
                      })()}

                      {/* AI Questions Answered - show couple's AI follow-up responses */}
                      {(() => {
                        const coupleAIAnswers = coupleFollowUps.filter(
                          fu => fu.coupleId === managedCoupleData.coupleId && fu.status === 'answered'
                        );
                        if (coupleAIAnswers.length === 0) return null;

                        return (
                          <div className="bg-gradient-to-r from-amber-500/10 to-yellow-500/10 rounded-xl p-4 border border-amber-500/20 mb-4">
                            <h4 className="text-amber-400 font-semibold text-sm mb-2 flex items-center gap-2">
                              🤖 Their AI Q&A ({coupleAIAnswers.length})
                            </h4>
                            <p className="text-zinc-500 text-xs mb-3">
                              What {managedCoupleData.herName} told us about their preferences:
                            </p>
                            <div className="space-y-3 max-h-64 overflow-y-auto">
                              {coupleAIAnswers.slice(-5).reverse().map(fu => {
                                const hasReply = coupleFollowUps.some(cfu => cfu.parentId === fu.id);
                                return (
                                <div key={fu.id} className={`rounded-lg p-3 ${hasReply ? 'bg-zinc-800/30 opacity-75' : 'bg-zinc-800/50 border-l-4 border-l-orange-400'}`}>
                                  {hasReply && (
                                    <span className="inline-block px-2 py-0.5 bg-green-500/20 text-green-400 text-xs font-medium rounded mb-2">
                                      ✓ Replied
                                    </span>
                                  )}
                                  <p className="text-zinc-400 text-xs mb-1">Q: {fu.question}</p>
                                  <p className="text-white text-sm">{fu.answer}</p>
                                  <div className="flex items-center justify-between mt-2">
                                    {fu.answeredAt && (
                                      <p className="text-zinc-600 text-xs">
                                        {new Date(fu.answeredAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                                      </p>
                                    )}
                                    <button
                                      onClick={() => {
                                        // Create a follow-up question to this AI answer
                                        const followUpQuestion = prompt(`Reply to their answer about:\n"${fu.question}"\n\nYour follow-up question:`);
                                        if (followUpQuestion?.trim()) {
                                          const newFollowUp: CoupleFollowUp = {
                                            id: `cfu-${Date.now()}`,
                                            coupleId: managedCoupleData.coupleId,
                                            question: followUpQuestion.trim(),
                                            context: `Follow-up to: ${fu.question}`,
                                            sentAt: now(),
                                            status: 'pending',
                                            parentId: fu.id
                                          };
                                          saveCoupleFollowUps([...coupleFollowUps, newFollowUp]);
                                        }
                                      }}
                                      className="text-amber-400 hover:text-amber-300 text-xs flex items-center gap-1"
                                    >
                                      <MessageCircle className="w-3 h-3" /> Reply
                                    </button>
                                  </div>
                                </div>
                                );
                              })}
                            </div>
                          </div>
                        );
                      })()}
                        </>
                      )}

                      {/* REQUESTS SUB-TAB */}
                      {inboxSubTab === 'requests' && (
                        <>
                      {/* Couple's content preferences */}
                      {managedCoupleData?.contentRequestPreferences && (
                        <div className="mb-3 bg-amber-500/10 border border-amber-500/20 rounded-lg p-3">
                          <p className="text-amber-400 text-xs font-medium mb-1">Content preferences from {managedCoupleData.herName || 'couple'}:</p>
                          <p className="text-sm text-zinc-300">{managedCoupleData.contentRequestPreferences}</p>
                        </div>
                      )}

                      {/* Request Content Button */}
                      <button onClick={() => setShowContentRequestForm(!showContentRequestForm)} className="px-3 py-1.5 bg-purple-500/20 hover:bg-purple-500/30 text-purple-400 rounded-lg text-xs border border-purple-500/30 mb-4">
                        📹 Request Content
                      </button>

                      {/* Content Request Form */}
                      {showContentRequestForm && (
                        <div className="bg-zinc-800 border border-purple-500/30 rounded-xl p-4 mb-4">
                          <h4 className="text-purple-400 font-semibold text-sm mb-3">Request Content from {managedCoupleData?.herName || 'Couple'}</h4>
                          <div className="space-y-3">
                            <div>
                              <label className="text-zinc-400 text-xs block mb-1">What would you like to see?</label>
                              <textarea
                                value={contentRequest.description}
                                onChange={e => setContentRequest(prev => ({ ...prev, description: e.target.value }))}
                                placeholder="Describe what you'd like them to create..."
                                className="w-full bg-zinc-700 border border-zinc-600 rounded-lg px-3 py-2 text-white text-sm"
                                rows={2}
                              />
                            </div>
                            <div className="grid grid-cols-3 gap-2">
                              <div>
                                <label className="text-zinc-400 text-xs block mb-1">Type</label>
                                <select value={contentRequest.type} onChange={e => setContentRequest(prev => ({ ...prev, type: e.target.value as 'solo' | 'couple' }))} className="w-full bg-zinc-700 border border-zinc-600 rounded px-2 py-1.5 text-white text-xs">
                                  <option value="solo">Solo Male</option>
                                  <option value="couple">Couple</option>
                                </select>
                              </div>
                              <div>
                                <label className="text-zinc-400 text-xs block mb-1">Angle</label>
                                <select value={contentRequest.angle} onChange={e => setContentRequest(prev => ({ ...prev, angle: e.target.value }))} className="w-full bg-zinc-700 border border-zinc-600 rounded px-2 py-1.5 text-white text-xs">
                                  <option value="">Any</option>
                                  <option value="POV">POV</option>
                                  <option value="Side">Side</option>
                                  <option value="Above">Above</option>
                                  <option value="Close-up">Close-up</option>
                                </select>
                              </div>
                              <div>
                                <label className="text-zinc-400 text-xs block mb-1">Finish</label>
                                <select value={contentRequest.finish} onChange={e => setContentRequest(prev => ({ ...prev, finish: e.target.value }))} className="w-full bg-zinc-700 border border-zinc-600 rounded px-2 py-1.5 text-white text-xs">
                                  <option value="">Any</option>
                                  <option value="On her">On her</option>
                                  <option value="Inside">Inside</option>
                                  <option value="Facial">Facial</option>
                                  <option value="Stomach">Stomach</option>
                                  <option value="Self">Self (solo)</option>
                                </select>
                              </div>
                            </div>
                            <div className="flex gap-2">
                              <button
                                onClick={async () => {
                                  if (!contentRequest.description.trim() || !managedCoupleData) return;
                                  const details = [contentRequest.description];
                                  if (contentRequest.angle) details.push(`Angle: ${contentRequest.angle}`);
                                  if (contentRequest.finish) details.push(`Finish: ${contentRequest.finish}`);
                                  if (contentRequest.type) details.push(`Type: ${contentRequest.type}`);
                                  const request = {
                                    id: `req-${Date.now()}`,
                                    coupleId: managedCoupleData.coupleId,
                                    type: 'clip' as const,
                                    description: details.join('\n'),
                                    status: 'pending' as const,
                                    createdAt: new Date().toISOString(),
                                    fromReviewerId: currentReviewer?.id,
                                    fromReviewerName: currentReviewer?.partnerName || currentReviewer?.code || 'Your Reviewer',
                                  };
                                  await d1Upsert('couple_requests', request);
                                  setContentRequest({ description: '', angle: '', finish: '', type: 'solo' });
                                  setShowContentRequestForm(false);
                                  // Reload requests
                                  const updatedRequests = await d1List('couple_requests', { coupleId: managedCoupleData.coupleId });
                                  setCoupleRequests(updatedRequests as any);
                                }}
                                className="px-3 py-1.5 bg-purple-500 hover:bg-purple-600 text-white rounded text-xs font-medium"
                              >
                                Send Request
                              </button>
                              <button onClick={() => setShowContentRequestForm(false)} className="px-3 py-1.5 bg-zinc-700 hover:bg-zinc-600 text-zinc-300 rounded text-xs">Cancel</button>
                            </div>
                          </div>
                        </div>
                      )}

                      {/* Content Requests from this couple */}
                      {(() => {
                        const allRequests = coupleRequests.filter(r => r.status === 'pending' || r.status === 'fulfilled' || r.status === 'declined');
                        if (allRequests.length === 0) return null;
                        const pendingCount = allRequests.filter(r => r.status === 'pending').length;

                        return (
                          <div className="bg-gradient-to-r from-purple-500/10 to-blue-500/10 border border-purple-500/30 rounded-xl p-4 mb-4">
                            <h4 className="text-purple-400 font-semibold text-sm mb-3 flex items-center gap-2">
                              <Lightbulb className="w-4 h-4" /> Content Requests
                              {pendingCount > 0 && (
                                <span className="px-2 py-0.5 bg-purple-500/20 rounded-full text-xs">
                                  {pendingCount} pending
                                </span>
                              )}
                            </h4>
                            <p className="text-zinc-400 text-xs mb-3">
                              {managedCoupleData.hisName} & {managedCoupleData.herName} want to see more of this:
                            </p>
                            <div className="space-y-3 max-h-[500px] overflow-y-auto">
                              {allRequests.sort((a, b) => (a.status === 'pending' ? -1 : 1) - (b.status === 'pending' ? -1 : 1)).map(req => {
                                const allUrls = [
                                  ...(req.exampleUrl ? [req.exampleUrl] : []),
                                  ...(req.exampleUrls || [])
                                ].filter(url => url.trim());
                                const redgifsUrls = allUrls.filter(url => url.includes('redgifs.com'));
                                const otherUrls = allUrls.filter(url => !url.includes('redgifs.com'));
                                const isResponding = respondingToRequestId === req.id;

                                return (
                                  <div key={req.id} className={`rounded-lg p-3 ${req.status === 'pending' ? 'bg-zinc-800/80 border border-purple-500/20' : 'bg-zinc-800/30'}`}>
                                    <div className="flex items-center gap-2 mb-2 flex-wrap">
                                      <span className="text-xs px-2 py-0.5 rounded bg-zinc-600 text-zinc-300">
                                        {req.type === 'clip' ? '🎬 Clip Request' : '📁 Category Idea'}
                                      </span>
                                      {req.categoryName && (
                                        <span className="text-purple-400 text-xs font-medium">"{req.categoryName}"</span>
                                      )}
                                      {req.status !== 'pending' && (
                                        <span className={`px-2 py-0.5 rounded text-xs font-medium ${
                                          req.status === 'fulfilled' ? 'bg-green-500/20 text-green-400' : 'bg-zinc-500/20 text-zinc-400'
                                        }`}>
                                          {req.status === 'fulfilled' ? '✅ Responded' : '❌ Declined'}
                                        </span>
                                      )}
                                      <span className="text-zinc-600 text-xs ml-auto">
                                        {new Date(req.createdAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                                      </span>
                                    </div>
                                    <p className="text-white text-sm">{req.description}</p>
                                    {req.categoryId && (
                                      <p className="text-zinc-500 text-xs mt-1">
                                        Related to: {CATEGORIES.find(c => c.id === req.categoryId)?.emoji} {CATEGORIES.find(c => c.id === req.categoryId)?.name}
                                      </p>
                                    )}
                                    {req.whyWanted && (
                                      <p className="text-zinc-400 text-xs mt-1 italic">💭 "{req.whyWanted}"</p>
                                    )}

                                    {/* Example clips/embeds */}
                                    {redgifsUrls.length > 0 && (
                                      <div className="mt-2">
                                        <p className="text-zinc-500 text-xs mb-1">📎 Example{redgifsUrls.length > 1 ? 's' : ''} they want more of:</p>
                                        <div className={`grid gap-2 ${redgifsUrls.length > 1 ? 'grid-cols-2' : 'grid-cols-1'}`}>
                                          {redgifsUrls.map((url, idx) => (
                                            <div key={idx} className="bg-zinc-900 rounded-lg overflow-hidden border border-zinc-700">
                                              <VideoPlayer url={url} className="w-full aspect-video"
                                              />
                                            </div>
                                          ))}
                                        </div>
                                      </div>
                                    )}
                                    {otherUrls.length > 0 && (
                                      <div className="mt-2 space-y-1">
                                        {otherUrls.map((url, idx) => (
                                          <a key={idx} href={url} target="_blank" rel="noopener noreferrer" className="text-blue-400 text-xs hover:underline block">
                                            📎 Example link ↗
                                          </a>
                                        ))}
                                      </div>
                                    )}

                                    {/* Existing response note */}
                                    {req.responseNote && !isResponding && (
                                      <div className="mt-2 pl-2 border-l-2 border-purple-500/40">
                                        <p className="text-purple-300 text-xs">Your response: "{req.responseNote}"</p>
                                      </div>
                                    )}

                                    {/* Response/feedback form */}
                                    {isResponding && (
                                      <div className="mt-3 space-y-2">
                                        <textarea
                                          value={requestResponseNote}
                                          onChange={e => setRequestResponseNote(e.target.value)}
                                          placeholder="Required: Tell them what you did... e.g. 'Added 3 clips like this!' or 'Great idea, I'll find some this week!'"
                                          className={`w-full bg-zinc-900 border rounded-lg px-3 py-2 text-white text-sm h-16 resize-none ${requestResponseNote.trim() ? 'border-zinc-600' : 'border-amber-500/50'}`}
                                          autoFocus
                                        />
                                        {!requestResponseNote.trim() && (
                                          <p className="text-amber-400 text-xs">Please leave a response before marking done</p>
                                        )}
                                        <div className="flex gap-2">
                                          <button
                                            onClick={async () => {
                                              if (!requestResponseNote.trim()) return;
                                              await markRequestFulfilled(req.id, requestResponseNote.trim());
                                              setRespondingToRequestId(null);
                                              setRequestResponseNote('');
                                            }}
                                            disabled={!requestResponseNote.trim()}
                                            className={`px-3 py-1.5 rounded text-xs font-medium transition-colors flex items-center gap-1 ${requestResponseNote.trim() ? 'bg-green-500/20 hover:bg-green-500/30 text-green-400' : 'bg-zinc-700/50 text-zinc-500 cursor-not-allowed'}`}
                                          >
                                            <Check className="w-3 h-3" /> Done
                                          </button>
                                          <button
                                            onClick={async () => {
                                              await declineRequest(req.id, requestResponseNote.trim() || undefined);
                                              setRespondingToRequestId(null);
                                              setRequestResponseNote('');
                                            }}
                                            className="px-3 py-1.5 bg-zinc-600/50 hover:bg-zinc-600 text-zinc-300 rounded text-xs font-medium transition-colors"
                                          >
                                            Decline
                                          </button>
                                          <button
                                            onClick={() => { setRespondingToRequestId(null); setRequestResponseNote(''); }}
                                            className="px-3 py-1.5 text-zinc-500 hover:text-zinc-300 text-xs transition-colors"
                                          >
                                            Cancel
                                          </button>
                                        </div>
                                      </div>
                                    )}

                                    {/* Action buttons */}
                                    {!isResponding && (
                                      <div className="flex items-center gap-2 mt-2 flex-wrap">
                                        {req.status === 'pending' && (
                                          <button
                                            onClick={() => { setRespondingToRequestId(req.id); setRequestResponseNote(req.responseNote || ''); }}
                                            className="px-3 py-1 bg-purple-500/20 hover:bg-purple-500/30 text-purple-300 rounded text-xs font-medium transition-colors"
                                          >
                                            Respond
                                          </button>
                                        )}
                                        <button
                                          onClick={() => setPendingQuestion({
                                            question: `Re: your content request — "${req.description.slice(0, 80)}" — `,
                                            category: req.categoryName || 'General',
                                            context: `Re: content request — ${req.description.slice(0, 100)}`
                                          })}
                                          className="px-3 py-1 bg-blue-500/20 hover:bg-blue-500/30 text-blue-300 rounded text-xs font-medium transition-colors flex items-center gap-1"
                                        >
                                          <MessageCircle className="w-3 h-3" /> Chat about this
                                        </button>
                                        <button
                                          onClick={async () => {
                                            const question = prompt(`Ask ${managedCoupleData.herName} about this request:\n"${req.description.slice(0, 80)}..."\n\nYour question:`);
                                            if (question?.trim() && saveCoupleQuestions && currentReviewer) {
                                              const newQuestion: CoupleQuestion = {
                                                id: generateId('cq'),
                                                coupleId: managedCoupleData.coupleId,
                                                fromReviewerId: currentReviewer.id,
                                                fromReviewerName: currentReviewer.partnerName || 'Your Reviewer',
                                                question: question.trim(),
                                                sentAt: now(),
                                                status: 'pending'
                                              };
                                              await saveCoupleQuestions([...coupleQuestions, newQuestion]);
                                            }
                                          }}
                                          className="text-purple-400 hover:text-purple-300 text-xs flex items-center gap-1"
                                        >
                                          <MessageCircle className="w-3 h-3" /> Ask About This
                                        </button>
                                      </div>
                                    )}
                                  </div>
                                );
                              })}
                            </div>
                          </div>
                        );
                      })()}
                        </>
                      )}

                      {/* Ask Questions - Profile-based prompts - Part of Q&A sub-tab */}
                      {inboxSubTab === 'qa' && (
                      <>
                      {(() => {
                        // Generate EXPLICIT profile-based question prompts (same as Profile tab)
                        const suggestedQuestions: { emoji: string; question: string; category: string }[] = [];
                        const profile = managedCoupleData.hisProfile;

                        // Size-based questions
                        if (profile?.size && profile.size >= 5) {
                          suggestedQuestions.push({ emoji: '🍆', question: `With ${managedCoupleData.hisName}'s size, what positions work best for you both?`, category: 'size' });
                          suggestedQuestions.push({ emoji: '👄', question: `Does ${managedCoupleData.herName} ever struggle with the size during oral?`, category: 'size' });
                          suggestedQuestions.push({ emoji: '💡', question: `Any tips you've learned for comfortable penetration?`, category: 'size' });
                        }

                        // Girth-based questions
                        if (profile?.girth && profile.girth >= 4) {
                          suggestedQuestions.push({ emoji: '💧', question: `What lube works best for you given the girth?`, category: 'girth' });
                          suggestedQuestions.push({ emoji: '🔥', question: `Does ${managedCoupleData.herName} need extra warm-up time?`, category: 'girth' });
                        }

                        // Cum-based questions
                        if (profile?.cumIntensity && profile.cumIntensity >= 4) {
                          suggestedQuestions.push({ emoji: '💦', question: `Where does ${managedCoupleData.herName} prefer ${managedCoupleData.hisName} to finish?`, category: 'cum' });
                          suggestedQuestions.push({ emoji: '👅', question: `Does ${managedCoupleData.herName} swallow? What's it like with the volume?`, category: 'cum' });
                          suggestedQuestions.push({ emoji: '😈', question: `How does ${managedCoupleData.herName} feel about facials given the load size?`, category: 'cum' });
                        }

                        // Precum-based questions
                        if (profile?.precumProduction && profile.precumProduction >= 3) {
                          suggestedQuestions.push({ emoji: '💧', question: `Does ${managedCoupleData.herName} enjoy the precum? How do you incorporate it?`, category: 'precum' });
                          suggestedQuestions.push({ emoji: '🤤', question: `Does the precum add to foreplay for you?`, category: 'precum' });
                        }

                        // Always have some general spicy prompts
                        suggestedQuestions.push({ emoji: '🌶️', question: `What's something you've both wanted to try but haven't yet?`, category: 'general' });
                        suggestedQuestions.push({ emoji: '❤️‍🔥', question: `What's your favorite thing about your intimate life together?`, category: 'general' });

                        // Research questions - always available
                        suggestedQuestions.push({ emoji: '🎮', question: `Do you use any toys together? What's your favorite and why?`, category: 'general' });
                        suggestedQuestions.push({ emoji: '🗣️', question: `How does dirty talk factor into your intimacy? Who's more vocal and what do they say?`, category: 'general' });
                        suggestedQuestions.push({ emoji: '👀', question: `Have you ever explored public play or exhibitionism? What's your comfort level?`, category: 'general' });
                        suggestedQuestions.push({ emoji: '🔒', question: `Do you ever play solo separately? How does that compare to partner play for you?`, category: 'general' });
                        suggestedQuestions.push({ emoji: '🍑', question: `Have you explored anal play together? What's been your experience?`, category: 'general' });
                        suggestedQuestions.push({ emoji: '👑', question: `Do either of you enjoy being dominant or submissive? How do power dynamics play into your intimacy?`, category: 'general' });
                        suggestedQuestions.push({ emoji: '💝', question: `What does aftercare look like for you two? How do you handle cleanup and connection after?`, category: 'general' });

                        // Filter out already-asked questions
                        const askedQuestions = coupleQuestions.filter(q => q.coupleId === managedCoupleData.coupleId);
                        const askedFollowUps = coupleFollowUps.filter(fu => fu.coupleId === managedCoupleData.coupleId);
                        const allAskedTexts = [
                          ...askedQuestions.map(q => q.question.toLowerCase()),
                          ...askedFollowUps.map(fu => fu.question.toLowerCase())
                        ];

                        const filteredSuggestedQuestions = suggestedQuestions.filter(sq => {
                          const questionLower = sq.question.toLowerCase();
                          const keyPhrase = questionLower.slice(0, 30);
                          return !allAskedTexts.some(asked =>
                            asked.includes(keyPhrase) ||
                            keyPhrase.includes(asked.slice(0, 30)) ||
                            // Semantic matches
                            (questionLower.includes('lube') && asked.includes('lube')) ||
                            (questionLower.includes('girth') && asked.includes('girth')) ||
                            (questionLower.includes('size') && asked.includes('size') && questionLower.includes('position')) ||
                            (questionLower.includes('oral') && asked.includes('oral')) ||
                            (questionLower.includes('swallow') && asked.includes('swallow')) ||
                            (questionLower.includes('facial') && asked.includes('facial')) ||
                            (questionLower.includes('precum') && asked.includes('precum')) ||
                            (questionLower.includes('warm-up') && asked.includes('warm-up')) ||
                            (questionLower.includes('finish') && asked.includes('finish')) ||
                            // Research question topics
                            (questionLower.includes('toy') && asked.includes('toy')) ||
                            (questionLower.includes('dirty talk') && asked.includes('dirty talk')) ||
                            (questionLower.includes('vocal') && asked.includes('vocal')) ||
                            (questionLower.includes('public') && asked.includes('public')) ||
                            (questionLower.includes('exhibitionism') && asked.includes('exhibitionism')) ||
                            (questionLower.includes('solo') && asked.includes('solo')) ||
                            (questionLower.includes('anal') && asked.includes('anal')) ||
                            (questionLower.includes('dominant') && asked.includes('dominant')) ||
                            (questionLower.includes('submissive') && asked.includes('submissive')) ||
                            (questionLower.includes('power dynamics') && asked.includes('power')) ||
                            (questionLower.includes('aftercare') && asked.includes('aftercare')) ||
                            (questionLower.includes('cleanup') && asked.includes('cleanup'))
                          );
                        });

                        const pendingQuestionsCount = coupleQuestions.filter(
                          q => q.coupleId === managedCoupleData.coupleId && q.status === 'pending'
                        ).length;

                        return (
                          <div className="bg-gradient-to-r from-purple-500/10 to-pink-500/10 rounded-xl p-4 border border-purple-500/30 mb-4">
                            <h4 className="text-purple-400 font-semibold text-sm mb-2 flex items-center gap-2">
                              ❓ Ask {managedCoupleData.herName} & {managedCoupleData.hisName}
                              {pendingQuestionsCount > 0 && (
                                <span className="px-2 py-0.5 bg-amber-500/20 text-amber-400 text-xs rounded-full">
                                  {pendingQuestionsCount} waiting
                                </span>
                              )}
                            </h4>
                            <p className="text-zinc-400 text-xs mb-3">
                              Don't be shy! They signed up for this - get to know their preferences so you can guide them better.
                            </p>

                            {/* Profile-Based Question Prompts */}
                            {filteredSuggestedQuestions.length > 0 && (
                              <div className="mb-3">
                                <p className="text-zinc-500 text-xs mb-2">Based on his stats:</p>
                                <div className="flex flex-wrap gap-2">
                                  {filteredSuggestedQuestions.slice(0, 6).map((sq, i) => (
                                    <button
                                      key={i}
                                      onClick={async () => {
                                        if (!saveCoupleQuestions || !currentReviewer) return;
                                        const newQuestion: CoupleQuestion = {
                                          id: generateId('cq'),
                                          coupleId: managedCoupleData.coupleId,
                                          fromReviewerId: currentReviewer.id,
                                          fromReviewerName: currentReviewer.partnerName || 'Your Reviewer',
                                          question: sq.question,
                                          category: sq.category as CoupleQuestion['category'],
                                          sentAt: now(),
                                          status: 'pending'
                                        };
                                        await saveCoupleQuestions([...coupleQuestions, newQuestion]);
                                      }}
                                      className="px-3 py-1.5 bg-purple-500/10 border border-purple-500/30 rounded-lg text-xs text-purple-300 hover:bg-purple-500/20 transition-colors text-left"
                                    >
                                      {sq.emoji} {sq.question.length > 45 ? sq.question.substring(0, 45) + '...' : sq.question}
                                    </button>
                                  ))}
                                </div>
                              </div>
                            )}

                            {/* Quick question suggestions based on submissions */}
                            {managedCoupleSubmittedClips.length > 0 && (
                              <div className="mb-2">
                                <p className="text-xs text-zinc-500 mb-1">Suggested questions:</p>
                                <div className="flex flex-wrap gap-1">
                                  {managedCoupleSubmittedClips.slice(-3).map(sub => (
                                    <button
                                      key={`q-${sub.id}`}
                                      onClick={() => {
                                        const question: CoupleQuestion = {
                                          id: `cq-${Date.now()}`,
                                          coupleId: managedCoupleData?.coupleId || '',
                                          fromReviewerId: currentReviewer?.id || '',
                                          fromReviewerName: currentReviewer?.partnerName || currentReviewer?.code || 'Reviewer',
                                          question: `Tell us more about "${sub.clipTitle || sub.title}" — what was the experience like?`,
                                          category: 'activity' as const,
                                          context: `About submission: ${sub.clipTitle || sub.title}`,
                                          sentAt: new Date().toISOString(),
                                          status: 'pending' as const,
                                        };
                                        d1Upsert('couple_questions', question);
                                        saveCoupleQuestions([...coupleQuestions, question]);
                                      }}
                                      className="px-2 py-1 bg-zinc-700/50 hover:bg-zinc-700 text-zinc-400 hover:text-white rounded text-xs border border-zinc-600/50"
                                    >
                                      Ask about &ldquo;{(sub.clipTitle || sub.title)?.substring(0, 30)}&rdquo;
                                    </button>
                                  ))}
                                  <button
                                    onClick={() => {
                                      const question: CoupleQuestion = {
                                        id: `cq-${Date.now()}`,
                                        coupleId: managedCoupleData?.coupleId || '',
                                        fromReviewerId: currentReviewer?.id || '',
                                        fromReviewerName: currentReviewer?.partnerName || currentReviewer?.code || 'Reviewer',
                                        question: 'What kind of content would you most enjoy creating next?',
                                        sentAt: new Date().toISOString(),
                                        status: 'pending' as const,
                                      };
                                      d1Upsert('couple_questions', question);
                                      saveCoupleQuestions([...coupleQuestions, question]);
                                    }}
                                    className="px-2 py-1 bg-zinc-700/50 hover:bg-zinc-700 text-zinc-400 hover:text-white rounded text-xs border border-zinc-600/50"
                                  >
                                    Ask what they want to create next
                                  </button>
                                </div>
                              </div>
                            )}

                            {/* Custom Question Input */}
                            <div className="flex gap-2">
                              <input
                                type="text"
                                id="inboxCustomQuestion"
                                placeholder="Ask something spicy..."
                                className="flex-1 bg-zinc-700 border border-zinc-600 rounded-lg px-3 py-2 text-white text-sm placeholder-zinc-500"
                                onKeyDown={async (e) => {
                                  if (e.key === 'Enter') {
                                    const input = e.target as HTMLInputElement;
                                    if (!input.value.trim() || !saveCoupleQuestions || !currentReviewer) return;
                                    const newQuestion: CoupleQuestion = {
                                      id: generateId('cq'),
                                      coupleId: managedCoupleData.coupleId,
                                      fromReviewerId: currentReviewer.id,
                                      fromReviewerName: currentReviewer.partnerName || 'Your Reviewer',
                                      question: input.value.trim(),
                                      category: 'general',
                                      sentAt: now(),
                                      status: 'pending'
                                    };
                                    await saveCoupleQuestions([...coupleQuestions, newQuestion]);
                                    input.value = '';
                                  }
                                }}
                              />
                              <button
                                onClick={async () => {
                                  const input = document.getElementById('inboxCustomQuestion') as HTMLInputElement;
                                  if (!input?.value.trim() || !saveCoupleQuestions || !currentReviewer) return;
                                  const newQuestion: CoupleQuestion = {
                                    id: generateId('cq'),
                                    coupleId: managedCoupleData.coupleId,
                                    fromReviewerId: currentReviewer.id,
                                    fromReviewerName: currentReviewer.partnerName || 'Your Reviewer',
                                    question: input.value.trim(),
                                    category: 'general',
                                    sentAt: now(),
                                    status: 'pending'
                                  };
                                  await saveCoupleQuestions([...coupleQuestions, newQuestion]);
                                  input.value = '';
                                }}
                                className="px-4 py-2 bg-purple-500 hover:bg-purple-600 text-white rounded-lg text-sm font-medium transition-colors"
                              >
                                Ask
                              </button>
                            </div>
                          </div>
                        );
                      })()}
                      </>
                      )}
                    </>
                  )}

                  {/* SUBMISSIONS TAB */}
                  {managedCoupleTab === 'submissions' && (
                    <div className="space-y-4">
                      <h3 className="text-lg font-bold text-white flex items-center gap-2">
                        <Star className="w-5 h-5 text-purple-400" /> Their Submissions
                      </h3>

                      {managedCoupleSubmittedClips.length === 0 ? (
                        <div className="text-center py-8 text-zinc-500">
                          <p className="text-sm">No submissions yet</p>
                          <p className="text-xs mt-1">Encourage your couple to submit their own content!</p>
                        </div>
                      ) : (
                        <div className="space-y-4">
                          {[...managedCoupleSubmittedClips].sort((a: any, b: any) => new Date(b.dateSubmitted || 0).getTime() - new Date(a.dateSubmitted || 0).getTime()).map((clip: SubmittedClip) => {
                            const allRevs = getSubmissionReviews(clip);
                            const myReview = allRevs.find(r => isMyReview(r.reviewerId));
                            const otherRevs = allRevs.filter(r => !isMyReview(r.reviewerId));
                            const canSeeCommunityFeedback = !!myReview;
                            const needsReflection = needsMySubmissionReflection(clip);
                            const avgQuality = canSeeCommunityFeedback && allRevs.length > 0 ? Math.round(allRevs.reduce((s, r) => s + (r.qualityRating || 0), 0) / allRevs.length * 10) / 10 : 0;

                            // Build prompted messages
                            const prompts: { label: string; message: string }[] = [];
                            if (canSeeCommunityFeedback) allRevs.forEach(r => {
                              if (r.howItMadeThemFeel && r.howItMadeThemFeel.length > 10)
                                prompts.push({ label: `"${r.howItMadeThemFeel.substring(0, 50)}..."`, message: `About "${clip.clipTitle}": "${r.howItMadeThemFeel}"` });
                              if (r.intrigues && r.intrigues.length > 10)
                                prompts.push({ label: `Intrigued: "${r.intrigues.substring(0, 50)}..."`, message: `What intrigued a reviewer about "${clip.clipTitle}": "${r.intrigues}"` });
                              if (r.notes && r.notes.length > 10 && r.qualityRating >= 4)
                                prompts.push({ label: `"${r.notes.substring(0, 50)}..."`, message: `Reviewer feedback on "${clip.clipTitle}": "${r.notes}"` });
                              if (r.maleAppearance)
                                prompts.push({ label: `Size: ${r.maleAppearance.cockSize} · ${r.maleAppearance.hardness}`, message: `About "${clip.clipTitle}" — size assessment: ${r.maleAppearance.cockSize}, hardness: ${r.maleAppearance.hardness}${r.maleAppearance.notes ? `. "${r.maleAppearance.notes}"` : ''}` });
                            });

                            return (
                              <div key={clip.id} className="bg-zinc-800 rounded-xl border border-zinc-700 p-4">
                                {/* Header */}
                                <div className="flex items-center gap-2 mb-2">
                                  <h4 className="font-semibold text-white">{clip.clipTitle}</h4>
                                  <span className={`px-2 py-0.5 rounded text-xs ${clip.status === 'Approved' ? 'bg-green-500/20 text-green-400' : 'bg-amber-500/20 text-amber-400'}`}>{clip.status}</span>
                                  {avgQuality > 0 && <span className="px-2 py-0.5 bg-yellow-500/20 text-yellow-400 rounded text-xs">{'★'.repeat(Math.round(avgQuality))} {avgQuality}/5</span>}
                                  {clip.maleAppearance && <span className="px-2 py-0.5 bg-blue-500/20 text-blue-400 rounded text-xs">Size: {clip.maleAppearance.cockSize}</span>}
                                </div>

                                {/* Your review status */}
                                {myReview ? (
                                  <div className={`mb-3 rounded-lg p-2 border ${
                                    needsReflection
                                      ? 'bg-amber-500/10 border-amber-500/30'
                                      : 'bg-lime-500/10 border-lime-500/20'
                                  }`}>
                                    <p className={`text-xs font-medium mb-1 ${needsReflection ? 'text-amber-300' : 'text-lime-400'}`}>
                                      {needsReflection ? 'Follow-up reflection required' : `Your Review: ${myReview.qualityRating}/5`}
                                    </p>
                                    {myReview.howItMadeThemFeel && <p className="text-zinc-300 text-xs italic">&ldquo;{myReview.howItMadeThemFeel}&rdquo;</p>}
                                    {needsReflection && (
                                      <>
                                        <p className="text-zinc-400 text-xs mt-1">
                                          Other reviewer notes are available. Save whether they changed how you feel.
                                        </p>
                                        <button
                                          onClick={() => setReviewingSubmission(clip)}
                                          className="mt-2 px-3 py-1.5 rounded-lg text-xs font-medium text-zinc-900"
                                          style={{ backgroundColor: COLORS.lime }}
                                        >
                                          Complete Reflection
                                        </button>
                                      </>
                                    )}
                                  </div>
                                ) : (
                                  <div className="mb-3 bg-lime-500/10 border border-lime-500/20 rounded-lg p-3">
                                    <p className="text-lime-400 text-xs font-medium mb-1">Blind review required</p>
                                    <p className="text-zinc-400 text-xs mb-3">Other reviewer feedback unlocks after you save your own review.</p>
                                    <button
                                      onClick={() => setReviewingSubmission(clip)}
                                      className="px-3 py-1.5 rounded-lg text-xs font-medium text-zinc-900 animate-pulse"
                                      style={{ backgroundColor: COLORS.lime }}
                                    >
                                      Review This Clip
                                    </button>
                                  </div>
                                )}

                                {/* Other reviewer feedback */}
                                {canSeeCommunityFeedback && otherRevs.length > 0 && (
                                  <div className="mb-3">
                                    <p className="text-xs text-zinc-500 mb-1">Other reviewers ({otherRevs.length}):</p>
                                    <div className="space-y-1">
                                      {otherRevs.map((r, i) => (
                                        <div key={i} className="bg-zinc-900/50 rounded p-2">
                                          <div className="flex items-center gap-2 mb-1">
                                            <span className="text-zinc-400 text-xs">{r.reviewerId.replace('Reviewer-', '#')}</span>
                                            <span className="text-yellow-400 text-xs">{'★'.repeat(r.qualityRating)}</span>
                                            {r.enjoyedIt && <span className="text-zinc-500 text-xs">{r.enjoyedIt.replace(/-/g, ' ')}</span>}
                                          </div>
                                          {r.howItMadeThemFeel && <p className="text-zinc-300 text-xs italic">&ldquo;{r.howItMadeThemFeel}&rdquo;</p>}
                                          {r.notes && r.notes.length > 5 && <p className="text-zinc-400 text-xs mt-0.5">{r.notes}</p>}
                                        </div>
                                      ))}
                                    </div>
                                  </div>
                                )}

                                {/* Prompted messages to share */}
                                {canSeeCommunityFeedback && prompts.length > 0 && (
                                  <div className="mb-3">
                                    <p className="text-xs text-zinc-500 mb-1">Share feedback with your couple (click to edit & send):</p>
                                    <div className="space-y-1">
                                      {prompts.slice(0, 5).map((pm, i) => (
                                        <button
                                          key={i}
                                          onClick={async () => {
                                            const edited = prompt('Edit message before sending:', pm.message);
                                            if (edited && currentReviewer) {
                                              await d1Upsert('couple_messages', {
                                                id: `msg-${Date.now()}`,
                                                coupleId: currentReviewer.managedCoupleId,
                                                fromType: 'reviewer',
                                                fromName: currentReviewer.partnerName || currentReviewer.code,
                                                message: edited,
                                                createdAt: new Date().toISOString(),
                                                seenByCouple: false,
                                                seenByReviewer: true,
                                              });
                                              alert('Sent to couple in chat!');
                                            }
                                          }}
                                          className="w-full text-left px-2 py-1.5 bg-purple-500/10 hover:bg-purple-500/20 rounded text-xs text-zinc-300 border border-purple-500/20 transition-colors"
                                        >
                                          💬 {pm.label}
                                        </button>
                                      ))}
                                    </div>
                                  </div>
                                )}

                                {/* Quick actions */}
                                <div className="flex gap-2">
                                  <button
                                    onClick={() => {
                                      const userQuestion = prompt(`Ask ${managedCoupleData?.herName || 'the couple'} about "${clip.clipTitle}":`);
                                      if (userQuestion && currentReviewer) {
                                        const question: CoupleQuestion = {
                                          id: `cq-${Date.now()}`,
                                          coupleId: currentReviewer.managedCoupleId!,
                                          fromReviewerId: currentReviewer.id,
                                          fromReviewerName: currentReviewer.partnerName || currentReviewer.code,
                                          question: userQuestion,
                                          category: 'activity' as const,
                                          context: `Discussing submitted clip: ${clip.clipTitle}`,
                                          sentAt: new Date().toISOString(),
                                          status: 'pending' as const,
                                          clipId: clip.id,
                                        };
                                        d1Upsert('couple_questions', question);
                                        saveCoupleQuestions([...coupleQuestions, question]);
                                      }
                                    }}
                                    className="px-2 py-1 bg-zinc-700 hover:bg-zinc-600 text-zinc-300 rounded text-xs"
                                  >
                                    💭 Ask About This
                                  </button>
                                  <button
                                    onClick={async () => {
                                      if (!currentReviewer) return;
                                      const encouragement = prompt(
                                        `Send a message to ${managedCoupleData?.herName || 'the couple'} about "${clip.clipTitle}":`,
                                        `We absolutely loved your "${clip.clipTitle}" — keep creating amazing content! 🔥`
                                      );
                                      if (encouragement) {
                                        await d1Upsert('couple_messages', {
                                          id: `msg-${Date.now()}`,
                                          coupleId: currentReviewer.managedCoupleId,
                                          fromType: 'reviewer',
                                          fromName: currentReviewer.partnerName || currentReviewer.code,
                                          message: encouragement,
                                          createdAt: new Date().toISOString(),
                                          seenByCouple: false,
                                          seenByReviewer: true,
                                        });
                                        alert('Message sent!');
                                      }
                                    }}
                                    className="px-2 py-1 bg-pink-500/20 hover:bg-pink-500/30 text-pink-400 rounded text-xs"
                                  >
                                    💖 Encourage Them
                                  </button>
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  )}

                  {/* PROFILE TAB */}
                  {managedCoupleTab === 'profile' && (
                    <>
                      {/* Profile Notes */}
                      {managedCoupleData.profileNotes && (
                        <div className="bg-zinc-900/50 rounded-xl p-4 border border-zinc-700 mb-4">
                          <p className="text-zinc-400 text-sm mb-2">📝 Profile Notes</p>
                          <p className="text-zinc-300 text-sm">{managedCoupleData.profileNotes}</p>
                        </div>
                      )}

                      {/* His Profile Stats */}
                  {managedCoupleData.hisProfile && (managedCoupleData.hisProfile.size || managedCoupleData.hisProfile.girth || managedCoupleData.hisProfile.cumIntensity || managedCoupleData.hisProfile.cumConsistency || managedCoupleData.hisProfile.cumTaste || managedCoupleData.hisProfile.precumProduction) && (
                    <div className="bg-gradient-to-r from-blue-500/10 to-purple-500/10 rounded-xl p-4 border border-blue-500/20 mb-4">
                      <p className="text-zinc-400 text-sm mb-3 flex items-center gap-2">
                        <span>🍆</span> His Stats
                      </p>
                      <div className="flex flex-wrap items-center gap-6">
                        {managedCoupleData.hisProfile.size && (() => {
                          const sizeData = HIS_SIZE_SCALE.find(s => s.value === managedCoupleData.hisProfile?.size);
                          return (
                            <div className="text-center">
                              <span className="text-3xl">{sizeData?.emoji}</span>
                              <p className="text-white text-sm mt-1">{sizeData?.label}</p>
                              <p className="text-purple-400 text-xs">{sizeData?.percentile}</p>
                              <p className="text-zinc-500 text-xs">Size</p>
                            </div>
                          );
                        })()}
                        {managedCoupleData.hisProfile.girth && (() => {
                          const girthData = HIS_GIRTH_SCALE.find(s => s.value === managedCoupleData.hisProfile?.girth);
                          return (
                            <div className="text-center">
                              <span className="text-3xl">{girthData?.emoji}</span>
                              <p className="text-white text-sm mt-1">{girthData?.size}</p>
                              <p className="text-purple-400 text-xs">{girthData?.percentile}</p>
                              <p className="text-zinc-500 text-xs">Girth</p>
                            </div>
                          );
                        })()}
                        {managedCoupleData.hisProfile.cumIntensity && (() => {
                          const cumData = HIS_CUM_SCALE.find(s => s.value === managedCoupleData.hisProfile?.cumIntensity);
                          return (
                            <div className="text-center">
                              <span className="text-3xl">{cumData?.emoji}</span>
                              <p className="text-white text-sm mt-1">{cumData?.label}</p>
                              <p className="text-purple-400 text-xs">{cumData?.percentile}</p>
                              <p className="text-zinc-500 text-xs">Volume</p>
                            </div>
                          );
                        })()}
                        {managedCoupleData.hisProfile.cumConsistency && (
                          <div className="text-center">
                            <span className="text-3xl">{HIS_CONSISTENCY_SCALE.find(s => s.value === managedCoupleData.hisProfile?.cumConsistency)?.emoji}</span>
                            <p className="text-white text-sm mt-1">{HIS_CONSISTENCY_SCALE.find(s => s.value === managedCoupleData.hisProfile?.cumConsistency)?.label}</p>
                            <p className="text-zinc-500 text-xs">Consistency</p>
                          </div>
                        )}
                        {managedCoupleData.hisProfile.cumTaste && (
                          <div className="text-center">
                            <span className="text-3xl">{HIS_TASTE_SCALE.find(s => s.value === managedCoupleData.hisProfile?.cumTaste)?.emoji}</span>
                            <p className="text-white text-sm mt-1">{HIS_TASTE_SCALE.find(s => s.value === managedCoupleData.hisProfile?.cumTaste)?.label}</p>
                            <p className="text-zinc-500 text-xs">Taste</p>
                          </div>
                        )}
                        {managedCoupleData.hisProfile.precumProduction && (
                          <div className="text-center">
                            <span className="text-3xl">{HIS_PRECUM_SCALE.find(s => s.value === managedCoupleData.hisProfile?.precumProduction)?.emoji}</span>
                            <p className="text-white text-sm mt-1">{HIS_PRECUM_SCALE.find(s => s.value === managedCoupleData.hisProfile?.precumProduction)?.label}</p>
                            <p className="text-zinc-500 text-xs">Precum</p>
                          </div>
                        )}
                      </div>
                      {/* Notes for consistency, taste, and precum */}
                      {(managedCoupleData.hisProfile.consistencyNotes || managedCoupleData.hisProfile.tasteNotes || managedCoupleData.hisProfile.precumNotes) && (
                        <div className="mt-3 pt-3 border-t border-blue-500/20 space-y-1">
                          {managedCoupleData.hisProfile.consistencyNotes && (
                            <p className="text-zinc-400 text-xs"><span className="text-purple-400">Consistency:</span> {managedCoupleData.hisProfile.consistencyNotes}</p>
                          )}
                          {managedCoupleData.hisProfile.tasteNotes && (
                            <p className="text-zinc-400 text-xs"><span className="text-amber-400">Taste:</span> {managedCoupleData.hisProfile.tasteNotes}</p>
                          )}
                          {managedCoupleData.hisProfile.precumNotes && (
                            <p className="text-zinc-400 text-xs"><span className="text-blue-400">Precum:</span> {managedCoupleData.hisProfile.precumNotes}</p>
                          )}
                        </div>
                      )}

                      {/* Visual Sharing Request - Show if they indicated yes/maybe */}
                      {(managedCoupleData.hisProfile?.visualSharingOpen === 'yes' || managedCoupleData.hisProfile?.visualSharingOpen === 'maybe') && (
                        <div className="mt-4 pt-4 border-t border-pink-500/30">
                          <div className="flex items-center gap-2 mb-3">
                            <span className="text-xl">📸</span>
                            <span className={`px-2 py-1 rounded text-xs font-medium ${
                              managedCoupleData.hisProfile.visualSharingOpen === 'yes'
                                ? 'bg-green-500/20 text-green-400'
                                : 'bg-amber-500/20 text-amber-400'
                            }`}>
                              {managedCoupleData.hisProfile.visualSharingOpen === 'yes' ? 'Open to sharing visuals!' : 'Maybe open to sharing'}
                            </span>
                          </div>

                          <p className="text-zinc-400 text-xs mb-3">
                            This couple has indicated they might be open to sharing visual verification.
                            Send a friendly request - it helps you picture their date night experiences!
                          </p>

                          {/* Pre-built request messages */}
                          <div className="space-y-2">
                            <p className="text-zinc-500 text-xs font-medium">Quick requests:</p>
                            <div className="flex flex-wrap gap-2">
                              {[
                                { label: '🔥 Curious about the stats', msg: `I noticed ${managedCoupleData.hisName}'s impressive stats! I'm curious to see if the visuals match - no pressure, but it would really help me picture your experiences better. Everything stays anonymous!` },
                                { label: '💕 Help with recommendations', msg: `To give you better clip recommendations, it would help to see ${managedCoupleData.hisName}'s actual visuals. This way I can match you with content featuring similar attributes. Only if you're comfortable - it's totally anonymous!` },
                                { label: '🎬 For your Date Night', msg: `I'd love to help you plan an amazing date night! If you're comfortable sharing some visual verification of ${managedCoupleData.hisName}'s stats, it helps me understand what content would really resonate with your experiences. Always anonymous!` }
                              ].map((preset, i) => (
                                <button
                                  key={i}
                                  onClick={() => {
                                    if (!saveCoupleQuestions || !currentReviewer) return;
                                    const newQuestion: CoupleQuestion = {
                                      id: `cq-${Date.now()}`,
                                      coupleId: managedCoupleData.coupleId,
                                      fromReviewerId: currentReviewer.id,
                                      fromReviewerName: currentReviewer.code,
                                      question: preset.msg,
                                      category: 'general',
                                      context: 'Visual sharing request',
                                      sentAt: now(),
                                      status: 'pending'
                                    };
                                    saveCoupleQuestions([...coupleQuestions, newQuestion]);
                                    alert('Request sent! The couple will see it in their inbox.');
                                  }}
                                  className="px-3 py-1.5 bg-pink-500/20 hover:bg-pink-500/30 text-pink-300 rounded-full text-xs transition-colors"
                                >
                                  {preset.label}
                                </button>
                              ))}
                            </div>

                            {/* Custom message option */}
                            <details className="mt-3">
                              <summary className="text-zinc-400 text-xs cursor-pointer hover:text-white">
                                Or write a custom request...
                              </summary>
                              <div className="mt-2 space-y-2">
                                <textarea
                                  id="visual-request-custom"
                                  placeholder="Write your friendly request here..."
                                  className="w-full bg-zinc-700 border border-zinc-600 rounded-lg px-3 py-2 text-white text-sm resize-none"
                                  rows={3}
                                />
                                <button
                                  onClick={() => {
                                    const textarea = document.getElementById('visual-request-custom') as HTMLTextAreaElement;
                                    const msg = textarea?.value?.trim();
                                    if (!msg || !saveCoupleQuestions || !currentReviewer) return;
                                    const newQuestion: CoupleQuestion = {
                                      id: `cq-${Date.now()}`,
                                      coupleId: managedCoupleData.coupleId,
                                      fromReviewerId: currentReviewer.id,
                                      fromReviewerName: currentReviewer.code,
                                      question: msg,
                                      category: 'general',
                                      context: 'Visual sharing request',
                                      sentAt: now(),
                                      status: 'pending'
                                    };
                                    saveCoupleQuestions([...coupleQuestions, newQuestion]);
                                    textarea.value = '';
                                    alert('Request sent!');
                                  }}
                                  className="px-4 py-2 rounded-lg text-sm font-medium text-zinc-900"
                                  style={{ backgroundColor: COLORS.lime }}
                                >
                                  Send Request
                                </button>
                              </div>
                            </details>
                          </div>

                          {/* Show if they've already shared */}
                          {managedCoupleData.hisProfile.visualUrls && Object.values(managedCoupleData.hisProfile.visualUrls).some(url => url) && (
                            <div className="mt-3 pt-3 border-t border-pink-500/20">
                              <p className="text-pink-400 text-xs font-medium mb-2">📸 Shared visuals:</p>
                              <div className="flex flex-wrap gap-2">
                                {managedCoupleData.hisProfile.visualUrls.sizeUrl && (
                                  <a href={managedCoupleData.hisProfile.visualUrls.sizeUrl} target="_blank" rel="noopener noreferrer" className="px-2 py-1 bg-pink-500/20 text-pink-400 rounded text-xs hover:bg-pink-500/30">
                                    Size →
                                  </a>
                                )}
                                {managedCoupleData.hisProfile.visualUrls.cumUrl && (
                                  <a href={managedCoupleData.hisProfile.visualUrls.cumUrl} target="_blank" rel="noopener noreferrer" className="px-2 py-1 bg-pink-500/20 text-pink-400 rounded text-xs hover:bg-pink-500/30">
                                    Cum →
                                  </a>
                                )}
                                {managedCoupleData.hisProfile.visualUrls.precumUrl && (
                                  <a href={managedCoupleData.hisProfile.visualUrls.precumUrl} target="_blank" rel="noopener noreferrer" className="px-2 py-1 bg-pink-500/20 text-pink-400 rounded text-xs hover:bg-pink-500/30">
                                    Precum →
                                  </a>
                                )}
                                {managedCoupleData.hisProfile.visualUrls.otherUrl && (
                                  <a href={managedCoupleData.hisProfile.visualUrls.otherUrl} target="_blank" rel="noopener noreferrer" className="px-2 py-1 bg-pink-500/20 text-pink-400 rounded text-xs hover:bg-pink-500/30">
                                    Other →
                                  </a>
                                )}
                              </div>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  )}

                  {/* Couple Feedback */}
                  {(managedCoupleData.wouldSubmitClips || managedCoupleData.productExcitement || managedCoupleData.generalFeedback) && (
                    <div className="bg-gradient-to-r from-amber-500/10 to-orange-500/10 rounded-xl p-4 border border-amber-500/20 mb-4">
                      <p className="text-zinc-400 text-sm mb-3 flex items-center gap-2">
                        <span>💬</span> Couple Feedback
                      </p>
                      <div className="space-y-3">
                        <div className="flex flex-wrap gap-3">
                          {managedCoupleData.wouldSubmitClips && (
                            <div className="flex items-center gap-2">
                              <span className="text-zinc-500 text-xs">Submit clips:</span>
                              <span className={`px-2 py-0.5 rounded text-xs font-medium ${
                                managedCoupleData.wouldSubmitClips === 'yes' ? 'bg-green-500/20 text-green-400' :
                                managedCoupleData.wouldSubmitClips === 'maybe' ? 'bg-amber-500/20 text-amber-400' :
                                'bg-zinc-500/20 text-zinc-400'
                              }`}>
                                {managedCoupleData.wouldSubmitClips === 'yes' ? '✅ Yes!' : managedCoupleData.wouldSubmitClips === 'maybe' ? '🤔 Maybe' : '❌ No'}
                              </span>
                            </div>
                          )}
                          {managedCoupleData.productExcitement && (
                            <div className="flex items-center gap-2">
                              <span className="text-zinc-500 text-xs">Excitement:</span>
                              <span className="text-amber-400">
                                {'★'.repeat(managedCoupleData.productExcitement)}
                                <span className="text-zinc-600">{'★'.repeat(5 - managedCoupleData.productExcitement)}</span>
                              </span>
                            </div>
                          )}
                        </div>
                        {managedCoupleData.generalFeedback && (
                          <div className="bg-zinc-900/50 rounded-lg p-3">
                            <p className="text-zinc-300 text-sm italic">"{managedCoupleData.generalFeedback}"</p>
                          </div>
                        )}
                        {managedCoupleData.feedbackSubmittedAt && (
                          <p className="text-zinc-600 text-xs">
                            Submitted {new Date(managedCoupleData.feedbackSubmittedAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                          </p>
                        )}
                      </div>
                    </div>
                  )}

                  {/* Couple's AI Follow-up Answers with Reviewer Response */}
                  {(() => {
                    const coupleAIAnswers = coupleFollowUps.filter(
                      fu => fu.coupleId === managedCoupleData.coupleId && fu.status === 'answered'
                    );
                    if (coupleAIAnswers.length === 0) return null;

                    return (
                      <div className="bg-gradient-to-r from-amber-500/10 to-yellow-500/10 rounded-xl p-4 border border-amber-500/20 mb-4">
                        <p className="text-zinc-400 text-sm mb-3 flex items-center gap-2">
                          <span>💬</span> Their Q&A Responses ({coupleAIAnswers.length})
                        </p>
                        <p className="text-zinc-500 text-xs mb-3">
                          The couple's responses to profile questions. You can respond or ask follow-up questions.
                        </p>
                        <div className="space-y-3 max-h-96 overflow-y-auto">
                          {coupleAIAnswers.slice(-10).reverse().map(fu => (
                            <div key={fu.id} className="bg-zinc-800/50 rounded-lg p-3">
                              <p className="text-zinc-400 text-xs mb-1">Q: {fu.question}</p>
                              <p className="text-white text-sm">{fu.answer}</p>
                              {fu.answeredAt && (
                                <p className="text-zinc-600 text-xs mt-1">
                                  Answered by {fu.answeredBy || 'couple'} • {new Date(fu.answeredAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                                </p>
                              )}

                              {/* Reviewer Response Display */}
                              {fu.reviewerResponse && (
                                <div className="mt-2 bg-purple-500/10 border border-purple-500/20 rounded-lg p-2">
                                  <p className="text-purple-400 text-xs mb-1">
                                    💬 {fu.reviewerResponderName || 'Reviewer'}'s response:
                                  </p>
                                  <p className="text-zinc-300 text-sm">{fu.reviewerResponse}</p>
                                  {fu.reviewerRespondedAt && (
                                    <p className="text-zinc-600 text-xs mt-1">
                                      {new Date(fu.reviewerRespondedAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                                    </p>
                                  )}
                                </div>
                              )}

                              {/* Response Input */}
                              {!fu.reviewerResponse && (
                                <div className="mt-2 flex gap-2">
                                  <input
                                    type="text"
                                    id={`ai-response-${fu.id}`}
                                    placeholder="Respond or ask follow-up..."
                                    className="flex-1 bg-zinc-700 border border-zinc-600 rounded px-2 py-1 text-white text-xs placeholder-zinc-500"
                                    onKeyDown={async (e) => {
                                      if (e.key === 'Enter') {
                                        const input = e.target as HTMLInputElement;
                                        if (!input.value.trim()) return;
                                        const updated = coupleFollowUps.map(f =>
                                          f.id === fu.id
                                            ? {
                                                ...f,
                                                reviewerResponse: input.value.trim(),
                                                reviewerRespondedAt: now(),
                                                reviewerResponderId: currentReviewer?.id,
                                                reviewerResponderName: currentReviewer?.partnerName || 'Reviewer'
                                              }
                                            : f
                                        );
                                        await saveCoupleFollowUps(updated);
                                        input.value = '';
                                      }
                                    }}
                                  />
                                  <button
                                    onClick={async () => {
                                      const input = document.getElementById(`ai-response-${fu.id}`) as HTMLInputElement;
                                      if (!input?.value.trim()) return;
                                      const updated = coupleFollowUps.map(f =>
                                        f.id === fu.id
                                          ? {
                                              ...f,
                                              reviewerResponse: input.value.trim(),
                                              reviewerRespondedAt: now(),
                                              reviewerResponderId: currentReviewer?.id,
                                              reviewerResponderName: currentReviewer?.partnerName || 'Reviewer'
                                            }
                                          : f
                                      );
                                      await saveCoupleFollowUps(updated);
                                      input.value = '';
                                    }}
                                    className="px-2 py-1 bg-purple-500 hover:bg-purple-600 text-white rounded text-xs"
                                  >
                                    Reply
                                  </button>
                                </div>
                              )}
                            </div>
                          ))}
                        </div>
                      </div>
                    );
                  })()}

                  {/* Ask Couple Questions */}
                  {(() => {
                    // Generate suggested questions based on profile
                    const suggestedQuestions: { question: string; category: 'size' | 'girth' | 'cum' | 'precum' | 'general' }[] = [];
                    const profile = managedCoupleData.hisProfile;

                    if (profile?.size && profile.size >= 5) {
                      suggestedQuestions.push({ question: `With ${managedCoupleData.hisName}'s size, what positions work best for you both?`, category: 'size' });
                      suggestedQuestions.push({ question: `Does ${managedCoupleData.herName} ever struggle with the size during oral?`, category: 'size' });
                      suggestedQuestions.push({ question: `Any tips you've learned for comfortable penetration?`, category: 'size' });
                    }
                    if (profile?.girth && profile.girth >= 4) {
                      suggestedQuestions.push({ question: `What lube works best for you given the girth?`, category: 'girth' });
                      suggestedQuestions.push({ question: `Does ${managedCoupleData.herName} need extra warm-up time?`, category: 'girth' });
                    }
                    if (profile?.cumIntensity && profile.cumIntensity >= 4) {
                      suggestedQuestions.push({ question: `Where does ${managedCoupleData.herName} prefer ${managedCoupleData.hisName} to finish?`, category: 'cum' });
                      suggestedQuestions.push({ question: `Does ${managedCoupleData.herName} swallow? What's it like with the volume?`, category: 'cum' });
                      suggestedQuestions.push({ question: `How does ${managedCoupleData.herName} feel about facials given the load size?`, category: 'cum' });
                    }
                    if (profile?.precumProduction && profile.precumProduction >= 3) {
                      suggestedQuestions.push({ question: `Does ${managedCoupleData.herName} enjoy the precum? How do you incorporate it?`, category: 'precum' });
                      suggestedQuestions.push({ question: `Does the precum add to foreplay for you?`, category: 'precum' });
                    }

                    // Always show some general questions
                    suggestedQuestions.push({ question: `What's something you've both wanted to try but haven't yet?`, category: 'general' });
                    suggestedQuestions.push({ question: `What's your favorite thing about your intimate life together?`, category: 'general' });

                    // Research questions - always available
                    suggestedQuestions.push({ question: `Do you use any toys together? What's your favorite and why?`, category: 'general' });
                    suggestedQuestions.push({ question: `How does dirty talk factor into your intimacy? Who's more vocal and what do they say?`, category: 'general' });
                    suggestedQuestions.push({ question: `Have you ever explored public play or exhibitionism? What's your comfort level?`, category: 'general' });
                    suggestedQuestions.push({ question: `Do you ever play solo separately? How does that compare to partner play for you?`, category: 'general' });
                    suggestedQuestions.push({ question: `Have you explored anal play together? What's been your experience?`, category: 'general' });
                    suggestedQuestions.push({ question: `Do either of you enjoy being dominant or submissive? How do power dynamics play into your intimacy?`, category: 'general' });
                    suggestedQuestions.push({ question: `What does aftercare look like for you two? How do you handle cleanup and connection after?`, category: 'general' });

                    const pendingQuestions = coupleQuestions.filter(q => q.coupleId === managedCoupleData.coupleId && q.status === 'pending');
                    const answeredQuestions = coupleQuestions.filter(q => q.coupleId === managedCoupleData.coupleId && q.status === 'answered');

                    return (
                      <div className="bg-gradient-to-r from-purple-500/10 to-pink-500/10 rounded-xl p-4 border border-purple-500/20 mb-4">
                        <p className="text-zinc-400 text-sm mb-3 flex items-center gap-2">
                          <span>❓</span> Ask Questions
                          {pendingQuestions.length > 0 && (
                            <span className="px-2 py-0.5 bg-amber-500/20 text-amber-400 text-xs rounded-full">{pendingQuestions.length} pending</span>
                          )}
                        </p>

                        {/* Suggested Questions */}
                        {suggestedQuestions.length > 0 && (
                          <div className="mb-4">
                            <p className="text-zinc-500 text-xs mb-2">Suggested based on profile:</p>
                            <div className="flex flex-wrap gap-2">
                              {suggestedQuestions.slice(0, 4).map((sq, i) => (
                                <button
                                  key={i}
                                  onClick={() => {
                                    // Open confirmation popup instead of sending immediately
                                    setPendingQuestion({
                                      question: sq.question,
                                      category: sq.category,
                                      context: 'Based on your profile stats'
                                    });
                                  }}
                                  className="px-3 py-1.5 bg-purple-500/10 border border-purple-500/30 rounded-lg text-xs text-purple-300 hover:bg-purple-500/20 transition-colors text-left"
                                >
                                  {sq.question.length > 50 ? sq.question.substring(0, 50) + '...' : sq.question}
                                </button>
                              ))}
                            </div>
                          </div>
                        )}

                        {/* Custom Question Input */}
                        <div className="flex gap-2 mb-4">
                          <input
                            type="text"
                            id="customCoupleQuestion"
                            placeholder="Ask a custom question..."
                            className="flex-1 bg-zinc-700 border border-zinc-600 rounded-lg px-3 py-2 text-white text-sm placeholder-zinc-500"
                            onKeyDown={async (e) => {
                              if (e.key === 'Enter') {
                                const input = e.target as HTMLInputElement;
                                if (!input.value.trim()) return;
                                const newQuestion: CoupleQuestion = {
                                  id: generateId('cq'),
                                  coupleId: managedCoupleData.coupleId,
                                  fromReviewerId: currentReviewer!.id,
                                  fromReviewerName: currentReviewer!.partnerName || 'Reviewer',
                                  question: input.value.trim(),
                                  category: 'general',
                                  sentAt: now(),
                                  status: 'pending'
                                };
                                await saveCoupleQuestions([...coupleQuestions, newQuestion]);
                                input.value = '';
                              }
                            }}
                          />
                          <button
                            onClick={async () => {
                              const input = document.getElementById('customCoupleQuestion') as HTMLInputElement;
                              if (!input?.value.trim()) return;
                              const newQuestion: CoupleQuestion = {
                                id: generateId('cq'),
                                coupleId: managedCoupleData.coupleId,
                                fromReviewerId: currentReviewer!.id,
                                fromReviewerName: currentReviewer!.partnerName || 'Reviewer',
                                question: input.value.trim(),
                                category: 'general',
                                sentAt: now(),
                                status: 'pending'
                              };
                              await saveCoupleQuestions([...coupleQuestions, newQuestion]);
                              input.value = '';
                            }}
                            className="px-4 py-2 bg-purple-500 hover:bg-purple-600 text-white rounded-lg text-sm font-medium"
                          >
                            Ask
                          </button>
                        </div>

                        {/* Answered Questions */}
                        {answeredQuestions.length > 0 && (
                          <div>
                            <p className="text-zinc-500 text-xs mb-2">Answered ({answeredQuestions.length}):</p>
                            <div className="space-y-2 max-h-48 overflow-y-auto">
                              {answeredQuestions.map(q => (
                                <div key={q.id} className="bg-zinc-800/50 rounded-lg p-3">
                                  <p className="text-white text-sm mb-1">Q: {q.question}</p>
                                  <p className="text-lime-400 text-sm">A: {q.answer}</p>
                                  {q.answeredBy && (
                                    <p className="text-zinc-500 text-xs mt-1">— answered by {q.answeredBy}</p>
                                  )}
                                </div>
                              ))}
                            </div>
                          </div>
                        )}

                        {/* Pending Questions */}
                        {pendingQuestions.length > 0 && (
                          <div className="mt-3">
                            <p className="text-zinc-500 text-xs mb-2">Waiting for answer:</p>
                            <div className="space-y-1">
                              {pendingQuestions.map(q => (
                                <div key={q.id} className="flex items-center justify-between bg-zinc-800/30 rounded-lg px-3 py-2">
                                  <p className="text-zinc-300 text-sm truncate flex-1">{q.question}</p>
                                  <span className="text-zinc-500 text-xs ml-2">{new Date(q.sentAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}</span>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                    );
                  })()}

                  {/* Category Preferences */}
                  {Object.keys(managedCoupleData.preferences || {}).length > 0 && (
                    <div className="bg-zinc-900/50 rounded-xl p-4 border border-zinc-700 mb-4">
                      <p className="text-zinc-400 text-sm mb-3">Category Preferences</p>
                      <div className="flex flex-wrap gap-2">
                        {Object.entries(managedCoupleData.preferences).map(([catId, pref]) => {
                          const category = CATEGORIES.find(c => c.id === catId);
                          if (!category) return null;
                          const prefColors: Record<string, string> = {
                            love: 'bg-pink-500/20 text-pink-400 border-pink-500/30',
                            like: 'bg-green-500/20 text-green-400 border-green-500/30',
                            curious: 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30',
                            nope: 'bg-red-500/20 text-red-400 border-red-500/30',
                            neutral: 'bg-zinc-500/20 text-zinc-400 border-zinc-500/30',
                          };
                          return (
                            <span key={catId} className={`px-3 py-1.5 rounded-lg text-sm border ${prefColors[pref] || prefColors.neutral}`}>
                              {category.emoji} {category.name}
                            </span>
                          );
                        })}
                      </div>
                    </div>
                  )}
                    </>
                  )}

                  {/* ACTIVITY TAB */}
                  {managedCoupleTab === 'activity' && (
                    <>
                      {/* Stats Summary */}
                      <div className="grid grid-cols-3 gap-4">
                        <div className="bg-zinc-900/50 rounded-xl p-4 text-center">
                          <p className="text-2xl font-bold text-white">{managedCoupleData.tried?.length || 0}</p>
                          <p className="text-zinc-500 text-sm">Activities Tried</p>
                        </div>
                        <div className="bg-zinc-900/50 rounded-xl p-4 text-center">
                          <p className="text-2xl font-bold text-white">{managedCoupleData.favorites?.length || 0}</p>
                          <p className="text-zinc-500 text-sm">Favorites</p>
                        </div>
                        <div className="bg-zinc-900/50 rounded-xl p-4 text-center cursor-pointer hover:bg-zinc-800/50" onClick={() => setManagedCoupleTab('calendar')}>
                          <p className="text-2xl font-bold text-white">{managedCoupleScheduledActivities.filter(a => !a.completedAt).length}</p>
                          <p className="text-zinc-500 text-sm">Scheduled</p>
                        </div>
                      </div>
                    </>
                  )}
                </div>

                {/* Activity History - Part of Activity Tab */}
                {managedCoupleTab === 'activity' && (
                  <>
                    {/* Activity History - actionable inbox style */}
                {((managedCoupleData.tried || []).length > 0) || managedCoupleFollowUps.length > 0 ? (() => {
                  const activityStorageKey = `vb-reviewed-activities:${currentReviewer?.id}`;
                  const storedReviewedIds: string[] = JSON.parse(localStorage.getItem(activityStorageKey) || '[]');
                  const reviewedSet = new Set(storedReviewedIds);

                  const markActivityReviewed = (activityId: string) => {
                    const updated = [...storedReviewedIds, activityId];
                    localStorage.setItem(activityStorageKey, JSON.stringify(updated));
                    reviewedSet.add(activityId);
                    // Force re-render
                    setActivityReviewTick(prev => prev + 1);
                  };

                  // Split follow-ups into needs attention vs reviewed
                  const needsAttentionFollowUps = managedCoupleFollowUps.filter(f => !reviewedSet.has(f.id));
                  const previouslyReviewedFollowUps = managedCoupleFollowUps.filter(f => reviewedSet.has(f.id));

                  // Split tried clips (without follow-ups) into needs attention vs reviewed
                  const triedWithoutFollowUps = (managedCoupleData.tried || [])
                    .slice().reverse().slice(0, 10)
                    .filter(tried => !managedCoupleFollowUps.some(f => f.clipId === tried.clipId));
                  const needsAttentionTried = triedWithoutFollowUps.filter(t => !reviewedSet.has(`tried-${t.clipId}`));
                  const previouslyReviewedTried = triedWithoutFollowUps.filter(t => reviewedSet.has(`tried-${t.clipId}`));

                  const totalNeedsAttention = needsAttentionFollowUps.length + needsAttentionTried.length;
                  const totalPreviouslyReviewed = previouslyReviewedFollowUps.length + previouslyReviewedTried.length;

                  const renderFollowUpCard = (followUp: ActivityFollowUp, isNew: boolean) => {
                    const clip = dateNiteClips.find(c => c.id === followUp.clipId);
                    const category = clip ? CATEGORIES.find(c => c.id === clip.categoryId) : null;
                    const ratingMap: Record<string, string> = { hot: '🔥🔥🔥', good: '🔥🔥', meh: '🔥', nope: '❌' };
                    const hisRating = followUp.hisReflection?.rating ? ratingMap[followUp.hisReflection.rating] : '';
                    const herRating = followUp.herReflection?.rating ? ratingMap[followUp.herReflection.rating] : '';
                    return (
                      <div key={followUp.id} className={`bg-zinc-900/50 rounded-xl p-4 border ${isNew ? 'border-amber-500/30 ring-1 ring-amber-500/10' : 'border-zinc-700'}`}>
                        <div className="flex items-start gap-3">
                          <span className="text-2xl">{category?.emoji}</span>
                          <div className="flex-1">
                            <div className="flex items-center justify-between">
                              <div className="flex items-center gap-2">
                                <p className="text-white font-medium">{clip?.title || followUp.clipTitle || 'Unknown'}</p>
                                {isNew && <span className="px-1.5 py-0.5 bg-amber-500/20 text-amber-400 text-xs rounded-full">New</span>}
                              </div>
                              <span className="text-sm">{hisRating}{herRating && herRating !== hisRating ? ` / ${herRating}` : ''}</span>
                            </div>
                            <p className="text-zinc-500 text-xs mt-1">
                              {new Date(followUp.scheduledDate).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                            </p>

                            {followUp.hisReflection && (
                              <div className="mt-3 bg-blue-500/10 border border-blue-500/20 rounded-lg p-3">
                                <p className="text-blue-400 text-xs font-semibold mb-1">His thoughts</p>
                                {followUp.hisReflection.howItWent && <p className="text-zinc-300 text-sm mb-1">"{followUp.hisReflection.howItWent}"</p>}
                                {followUp.hisReflection.differenceFromClip && <p className="text-zinc-400 text-xs"><span className="text-zinc-500">vs clip:</span> {followUp.hisReflection.differenceFromClip}</p>}
                                {followUp.hisReflection.nextTimeDifferently && <p className="text-amber-400 text-xs mt-1"><span className="text-amber-500">Next time:</span> {followUp.hisReflection.nextTimeDifferently}</p>}
                              </div>
                            )}

                            {followUp.herReflection && (
                              <div className="mt-2 bg-pink-500/10 border border-pink-500/20 rounded-lg p-3">
                                <p className="text-pink-400 text-xs font-semibold mb-1">Her thoughts</p>
                                {followUp.herReflection.howItWent && <p className="text-zinc-300 text-sm mb-1">"{followUp.herReflection.howItWent}"</p>}
                                {followUp.herReflection.differenceFromClip && <p className="text-zinc-400 text-xs"><span className="text-zinc-500">vs clip:</span> {followUp.herReflection.differenceFromClip}</p>}
                                {followUp.herReflection.nextTimeDifferently && <p className="text-amber-400 text-xs mt-1"><span className="text-amber-500">Next time:</span> {followUp.herReflection.nextTimeDifferently}</p>}
                              </div>
                            )}

                            {/* Action buttons */}
                            <div className="flex flex-wrap items-center gap-2 mt-3 pt-2 border-t border-zinc-700/50">
                              <button
                                onClick={() => setPendingQuestion({
                                  question: `About your "${clip?.title || followUp.clipTitle || 'activity'}" experience — `,
                                  category: category?.name || 'General',
                                  context: `Follow-up on activity: ${clip?.title || followUp.clipTitle}`
                                })}
                                className="px-2.5 py-1 bg-purple-500/20 hover:bg-purple-500/30 text-purple-300 rounded text-xs font-medium transition-colors flex items-center gap-1"
                              >
                                <MessageCircle className="w-3 h-3" /> Ask Follow-up
                              </button>
                              {followUp.herReflection && (
                                <button
                                  onClick={() => setPendingQuestion({
                                    question: `Following up on her thoughts about "${clip?.title || followUp.clipTitle}": "${(followUp.herReflection?.howItWent || '').slice(0, 120)}${(followUp.herReflection?.howItWent || '').length > 120 ? '...' : ''}" — `,
                                    category: category?.name || 'General',
                                    context: `Follow-up on her reflection`
                                  })}
                                  className="px-2.5 py-1 bg-pink-500/20 hover:bg-pink-500/30 text-pink-300 rounded text-xs font-medium transition-colors flex items-center gap-1"
                                >
                                  <MessageCircle className="w-3 h-3" /> Follow Up Her Thoughts
                                </button>
                              )}
                              {followUp.hisReflection && (
                                <button
                                  onClick={() => setPendingQuestion({
                                    question: `Following up on his thoughts about "${clip?.title || followUp.clipTitle}": "${(followUp.hisReflection?.howItWent || '').slice(0, 120)}${(followUp.hisReflection?.howItWent || '').length > 120 ? '...' : ''}" — `,
                                    category: category?.name || 'General',
                                    context: `Follow-up on his reflection`
                                  })}
                                  className="px-2.5 py-1 bg-blue-500/20 hover:bg-blue-500/30 text-blue-300 rounded text-xs font-medium transition-colors flex items-center gap-1"
                                >
                                  <MessageCircle className="w-3 h-3" /> Follow Up His Thoughts
                                </button>
                              )}
                              {isNew && (
                                <button
                                  onClick={() => markActivityReviewed(followUp.id)}
                                  className="px-2.5 py-1 bg-zinc-600/50 hover:bg-zinc-600 text-zinc-300 rounded text-xs font-medium transition-colors flex items-center gap-1 ml-auto"
                                >
                                  <Check className="w-3 h-3" /> Mark Reviewed
                                </button>
                              )}
                            </div>
                          </div>
                        </div>
                      </div>
                    );
                  };

                  const renderTriedCard = (tried: NonNullable<CoupleData['tried']>[number], isNew: boolean) => {
                    const clip = dateNiteClips.find(c => c.id === tried.clipId);
                    const category = clip ? CATEGORIES.find(c => c.id === clip.categoryId) : null;
                    const ratingInfo = tried.rating ? { hot: '🔥🔥🔥 Hot!', good: '🔥🔥 Good', meh: '🔥 Meh', nope: '❌ Nope' }[tried.rating] : '';
                    return (
                      <div key={tried.clipId} className={`bg-zinc-900/50 rounded-xl p-4 border ${isNew ? 'border-amber-500/30 ring-1 ring-amber-500/10' : 'border-zinc-700'}`}>
                        <div className="flex items-start gap-3">
                          <span className="text-2xl">{category?.emoji}</span>
                          <div className="flex-1">
                            <div className="flex items-center justify-between">
                              <div className="flex items-center gap-2">
                                <p className="text-white font-medium">{clip?.title || 'Unknown'}</p>
                                {isNew && <span className="px-1.5 py-0.5 bg-amber-500/20 text-amber-400 text-xs rounded-full">New</span>}
                              </div>
                              <span className="text-sm">{ratingInfo}</span>
                            </div>
                            <p className="text-zinc-500 text-xs mt-1">
                              {new Date(tried.triedAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                            </p>
                            {(tried.hisThoughts || tried.herThoughts) && (
                              <div className="mt-2 space-y-1">
                                {tried.hisThoughts && <p className="text-zinc-400 text-sm"><span className="text-blue-400">Him:</span> "{tried.hisThoughts}"</p>}
                                {tried.herThoughts && <p className="text-zinc-400 text-sm"><span className="text-pink-400">Her:</span> "{tried.herThoughts}"</p>}
                              </div>
                            )}
                            {(tried.cumVolume || tried.cumConsistency || tried.cumTaste) && (
                              <div className="mt-2 pt-2 border-t border-zinc-700">
                                <p className="text-zinc-500 text-xs mb-1">💦 Load Review</p>
                                <div className="flex gap-3 text-xs">
                                  {tried.cumVolume && <span className="text-blue-300">Vol: {['', '💧Drip', '💦Splash', '🚿Firehose', '🌊Flood', '🌊TSUNAMI'][tried.cumVolume]}</span>}
                                  {tried.cumConsistency && <span className="text-purple-300">Thick: {['', '💧Watery', '🥛Thin', '🍯Normal', '🧁Thick', '🍦Creamy'][tried.cumConsistency]}</span>}
                                  {tried.cumTaste && <span className="text-amber-300">Taste: {['', '😣Bad', '😕Meh', '😐OK', '🙂Good', '😋Yum'][tried.cumTaste]}</span>}
                                </div>
                                {tried.cumNotes && <p className="text-zinc-400 text-xs mt-1">"{tried.cumNotes}"</p>}
                              </div>
                            )}

                            {/* Action buttons */}
                            <div className="flex flex-wrap items-center gap-2 mt-3 pt-2 border-t border-zinc-700/50">
                              <button
                                onClick={() => setPendingQuestion({
                                  question: `About your "${clip?.title || 'activity'}" experience — `,
                                  category: category?.name || 'General',
                                  context: `Follow-up on tried activity: ${clip?.title || tried.clipId}`
                                })}
                                className="px-2.5 py-1 bg-purple-500/20 hover:bg-purple-500/30 text-purple-300 rounded text-xs font-medium transition-colors flex items-center gap-1"
                              >
                                <MessageCircle className="w-3 h-3" /> Ask Follow-up
                              </button>
                              {tried.herThoughts && (
                                <button
                                  onClick={() => setPendingQuestion({
                                    question: `Following up on her thoughts about "${clip?.title}": "${tried.herThoughts?.slice(0, 120)}${(tried.herThoughts?.length || 0) > 120 ? '...' : ''}" — `,
                                    category: category?.name || 'General',
                                    context: `Follow-up on her thoughts`
                                  })}
                                  className="px-2.5 py-1 bg-pink-500/20 hover:bg-pink-500/30 text-pink-300 rounded text-xs font-medium transition-colors flex items-center gap-1"
                                >
                                  <MessageCircle className="w-3 h-3" /> Follow Up Her Thoughts
                                </button>
                              )}
                              {tried.hisThoughts && (
                                <button
                                  onClick={() => setPendingQuestion({
                                    question: `Following up on his thoughts about "${clip?.title}": "${tried.hisThoughts?.slice(0, 120)}${(tried.hisThoughts?.length || 0) > 120 ? '...' : ''}" — `,
                                    category: category?.name || 'General',
                                    context: `Follow-up on his thoughts`
                                  })}
                                  className="px-2.5 py-1 bg-blue-500/20 hover:bg-blue-500/30 text-blue-300 rounded text-xs font-medium transition-colors flex items-center gap-1"
                                >
                                  <MessageCircle className="w-3 h-3" /> Follow Up His Thoughts
                                </button>
                              )}
                              {isNew && (
                                <button
                                  onClick={() => markActivityReviewed(`tried-${tried.clipId}`)}
                                  className="px-2.5 py-1 bg-zinc-600/50 hover:bg-zinc-600 text-zinc-300 rounded text-xs font-medium transition-colors flex items-center gap-1 ml-auto"
                                >
                                  <Check className="w-3 h-3" /> Mark Reviewed
                                </button>
                              )}
                            </div>
                          </div>
                        </div>
                      </div>
                    );
                  };

                  return (
                  <div className="bg-zinc-800 rounded-2xl p-6 border border-zinc-700">
                    <h3 className="text-lg font-bold text-white mb-4 flex items-center gap-2">
                      <History className="w-5 h-5" /> Activity History
                      {totalNeedsAttention > 0 && (
                        <span className="px-2 py-0.5 bg-amber-500/20 text-amber-400 text-xs rounded-full">
                          {totalNeedsAttention} needs attention
                        </span>
                      )}
                    </h3>

                    {/* Needs Your Attention */}
                    {totalNeedsAttention > 0 && (
                      <div className="mb-4">
                        <p className="text-amber-400 text-xs font-semibold mb-2 uppercase tracking-wider">Needs Your Attention</p>
                        <div className="space-y-3">
                          {needsAttentionFollowUps.slice(0, 10).map(f => renderFollowUpCard(f, true))}
                          {needsAttentionTried.map(t => renderTriedCard(t, true))}
                        </div>
                      </div>
                    )}

                    {/* Previously Reviewed (collapsed) */}
                    {totalPreviouslyReviewed > 0 && (
                      <div className="border-t border-zinc-700 pt-4">
                        <button
                          onClick={() => setShowPreviouslyReviewedActivity(!showPreviouslyReviewedActivity)}
                          className="flex items-center gap-2 text-zinc-500 text-xs hover:text-zinc-400 transition-colors mb-2"
                        >
                          <ChevronDown className={`w-3.5 h-3.5 transition-transform ${showPreviouslyReviewedActivity ? 'rotate-180' : ''}`} />
                          Previously Reviewed ({totalPreviouslyReviewed})
                        </button>
                        {showPreviouslyReviewedActivity && (
                          <div className="space-y-3">
                            {previouslyReviewedFollowUps.slice(0, 10).map(f => renderFollowUpCard(f, false))}
                            {previouslyReviewedTried.map(t => renderTriedCard(t, false))}
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                  );
                })() : null}

                {/* Favorites with Notes */}
                {(managedCoupleData.favorites || []).length > 0 && (
                  <div className="bg-zinc-800 rounded-2xl p-6 border border-zinc-700">
                    <h3 className="text-lg font-bold text-white mb-4 flex items-center gap-2">
                      <Heart className="w-5 h-5 text-pink-400" /> Their Favorites
                    </h3>
                    <div className="space-y-3">
                      {(managedCoupleData.favorites || []).map(clipId => {
                        const clip = dateNiteClips.find(c => c.id === clipId);
                        const category = clip ? CATEGORIES.find(c => c.id === clip.categoryId) : null;
                        const note = managedCoupleData.favoriteNotes?.[clipId];
                        return clip && (
                          <div key={clipId} className="bg-pink-500/10 border border-pink-500/20 rounded-xl p-4">
                            <div className="flex items-center gap-3">
                              <span className="text-2xl">{category?.emoji}</span>
                              <div className="flex-1">
                                <p className="text-white font-medium">{clip.title}</p>
                                <p className="text-zinc-500 text-xs">{category?.name}</p>
                              </div>
                              <Heart className="w-5 h-5 text-pink-400 fill-pink-400" />
                            </div>
                            {note && (
                              <div className="mt-3 pl-11">
                                <p className="text-pink-300 text-sm italic">"{note}"</p>
                              </div>
                            )}
                            {/* Discuss / Ask about this favorite */}
                            <div className="mt-2 pl-11 flex gap-2">
                              <button
                                onClick={() => {
                                  const userQuestion = prompt(`Ask ${managedCoupleData?.herName || 'the couple'} about "${clip.title}":`);
                                  if (userQuestion && currentReviewer) {
                                    const question = {
                                      id: `cq-${Date.now()}`,
                                      coupleId: managedCoupleData.coupleId,
                                      fromReviewerId: currentReviewer.id,
                                      fromReviewerName: currentReviewer.partnerName || currentReviewer.code,
                                      question: userQuestion,
                                      category: 'activity' as const,
                                      context: `About their favorite: ${clip.title}`,
                                      sentAt: new Date().toISOString(),
                                      status: 'pending' as const,
                                      clipId: clip.id,
                                    };
                                    d1Upsert('couple_questions', question);
                                    saveCoupleQuestions([...coupleQuestions, question]);
                                  }
                                }}
                                className="px-2 py-1 bg-pink-500/20 hover:bg-pink-500/30 text-pink-400 rounded text-xs"
                              >
                                💭 Ask About This
                              </button>
                              <button
                                onClick={async () => {
                                  if (!currentReviewer) return;
                                  const message = `I noticed "${clip.title}" is one of your favorites${note ? ` — love that you said "${note}"` : ''}! What is it about this one that really does it for you?`;
                                  await d1Upsert('couple_messages', {
                                    id: `msg-${Date.now()}`,
                                    coupleId: managedCoupleData.coupleId,
                                    fromType: 'reviewer',
                                    fromName: currentReviewer.partnerName || currentReviewer.code,
                                    message,
                                    createdAt: new Date().toISOString(),
                                    seenByCouple: false,
                                    seenByReviewer: true,
                                    clipId: clip.id,
                                  });
                                  alert('Message sent in chat!');
                                }}
                                className="px-2 py-1 bg-zinc-700 hover:bg-zinc-600 text-zinc-300 rounded text-xs"
                              >
                                💬 Discuss in Chat
                              </button>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}
                  </>
                )}

                {/* CALENDAR TAB */}
                {managedCoupleTab === 'calendar' && (
                  <DndContext
                    sensors={sensors}
                    collisionDetection={closestCorners}
                    onDragStart={handleManagedDragStart}
                    onDragEnd={handleManagedDragEnd}
                  >
                    <div className="space-y-6">
                      {/* Activity Timeline */}
                      <div className="w-full">
                        <div className="flex items-center justify-between mb-4">
                          <h2 className="text-lg font-bold text-white flex items-center gap-2">
                            <History className="w-5 h-5" style={{ color: COLORS.lime }} /> {managedCoupleData.hisName} & {managedCoupleData.herName}'s Timeline
                          </h2>
                          <PrimaryButton
                            onClick={() => {
                              setSelectedManagedCalendarDate(undefined);
                              setShowManagedScheduleModal(true);
                            }}
                          >
                            <Calendar className="w-4 h-4" /> Schedule Activity
                          </PrimaryButton>
                        </div>
                        <UpcomingActivities
                          scheduledActivities={managedCoupleScheduledActivities}
                          clips={dateNiteClips}
                          onViewClip={clip => setWatchingClip(clip)}
                          onMarkComplete={handleManagedMarkComplete}
                          onReschedule={handleManagedRescheduleActivity}
                          onDelete={handleManagedDeleteActivity}
                          showCompleted
                          limit={10}
                        />
                      </div>

                      {/* Clips Sidebar + Calendar Row */}
                      <div className="flex gap-6">
                        <ClipSidebar
                          clips={dateNiteClips}
                          isCollapsed={managedSidebarCollapsed}
                          onToggleCollapse={() => setManagedSidebarCollapsed(!managedSidebarCollapsed)}
                        />
                        <div className="flex-1">
                          <DragDropCalendarView
                            scheduledActivities={managedCoupleScheduledActivities}
                            clips={dateNiteClips}
                            onActivityClick={(activity) => {
                              const clip = dateNiteClips.find(c => c.id === activity.clipId);
                              if (clip) setWatchingClip(clip);
                            }}
                          />
                        </div>
                      </div>
                    </div>

                    <DragOverlay>
                      {activeClip && <ClipDragPreview clip={activeClip} />}
                      {activeActivity && activeActivity.clip && (
                        <ActivityDragPreview clip={activeActivity.clip} />
                      )}
                    </DragOverlay>
                  </DndContext>
                )}

                {/* Quick Actions - Always visible */}
                <div className="bg-zinc-800 rounded-2xl p-6 border border-zinc-700">
                  <h3 className="text-lg font-bold text-white mb-4">Quick Actions</h3>
                  <div className="grid grid-cols-2 gap-4">
                    <button
                      onClick={() => setManagedCoupleTab('calendar')}
                      className="flex items-center justify-center gap-2 bg-gradient-to-r from-purple-500/20 to-pink-500/20 border border-purple-500/30 rounded-xl p-4 text-purple-300 hover:border-purple-500/50 transition-colors"
                    >
                      <Calendar className="w-5 h-5" />
                      <span>View Calendar</span>
                    </button>
                  </div>
                </div>

                {/* Clip Search for Quick Recommend */}
                <div className="bg-zinc-800 rounded-2xl p-6 border border-zinc-700">
                  <h3 className="text-lg font-bold text-white mb-4 flex items-center gap-2">
                    <Gift className="w-5 h-5" /> Quick Recommend a Clip
                  </h3>
                  <p className="text-zinc-400 text-sm mb-4">Search for clips to recommend to {managedCoupleData.hisName} & {managedCoupleData.herName}</p>

                  {/* Search Input */}
                  <div className="flex gap-2 mb-4">
                    <div className="relative flex-1">
                      <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-400" />
                      <input
                        type="text"
                        value={clipSearchTerm}
                        onChange={e => setClipSearchTerm(e.target.value)}
                        placeholder="Search clips by title, description, or tags..."
                        className="w-full bg-zinc-700 border border-zinc-600 rounded-xl pl-10 pr-4 py-3 text-white placeholder-zinc-400"
                      />
                    </div>
                    <select
                      value={clipCategoryFilter}
                      onChange={e => setClipCategoryFilter(e.target.value)}
                      className="bg-zinc-700 border border-zinc-600 rounded-xl px-4 py-3 text-white"
                    >
                      <option value="all">All Categories</option>
                      {CATEGORIES.map(cat => (
                        <option key={cat.id} value={cat.id}>{cat.emoji} {cat.name}</option>
                      ))}
                    </select>
                  </div>

                  {/* Search Results */}
                  {clipSearchTerm.trim() && (
                    <div className="space-y-2 max-h-64 overflow-y-auto">
                      {searchedClips.slice(0, 10).map(clip => {
                        const category = CATEGORIES.find(c => c.id === clip.categoryId);
                        const alreadyRecommended = recommendations.some(r => r.clipId === clip.id);
                        return (
                          <div
                            key={clip.id}
                            className="flex items-center justify-between bg-zinc-900/50 rounded-xl p-3 border border-zinc-700"
                          >
                            <div className="flex items-center gap-3">
                              <span className="text-2xl">{category?.emoji}</span>
                              <div>
                                <p className="text-white font-medium">{clip.title}</p>
                                <p className="text-zinc-500 text-xs">{category?.name}</p>
                              </div>
                            </div>
                            <button
                              onClick={async () => {
                                // Quick recommend
                                const newRec: CoupleRecommendation = {
                                  id: generateId('rec'),
                                  coupleId: managedCoupleData.coupleId,
                                  clipId: clip.id,
                                  fromName: currentReviewer?.partnerName || 'Your Reviewer',
                                  message: '',
                                  createdAt: now(),
                                  seen: false,
                                };
                                await d1Upsert('couple_recommendations', newRec);
                                setRecommendations(prev => [...prev, newRec]);
                              }}
                              disabled={alreadyRecommended}
                              className={`px-3 py-1.5 rounded-lg text-sm transition-colors ${
                                alreadyRecommended
                                  ? 'bg-zinc-700 text-zinc-500 cursor-not-allowed'
                                  : 'bg-pink-500/20 text-pink-400 border border-pink-500/30 hover:bg-pink-500/30'
                              }`}
                            >
                              {alreadyRecommended ? 'Sent' : 'Recommend'}
                            </button>
                          </div>
                        );
                      })}
                      {searchedClips.length === 0 && (
                        <p className="text-zinc-500 text-center py-4">No clips match your search</p>
                      )}
                      {searchedClips.length > 10 && (
                        <p className="text-zinc-500 text-center text-sm py-2">Showing 10 of {searchedClips.length} results</p>
                      )}
                    </div>
                  )}
                </div>

                {/* Link to Their Calendar Tab */}
                <div className="bg-gradient-to-r from-purple-500/10 to-pink-500/10 border border-purple-500/30 rounded-2xl p-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <h3 className="text-lg font-bold text-white flex items-center gap-2 mb-2">
                        <Calendar className="w-5 h-5" /> Their Schedule
                      </h3>
                      <p className="text-zinc-400 text-sm">
                        {managedCoupleScheduledActivities.filter(a => !a.completedAt).length} upcoming activities planned
                      </p>
                    </div>
                    <button
                      onClick={() => setManagedCoupleTab('calendar')}
                      className="px-6 py-3 rounded-xl font-medium text-zinc-900 flex items-center gap-2"
                      style={{ backgroundColor: COLORS.lime }}
                    >
                      <Calendar className="w-5 h-5" /> View Their Calendar
                    </button>
                  </div>
                </div>
              </>
            )}

            {/* No couple linked yet and no code entered */}
            {!currentReviewer?.coupleId && !managedCoupleData && (
              <div className="text-center py-12">
                <Users className="w-16 h-16 text-zinc-600 mx-auto mb-4" />
                <p className="text-zinc-400">Link to a couple to manage their date nights</p>
              </div>
            )}
          </div>
        )}

        {/* Inbox Tab */}
        {tab === 'inbox' && (
          <div className="space-y-6">
            {/* Edit Suggestion Notifications */}
            {(() => {
              // Find edit notifications for this reviewer
              const myEditNotifications = reviewerFollowUps.filter(
                fu => fu.reviewerId === currentReviewer?.id && fu.editOutcome && fu.status !== 'dismissed'
              );

              if (myEditNotifications.length === 0) return null;

              return (
                <div className="bg-zinc-800 rounded-2xl border border-zinc-700 overflow-hidden">
                  <button
                    onClick={() => setShowEditNotifications(!showEditNotifications)}
                    className="w-full px-6 py-4 flex items-center justify-between"
                  >
                    <h3 className="text-xl font-bold text-white flex items-center gap-2">
                      📝 Edit Suggestions
                      <span className="text-sm font-normal text-zinc-400">({myEditNotifications.length})</span>
                    </h3>
                    <ChevronDown className={`w-5 h-5 text-zinc-400 transition-transform ${showEditNotifications ? 'rotate-180' : ''}`} />
                  </button>
                  {showEditNotifications && (
                    <div className="px-6 pb-6 space-y-3">
                      {myEditNotifications.map(notification => (
                        <div
                          key={notification.id}
                          className={`p-4 rounded-xl border ${
                            notification.editOutcome?.outcome === 'approved'
                              ? 'bg-green-500/10 border-green-500/30'
                              : 'bg-red-500/10 border-red-500/30'
                          }`}
                        >
                          <div className="flex justify-between items-start">
                            <div>
                              <p className="text-white font-medium">
                                {notification.editOutcome?.outcome === 'approved'
                                  ? '✓ Your edit was approved!'
                                  : '✗ Your edit was not approved'}
                              </p>
                              <p className="text-zinc-400 text-sm">
                                Clip: {notification.editOutcome?.clipTitle}
                              </p>
                            </div>
                          </div>

                          {notification.editOutcome?.rejectionReason && (
                            <p className="text-red-400 text-sm mt-2 italic">
                              Reason: "{notification.editOutcome.rejectionReason}"
                            </p>
                          )}

                          <button
                            onClick={() => {
                              const updated = reviewerFollowUps.map(fu =>
                                fu.id === notification.id ? { ...fu, status: 'dismissed' as const } : fu
                              );
                              saveReviewerFollowUps(updated);
                            }}
                            className="text-zinc-500 text-xs mt-3 hover:text-zinc-300"
                          >
                            Dismiss
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              );
            })()}

            {/* Pending Edit Suggestions */}
            {(() => {
              const myPendingSuggestions = suggestedEdits.filter(
                e => e.reviewerId === currentReviewer?.id && e.status === 'pending'
              );

              if (myPendingSuggestions.length === 0) return null;

              return (
                <div className="bg-zinc-800 rounded-2xl p-6 border border-zinc-700">
                  <button
                    onClick={() => setShowPendingEdits(!showPendingEdits)}
                    className="w-full flex items-center justify-between"
                  >
                    <h3 className="text-xl font-bold text-white flex items-center gap-2">
                      ⏳ Pending Edit Suggestions
                      <span className="px-2 py-1 bg-amber-500/20 text-amber-400 text-xs rounded-full">
                        {myPendingSuggestions.length} awaiting review
                      </span>
                    </h3>
                    <ChevronDown className={`w-5 h-5 text-zinc-400 transition-transform ${showPendingEdits ? 'rotate-180' : ''}`} />
                  </button>
                  {showPendingEdits && (
                    <>
                      <p className="text-zinc-400 text-sm mb-4 mt-4">
                        These edits are waiting for approval.
                      </p>
                      <div className="space-y-3">
                        {myPendingSuggestions.map(suggestion => {
                          const clip = dateNiteClips.find(c => c.id === suggestion.clipId);
                          return (
                            <div
                              key={suggestion.id}
                              className="bg-amber-500/10 border border-amber-500/30 rounded-xl p-4"
                            >
                              <div className="flex justify-between items-start">
                                <div>
                                  <p className="text-white font-medium">{clip?.title || suggestion.clipId}</p>
                                  <p className="text-zinc-400 text-sm mt-1">
                                    Changes: {Object.keys(suggestion.suggestedChanges).join(', ')}
                                  </p>
                                </div>
                                <span className="text-zinc-500 text-xs">
                                  {new Date(suggestion.submittedAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                                </span>
                              </div>
                              {suggestion.reason && (
                                <p className="text-zinc-400 text-xs mt-2 italic">"{suggestion.reason}"</p>
                              )}
                              <button
                                onClick={async () => {
                                  if (!confirm('Withdraw this edit suggestion?')) return;
                                  const updated = suggestedEdits.filter(s => s.id !== suggestion.id);
                                  await saveSuggestedEdits(updated);
                                }}
                                className="mt-3 text-red-400 text-xs hover:text-red-300"
                              >
                                ✕ Withdraw suggestion
                              </button>
                            </div>
                          );
                        })}
                      </div>
                    </>
                  )}
                </div>
              );
            })()}

            {/* Clip Suggestions from Admin */}
            {(() => {
              const myClipSuggestions = clipSuggestions.filter(cs => cs.reviewerId === currentReviewer?.id || cs.reviewerId === currentReviewer?.code);
              const pendingSuggestions = myClipSuggestions.filter(cs => cs.status === 'pending');

              if (myClipSuggestions.length === 0) return null;

              return (
                <div className="bg-zinc-800 rounded-2xl p-6 border border-pink-500/30">
                  <button
                    onClick={() => setShowClipsToReview(!showClipsToReview)}
                    className="w-full flex items-center justify-between"
                  >
                    <h3 className="text-xl font-bold text-white flex items-center gap-2">
                      <Film className="w-5 h-5 text-pink-400" /> Clips to Review
                      {pendingSuggestions.length > 0 && (
                        <span className="px-2 py-1 bg-pink-500 text-white text-xs rounded-full">
                          {pendingSuggestions.length} pending
                        </span>
                      )}
                    </h3>
                    <ChevronDown className={`w-5 h-5 text-zinc-400 transition-transform ${showClipsToReview ? 'rotate-180' : ''}`} />
                  </button>
                  {showClipsToReview && (
                  <>
                  <p className="text-zinc-500 text-sm mb-4 mt-2">These clips have been curated for you to review and rate. Like one? Share it to your couple!</p>

                  <div className="space-y-4">
                    {myClipSuggestions.map(suggestion => (
                      <div
                        key={suggestion.id}
                        className={`rounded-xl p-4 border ${
                          suggestion.status === 'completed'
                            ? 'bg-zinc-700/30 border-zinc-600'
                            : 'bg-pink-500/10 border-pink-500/30'
                        }`}
                      >
                        {/* Context */}
                        {suggestion.context && (
                          <p className="text-zinc-300 text-sm mb-3 italic">"{suggestion.context}"</p>
                        )}

                        {/* Clips grid */}
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                          {(suggestion.clips || []).map(clipItem => {
                            const isRated = !!clipItem.rating;

                            return (
                              <div key={clipItem.id} className={`bg-zinc-700 rounded-lg p-3 ${isRated ? 'border border-lime-500/30' : ''}`}>
                                <p className="text-white text-sm font-medium truncate">{clipItem.title}</p>
                                {clipItem.description && (
                                  <p className="text-zinc-400 text-xs mt-1 truncate">{clipItem.description}</p>
                                )}

                                {/* Show rating if already rated */}
                                {isRated ? (
                                  <div className="mt-2 space-y-1">
                                    <div className="flex items-center gap-2">
                                      <div className="flex">
                                        {[1,2,3,4,5].map(s => (
                                          <Star key={s} className={`w-3 h-3 ${s <= (clipItem.rating || 0) ? 'text-yellow-400 fill-yellow-400' : 'text-zinc-600'}`} />
                                        ))}
                                      </div>
                                      {clipItem.interested !== undefined && (
                                        <span className={`text-xs ${clipItem.interested ? 'text-lime-400' : 'text-zinc-500'}`}>
                                          {clipItem.interested ? '✓ Would try' : '✗ Not interested'}
                                        </span>
                                      )}
                                    </div>
                                    {clipItem.comment && (
                                      <p className="text-zinc-400 text-xs italic">"{clipItem.comment}"</p>
                                    )}
                                    <div className="flex items-center gap-3 mt-1">
                                      <button
                                        onClick={() => {
                                          setReviewingSuggestionClip({ suggestionId: suggestion.id, clipItem });
                                          setSuggestionClipRating(clipItem.rating || 0);
                                          setSuggestionClipComment(clipItem.comment || '');
                                          setSuggestionClipInterested(clipItem.interested ?? null);
                                        }}
                                        className="text-zinc-500 hover:text-zinc-300 text-xs"
                                      >
                                        Edit review
                                      </button>
                                      {(clipItem.interested || (clipItem.rating && clipItem.rating >= 3)) && (
                                        <button
                                          onClick={() => setShowScheduleModal(true)}
                                          className="text-pink-400 hover:text-pink-300 text-xs flex items-center gap-1 bg-pink-500/10 px-2 py-0.5 rounded"
                                        >
                                          <Send className="w-3 h-3" /> Share to Couple
                                        </button>
                                      )}
                                    </div>
                                  </div>
                                ) : (
                                  <button
                                    onClick={() => {
                                      setReviewingSuggestionClip({ suggestionId: suggestion.id, clipItem });
                                      setSuggestionClipRating(0);
                                      setSuggestionClipComment('');
                                      setSuggestionClipInterested(null);
                                    }}
                                    className="mt-2 w-full flex items-center justify-center gap-1 px-2 py-2 bg-pink-500/20 hover:bg-pink-500/30 text-pink-400 text-sm rounded-lg transition-colors"
                                  >
                                    <Play className="w-4 h-4" /> Watch & Review
                                  </button>
                                )}
                              </div>
                            );
                          })}
                        </div>

                        {/* Mark as complete button */}
                        {suggestion.status === 'pending' && (
                          <button
                            onClick={async () => {
                              const updated = clipSuggestions.map(cs =>
                                cs.id === suggestion.id
                                  ? { ...cs, status: 'completed' as const, completedAt: now() }
                                  : cs
                              );
                              await saveClipSuggestions(updated);
                            }}
                            className="mt-3 w-full flex items-center justify-center gap-2 px-3 py-2 bg-lime-500/20 hover:bg-lime-500/30 text-lime-400 rounded-lg text-sm transition-colors"
                          >
                            <Check className="w-4 h-4" /> Mark as Reviewed
                          </button>
                        )}

                        {/* Metadata */}
                        <p className="text-zinc-500 text-xs mt-2">
                          Sent {new Date(suggestion.sentAt).toLocaleDateString()}
                          {suggestion.status === 'completed' && suggestion.completedAt && (
                            <span className="text-lime-400"> • Completed {new Date(suggestion.completedAt).toLocaleDateString()}</span>
                          )}
                        </p>
                      </div>
                    ))}
                  </div>
                  </>
                  )}
                </div>
              );
            })()}

            {/* Admin Follow-up Questions Section */}
            {(() => {
              const myFollowUps = reviewerFollowUps.filter(fu => fu.reviewerId === currentReviewer?.id && !fu.editOutcome);
              const pendingFollowUps = myFollowUps.filter(fu => fu.status === 'pending');
              const answeredFollowUps = myFollowUps.filter(fu => fu.status === 'answered');

              if (myFollowUps.length === 0) return null;

              return (
                <div className="bg-zinc-800 rounded-2xl p-6 border border-zinc-700">
                  <h3 className="text-xl font-bold text-white mb-2 flex items-center gap-2">
                    📋 Questions for You
                    {pendingFollowUps.length > 0 && (
                      <span className="px-2 py-1 bg-amber-500 text-white text-xs rounded-full">
                        {pendingFollowUps.length} to answer
                      </span>
                    )}
                  </h3>
                  <p className="text-zinc-400 text-sm mb-1">
                    Questions about YOUR preferences (not your managed couple).
                  </p>
                  <p className="text-zinc-500 text-xs mb-2">
                    These are follow-ups based on your responses. They'll filter in throughout the day as we process your anonymous answers into our system.
                  </p>
                  <p className="text-zinc-500 text-xs mb-4 bg-zinc-700/50 rounded-lg px-3 py-2">
                    🔒 <span className="text-zinc-400">Your answers help train our recommendation system.</span> The more honest and detailed, the better we can match you with content you'll love. <span className="text-lime-400">Always anonymous — no humans read your responses directly.</span>
                  </p>

                  <div className="space-y-4">
                    {/* Pending questions first */}
                    {pendingFollowUps.map(fu => {
                      // Build full thread chain (oldest first)
                      const threadChain: typeof myFollowUps = [];
                      let currentParentId = fu.parentId;
                      while (currentParentId) {
                        const found = myFollowUps.find(p => p.id === currentParentId);
                        if (!found) break;
                        threadChain.unshift(found);
                        currentParentId = found.parentId;
                      }

                      return (
                      <div key={fu.id} className="bg-gradient-to-r from-amber-500/10 to-orange-500/10 border border-amber-500/30 rounded-xl p-4">
                        {/* Thread context - full conversation history */}
                        {threadChain.length > 0 && (
                          <div className="mb-3 pb-3 border-b border-amber-500/20">
                            <p className="text-zinc-500 text-xs mb-1">Conversation history:</p>
                            <div className="space-y-2 max-h-48 overflow-y-auto">
                              {threadChain.map((t, i) => (
                                <div key={t.id} className="bg-zinc-700/50 rounded-lg p-2 text-sm" style={{ marginLeft: `${i * 8}px` }}>
                                  <p className="text-zinc-400 italic">Q: {t.question.slice(0, 150)}{t.question.length > 150 ? '...' : ''}</p>
                                  {t.answer && (
                                    <p className="text-zinc-300 mt-1">A: {t.answer.slice(0, 200)}{(t.answer?.length || 0) > 200 ? '...' : ''}</p>
                                  )}
                                </div>
                              ))}
                            </div>
                          </div>
                        )}
                        <div className="flex items-start gap-3">
                          <div className="w-10 h-10 rounded-full bg-amber-500/20 flex items-center justify-center flex-shrink-0">
                            <span className="text-lg">{threadChain.length > 0 ? '↩️' : '❓'}</span>
                          </div>
                          <div className="flex-1">
                            <p className="text-white font-medium mb-1 whitespace-pre-wrap">{renderTextWithLinks(fu.question)}</p>
                            {fu.context && (
                              <p className="text-zinc-400 text-xs mb-3 italic">Context: {fu.context}</p>
                            )}
                            {/* Linked clip previews (DateNite clips + submitted clips) — tap to open modal */}
                            {fu.clipIds && fu.clipIds.length > 0 && (() => {
                              const items: { id: string; url: string; title: string; subtitle: string; clipObj?: Clip }[] = [];
                              fu.clipIds!.forEach(id => {
                                const dnClip = dateNiteClips.find(c => c.id === id);
                                if (dnClip) {
                                  const cat = CATEGORIES.find(c => c.id === dnClip.categoryId);
                                  items.push({ id: dnClip.id, url: dnClip.link, title: dnClip.title, subtitle: `${cat?.emoji || ''} ${cat?.name || ''}`, clipObj: dnClip });
                                  return;
                                }
                                const subClip = submittedClips.find(s => s.id === id);
                                if (subClip) {
                                  // Create a Clip-like object for the modal viewer
                                  const fakeClip: Clip = { id: subClip.id, categoryId: '' as any, title: subClip.clipTitle, link: subClip.contentLink, description: '', tags: [], rating: '', notesForHim: '', notesForHer: '', reviews: [], picks: [] } as Clip;
                                  items.push({ id: subClip.id, url: subClip.contentLink, title: subClip.clipTitle, subtitle: subClip.product || 'Submission', clipObj: fakeClip });
                                }
                              });
                              if (items.length === 0) return null;
                              return (
                                <div className="mb-3 space-y-2">
                                  {items.map(item => (
                                    <div key={item.id} className="rounded-lg overflow-hidden border border-zinc-600 bg-zinc-900">
                                      <VideoPlayer url={item.url} title={item.title} showThumbnail onClick={() => item.clipObj && setWatchingClip(item.clipObj)} />
                                      <div className="px-3 py-2">
                                        <p className="text-zinc-300 text-sm font-medium">{item.title}</p>
                                        <p className="text-zinc-500 text-xs">{item.subtitle}</p>
                                      </div>
                                    </div>
                                  ))}
                                </div>
                              );
                            })()}
                            {/* Also check legacy single clipId */}
                            {fu.clipId && !fu.clipIds && (() => {
                              const dnClip = dateNiteClips.find(c => c.id === fu.clipId);
                              const subClip = !dnClip ? submittedClips.find(s => s.id === fu.clipId) : null;
                              const clipObj: Clip | null = dnClip || (subClip ? { id: subClip.id, categoryId: '' as any, title: subClip.clipTitle, link: subClip.contentLink, description: '', tags: [], rating: '', notesForHim: '', notesForHer: '', reviews: [], picks: [] } as Clip : null);
                              if (!clipObj) return null;
                              const cat = dnClip ? CATEGORIES.find(c => c.id === dnClip.categoryId) : null;
                              const subtitle = cat ? `${cat.emoji} ${cat.name}` : (subClip?.product || 'Submission');
                              return (
                                <div className="mb-3 rounded-lg overflow-hidden border border-zinc-600 bg-zinc-900">
                                  <VideoPlayer url={clipObj.link} title={clipObj.title} showThumbnail onClick={() => setWatchingClip(clipObj)} />
                                  <div className="px-3 py-2">
                                    <p className="text-zinc-300 text-sm font-medium">{clipObj.title}</p>
                                    <p className="text-zinc-500 text-xs">{subtitle}</p>
                                  </div>
                                </div>
                              );
                            })()}
                            <textarea
                              placeholder="Type your answer here..."
                              className="w-full bg-zinc-700 border border-zinc-600 rounded-lg px-3 py-2 text-white text-sm resize-none"
                              rows={3}
                              id={`followup-${fu.id}`}
                            />
                            <button
                              onClick={async () => {
                                const textarea = document.getElementById(`followup-${fu.id}`) as HTMLTextAreaElement;
                                const answer = textarea?.value?.trim();
                                if (!answer) return;

                                // Mark this question as answered
                                let updated = reviewerFollowUps.map(f =>
                                  f.id === fu.id
                                    ? { ...f, answer, answeredAt: now(), status: 'answered' as const }
                                    : f
                                );

                                // If this was part of a sequence, activate the next queued question
                                if (fu.sequenceId && fu.sequenceOrder !== undefined) {
                                  const currentOrder = fu.sequenceOrder;
                                  const nextInSequence = updated.find(f =>
                                    f.sequenceId === fu.sequenceId &&
                                    f.sequenceOrder === currentOrder + 1 &&
                                    f.status === 'queued'
                                  );
                                  if (nextInSequence) {
                                    updated = updated.map(f =>
                                      f.id === nextInSequence.id
                                        ? { ...f, status: 'pending' as const }
                                        : f
                                    );
                                  }
                                }

                                await saveReviewerFollowUps(updated);
                                // Clear the textarea after successful save
                                if (textarea) textarea.value = '';
                              }}
                              className="mt-2 px-4 py-2 rounded-lg font-medium text-zinc-900 text-sm"
                              style={{ backgroundColor: COLORS.lime }}
                            >
                              <Check className="w-4 h-4 inline mr-1" /> Submit Answer
                            </button>
                          </div>
                        </div>
                      </div>
                      );
                    })}

                    {/* Answered questions (collapsible toggle) */}
                    {answeredFollowUps.length > 0 && (
                      <div className="border-t border-zinc-700 pt-4 mt-4">
                        <button
                          onClick={() => setShowPreviouslyAnswered(!showPreviouslyAnswered)}
                          className="flex items-center gap-2 text-zinc-500 text-xs hover:text-zinc-400 transition-colors"
                        >
                          <span className={`transform transition-transform ${showPreviouslyAnswered ? 'rotate-90' : ''}`}>▶</span>
                          Previously answered ({answeredFollowUps.length})
                        </button>
                        {showPreviouslyAnswered && (
                          <div className="mt-2 space-y-2">
                            {answeredFollowUps.map(fu => (
                              <div key={fu.id} className="bg-zinc-700/30 rounded-lg p-3">
                                <p className="text-zinc-400 text-sm">{fu.question}</p>
                                <p className="text-white text-sm mt-1">→ {fu.answer}</p>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              );
            })()}

            {/* Tag Education Tutorials Section */}
            {(() => {
              const myTutorialProgress = tagTutorials.filter(t => t.reviewerId === currentReviewer?.id);
              const completedTags = myTutorialProgress.filter(t => t.status === 'completed').map(t => t.tagId);
              // Only show tutorials that have been explicitly assigned by admin
              const assignedTutorials = myTutorialProgress.filter(t => t.assignedBy && t.status !== 'completed');
              const availableTutorials = TAG_TUTORIAL_DEFINITIONS
                .filter(def => assignedTutorials.some(t => t.tagId === def.tagId))
                .sort((a, b) => {
                  // Priority tutorials first
                  const aRecord = assignedTutorials.find(t => t.tagId === a.tagId);
                  const bRecord = assignedTutorials.find(t => t.tagId === b.tagId);
                  if (aRecord?.priority && !bRecord?.priority) return -1;
                  if (!aRecord?.priority && bRecord?.priority) return 1;
                  return 0;
                });
              const viewedTags = myTutorialProgress.filter(t => t.status === 'viewed').map(t => t.tagId);

              const handleViewTutorial = (tagId: string) => {
                setExpandedTutorialTag(expandedTutorialTag === tagId ? null : tagId);
                // Mark as viewed if not already
                if (!myTutorialProgress.some(t => t.tagId === tagId)) {
                  const newTutorial: TagTutorial = {
                    id: generateId('tt'),
                    reviewerId: currentReviewer?.id || '',
                    tagId,
                    status: 'viewed',
                    sentAt: now(),
                    viewedAt: now()
                  };
                  saveTagTutorials([...tagTutorials, newTutorial]);
                }
              };

              const handleCompleteTutorial = (tagId: string) => {
                const existing = myTutorialProgress.find(t => t.tagId === tagId);
                if (existing) {
                  const updated = tagTutorials.map(t =>
                    t.id === existing.id
                      ? { ...t, status: 'completed' as const, completedAt: now() }
                      : t
                  );
                  saveTagTutorials(updated);
                } else {
                  const newTutorial: TagTutorial = {
                    id: generateId('tt'),
                    reviewerId: currentReviewer?.id || '',
                    tagId,
                    status: 'completed',
                    sentAt: now(),
                    viewedAt: now(),
                    completedAt: now()
                  };
                  saveTagTutorials([...tagTutorials, newTutorial]);
                }
                setExpandedTutorialTag(null);
              };

              // Get clips that match a specific tag+category combo for examples
              const getExampleClips = (tagId: string, categoryId: string) => {
                return dateNiteClips.filter(c =>
                  c.tags?.includes(tagId) && c.categoryId === categoryId
                ).slice(0, 2);
              };

              // Get couple's tried clips with this tag
              const getCoupleTriedWithTag = (tagId: string) => {
                if (!linkedCoupleData) return [];
                return linkedCoupleData.tried
                  .map(t => {
                    const clip = dateNiteClips.find(c => c.id === t.clipId);
                    return clip && clip.tags?.includes(tagId) ? { ...t, clip } : null;
                  })
                  .filter(Boolean);
              };

              return (
                <div className="bg-zinc-800 rounded-2xl p-6 border border-zinc-700">
                  <h3 className="text-xl font-bold text-white mb-2 flex items-center gap-2">
                    🏷️ Tag Education
                    {availableTutorials.length > 0 && (
                      <span className="px-2 py-1 bg-blue-500 text-white text-xs rounded-full">
                        {availableTutorials.length} to learn
                      </span>
                    )}
                  </h3>
                  <p className="text-zinc-400 text-sm mb-4">
                    Learn how to combine tags with categories for better content discovery. Complete tutorials to become a tagging pro!
                  </p>

                  {/* Only show section if there are assigned tutorials */}
                  {availableTutorials.length === 0 && completedTags.length === 0 ? (
                    <div className="text-center py-6">
                      <p className="text-zinc-400 text-sm">No tag tutorials assigned yet.</p>
                      <p className="text-zinc-500 text-xs mt-1">Check back later for personalized learning content.</p>
                    </div>
                  ) : availableTutorials.length === 0 ? (
                    <div className="text-center py-6">
                      <div className="w-16 h-16 mx-auto mb-3 rounded-full bg-gradient-to-r from-green-500 to-emerald-500 flex items-center justify-center">
                        <Check className="w-8 h-8 text-white" />
                      </div>
                      <p className="text-white font-medium">You've completed all assigned tutorials!</p>
                      <p className="text-zinc-400 text-sm mt-1">{completedTags.length} tutorial{completedTags.length !== 1 ? 's' : ''} completed 🎉</p>
                    </div>
                  ) : (
                    <div className="space-y-3">
                      {availableTutorials.map(tutorial => {
                        const isExpanded = expandedTutorialTag === tutorial.tagId;
                        const isViewed = viewedTags.includes(tutorial.tagId);
                        const isAssigned = assignedTutorials.some(t => t.tagId === tutorial.tagId);
                        const tagDef = TAGS.find(t => t.id === tutorial.tagId);
                        const coupleTriedWithTag = getCoupleTriedWithTag(tutorial.tagId);

                        return (
                          <div
                            key={tutorial.tagId}
                            className={`rounded-xl border transition-all duration-200 ${
                              isExpanded
                                ? 'bg-gradient-to-r from-blue-500/10 to-cyan-500/10 border-blue-500/50'
                                : isAssigned
                                  ? 'bg-gradient-to-r from-purple-500/10 to-pink-500/10 border-purple-500/50'
                                  : isViewed
                                    ? 'bg-zinc-700/30 border-zinc-600'
                                    : 'bg-zinc-700/50 border-amber-500/50'
                            }`}
                          >
                            {/* Header - clickable to expand */}
                            <button
                              onClick={() => handleViewTutorial(tutorial.tagId)}
                              className="w-full p-4 text-left flex items-center justify-between"
                            >
                              <div className="flex items-center gap-3">
                                <span className={`px-3 py-1.5 rounded-lg text-sm font-medium border ${tagDef?.color || 'bg-zinc-600 text-zinc-300'}`}>
                                  {tutorial.tagLabel}
                                </span>
                                {isAssigned && (
                                  <span className="px-2 py-0.5 bg-purple-500/20 text-purple-400 text-xs rounded-full flex items-center gap-1">
                                    ⭐ Assigned
                                  </span>
                                )}
                                {!isViewed && !isAssigned && (
                                  <span className="px-2 py-0.5 bg-amber-500/20 text-amber-400 text-xs rounded-full">
                                    New
                                  </span>
                                )}
                              </div>
                              <ArrowRight className={`w-5 h-5 text-zinc-400 transition-transform ${isExpanded ? 'rotate-90' : ''}`} />
                            </button>

                            {/* Expanded content */}
                            {isExpanded && (
                              <div className="px-4 pb-4 space-y-4">
                                {/* Intro */}
                                <p className="text-zinc-300 text-sm">{tutorial.intro}</p>

                                {/* Tag + Category Combinations */}
                                <div>
                                  <h4 className="text-white font-medium mb-2 flex items-center gap-2">
                                    <Sparkles className="w-4 h-4 text-cyan-400" />
                                    Example Combinations
                                  </h4>
                                  <div className="space-y-2">
                                    {tutorial.combinations.map(combo => {
                                      const category = CATEGORIES.find(c => c.id === combo.categoryId);
                                      const exampleClips = getExampleClips(combo.tagId, combo.categoryId);
                                      return (
                                        <div key={`${combo.tagId}-${combo.categoryId}`} className="bg-zinc-800 rounded-lg p-3">
                                          <div className="flex items-center gap-2 mb-1">
                                            <span className={`px-2 py-0.5 rounded text-xs ${tagDef?.color || 'bg-zinc-600 text-zinc-300'}`}>
                                              {tutorial.tagLabel}
                                            </span>
                                            <span className="text-zinc-500">+</span>
                                            <span className="text-lg">{category?.emoji}</span>
                                            <span className="text-white text-sm">{category?.name}</span>
                                          </div>
                                          <p className="text-zinc-400 text-sm">{combo.description}</p>
                                          {exampleClips.length > 0 && (
                                            <div className="mt-2 flex flex-wrap gap-2">
                                              {exampleClips.map(clip => (
                                                <button
                                                  key={clip.id}
                                                  onClick={() => setWatchingClip(clip)}
                                                  className="text-xs px-2 py-1 bg-zinc-700 text-pink-400 rounded hover:bg-zinc-600 transition-colors"
                                                >
                                                  ▶ {clip.title.slice(0, 20)}...
                                                </button>
                                              ))}
                                            </div>
                                          )}
                                        </div>
                                      );
                                    })}
                                  </div>
                                </div>

                                {/* Pro Tips */}
                                <div>
                                  <h4 className="text-white font-medium mb-2 flex items-center gap-2">
                                    <Lightbulb className="w-4 h-4 text-yellow-400" />
                                    Pro Tips
                                  </h4>
                                  <ul className="space-y-1">
                                    {tutorial.proTips.map((tip, i) => (
                                      <li key={i} className="text-zinc-400 text-sm flex items-start gap-2">
                                        <span className="text-yellow-400">•</span> {tip}
                                      </li>
                                    ))}
                                  </ul>
                                </div>

                                {/* Couple Context (if linked) */}
                                {linkedCoupleData && coupleTriedWithTag.length > 0 && (
                                  <div className="bg-pink-500/10 border border-pink-500/30 rounded-lg p-3">
                                    <h4 className="text-white font-medium mb-2 flex items-center gap-2">
                                      <Heart className="w-4 h-4 text-pink-400" />
                                      With {linkedCoupleData.herName}
                                    </h4>
                                    <p className="text-zinc-300 text-sm">
                                      You've tried <span className="text-pink-400 font-medium">{coupleTriedWithTag.length}</span> {tutorial.tagLabel.toLowerCase()} clips together!
                                    </p>
                                  </div>
                                )}

                                {/* Your Feedback */}
                                <div className="bg-zinc-800 rounded-lg p-3 space-y-3">
                                  <h4 className="text-white font-medium flex items-center gap-2">
                                    <Edit3 className="w-4 h-4 text-lime-400" />
                                    Your Feedback
                                  </h4>

                                  {/* Understanding Rating */}
                                  <div>
                                    <p className="text-zinc-400 text-xs mb-2">How well do you understand this tag?</p>
                                    <div className="flex gap-1">
                                      {[1, 2, 3, 4, 5].map(rating => (
                                        <button
                                          key={rating}
                                          onClick={() => {
                                            const tutorialRecord = myTutorialProgress.find(t => t.tagId === tutorial.tagId);
                                            if (tutorialRecord) {
                                              const updated = tagTutorials.map(t =>
                                                t.id === tutorialRecord.id ? { ...t, understanding: rating as 1|2|3|4|5 } : t
                                              );
                                              saveTagTutorials(updated);
                                            }
                                          }}
                                          className={`px-3 py-1.5 rounded text-sm ${
                                            myTutorialProgress.find(t => t.tagId === tutorial.tagId)?.understanding === rating
                                              ? 'bg-lime-500 text-zinc-900'
                                              : 'bg-zinc-700 text-zinc-400 hover:bg-zinc-600'
                                          }`}
                                        >
                                          {rating}
                                        </button>
                                      ))}
                                    </div>
                                    <p className="text-zinc-500 text-xs mt-1">1 = Still confused, 5 = Crystal clear</p>
                                  </div>

                                  {/* Notes */}
                                  <div>
                                    <p className="text-zinc-400 text-xs mb-2">Notes & considerations about this tag:</p>
                                    <textarea
                                      placeholder="What makes this tag unique? How would you use it? Any thoughts on the combinations..."
                                      className="w-full bg-zinc-700 border border-zinc-600 rounded-lg px-3 py-2 text-white text-sm resize-none focus:outline-none focus:border-lime-500"
                                      rows={3}
                                      defaultValue={myTutorialProgress.find(t => t.tagId === tutorial.tagId)?.notes || ''}
                                      onBlur={(e) => {
                                        const tutorialRecord = myTutorialProgress.find(t => t.tagId === tutorial.tagId);
                                        if (tutorialRecord) {
                                          const updated = tagTutorials.map(t =>
                                            t.id === tutorialRecord.id ? { ...t, notes: e.target.value } : t
                                          );
                                          saveTagTutorials(updated);
                                        }
                                      }}
                                    />
                                  </div>
                                </div>

                                {/* Complete Button */}
                                <button
                                  onClick={() => handleCompleteTutorial(tutorial.tagId)}
                                  className="w-full py-2.5 rounded-xl font-medium text-zinc-900 transition-transform hover:scale-[1.02]"
                                  style={{ backgroundColor: COLORS.lime }}
                                >
                                  <Check className="w-4 h-4 inline mr-2" />
                                  Mark as Complete
                                </button>
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  )}

                  {/* Show completed tutorials summary */}
                  {completedTags.length > 0 && (
                    <div className="mt-4 pt-4 border-t border-zinc-700">
                      <p className="text-zinc-500 text-xs mb-2">Completed:</p>
                      <div className="flex flex-wrap gap-2">
                        {completedTags.map(tagId => {
                          const tagDef = TAGS.find(t => t.id === tagId);
                          const tutorial = getTutorialByTagId(tagId);
                          return (
                            <span key={tagId} className={`px-2 py-1 rounded text-xs ${tagDef?.color || 'bg-zinc-600 text-zinc-300'}`}>
                              ✓ {tutorial?.tagLabel || tagId}
                            </span>
                          );
                        })}
                      </div>
                    </div>
                  )}
                </div>
              );
            })()}

            {/* Managed-couple sections (data loads from managedCoupleId) */}
            {currentReviewer?.managedCoupleId && (
              <>
            <div className="bg-zinc-800 rounded-2xl p-6 border border-zinc-700">
              <h3 className="text-xl font-bold text-white mb-4 flex items-center gap-2">
                <Video className="w-6 h-6" /> Video Submissions
                {(() => {
                  const myPendingCount = coupleSubmissions.filter(s => {
                    const reviewedByMe = currentReviewer && (s.reviewedByIds?.includes(currentReviewer.id) || (!s.reviewedByIds && s.reviewedBy === currentReviewer.id));
                    return !reviewedByMe && (s.status === 'pending' || s.status === 'reviewed');
                  }).length;
                  return myPendingCount > 0 ? (
                    <span className="px-2 py-1 bg-pink-500 text-white text-xs rounded-full">
                      {myPendingCount} pending
                    </span>
                  ) : null;
                })()}
              </h3>

              {coupleSubmissions.length === 0 ? (
                <div className="text-center py-8">
                  <Video className="w-12 h-12 text-zinc-600 mx-auto mb-2" />
                  <p className="text-zinc-400 text-sm">No video submissions yet</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {coupleSubmissions.slice().reverse().map(sub => {
                    const reviewedByMe = currentReviewer && (sub.reviewedByIds?.includes(currentReviewer.id) || (!sub.reviewedByIds && sub.reviewedBy === currentReviewer.id));
                    const myStatus = reviewedByMe ? sub.status : (sub.status === 'reviewed' ? 'pending' : sub.status);
                    return (
                    <div key={sub.id} className={`bg-zinc-700/50 rounded-xl p-4 border ${myStatus === 'pending' ? 'border-amber-500/50' : 'border-zinc-600'}`}>
                      <div className="flex items-start justify-between gap-4">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 mb-1">
                            <span className={`px-2 py-0.5 rounded text-xs font-medium ${
                              myStatus === 'pending' ? 'bg-amber-500/20 text-amber-400' :
                              myStatus === 'approved' ? 'bg-green-500/20 text-green-400' :
                              myStatus === 'reviewed' ? 'bg-blue-500/20 text-blue-400' :
                              'bg-zinc-500/20 text-zinc-400'
                            }`}>
                              {myStatus === 'pending' ? '⏳ Pending' :
                               myStatus === 'approved' ? '✅ Approved' :
                               myStatus === 'reviewed' ? '👀 Reviewed' :
                               '📝 Needs Work'}
                            </span>
                            <span className="text-zinc-500 text-xs">
                              {new Date(sub.createdAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                            </span>
                          </div>
                          <h4 className="text-white font-medium">{sub.title}</h4>
                          {sub.description && <p className="text-zinc-400 text-sm mt-1">{sub.description}</p>}
                          <a
                            href={sub.videoUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-flex items-center gap-1 text-pink-400 text-sm mt-2 hover:text-pink-300"
                          >
                            <Play className="w-4 h-4" /> Watch Video
                          </a>
                        </div>
                        {myStatus === 'pending' && (
                          <button
                            onClick={() => markSubmissionReviewed(sub.id)}
                            className="px-4 py-2 rounded-xl font-medium text-zinc-900"
                            style={{ backgroundColor: COLORS.lime }}
                          >
                            <Check className="w-4 h-4 inline mr-1" /> Mark Reviewed
                          </button>
                        )}
                      </div>
                    </div>
                    );
                  })}
                </div>
              )}
            </div>

            {/* Content Requests Section */}
            <div className="bg-zinc-800 rounded-2xl p-6 border border-zinc-700">
              <h3 className="text-xl font-bold text-white mb-4 flex items-center gap-2">
                <Lightbulb className="w-6 h-6" /> Content Requests
                {coupleRequests.filter(r => r.status === 'pending').length > 0 && (
                  <span className="px-2 py-1 bg-purple-500 text-white text-xs rounded-full">
                    {coupleRequests.filter(r => r.status === 'pending').length} pending
                  </span>
                )}
              </h3>

              {coupleRequests.length === 0 ? (
                <div className="text-center py-8">
                  <Lightbulb className="w-12 h-12 text-zinc-600 mx-auto mb-2" />
                  <p className="text-zinc-400 text-sm">No content requests yet</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {coupleRequests.slice().reverse().map(req => (
                    <div key={req.id} className={`bg-zinc-700/50 rounded-xl p-4 border ${req.status === 'pending' ? 'border-purple-500/50' : 'border-zinc-600'}`}>
                      <div className="flex items-start justify-between gap-4">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 mb-2 flex-wrap">
                            <span className="px-2 py-0.5 rounded text-xs font-medium bg-zinc-600 text-zinc-300">
                              {req.type === 'clip' ? '🎬 Clip Request' : '📁 Category Idea'}
                            </span>
                            {req.categoryName && (
                              <span className="px-2 py-0.5 rounded text-xs font-medium bg-purple-500/30 text-purple-300">
                                "{req.categoryName}"
                              </span>
                            )}
                            <span className={`px-2 py-0.5 rounded text-xs font-medium ${
                              req.status === 'pending' ? 'bg-amber-500/20 text-amber-400' :
                              req.status === 'fulfilled' ? 'bg-green-500/20 text-green-400' :
                              'bg-zinc-500/20 text-zinc-400'
                            }`}>
                              {req.status === 'pending' ? '⏳ Pending' :
                               req.status === 'fulfilled' ? '✅ Responded' :
                               '❌ Declined'}
                            </span>
                            <span className="text-zinc-500 text-xs">
                              {new Date(req.createdAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                            </span>
                          </div>
                          <p className="text-white">{req.description}</p>
                          {req.categoryId && (
                            <p className="text-zinc-400 text-sm mt-1">
                              Related to: {CATEGORIES.find(c => c.id === req.categoryId)?.emoji} {CATEGORIES.find(c => c.id === req.categoryId)?.name}
                            </p>
                          )}
                          {req.whyWanted && (
                            <p className="text-zinc-400 text-sm mt-2 italic">
                              💭 "{req.whyWanted}"
                            </p>
                          )}
                          {/* Example RedGifs Embeds - Multiple URLs */}
                          {(() => {
                            // Combine legacy single URL with new multiple URLs
                            const allUrls = [
                              ...(req.exampleUrl ? [req.exampleUrl] : []),
                              ...(req.exampleUrls || [])
                            ].filter(url => url.trim());

                            if (allUrls.length === 0) return null;

                            const redgifsUrls = allUrls.filter(url => url.includes('redgifs.com'));
                            const otherUrls = allUrls.filter(url => !url.includes('redgifs.com'));

                            return (
                              <div className="mt-3">
                                <p className="text-zinc-500 text-xs mb-2">📎 Example{allUrls.length > 1 ? 's' : ''} they provided ({allUrls.length}):</p>

                                {/* RedGifs embeds */}
                                {redgifsUrls.length > 0 && (
                                  <div className={`grid gap-3 ${redgifsUrls.length > 1 ? 'grid-cols-2' : 'grid-cols-1'}`}>
                                    {redgifsUrls.map((url, idx) => (
                                      <div key={idx} className="bg-zinc-800 rounded-lg overflow-hidden border border-zinc-600">
                                        <VideoPlayer url={url} />
                                        <a
                                          href={url}
                                          target="_blank"
                                          rel="noopener noreferrer"
                                          className="text-blue-400 text-xs hover:underline p-2 block"
                                        >
                                          Open #{idx + 1} ↗
                                        </a>
                                      </div>
                                    ))}
                                  </div>
                                )}

                                {/* Other URLs as links */}
                                {otherUrls.length > 0 && (
                                  <div className="mt-2 space-y-1">
                                    {otherUrls.map((url, idx) => (
                                      <a
                                        key={idx}
                                        href={url}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="text-blue-400 text-sm hover:underline flex items-center gap-1"
                                      >
                                        📎 Link {redgifsUrls.length + idx + 1} ↗
                                      </a>
                                    ))}
                                  </div>
                                )}
                              </div>
                            );
                          })()}
                        </div>
                        {req.status === 'pending' && (
                          <button
                            onClick={() => markRequestFulfilled(req.id)}
                            className="px-4 py-2 rounded-xl font-medium text-white bg-purple-500 hover:bg-purple-600 flex-shrink-0"
                          >
                            <Check className="w-4 h-4 inline mr-1" /> Mark Done
                          </button>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
              </>
            )}

            {/* Your Profile Settings */}
            <div className="bg-zinc-800 rounded-2xl p-6 border border-zinc-700">
              <h3 className="text-xl font-bold text-white mb-4 flex items-center gap-2">
                👤 Your Profile
              </h3>
              <div className="space-y-4">
                <div>
                  <label className="text-zinc-400 text-sm block mb-2">
                    Display Name <span className="text-zinc-500">(shown to couples you manage)</span>
                  </label>
                  <div className="flex gap-2">
                    <input
                      type="text"
                      placeholder="Enter your name..."
                      defaultValue={currentReviewer?.name || ''}
                      id="reviewer-display-name"
                      className="flex-1 bg-zinc-700 border border-zinc-600 rounded-lg px-4 py-2 text-white"
                    />
                    <button
                      onClick={async () => {
                        const input = document.getElementById('reviewer-display-name') as HTMLInputElement;
                        const newName = input?.value?.trim();
                        if (currentReviewer && newName !== currentReviewer.name) {
                          const updated = { ...currentReviewer, name: newName || undefined };
                          const allUpdated = reviewers.map(r => r.id === currentReviewer.id ? updated : r);
                          await saveReviewers(allUpdated);
                          setCurrentReviewer(updated);
                          // Also update any couple we're managing
                          if (managedCoupleData && managedCoupleData.managedByReviewerId === currentReviewer.id) {
                            const updatedCouple = { ...managedCoupleData, managedByReviewerName: newName || `Reviewer ${currentReviewer.id.slice(-4)}` };
                            await saveCoupleByCode(updatedCouple);
                            setManagedCoupleData(updatedCouple);
                          }
                        }
                      }}
                      className="px-4 py-2 rounded-lg font-medium text-zinc-900"
                      style={{ backgroundColor: COLORS.lime }}
                    >
                      Save
                    </button>
                  </div>
                  {currentReviewer?.name && (
                    <p className="text-green-400 text-xs mt-2">
                      ✓ Couples will see you as "{currentReviewer.name}"
                    </p>
                  )}
                </div>
                <div className="text-zinc-500 text-xs border-t border-zinc-700 pt-4">
                  <p>Your code: <span className="text-zinc-400 font-mono">{currentReviewer?.code}</span></p>
                  {currentReviewer?.code && <p><CopyLinkButton url={reviewerLoginLink(currentReviewer.code)} label="Copy my login link" /></p>}
                  <p>Reviewer ID: <span className="text-zinc-400 font-mono">{currentReviewer?.id}</span></p>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Creator Submissions Tab */}
        {tab === 'submissions' && (() => {
          const queue = submittedClips.filter(needsMySubmissionAction);
          const reviewedByMe = submittedClips.filter(s => hasMySubmissionReview(s) && !needsMySubmissionAction(s));
          const blindCount = submittedClips.filter(needsMySubmissionReview).length;
          const reflectCount = submittedClips.filter(needsMySubmissionReflection).length;
          const myReviewCount = submittedClips.filter(hasMySubmissionReview).length;
          const submittedAt = (s: SubmittedClip) => new Date(s.dateSubmitted || 0).getTime() || 0;
          // Your turn: quick reflections first, then clips with the fewest eyes on them, oldest first
          const listed = submissionStatusFilter === 'needs-my-review'
            ? [...queue].sort((a, b) =>
                Number(needsMySubmissionReflection(b)) - Number(needsMySubmissionReflection(a))
                || getSubmissionReviews(a).length - getSubmissionReviews(b).length
                || submittedAt(a) - submittedAt(b))
            : submissionStatusFilter === 'reviewed'
              ? [...reviewedByMe].sort((a, b) => submittedAt(b) - submittedAt(a))
              : [...submittedClips].sort((a, b) => submittedAt(b) - submittedAt(a));
          const firstInQueue = [...queue].sort((a, b) => getSubmissionReviews(a).length - getSubmissionReviews(b).length || submittedAt(a) - submittedAt(b))[0];
          const summary = queue.length === 0
            ? 'You’re all caught up.'
            : [blindCount > 0 && `${blindCount} to review`, reflectCount > 0 && `${reflectCount} reflection${reflectCount === 1 ? '' : 's'} due`].filter(Boolean).join(' · ');
          const dismissWelcome = () => {
            setSubmissionWelcomeDismissed(true);
            try { localStorage.setItem('vb-submissions-welcome-dismissed', '1'); } catch { /* storage unavailable */ }
          };
          const segments = [
            { id: 'needs-my-review' as const, label: 'Your turn', count: queue.length },
            { id: 'reviewed' as const, label: 'Reviewed', count: reviewedByMe.length },
            { id: 'all' as const, label: 'All', count: submittedClips.length },
          ];

          return (
          <div className="space-y-4">
            <div>
              <h3 className="font-display text-3xl font-medium text-white">Submissions</h3>
              <p className="text-sm text-zinc-400">{summary}</p>
            </div>

            {/* First-run guide for reviewers who haven't reviewed a submission yet */}
            {myReviewCount === 0 && !submissionWelcomeDismissed && submittedClips.length > 0 && (
              <section className="relative bg-zinc-800 border border-lime-500/30 rounded-2xl p-5">
                <button onClick={dismissWelcome} className="absolute top-3 right-3 p-1.5 text-zinc-500 hover:text-white" aria-label="Hide this guide">
                  <X className="w-4 h-4" />
                </button>
                <p className="text-xs font-bold uppercase tracking-wider text-lime-400">New here? Start here</p>
                <h4 className="font-display text-2xl text-white mt-1">How reviewing works</h4>
                <ol className="mt-3 space-y-2.5">
                  {[
                    ['Watch the clip', 'Open it from the list below and watch it all the way through.'],
                    ['Tick the three checks', 'Is it on brand, does it show the product, and does it avoid health claims?'],
                    ['Give your honest take and a verdict', 'Approve, needs revisions or reject. The admin makes the final call.'],
                  ].map(([title, body], i) => (
                    <li key={title} className="flex gap-3">
                      <span className="w-6 h-6 shrink-0 rounded-full bg-lime-500/20 text-lime-300 text-xs font-bold flex items-center justify-center">{i + 1}</span>
                      <span className="text-sm"><span className="text-white font-semibold">{title}.</span> <span className="text-zinc-400">{body}</span></span>
                    </li>
                  ))}
                </ol>
                <p className="text-xs text-zinc-500 mt-3">Reviews are blind: you’ll see what others said once you’ve submitted yours.</p>
                {firstInQueue && (
                  <button
                    onClick={() => setReviewingSubmission(firstInQueue)}
                    className="mt-4 w-full sm:w-auto px-5 py-2.5 rounded-xl font-semibold text-zinc-900"
                    style={{ backgroundColor: COLORS.lime }}
                  >
                    Start my first review
                  </button>
                )}
              </section>
            )}

            {myReviewCount > 0 && myReviewCount < 3 && (
              <div className="bg-zinc-800/70 border border-zinc-700 rounded-xl px-4 py-3">
                <div className="flex justify-between text-xs text-zinc-400 mb-1.5">
                  <span>Getting started</span>
                  <span>{myReviewCount} of 3 reviews done</span>
                </div>
                <div className="h-1.5 rounded-full bg-zinc-700 overflow-hidden">
                  <div className="h-full rounded-full bg-lime-400" style={{ width: `${(myReviewCount / 3) * 100}%` }} />
                </div>
              </div>
            )}

            <div role="group" aria-label="Show submissions" className="grid grid-cols-3 gap-1 p-1 bg-zinc-800 border border-zinc-700 rounded-xl">
              {segments.map(seg => (
                <button
                  key={seg.id}
                  onClick={() => setSubmissionStatusFilter(seg.id)}
                  aria-pressed={submissionStatusFilter === seg.id}
                  className={`py-2 rounded-lg text-sm font-semibold ${submissionStatusFilter === seg.id ? 'bg-zinc-700 text-white' : 'text-zinc-400 hover:text-white'}`}
                >
                  {seg.label} <span className={seg.id === 'needs-my-review' && seg.count > 0 ? 'text-lime-400' : 'text-zinc-500'}>{seg.count}</span>
                </button>
              ))}
            </div>

            {submissionReviewNotice && (
              <div className="bg-lime-500/10 border border-lime-500/30 rounded-xl p-4 mb-4">
                <div className="flex items-start justify-between gap-4">
                  <div>
                    <h4 className="text-lime-300 font-semibold mb-1 flex items-center gap-2">
                      <Check className="w-4 h-4" />
                      {submissionReviewNotice.type === 'reflection'
                        ? 'Reflection saved'
                        : submissionReviewNotice.requiresReflection
                          ? 'Review saved - follow-up needed'
                          : 'Review saved'
                      }
                    </h4>
                    <p className="text-zinc-300 text-sm">
                      {submissionReviewNotice.clipTitle}
                      {submissionReviewNotice.requiresReflection ? (
                        <span className="text-zinc-400">
                          {' '}has {submissionReviewNotice.otherReviewCount} other review{submissionReviewNotice.otherReviewCount !== 1 ? 's' : ''} unlocked. Read them and save whether they changed your thoughts.
                        </span>
                      ) : submissionReviewNotice.type === 'review' && submissionReviewNotice.otherReviewCount > 0 && (
                        <span className="text-zinc-400">
                          {' '}now has {submissionReviewNotice.otherReviewCount} other review{submissionReviewNotice.otherReviewCount !== 1 ? 's' : ''} unlocked.
                        </span>
                      )}
                    </p>
                  </div>
                  <div className="flex items-center gap-2 flex-shrink-0">
                    <button
                      onClick={() => {
                        setSubmissionStatusFilter(submissionReviewNotice.requiresReflection ? 'needs-my-review' : 'reviewed');
                        if (submissionReviewNotice.requiresReflection) {
                          const clip = submittedClips.find(s => s.id === submissionReviewNotice.clipId);
                          if (clip) setReviewingSubmission(clip);
                        }
                      }}
                      className="px-3 py-1.5 rounded-lg bg-zinc-800 text-zinc-200 hover:bg-zinc-700 text-xs border border-zinc-700"
                    >
                      {submissionReviewNotice.requiresReflection ? 'Complete reflection' : 'Show my reviews'}
                    </button>
                    <button
                      onClick={() => setSubmissionReviewNotice(null)}
                      className="text-zinc-400 hover:text-white"
                      aria-label="Dismiss review notice"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              </div>
            )}

            {/* Content Request Signals - aggregate wantMore/maleSolo from all reviews */}
            {(() => {
              if (queue.length > 0) return null;
              const wantMoreCount = submittedClips.reduce((acc, clip) => {
                const revs = getSubmissionReviews(clip);
                return acc + revs.filter(r => r.wantMoreFromCreator === 'yes').length;
              }, 0);
              const maleSoloCount = submittedClips.reduce((acc, clip) => {
                const revs = getSubmissionReviews(clip);
                return acc + revs.filter(r => r.wouldWatchMaleSolo === 'yes').length;
              }, 0);

              // Find couple follow-up responses with shared links (clip requests answered)
              const clipRequestResponses = coupleFollowUps.filter(fu =>
                fu.context === 'Reviewer clip request' && fu.status === 'answered' && fu.sharedLinks && fu.sharedLinks.length > 0
              );

              if (wantMoreCount === 0 && maleSoloCount === 0 && clipRequestResponses.length === 0) return null;

              return (
                <div className="bg-gradient-to-r from-pink-500/10 to-purple-500/10 border border-pink-500/30 rounded-xl p-4 mb-4">
                  <h4 className="text-white font-semibold mb-2 flex items-center gap-2">
                    <Heart className="w-4 h-4 text-pink-400" /> Content Requests
                  </h4>

                  {(wantMoreCount > 0 || maleSoloCount > 0) && (
                    <div className="flex flex-wrap gap-3 mb-3">
                      {wantMoreCount > 0 && (
                        <div className="flex items-center gap-2 bg-pink-500/20 rounded-lg px-3 py-2">
                          <Film className="w-4 h-4 text-pink-400" />
                          <span className="text-pink-300 text-sm font-medium">{wantMoreCount} reviewer{wantMoreCount !== 1 ? 's' : ''} want more couple clips</span>
                        </div>
                      )}
                      {maleSoloCount > 0 && (
                        <div className="flex items-center gap-2 bg-purple-500/20 rounded-lg px-3 py-2">
                          <User className="w-4 h-4 text-purple-400" />
                          <span className="text-purple-300 text-sm font-medium">{maleSoloCount} reviewer{maleSoloCount !== 1 ? 's' : ''} want solo male clips</span>
                        </div>
                      )}
                    </div>
                  )}

                  {/* Show shared clips from couple responses */}
                  {clipRequestResponses.length > 0 && (
                    <div className="space-y-3">
                      <p className="text-lime-400 text-sm font-medium">Shared by couple:</p>
                      {clipRequestResponses.map(fu => (
                        <div key={fu.id} className="space-y-2">
                          {fu.answer && fu.answer !== `Shared ${fu.sharedLinks?.length} link${(fu.sharedLinks?.length || 0) !== 1 ? 's' : ''}` && (
                            <p className="text-zinc-300 text-sm italic">"{fu.answer}"</p>
                          )}
                          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                            {fu.sharedLinks!.map((link, i) => (
                              <div key={i} className="bg-zinc-800 rounded-lg overflow-hidden border border-zinc-700">
                                <VideoPlayer url={link.url} />
                                <div className="p-2">
                                  <p className="text-zinc-300 text-xs truncate">{link.label || link.url}</p>
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              );
            })()}

            {submittedClips.length === 0 ? (
              <EmptyState
                icon={<Video className="w-8 h-8" />}
                title="No submissions yet"
                description="Creator clip submissions will appear here for review"
              />
            ) : listed.length === 0 ? (
              <div className="text-center py-10 bg-zinc-800/50 border border-zinc-700 rounded-2xl">
                <p className="text-white font-semibold">
                  {submissionStatusFilter === 'needs-my-review' ? 'You’re all caught up' : myReviewCount > 0 ? 'Nothing finished yet' : 'You haven’t reviewed any submissions yet'}
                </p>
                <p className="text-sm text-zinc-400 mt-1">
                  {submissionStatusFilter === 'needs-my-review' ? 'New submissions will show up here.' : myReviewCount > 0 ? 'Your reviews are waiting on a verdict or reflection in Your turn.' : 'Start with the ones waiting on you.'}
                </p>
                <button
                  onClick={() => setSubmissionStatusFilter(submissionStatusFilter === 'needs-my-review' ? 'reviewed' : 'needs-my-review')}
                  className="mt-3 text-sm font-semibold text-lime-400 hover:underline"
                >
                  {submissionStatusFilter === 'needs-my-review' ? 'See what others said on yours' : 'Go to your turn'}
                </button>
              </div>
            ) : (
              <div className="space-y-3">
                {listed.map(clip => {
                    const myReview = getMySubmissionReview(clip);
                    const hasMyReview = !!myReview;
                    const needsBlindReview = needsMySubmissionReview(clip);
                    const needsReflection = needsMySubmissionReflection(clip);
                    const needsMyReview = needsMySubmissionAction(clip);
                    const others = getOtherSubmissionReviews(clip);
                    const otherTally = verdictTally(others);
                    const othersText = others.length === 0
                      ? 'No one else has reviewed this yet'
                      : hasMyReview
                        ? `${others.length} other review${others.length === 1 ? '' : 's'}`
                        : `${others.length} other review${others.length === 1 ? '' : 's'} · hidden until you submit yours`;
                    return (
                      <article
                        key={clip.id}
                        className={`rounded-2xl border overflow-hidden ${needsBlindReview ? 'bg-zinc-800 border-lime-500/40' : needsReflection ? 'bg-zinc-800 border-amber-500/40' : 'bg-zinc-800/70 border-zinc-700'}`}
                      >
                        <div className="px-4 pt-4 empty:hidden">
                        {/* Managed couple engagement — AT THE TOP */}
                        {hasMyReview && clip.creatorId === currentReviewer?.managedCoupleId && (() => {
                          const allRevs = getSubmissionReviews(clip);
                          const avgQuality = allRevs.length > 0 ? Math.round(allRevs.reduce((s, r) => s + (r.qualityRating || 0), 0) / allRevs.length * 10) / 10 : 0;
                          const wantMore = allRevs.filter(r => r.wantMoreFromCreator === 'yes').length;

                          // Build prompted messages from each reviewer's feedback
                          const promptedMessages: { label: string; message: string }[] = [];
                          allRevs.forEach(r => {
                            if (r.howItMadeThemFeel && r.howItMadeThemFeel.length > 10) {
                              promptedMessages.push({
                                label: `#${r.reviewerId.replace('Reviewer-', '')} felt: "${r.howItMadeThemFeel.substring(0, 60)}..."`,
                                message: `A reviewer said about "${clip.clipTitle}": "${r.howItMadeThemFeel}"`,
                              });
                            }
                            if (r.intrigues && r.intrigues.length > 10) {
                              promptedMessages.push({
                                label: `#${r.reviewerId.replace('Reviewer-', '')} intrigued by: "${r.intrigues.substring(0, 60)}..."`,
                                message: `A reviewer was intrigued by your "${clip.clipTitle}": "${r.intrigues}"`,
                              });
                            }
                            if (r.notes && r.notes.length > 10) {
                              promptedMessages.push({
                                label: `#${r.reviewerId.replace('Reviewer-', '')} notes: "${r.notes.substring(0, 60)}..."`,
                                message: `Reviewer feedback on "${clip.clipTitle}": "${r.notes}"`,
                              });
                            }
                            if (r.maleAppearance) {
                              const sizeNote = `Size: ${r.maleAppearance.cockSize}, Hardness: ${r.maleAppearance.hardness}${r.maleAppearance.notes ? ` — "${r.maleAppearance.notes}"` : ''}`;
                              promptedMessages.push({
                                label: `#${r.reviewerId.replace('Reviewer-', '')} on his size: ${r.maleAppearance.cockSize} / ${r.maleAppearance.hardness}`,
                                message: `A reviewer assessed "${clip.clipTitle}" — ${sizeNote}. They loved what they saw!`,
                              });
                            }
                            if (r.loadBoostProfile) {
                              promptedMessages.push({
                                label: `#${r.reviewerId.replace('Reviewer-', '')} on load: ${r.loadBoostProfile.consistencyType} / ${r.loadBoostProfile.finishLocation}`,
                                message: `Load feedback on "${clip.clipTitle}": ${r.loadBoostProfile.consistencyType} consistency, finished on ${r.loadBoostProfile.finishLocation}${r.loadBoostProfile.standoutNotes ? `. "${r.loadBoostProfile.standoutNotes}"` : ''}`,
                              });
                            }
                          });

                          return (
                            <div className="mb-3 bg-gradient-to-r from-purple-500/10 to-pink-500/10 border border-purple-500/20 rounded-xl p-3">
                              <div className="flex items-center gap-2 mb-2">
                                <span className="text-purple-400 text-xs font-semibold">Your Managed Couple's Clip</span>
                                <span className="px-2 py-0.5 bg-purple-500/20 text-purple-300 rounded text-xs">{allRevs.length} review{allRevs.length !== 1 ? 's' : ''}</span>
                                {avgQuality > 0 && <span className="px-2 py-0.5 bg-yellow-500/20 text-yellow-400 rounded text-xs">{'★'.repeat(Math.round(avgQuality))} {avgQuality}/5</span>}
                                {wantMore > 0 && <span className="px-2 py-0.5 bg-pink-500/20 text-pink-400 rounded text-xs">🔥 {wantMore} want more</span>}
                                {clip.maleAppearance && <span className="px-2 py-0.5 bg-blue-500/20 text-blue-400 rounded text-xs">Size: {clip.maleAppearance.cockSize} · {clip.maleAppearance.hardness}</span>}
                              </div>

                              {/* Prompted messages to share with couple */}
                              {promptedMessages.length > 0 && (
                                <div className="space-y-1 mb-2">
                                  <p className="text-xs text-zinc-500">Share feedback with your couple (click to edit before sending):</p>
                                  {promptedMessages.slice(0, 6).map((pm, i) => (
                                    <button
                                      key={i}
                                      onClick={async () => {
                                        const edited = prompt('Edit message before sending:', pm.message);
                                        if (edited && currentReviewer) {
                                          await d1Upsert('couple_messages', {
                                            id: `msg-${Date.now()}`,
                                            coupleId: currentReviewer.managedCoupleId,
                                            fromType: 'reviewer',
                                            fromName: currentReviewer.partnerName || currentReviewer.code,
                                            message: edited,
                                            createdAt: new Date().toISOString(),
                                            seenByCouple: false,
                                            seenByReviewer: true,
                                          });
                                          alert('Sent to couple in chat!');
                                        }
                                      }}
                                      className="w-full text-left px-2 py-1.5 bg-zinc-800/80 hover:bg-zinc-700 rounded text-xs text-zinc-300 border border-zinc-700/50 transition-colors"
                                    >
                                      💬 {pm.label}
                                    </button>
                                  ))}
                                </div>
                              )}

                              <div className="flex gap-2">
                                <button
                                  onClick={() => {
                                    const userQuestion = prompt(`Ask ${managedCoupleData?.herName || 'the couple'} about "${clip.clipTitle}":`);
                                    if (userQuestion && currentReviewer) {
                                      const question: CoupleQuestion = {
                                        id: `cq-${Date.now()}`,
                                        coupleId: currentReviewer.managedCoupleId!,
                                        fromReviewerId: currentReviewer.id,
                                        fromReviewerName: currentReviewer.partnerName || currentReviewer.code,
                                        question: userQuestion,
                                        category: 'activity' as const,
                                        context: `Discussing submitted clip: ${clip.clipTitle}`,
                                        sentAt: new Date().toISOString(),
                                        status: 'pending' as const,
                                        clipId: clip.id,
                                      };
                                      d1Upsert('couple_questions', question);
                                      saveCoupleQuestions([...coupleQuestions, question]);
                                    }
                                  }}
                                  className="px-2 py-1 bg-zinc-700 hover:bg-zinc-600 text-zinc-300 rounded text-xs"
                                >
                                  💭 Ask About This
                                </button>
                              </div>
                            </div>
                          );
                        })()}
                        </div>

                        <div className="flex flex-col sm:flex-row gap-4 p-4">
                          <button onClick={() => setReviewingSubmission(clip)} className="shrink-0 self-stretch sm:self-start" aria-label={`Open ${clip.clipTitle}`}>
                            <ClipThumb url={clip.contentLink} className="w-full sm:w-28 h-44 sm:h-28" showPlay />
                          </button>
                          <div className="flex-1 min-w-0 space-y-1.5">
                            <div className="flex flex-wrap items-center gap-2">
                              {needsBlindReview && <span className="px-2 py-0.5 rounded-full bg-lime-500/15 border border-lime-500/30 text-lime-300 text-xs font-semibold">Your turn</span>}
                              {needsReflection && <span className="px-2 py-0.5 rounded-full bg-amber-500/15 border border-amber-500/30 text-amber-300 text-xs font-semibold">Reflection due</span>}
                              {myReview?.verdict && !needsReflection && <span className="px-2 py-0.5 rounded-full bg-zinc-700 text-zinc-300 text-xs font-semibold flex items-center gap-1"><Check className="w-3 h-3" /> You reviewed</span>}
                              <span className={`px-2 py-0.5 rounded-full border text-xs font-semibold ${STATUS_CHIPS[clip.status]}`}>
                                {isDecided(clip) ? clip.status : 'Awaiting decision'}
                              </span>
                              {clip.promotedToClipId && <span className="px-2 py-0.5 rounded-full bg-lime-500/15 text-lime-300 text-xs font-semibold">In library</span>}
                            </div>
                            <h4 className="text-lg font-semibold text-white leading-snug">{clip.clipTitle}</h4>
                            <p className="text-sm text-zinc-400">
                              {[clip.product, genderLabel(clip.gender), `submitted ${ageLabel(clip.dateSubmitted)}`].filter(Boolean).join(' · ')}
                            </p>
                            <p className="text-xs text-zinc-500">{othersText}</p>
                            {myReview && (
                              <div className="flex flex-wrap items-center gap-2 text-xs">
                                <span className="text-zinc-500">You said:</span>
                                {myReview.verdict
                                  ? <span className={`px-2 py-0.5 rounded-full border font-semibold ${VERDICTS[myReview.verdict].chip}`}>{VERDICTS[myReview.verdict].label}</span>
                                  : <span className="text-zinc-400">no verdict yet</span>}
                                {myReview.qualityRating > 0 && <span className="text-yellow-400">{'★'.repeat(myReview.qualityRating)}</span>}
                              </div>
                            )}
                          </div>
                          <div className="sm:self-center">
                            <button
                              onClick={() => setReviewingSubmission(clip)}
                              className={`w-full sm:w-auto px-4 py-2.5 rounded-xl font-semibold text-sm whitespace-nowrap ${needsMyReview ? 'text-zinc-900' : 'bg-zinc-700 text-white hover:bg-zinc-600'}`}
                              style={needsMyReview ? { backgroundColor: COLORS.lime } : {}}
                            >
                              {needsReflection ? 'Read & reflect' : hasMyReview ? (myReview?.verdict ? 'Edit my review' : 'Add your verdict') : 'Start review'}
                            </button>
                          </div>
                        </div>

                        {hasMyReview && others.length > 0 && (
                          <details className="border-t border-zinc-700/70 px-4 py-3">
                            <summary className="cursor-pointer text-sm text-zinc-300 flex flex-wrap items-center gap-2">
                              What others said
                              {(['approve', 'revise', 'reject'] as const).map(v => otherTally[v] > 0 && (
                                <span key={v} className={`px-2 py-0.5 rounded-full border text-xs font-semibold ${VERDICTS[v].chip}`}>{otherTally[v]} {VERDICTS[v].short.toLowerCase()}</span>
                              ))}
                            </summary>
                            <div className="mt-3 space-y-2">
                              {others.map((rev, idx) => (
                                <div key={`${rev.reviewerId}-${idx}`} className="bg-zinc-900 rounded-lg p-3">
                                  <div className="flex flex-wrap items-center gap-2 mb-1">
                                    <span className="text-xs text-zinc-400">{reviewerLabel(reviewers, rev.reviewerId)}</span>
                                    {rev.verdict && <span className={`px-2 py-0.5 rounded-full border text-xs font-semibold ${VERDICTS[rev.verdict].chip}`}>{VERDICTS[rev.verdict].label}</span>}
                                    {rev.qualityRating > 0 && <span className="text-xs text-yellow-400">{'★'.repeat(rev.qualityRating)}</span>}
                                  </div>
                                  {rev.notes && <p className="text-zinc-300 text-sm">“{rev.notes}”</p>}
                                </div>
                              ))}
                            </div>
                          </details>
                        )}
                      </article>
                    );
                  })}
              </div>
            )}
          </div>
          );
        })()}

        {/* Review Queue Tab */}
        {tab === 'datenite' && (() => {
          // Calculate review progress per category
          const categoryProgress = CATEGORIES.map(cat => {
            const catClips = dateNiteClips.filter(c => c.categoryId === cat.id);
            const reviewed = catClips.filter(c => c.reviews?.some(r => isMyReview(r.reviewerId))).length;
            return { ...cat, total: catClips.length, reviewed };
          }).filter(c => c.total > 0);

          const totalReviewed = categoryProgress.reduce((sum, c) => sum + c.reviewed, 0);
          const totalClips = categoryProgress.reduce((sum, c) => sum + c.total, 0);
          const totalRemaining = totalClips - totalReviewed;
          // Must-try clips: picked by others but not yet reviewed by this reviewer
          const mustTryClips = dateNiteClips.filter(c =>
            (c.picks?.length || 0) > 0 &&
            !c.reviews?.some(r => isMyReview(r.reviewerId))
          );

          // Generate fake reviewer activity based on clip ID hash for consistency
          const getFakeReviewerCount = (clipId: string) => {
            const hash = clipId.split('').reduce((a, b) => { a = ((a << 5) - a) + b.charCodeAt(0); return a & a; }, 0);
            return Math.abs(hash % 47) + 8; // 8-54 "reviewers"
          };

          const getClipStatus = (clip: Clip) => {
            const fakeCount = getFakeReviewerCount(clip.id);
            const hasReviewed = clip.reviews?.some(r => isMyReview(r.reviewerId));
            const isMustTry = (clip.picks?.length || 0) > 0 && !hasReviewed;
            if (hasReviewed) return { label: '✅ Reviewed', color: 'bg-green-500/20 text-green-400 border-green-500/50', reviewed: true, mustTry: false };
            if (isMustTry) return { label: '⭐ Must Try', color: 'bg-yellow-500/20 text-yellow-400 border-yellow-500/50', reviewed: false, mustTry: true };
            if (fakeCount < 15) return { label: 'New!', color: 'bg-orange-500/20 text-orange-400 border-orange-500/50', reviewed: false, mustTry: false };
            if (fakeCount > 35) return { label: 'Popular', color: 'bg-purple-500/20 text-purple-400 border-purple-500/50', reviewed: false, mustTry: false };
            return { label: 'Needs Review', color: 'bg-blue-500/20 text-blue-400 border-blue-500/50', reviewed: false, mustTry: false };
          };

          return (
          <div>
            {/* Review Progress Header */}
            <div className="bg-gradient-to-r from-lime-500/20 to-emerald-500/20 border border-lime-500/30 rounded-xl p-4 mb-4">
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 rounded-full bg-lime-500/20 flex items-center justify-center">
                    <ClipboardList className="w-6 h-6" style={{ color: COLORS.lime }} />
                  </div>
                  <div>
                    <h3 className="font-bold text-white">Your Review Queue</h3>
                    <div className="flex items-center gap-3 text-sm">
                      <span className="text-green-400">✅ {totalReviewed} reviewed</span>
                      <span className="text-zinc-400">·</span>
                      <span className="text-amber-400">⏳ {totalRemaining} remaining</span>
                      {mustTryClips.length > 0 && (
                        <>
                          <span className="text-zinc-400">·</span>
                          <span className="text-yellow-400">⭐ {mustTryClips.length} must-try</span>
                        </>
                      )}
                    </div>
                  </div>
                </div>
                <div className="flex items-center gap-4">
                  <button
                    onClick={() => {
                      setShowAddClipModal(true);
                      setEditingClipData({ title: '', description: '', tags: [], categoryIds: [], link: '', reason: '' });
                      setNewClipUrlError(null);
                    }}
                    className="flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium text-zinc-900 hover:opacity-90 transition-all"
                    style={{ backgroundColor: COLORS.lime }}
                  >
                    <Plus className="w-4 h-4" /> Add Clip
                  </button>
                  <div className="text-right">
                    <p className="text-2xl font-bold" style={{ color: COLORS.lime }}>{reviewers.length + 127}+</p>
                    <p className="text-xs text-zinc-500">reviewers active</p>
                  </div>
                </div>
              </div>

              {/* Category progress pills */}
              <div className="flex flex-wrap gap-2">
                {categoryProgress.slice(0, 8).map(cat => (
                  <button
                    key={cat.id}
                    onClick={() => setCategoryFilter(cat.id)}
                    className={`px-3 py-1.5 rounded-full text-xs font-medium flex items-center gap-1.5 border transition-all ${
                      categoryFilter === cat.id
                        ? 'bg-lime-500/30 text-lime-400 border-lime-500'
                        : cat.reviewed === cat.total
                          ? 'bg-green-500/20 text-green-400 border-green-500/40'
                          : cat.reviewed > 0
                            ? 'bg-green-500/10 text-green-400 border-green-500/30'
                            : 'bg-zinc-700 text-zinc-400 border-zinc-600 hover:border-zinc-500'
                    }`}
                  >
                    <span>{cat.emoji}</span>
                    <span>{cat.reviewed}/{cat.total}</span>
                    {cat.reviewed > 0 && <Check className="w-3 h-3" />}
                  </button>
                ))}
                {categoryFilter !== 'all' && (
                  <button
                    onClick={() => setCategoryFilter('all')}
                    className="px-3 py-1.5 rounded-full text-xs font-medium text-zinc-400 hover:text-white"
                  >
                    Show All
                  </button>
                )}
              </div>

              {/* Categories to Explore Nudge */}
              {(() => {
                const viewedClipIds = clipViews.filter(v => v.viewerId === currentReviewer.id).map(v => v.clipId);
                const viewedCategories = new Set(
                  dateNiteClips
                    .filter(c => viewedClipIds.includes(c.id))
                    .map(c => c.categoryId)
                );
                const unexploredCategories = CATEGORIES.filter(cat =>
                  !viewedCategories.has(cat.id) && dateNiteClips.some(c => c.categoryId === cat.id)
                );
                if (unexploredCategories.length > 0 && unexploredCategories.length <= 5) {
                  return (
                    <div className="mt-3 pt-3 border-t border-lime-500/20">
                      <p className="text-zinc-400 text-xs mb-2 flex items-center gap-1">
                        <span>💡</span> Try exploring: {unexploredCategories.map(cat => (
                          <button
                            key={cat.id}
                            onClick={() => setCategoryFilter(cat.id)}
                            className="ml-1 px-2 py-0.5 bg-amber-500/20 text-amber-400 rounded-full text-xs hover:bg-amber-500/30 transition-colors"
                          >
                            {cat.emoji} {cat.name}
                          </button>
                        ))}
                      </p>
                    </div>
                  );
                }
                return null;
              })()}
            </div>

            {/* Tag Filters - only show tags that have clips */}
            {usedTags.length > 0 && (
              <div className="bg-zinc-800 rounded-xl p-3 mb-4 border border-zinc-700">
                <div className="flex flex-wrap gap-1">
                  {usedTags.map(tag => (
                    <TagBadge
                      key={tag.id}
                      tag={tag.id}
                      selected={tagFilter.includes(tag.id)}
                      onClick={() => setTagFilter(prev => prev.includes(tag.id) ? prev.filter(t => t !== tag.id) : [...prev, tag.id])}
                      small
                    />
                  ))}
                </div>
              </div>
            )}

            {/* Hide Reviewed Toggle */}
            <div className="flex items-center justify-between bg-zinc-800 rounded-xl p-3 mb-4 border border-zinc-700">
              <div className="flex items-center gap-2">
                <Eye className="w-4 h-4 text-zinc-400" />
                <span className="text-zinc-300 text-sm">Hide clips I've reviewed</span>
              </div>
              <button
                onClick={() => setHideReviewed(!hideReviewed)}
                className={`relative w-12 h-6 rounded-full transition-colors ${hideReviewed ? 'bg-lime-500' : 'bg-zinc-600'}`}
              >
                <span className={`absolute top-1 w-4 h-4 rounded-full bg-white transition-transform ${hideReviewed ? 'left-7' : 'left-1'}`} />
              </button>
            </div>

            {/* Clips Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {filteredDateNiteClips.map(clip => {
                const cat = CATEGORIES.find(c => c.id === clip.categoryId);
                const avgRating = getAverageRating(clip);
                const myReview = clip.reviews?.find(r => isMyReview(r.reviewerId));
                const hasReviewed = !!myReview;
                const fakeReviewers = getFakeReviewerCount(clip.id);
                const status = getClipStatus(clip);

                // Review completeness: check which optional fields were filled
                const isFemCat = FEMALE_CATEGORY_IDS.includes(clip.categoryId);
                const hasComment = !!myReview?.comment?.trim();
                const hasMetrics = isFemCat
                  ? (myReview?.wetnessLevel !== undefined && myReview.wetnessLevel !== 3) || (myReview?.squirtLevel !== undefined && myReview.squirtLevel !== 0)
                  : (myReview?.cumLevel !== undefined && myReview.cumLevel !== 3) || (myReview?.cumConsistency !== undefined && myReview.cumConsistency !== 3);
                const isFullReview = hasComment && hasMetrics;

                return (
                  <div
                    key={clip.id}
                    className={`bg-zinc-800 rounded-xl border overflow-hidden transition-all relative ${
                      hasReviewed
                        ? 'border-green-500/30 opacity-70'
                        : status.mustTry
                          ? 'border-yellow-500/40 ring-1 ring-yellow-500/20'
                          : 'border-zinc-700 hover:border-zinc-600'
                    }`}
                  >
                    {/* Review status overlay indicator */}
                    {hasReviewed && (
                      <div className="absolute top-3 right-3 z-10 w-7 h-7 rounded-full bg-green-500/90 flex items-center justify-center shadow-lg">
                        <Check className="w-4 h-4 text-white" />
                      </div>
                    )}
                    {status.mustTry && (
                      <div className="absolute top-3 right-3 z-10 w-7 h-7 rounded-full bg-yellow-500/90 flex items-center justify-center shadow-lg">
                        <Star className="w-4 h-4 text-white" />
                      </div>
                    )}
                    <div className={`bg-gradient-to-r ${cat?.color} px-4 py-2 flex justify-between items-center`}>
                      <span className="text-white text-sm font-medium">{cat?.emoji} {cat?.name}</span>
                      <div className="flex items-center gap-2">
                        <span className={`px-2 py-0.5 rounded-full text-xs font-medium border ${status.color}`}>
                          {status.label}
                        </span>
                      </div>
                    </div>
                    <div className="p-4">
                      <div className="flex items-start justify-between mb-2">
                        <h3 className="font-semibold text-white">{clip.title}</h3>
                        {avgRating && <span className="text-lg">{avgRating}</span>}
                      </div>
                      <p className="text-zinc-400 text-sm mb-2 line-clamp-2">{clip.description}</p>

                      {/* Fake activity indicator */}
                      <div className="flex items-center gap-3 mb-3 text-xs text-zinc-500">
                        <span className="flex items-center gap-1">
                          <Users className="w-3 h-3" /> {fakeReviewers} reviewers
                        </span>
                        {(clip.picks?.length || 0) > 0 && (
                          <span className="flex items-center gap-1 text-amber-400">
                            <Star className="w-3 h-3" /> {clip.picks?.length} picks
                          </span>
                        )}
                      </div>

                      {(clip.tags || []).length > 0 && (
                        <div className="flex flex-wrap gap-1 mb-3">
                          {(clip.tags || []).slice(0, 3).map(tag => <TagBadge key={tag} tag={tag} small />)}
                          {(clip.tags || []).length > 3 && <span className="text-xs text-zinc-500">+{(clip.tags || []).length - 3}</span>}
                        </div>
                      )}

                      {/* Review completeness indicator */}
                      {hasReviewed && !isFullReview && (
                        <div className="flex items-center gap-2 mb-2 px-2 py-1.5 bg-amber-500/10 border border-amber-500/20 rounded-lg">
                          <span className="text-amber-400 text-xs font-medium">Incomplete review:</span>
                          <div className="flex items-center gap-1.5 text-xs">
                            {!hasComment && (
                              <span className="text-zinc-500">💬 no comment</span>
                            )}
                            {!hasMetrics && (
                              <span className="text-zinc-500">{isFemCat ? '💦 no metrics' : '💧 no metrics'}</span>
                            )}
                          </div>
                        </div>
                      )}
                      {hasReviewed && isFullReview && (
                        <div className="flex items-center gap-1.5 mb-2 text-xs text-green-500/70">
                          <Check className="w-3 h-3" /> Full review
                        </div>
                      )}

                      <button
                        onClick={() => {
                          setReviewingClip(clip);
                          setClipReview({ rating: '', comment: '', cumLevel: 3, cumConsistency: 3, wetnessLevel: 3, squirtLevel: 0, suggestedTags: [], dateNightNotes: '' });
                          // Start in edit mode by default
                          setIsEditingClip(true);
                          setEditingClipData({
                            title: clip.title,
                            description: clip.description || '',
                            tags: clip.tags || [],
                            categoryIds: getClipCategoryIds(clip),
                            link: clip.link || '',
                            reason: ''
                          });
                          setNewClipUrlError(null);
                          // Track clip view
                          trackClipView(clip, 'datenite');
                        }}
                        className={`w-full flex items-center justify-center gap-2 px-3 py-2.5 rounded-lg text-sm font-medium transition-all ${
                          hasReviewed
                            ? 'bg-green-500/20 text-green-400 border border-green-500/50'
                            : 'text-zinc-900'
                        }`}
                        style={hasReviewed ? {} : { backgroundColor: COLORS.lime }}
                      >
                        {hasReviewed ? (
                          <><Check className="w-4 h-4" /> Edit Content</>
                        ) : (
                          <><Edit3 className="w-4 h-4" /> Review Content</>
                        )}
                      </button>

                      {/* Recommend to managed couple */}
                      {currentReviewer?.managedCoupleId && (
                        <button
                          onClick={() => setPendingQuestion({
                            question: `I think you'll love this clip: "${clip.title}" — it's a great ${cat?.name || 'date night'} idea. Want to try it on your next date night?`,
                            category: cat?.name || 'General',
                            context: 'Clip recommendation'
                          })}
                          className="w-full flex items-center justify-center gap-2 px-3 py-1.5 rounded-lg text-xs font-medium transition-all bg-purple-500/20 text-purple-300 hover:bg-purple-500/30 mt-1.5"
                        >
                          <Send className="w-3 h-3" /> Recommend to Couple
                        </button>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>

            {filteredDateNiteClips.length === 0 && (
              <div className="bg-zinc-800 rounded-xl p-12 text-center border border-zinc-700">
                <ClipboardList className="w-12 h-12 mx-auto mb-4 text-zinc-600" />
                <p className="text-zinc-500">No clips match your filters</p>
              </div>
            )}
          </div>
          );
        })()}

        {/* Challenge Video Modal */}
        <VideoModal
          isOpen={showChallengeVideo && !!tryAtHomeClip}
          onClose={() => setShowChallengeVideo(false)}
          url={tryAtHomeClip?.link}
          title={tryAtHomeClip?.title}
          description={tryAtHomeClip?.description}
          clipId={tryAtHomeClip?.id}
        >
          <div className="flex gap-3 justify-center">
            <button
              onClick={() => { setShowChallengeVideo(false); setShowTryAtHomeFeedback(true); }}
              className="flex items-center justify-center gap-2 px-6 py-3 rounded-xl text-sm font-semibold text-zinc-900"
              style={{ backgroundColor: COLORS.lime }}
            >
              <Check className="w-4 h-4" /> We Tried This!
            </button>
            <button
              onClick={() => setShowChallengeVideo(false)}
              className="px-4 py-3 rounded-xl text-sm bg-zinc-700 text-zinc-300 hover:bg-zinc-600"
            >
              Close
            </button>
          </div>
        </VideoModal>

        {/* Try at Home Feedback Modal */}
        <Modal
          isOpen={showTryAtHomeFeedback && !!tryAtHomeClip}
          onClose={() => setShowTryAtHomeFeedback(false)}
          title="Homework Complete!"
        >
          {tryAtHomeClip && (
            <div className="p-6">
              <div className="bg-zinc-900 rounded-lg p-3 mb-4">
                <div className="flex items-center gap-2">
                  <span className="text-xl">{CATEGORIES.find(c => c.id === tryAtHomeClip.categoryId)?.emoji}</span>
                  <p className="text-white font-medium">{tryAtHomeClip.title}</p>
                </div>
              </div>

              <p className="text-zinc-400 text-sm mb-4">How did it go? Your partner's thoughts?</p>

              <div className="grid grid-cols-2 gap-2 mb-4">
                {[
                  { value: '🔥 Amazing', label: '🔥 Amazing!' },
                  { value: '👍 Good', label: '👍 Good' },
                  { value: '😐 Meh', label: '😐 Meh' },
                  { value: '👎 Not for us', label: '👎 Not for us' },
                ].map(opt => (
                  <button
                    key={opt.value}
                    onClick={() => setTryAtHomeFeedback({ ...tryAtHomeFeedback, rating: opt.value })}
                    className={`px-4 py-3 rounded-xl text-sm font-medium border-2 ${tryAtHomeFeedback.rating === opt.value ? 'border-orange-500 bg-orange-500/20 text-orange-400' : 'border-zinc-600 bg-zinc-700 text-zinc-300'}`}
                  >
                    {opt.label}
                  </button>
                ))}
              </div>

              {/* How did it compare? */}
              <div className="mb-4">
                <p className="text-zinc-400 text-sm mb-2">How did it compare to the clip?</p>
                <textarea
                  value={tryAtHomeFeedback.comparisonToClip}
                  onChange={e => setTryAtHomeFeedback({ ...tryAtHomeFeedback, comparisonToClip: e.target.value })}
                  placeholder="Was it similar? Better? Different vibe? Any surprises?"
                  className="w-full bg-zinc-700 border border-zinc-600 rounded-xl px-4 py-3 text-white placeholder-zinc-500 h-16 resize-none"
                />
              </div>

              {/* Media capture question */}
              <div className="bg-zinc-900/50 rounded-xl p-4 mb-4 border border-zinc-700">
                <p className="text-zinc-300 text-sm font-medium mb-3">📸 Did you take any pics or videos?</p>
                <div className="flex gap-2 mb-3">
                  {[
                    { value: 'yes', label: 'Yes!' },
                    { value: 'no', label: 'No' },
                  ].map(opt => (
                    <button
                      key={opt.value}
                      onClick={() => setTryAtHomeFeedback({ ...tryAtHomeFeedback, tookMedia: opt.value as 'yes' | 'no' })}
                      className={`flex-1 px-4 py-2 rounded-lg text-sm font-medium border-2 ${tryAtHomeFeedback.tookMedia === opt.value ? 'border-pink-500 bg-pink-500/20 text-pink-400' : 'border-zinc-600 bg-zinc-700 text-zinc-300'}`}
                    >
                      {opt.label}
                    </button>
                  ))}
                </div>

                {/* Conditional: Ask if willing to share */}
                {tryAtHomeFeedback.tookMedia === 'yes' && (
                  <div className="mt-3 pt-3 border-t border-zinc-700">
                    <p className="text-zinc-400 text-xs mb-2">Would you be open to sharing? (totally optional!)</p>
                    <div className="flex gap-2 mb-3">
                      {[
                        { value: 'yes', label: '💕 Yes!' },
                        { value: 'maybe', label: '🤔 Maybe' },
                        { value: 'no', label: 'No thanks' },
                      ].map(opt => (
                        <button
                          key={opt.value}
                          onClick={() => setTryAtHomeFeedback({ ...tryAtHomeFeedback, willingToShare: opt.value as 'yes' | 'maybe' | 'no' })}
                          className={`flex-1 px-3 py-2 rounded-lg text-xs font-medium border ${tryAtHomeFeedback.willingToShare === opt.value ? 'border-pink-500 bg-pink-500/20 text-pink-400' : 'border-zinc-600 bg-zinc-700 text-zinc-300'}`}
                        >
                          {opt.label}
                        </button>
                      ))}
                    </div>

                    {/* Show URL input if willing to share */}
                    {(tryAtHomeFeedback.willingToShare === 'yes' || tryAtHomeFeedback.willingToShare === 'maybe') && (
                      <div className="mt-2">
                        <input
                          type="text"
                          value={tryAtHomeFeedback.mediaUrl}
                          onChange={e => setTryAtHomeFeedback({ ...tryAtHomeFeedback, mediaUrl: e.target.value })}
                          placeholder="Paste link (Google Drive, Dropbox, etc.)"
                          className="w-full bg-zinc-700 border border-zinc-600 rounded-lg px-3 py-2 text-white placeholder-zinc-500 text-sm"
                        />
                        <p className="text-zinc-500 text-xs mt-1">Private link only seen by admins</p>
                      </div>
                    )}
                  </div>
                )}
              </div>

              <textarea
                value={tryAtHomeFeedback.feedback}
                onChange={e => setTryAtHomeFeedback({ ...tryAtHomeFeedback, feedback: e.target.value })}
                placeholder="Any other thoughts? Tips for other couples?"
                className="w-full bg-zinc-700 border border-zinc-600 rounded-xl px-4 py-3 text-white placeholder-zinc-500 h-20 resize-none mb-4"
              />

              <PrimaryButton
                onClick={completeTryAtHome}
                disabled={!tryAtHomeFeedback.rating}
                className="w-full"
              >
                <Check className="w-4 h-4" /> Submit Feedback
              </PrimaryButton>
            </div>
          )}
        </Modal>

        {/* Content Editor Modal - Contained modal */}
        {reviewingClip && (
          <div className="fixed inset-0 bg-black/80 z-50 flex items-start justify-center p-4 overflow-y-auto">
            <div className="bg-zinc-800 rounded-2xl w-full max-w-2xl my-8 border border-zinc-700 overflow-hidden">
              {/* Header with close */}
              <div className="flex items-center justify-between p-4 border-b border-zinc-700 bg-zinc-900">
                <h2 className="text-lg font-bold text-white flex items-center gap-2">
                  <Edit3 className="w-5 h-5 text-lime-400" />
                  {reviewingClip.title}
                </h2>
                <button
                  onClick={() => { setReviewingClip(null); setIsEditingClip(false); }}
                  className="text-zinc-400 hover:text-white"
                >
                  <X className="w-6 h-6" />
                </button>
              </div>

              {/* Video - Native player via proxy */}
              <div className="bg-black">
                <VideoPlayer url={reviewingClip.link} />
                {/* Outlink bar */}
                {(() => {
                  const watchUrl = getRedGifsWatchUrl(reviewingClip.link);
                  return watchUrl && (
                    <div className="bg-zinc-900/80 px-4 py-2 flex items-center justify-center">
                      <a href={watchUrl} target="_blank" rel="noopener noreferrer" className="text-zinc-500 hover:text-lime-400 text-sm flex items-center gap-2">
                        <Eye className="w-4 h-4" /> Open on RedGifs
                      </a>
                    </div>
                  );
                })()}
              </div>

              {/* Form Fields - Scrollable content */}
              <div className="p-4 space-y-4 max-h-[50vh] overflow-y-auto">
                  {/* Title */}
                  <div>
                    <label className="block text-xs font-semibold text-zinc-500 uppercase tracking-wide mb-1">Title</label>
                    <input
                      type="text"
                      value={editingClipData.title}
                      onChange={e => setEditingClipData({ ...editingClipData, title: e.target.value })}
                      className="w-full bg-zinc-700 border border-zinc-600 rounded-lg px-3 py-2 text-white font-medium"
                      placeholder="Clip title..."
                    />
                  </div>

                  {/* URL */}
                  <div>
                    <label className="block text-xs font-semibold text-zinc-500 uppercase tracking-wide mb-1">
                      <Link className="w-3 h-3 inline mr-1" />RedGifs URL
                    </label>
                    <input
                      type="url"
                      value={editingClipData.link}
                      onChange={e => {
                        setEditingClipData({ ...editingClipData, link: e.target.value });
                        setNewClipUrlError(null);
                      }}
                      className={`w-full bg-zinc-700 border rounded-lg px-3 py-2 text-white placeholder-zinc-500 text-sm ${
                        newClipUrlError ? 'border-red-500' : 'border-zinc-600'
                      }`}
                      placeholder="https://redgifs.com/watch/..."
                    />
                  </div>

                  {/* Category - Multi-select */}
                  <div>
                    <label className="block text-xs font-semibold text-zinc-500 uppercase tracking-wide mb-1">
                      Categories <span className="text-zinc-600 font-normal">(tap to select multiple, 1st = primary)</span>
                    </label>
                    <div className="grid grid-cols-3 gap-1.5">
                      {CATEGORIES.map(cat => {
                        const isSelected = editingClipData.categoryIds.includes(cat.id);
                        const isPrimary = editingClipData.categoryIds[0] === cat.id;
                        return (
                          <button
                            key={cat.id}
                            onClick={() => {
                              if (isSelected) {
                                // Don't remove if it's the only one
                                if (editingClipData.categoryIds.length > 1) {
                                  setEditingClipData({
                                    ...editingClipData,
                                    categoryIds: editingClipData.categoryIds.filter(id => id !== cat.id)
                                  });
                                }
                              } else {
                                setEditingClipData({
                                  ...editingClipData,
                                  categoryIds: [...editingClipData.categoryIds, cat.id]
                                });
                              }
                            }}
                            className={`p-1.5 rounded-lg text-center transition-all text-xs relative ${
                              isSelected
                                ? isPrimary
                                  ? 'bg-emerald-500/20 border-2 border-emerald-500 text-emerald-400'
                                  : 'bg-blue-500/20 border-2 border-blue-500 text-blue-400'
                                : 'bg-zinc-700 border border-zinc-600 text-zinc-400 hover:border-zinc-500'
                            }`}
                          >
                            {isPrimary && (
                              <span className="absolute -top-1 -right-1 text-[8px] bg-emerald-500 text-white px-1 rounded">1st</span>
                            )}
                            <span className="text-base block">{cat.emoji}</span>
                            <span className="text-[10px]">{cat.name}</span>
                          </button>
                        );
                      })}
                    </div>
                  </div>

                  {/* Description */}
                  <div>
                    <label className="block text-xs font-semibold text-zinc-500 uppercase tracking-wide mb-1">Description</label>
                    <textarea
                      value={editingClipData.description}
                      onChange={e => setEditingClipData({ ...editingClipData, description: e.target.value })}
                      className="w-full bg-zinc-700 border border-zinc-600 rounded-lg px-3 py-2 text-white placeholder-zinc-500 h-20 resize-none text-sm"
                      placeholder="What happens in this clip..."
                    />
                  </div>

                  {/* Tags */}
                  <div>
                    <label className="block text-xs font-semibold text-zinc-500 uppercase tracking-wide mb-1">Tags</label>
                    <div className="flex flex-wrap gap-1.5">
                      {TAGS.map(tag => (
                        <button
                          key={tag.id}
                          onClick={() => toggleEditTag(tag.id)}
                          className={`px-2 py-1 rounded-full text-xs font-medium transition-all ${
                            editingClipData.tags.includes(tag.id)
                              ? `${tag.color} border`
                              : 'bg-zinc-700 text-zinc-400 border border-zinc-600 hover:border-zinc-500'
                          }`}
                        >
                          {tag.label}
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* Validation Error */}
                  {newClipUrlError && (
                    <div className="flex items-center gap-2 p-3 bg-red-500/10 border border-red-500/30 rounded-lg">
                      <AlertCircle className="w-4 h-4 text-red-400 flex-shrink-0" />
                      <p className="text-red-400 text-sm">{newClipUrlError}</p>
                    </div>
                  )}

                  {/* Reason for edit */}
                  <div>
                    <label className="block text-xs font-semibold text-zinc-500 uppercase tracking-wide mb-1">Why are you suggesting this change?</label>
                    <textarea
                      value={editingClipData.reason}
                      onChange={e => setEditingClipData({ ...editingClipData, reason: e.target.value })}
                      className="w-full bg-zinc-700 border border-zinc-600 rounded-lg px-3 py-2 text-white placeholder-zinc-500 h-16 resize-none text-sm"
                      placeholder="e.g. Wrong link format, better title, miscategorized..."
                    />
                  </div>

                  {/* Submit Edit Suggestion Button */}
                  <button
                    onClick={saveClipEdits}
                    className="w-full flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg text-sm font-medium text-zinc-900"
                    style={{ backgroundColor: COLORS.lime }}
                  >
                    <Send className="w-4 h-4" /> Submit for Review
                  </button>
                  <p className="text-zinc-500 text-xs text-center mt-1">Edits require approval before going live</p>
                </div>

              {/* Couple Actions Panel - separate from review */}
              {currentReviewer?.coupleId && (
                <div className="p-4 border-t border-zinc-700 bg-gradient-to-r from-pink-500/10 to-purple-500/10">
                  <div className="flex items-start justify-between gap-4 mb-3">
                    <div>
                      <h3 className="font-semibold text-white text-sm uppercase tracking-wide flex items-center gap-2">
                        <Heart className="w-4 h-4 text-pink-400" /> For Your Date Night
                      </h3>
                      <p className="text-zinc-400 text-xs mt-1">
                        Save clips for you & {currentReviewer.partnerName || 'your partner'}. These won't affect your review.
                      </p>
                    </div>
                  </div>

                  {/* Notes for date night */}
                  <div className="mb-3">
                    <textarea
                      value={clipReview.dateNightNotes || ''}
                      onChange={e => setClipReview({ ...clipReview, dateNightNotes: e.target.value })}
                      placeholder="Add a note for date night... (e.g., 'This looks fun to try!', 'Perfect for Saturday')"
                      className="w-full bg-zinc-800/50 border border-pink-500/20 rounded-lg px-3 py-2 text-white placeholder-zinc-500 text-sm h-14 resize-none focus:border-pink-500/50 focus:outline-none"
                    />
                  </div>

                  <div className="flex gap-2">
                    <button
                      onClick={() => {
                        if (reviewingClip) {
                          // Toggle pick status
                          const isPicked = reviewingClip.picks?.includes(currentReviewer.id);
                          const updated = dateNiteClips.map(c => c.id === reviewingClip.id ? {
                            ...c,
                            picks: isPicked
                              ? (c.picks || []).filter(id => id !== currentReviewer.id)
                              : [...(c.picks || []), currentReviewer.id]
                          } : c);
                          saveDateNiteClips(updated);
                          setReviewingClip({
                            ...reviewingClip,
                            picks: isPicked
                              ? (reviewingClip.picks || []).filter(id => id !== currentReviewer.id)
                              : [...(reviewingClip.picks || []), currentReviewer.id]
                          });
                        }
                      }}
                      className={`flex-1 flex items-center justify-center gap-2 px-3 py-2.5 rounded-lg text-sm transition-colors ${
                        reviewingClip?.picks?.includes(currentReviewer.id)
                          ? 'bg-amber-500/20 text-amber-400 border border-amber-500/50'
                          : 'bg-zinc-700 text-zinc-300 hover:bg-zinc-600'
                      }`}
                    >
                      <Star className="w-4 h-4" />
                      {reviewingClip?.picks?.includes(currentReviewer.id) ? 'Picked' : 'My Picks'}
                    </button>
                    <button
                      onClick={() => {
                        if (reviewingClip) addToWishlist(reviewingClip, clipReview.dateNightNotes);
                      }}
                      disabled={isInWishlist(reviewingClip?.id || '')}
                      className={`flex-1 flex items-center justify-center gap-2 px-3 py-2.5 rounded-lg text-sm transition-colors ${
                        isInWishlist(reviewingClip?.id || '')
                          ? 'bg-pink-500/20 text-pink-400 border border-pink-500/50'
                          : 'bg-zinc-700 text-zinc-300 hover:bg-zinc-600'
                      }`}
                    >
                      <Heart className="w-4 h-4" />
                      {isInWishlist(reviewingClip?.id || '') ? 'In Wishlist' : 'Want to Try'}
                    </button>
                  </div>
                </div>
              )}

              {/* Review Section */}

              <div className="p-4 border-t border-zinc-700 bg-zinc-900/50 space-y-4">
                <h3 className="font-semibold text-white text-sm uppercase tracking-wide">Your Review</h3>
                <div className="grid grid-cols-2 gap-2">
                  {RATING_OPTIONS.map(opt => (
                    <button
                      key={opt.value}
                      onClick={() => setClipReview({ ...clipReview, rating: opt.value })}
                      className={`px-3 py-2 rounded-lg text-sm font-medium border-2 ${clipReview.rating === opt.value ? 'border-lime-500 bg-lime-500/20 text-lime-400' : 'border-zinc-600 bg-zinc-700 text-zinc-300'}`}
                    >
                      {opt.label}
                    </button>
                  ))}
                </div>

                {/* Conditional meters based on category type */}
                {FEMALE_CATEGORY_IDS.includes(reviewingClip.categoryId) ? (
                  <>
                    {/* Wetness-O-Meter for female categories */}
                    <div>
                      <p className="text-zinc-400 text-xs mb-2 text-center">Rate Wetness Level 💦</p>
                      <div className="flex justify-between gap-1">
                        {WETNESS_LEVELS.map(level => (
                          <button
                            key={level.value}
                            onClick={() => setClipReview({ ...clipReview, wetnessLevel: level.value })}
                            className={`flex-1 flex flex-col items-center p-1.5 rounded-lg transition-all ${
                              clipReview.wetnessLevel === level.value
                                ? `bg-gradient-to-b ${level.color} scale-105`
                                : clipReview.wetnessLevel >= level.value
                                  ? 'bg-zinc-700/80'
                                  : 'bg-zinc-800 hover:bg-zinc-700'
                            }`}
                          >
                            <span className="text-lg">{level.emoji}</span>
                            <span className={`text-[10px] font-medium ${clipReview.wetnessLevel === level.value ? 'text-white' : 'text-zinc-500'}`}>
                              {level.label}
                            </span>
                          </button>
                        ))}
                      </div>
                    </div>

                    {/* Squirt Rating for female categories */}
                    <div>
                      <p className="text-zinc-400 text-xs mb-2 text-center">Squirt Level 🌊</p>
                      <div className="flex justify-between gap-1">
                        {SQUIRT_LEVELS.map(level => (
                          <button
                            key={level.value}
                            onClick={() => setClipReview({ ...clipReview, squirtLevel: level.value })}
                            className={`flex-1 flex flex-col items-center p-1.5 rounded-lg transition-all ${
                              clipReview.squirtLevel === level.value
                                ? `bg-gradient-to-b ${level.color} scale-105`
                                : 'bg-zinc-800 hover:bg-zinc-700'
                            }`}
                          >
                            <span className="text-lg">{level.emoji}</span>
                            <span className={`text-[10px] font-medium ${clipReview.squirtLevel === level.value ? 'text-white' : 'text-zinc-500'}`}>
                              {level.label}
                            </span>
                          </button>
                        ))}
                      </div>
                    </div>
                  </>
                ) : (
                  <>
                    {/* Load-O-Meter for male-focused categories */}
                    <div>
                      <p className="text-zinc-400 text-xs mb-2 text-center">Rate the Load 💦</p>
                      <div className="flex justify-between gap-1">
                        {CUM_LEVELS.map(level => (
                          <button
                            key={level.value}
                            onClick={() => setClipReview({ ...clipReview, cumLevel: level.value })}
                            className={`flex-1 flex flex-col items-center p-1.5 rounded-lg transition-all ${
                              clipReview.cumLevel === level.value
                                ? `bg-gradient-to-b ${level.color} scale-105`
                                : clipReview.cumLevel >= level.value
                                  ? 'bg-zinc-700/80'
                                  : 'bg-zinc-800 hover:bg-zinc-700'
                            }`}
                          >
                            <span className="text-lg">{level.emoji}</span>
                            <span className={`text-[10px] font-medium ${clipReview.cumLevel === level.value ? 'text-white' : 'text-zinc-500'}`}>
                              {level.label}
                            </span>
                          </button>
                        ))}
                      </div>
                    </div>

                    {/* Consistency Rating for male-focused categories */}
                    <div>
                      <p className="text-zinc-400 text-xs mb-2 text-center">Consistency / Thickness 🧁</p>
                      <div className="flex justify-between gap-1">
                        {HIS_CONSISTENCY_SCALE.map(level => (
                          <button
                            key={level.value}
                            onClick={() => setClipReview({ ...clipReview, cumConsistency: level.value })}
                            className={`flex-1 flex flex-col items-center p-1.5 rounded-lg transition-all ${
                              clipReview.cumConsistency === level.value
                                ? 'bg-gradient-to-b from-purple-500/50 to-pink-500/50 scale-105'
                                : 'bg-zinc-800 hover:bg-zinc-700'
                            }`}
                          >
                            <span className="text-lg">{level.emoji}</span>
                            <span className={`text-[10px] font-medium ${clipReview.cumConsistency === level.value ? 'text-white' : 'text-zinc-500'}`}>
                              {level.label}
                            </span>
                          </button>
                        ))}
                      </div>
                    </div>
                  </>
                )}

                <textarea
                  value={clipReview.comment}
                  onChange={e => setClipReview({ ...clipReview, comment: e.target.value })}
                  placeholder="Comments - what makes this clip good..."
                  className="w-full bg-zinc-700 border border-zinc-600 rounded-lg px-3 py-2 text-white placeholder-zinc-500 h-16 resize-none text-sm"
                />

                {/* Tag Suggestions */}
                <div>
                  <p className="text-zinc-400 text-xs mb-2">Suggest Tags (helps categorize)</p>
                  <div className="flex flex-wrap gap-1.5">
                    {TAGS.map(tag => {
                      const isSelected = clipReview.suggestedTags.includes(tag.id);
                      const alreadyOnClip = reviewingClip.tags?.includes(tag.id);
                      return (
                        <button
                          key={tag.id}
                          onClick={() => {
                            if (alreadyOnClip) return;
                            if (isSelected) {
                              setClipReview({
                                ...clipReview,
                                suggestedTags: clipReview.suggestedTags.filter(t => t !== tag.id)
                              });
                            } else {
                              setClipReview({
                                ...clipReview,
                                suggestedTags: [...clipReview.suggestedTags, tag.id]
                              });
                            }
                          }}
                          disabled={alreadyOnClip}
                          className={`px-2 py-1 rounded text-xs font-medium transition-all border ${
                            alreadyOnClip
                              ? 'bg-zinc-800 text-zinc-600 border-zinc-700 cursor-not-allowed'
                              : isSelected
                                ? tag.color
                                : 'bg-zinc-700 text-zinc-400 border-zinc-600 hover:bg-zinc-600'
                          }`}
                          title={alreadyOnClip ? 'Already tagged' : isSelected ? 'Click to remove' : 'Click to suggest'}
                        >
                          {tag.label} {alreadyOnClip && '✓'}
                        </button>
                      );
                    })}
                  </div>
                  {clipReview.suggestedTags.length > 0 && (
                    <p className="text-xs text-lime-400 mt-1">+{clipReview.suggestedTags.length} new tag{clipReview.suggestedTags.length !== 1 ? 's' : ''}</p>
                  )}
                </div>

                <div className="flex gap-2">
                  <PrimaryButton onClick={submitClipReview} disabled={!clipReview.rating} className="flex-1">
                    Submit Review
                  </PrimaryButton>
                  {currentReviewer?.coupleId && (
                    <button
                      onClick={() => { assignTryAtHome(reviewingClip); setReviewingClip(null); }}
                      className={`px-4 py-2 rounded-lg font-medium flex items-center gap-2 ${currentReviewer?.tryAtHome?.clipId === reviewingClip.id ? 'bg-orange-500/20 text-orange-400 border-2 border-orange-500' : 'bg-zinc-700 text-zinc-300 hover:bg-zinc-600'}`}
                      title="Set as your couple's challenge"
                    >
                      <Home className="w-4 h-4" />
                    </button>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Recommendations Modal */}
        <Modal
          isOpen={showRecommendations}
          onClose={() => setShowRecommendations(false)}
          title="Recommendations"
          size="lg"
        >
          <div className="p-6 max-h-[60vh] overflow-y-auto">
            {recommendations.length === 0 ? (
              <EmptyState
                icon={<Gift className="w-8 h-8" style={{ color: COLORS.lime }} />}
                title="No recommendations yet"
                description="Share your couple code with friends!"
              />
            ) : (
              <div className="space-y-4">
                {recommendations.map(rec => {
                  const clip = dateNiteClips.find(c => c.id === rec.clipId);
                  const cat = clip ? CATEGORIES.find(c => c.id === clip.categoryId) : null;
                  return (
                    <div
                      key={rec.id}
                      className={`bg-zinc-900 rounded-xl p-4 border ${!rec.seen ? 'border-purple-500/50' : 'border-zinc-700'}`}
                    >
                      <div className="flex items-start justify-between mb-3">
                        <div>
                          <p className="text-white font-medium">From {rec.fromName}</p>
                          <p className="text-zinc-500 text-xs">{new Date(rec.createdAt).toLocaleDateString()}</p>
                        </div>
                        {!rec.seen && <span className="px-2 py-1 bg-purple-500/20 text-purple-400 text-xs rounded-full">New</span>}
                      </div>
                      {clip ? (
                        <div className="bg-zinc-800 rounded-lg p-3 mb-3">
                          <div className="flex items-center gap-2">
                            <span className="text-xl">{cat?.emoji}</span>
                            <div>
                              <p className="text-white font-medium">{clip.title}</p>
                              <p className="text-zinc-400 text-sm">{clip.description}</p>
                            </div>
                          </div>
                        </div>
                      ) : (
                        <p className="text-zinc-500 text-sm mb-3">Clip no longer available</p>
                      )}
                      {rec.message && (
                        <p className="text-zinc-300 text-sm italic mb-3">"{rec.message}"</p>
                      )}
                      {rec.coupleReply && (
                        <div className="mt-1 bg-lime-500/10 border border-lime-500/20 rounded p-2 mb-3">
                          <p className="text-lime-400 text-xs">Couple's reply:</p>
                          <p className="text-sm text-white">{rec.coupleReply}</p>
                          {rec.coupleRepliedAt && (
                            <p className="text-xs text-zinc-500 mt-1">{new Date(rec.coupleRepliedAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}</p>
                          )}
                        </div>
                      )}
                      {clip && (
                        <div className="flex gap-2">
                          <button
                            onClick={() => { setWatchingClip(clip); setShowRecommendations(false); }}
                            className="flex-1 flex items-center justify-center gap-1 bg-zinc-700 text-white px-3 py-2 rounded-lg text-sm hover:bg-zinc-600"
                          >
                            <Play className="w-4 h-4" /> Watch
                          </button>
                          <button
                            onClick={() => { assignTryAtHome(clip); setShowRecommendations(false); }}
                            className="flex-1 flex items-center justify-center gap-1 px-3 py-2 rounded-lg text-sm font-medium text-zinc-900"
                            style={{ backgroundColor: COLORS.lime }}
                          >
                            <Home className="w-4 h-4" /> Try This!
                          </button>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </Modal>

        {/* Pick-for-Us Confirmation Modal */}
        {pendingPick && (
          <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-[60] p-4">
            <div className="bg-zinc-800 rounded-2xl p-6 w-full max-w-md border border-purple-500/30 shadow-xl">
              <h3 className="text-lg font-bold text-white mb-2 flex items-center gap-2">
                🎯 Pick This Clip
              </h3>
              <p className="text-zinc-400 text-sm mb-4">
                You're picking <span className="text-purple-400 font-medium">"{pendingPick.clipTitle}"</span> for {managedCoupleData?.hisName} & {managedCoupleData?.herName}
              </p>

              <div className="space-y-4">
                {/* Why picked */}
                <div>
                  <label className="text-xs text-zinc-500 block mb-1">Why this one? (optional)</label>
                  <textarea
                    value={pendingPick.whyPicked}
                    onChange={e => setPendingPick({ ...pendingPick, whyPicked: e.target.value })}
                    placeholder="I think you'll love this because..."
                    className="w-full bg-zinc-700 border border-zinc-600 rounded-lg px-3 py-2 text-white placeholder-zinc-500 text-sm resize-none h-16"
                  />
                </div>

                {/* Follow-up question */}
                <div>
                  <label className="text-xs text-zinc-500 block mb-1">What would you love to know after they try it?</label>
                  <textarea
                    value={pendingPick.followUpQuestion}
                    onChange={e => setPendingPick({ ...pendingPick, followUpQuestion: e.target.value })}
                    placeholder="How did it compare to what you imagined?"
                    className="w-full bg-zinc-700 border border-zinc-600 rounded-lg px-3 py-2 text-white placeholder-zinc-500 text-sm resize-none h-16"
                  />
                  <p className="text-zinc-600 text-xs mt-1">This will be sent to them so they know what to report back on</p>
                </div>

                {/* Suggested prompts */}
                <div>
                  <p className="text-xs text-zinc-500 mb-2">Quick prompts:</p>
                  <div className="flex flex-wrap gap-1">
                    {[
                      "How did it feel?",
                      "Would you do it again?",
                      "What would you change?",
                      "Rate it 1-10?",
                      "Better or worse than expected?"
                    ].map(prompt => (
                      <button
                        key={prompt}
                        onClick={() => setPendingPick({
                          ...pendingPick,
                          followUpQuestion: pendingPick.followUpQuestion
                            ? `${pendingPick.followUpQuestion} ${prompt}`
                            : prompt
                        })}
                        className="px-2 py-1 bg-zinc-700 hover:bg-zinc-600 text-zinc-300 text-xs rounded transition-colors"
                      >
                        {prompt}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Date picker */}
                <div>
                  <label className="text-xs text-zinc-500 block mb-2">
                    📅 Schedule for:
                    {managedCoupleData?.availableDays && managedCoupleData.availableDays.length > 0 && (
                      <span className="text-purple-400 ml-1">
                        (They're free on {managedCoupleData.availableDays.map(d => ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'][d]).join(', ')})
                      </span>
                    )}
                  </label>
                  <div className="grid grid-cols-7 gap-1">
                    {(() => {
                      const today = new Date();
                      const dates: Date[] = [];
                      // Generate next 21 days (3 weeks)
                      for (let i = 1; i <= 21; i++) {
                        const d = new Date(today);
                        d.setDate(today.getDate() + i);
                        dates.push(d);
                      }
                      const availDays = managedCoupleData?.availableDays || [];

                      return dates.map((d, idx) => {
                        const dateStr = d.toISOString().split('T')[0] || '';
                        const dayOfWeek = d.getDay();
                        const isAvailable = availDays.length === 0 || availDays.includes(dayOfWeek);
                        const isSelected = pendingPick.scheduledDate === dateStr;

                        return (
                          <button
                            key={idx}
                            onClick={() => pendingPick && setPendingPick({ ...pendingPick, scheduledDate: dateStr })}
                            disabled={!isAvailable}
                            className={`
                              p-1 rounded text-xs text-center transition-colors
                              ${isSelected
                                ? 'bg-purple-500 text-white ring-2 ring-purple-400'
                                : isAvailable
                                  ? 'bg-zinc-700 hover:bg-zinc-600 text-white'
                                  : 'bg-zinc-800 text-zinc-600 cursor-not-allowed'
                              }
                            `}
                            title={d.toLocaleDateString('en-US', { weekday: 'long', month: 'short', day: 'numeric' })}
                          >
                            <div className="text-[10px] text-zinc-400">{['S', 'M', 'T', 'W', 'T', 'F', 'S'][dayOfWeek]}</div>
                            <div className="font-medium">{d.getDate()}</div>
                          </button>
                        );
                      });
                    })()}
                  </div>
                  {pendingPick.scheduledDate && (
                    <p className="text-purple-400 text-xs mt-2 flex items-center gap-1">
                      <Calendar className="w-3 h-3" />
                      {new Date(pendingPick.scheduledDate + 'T12:00:00').toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' })}
                    </p>
                  )}
                </div>
              </div>

              <div className="flex gap-2 mt-6">
                <button
                  onClick={handleConfirmPick}
                  disabled={!pendingPick.scheduledDate}
                  className={`flex-1 flex items-center justify-center gap-2 px-4 py-2 rounded-lg text-sm font-medium ${
                    pendingPick.scheduledDate
                      ? 'text-zinc-900'
                      : 'text-zinc-500 cursor-not-allowed'
                  }`}
                  style={{ backgroundColor: pendingPick.scheduledDate ? COLORS.lime : '#3f3f46' }}
                >
                  <Check className="w-4 h-4" />
                  {pendingPick.scheduledDate ? 'Confirm & Schedule' : 'Pick a date first'}
                </button>
                <button
                  onClick={() => setPendingPick(null)}
                  className="px-4 py-2 rounded-lg text-sm text-zinc-400 hover:text-white bg-zinc-700"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Schedule Activity Modal */}
        <ScheduleActivityModal
          isOpen={showScheduleModal}
          onClose={() => {
            setShowScheduleModal(false);
            setSelectedCalendarDate(undefined);
            setRescheduleActivityId(null);
          }}
          clips={dateNiteClips}
          preferences={currentReviewer?.couplePreferences || []}
          initialDate={selectedCalendarDate}
          onSchedule={handleScheduleActivity}
          context={getReviewerContext()}
        />

        {/* Schedule Activity Modal for Managed Couple */}
        <ScheduleActivityModal
          isOpen={showManagedScheduleModal}
          onClose={() => {
            setShowManagedScheduleModal(false);
            setSelectedManagedCalendarDate(undefined);
            setManagedRescheduleActivityId(null);
          }}
          clips={dateNiteClips}
          initialDate={selectedManagedCalendarDate}
          onSchedule={handleManagedScheduleActivity}
          context={getReviewerContext()}
        />

        {/* Video Watch Modal */}
        <VideoModal
          isOpen={!!watchingClip}
          onClose={() => setWatchingClip(null)}
          url={watchingClip?.link}
          title={watchingClip?.title}
          description={watchingClip?.description}
          clipId={watchingClip?.id}
        />

        {/* Creator Submission Review Modal */}
        {reviewingSubmission && currentReviewer && (
          <SubmissionReviewModal
            key={reviewingSubmission.id}
            isOpen={!!reviewingSubmission}
            onClose={() => setReviewingSubmission(null)}
            submission={reviewingSubmission}
            creator={creators.find(c => c.id === reviewingSubmission.creatorId)}
            reviewerId={currentReviewer.id}
            reviewerCode={currentReviewer.code}
            onSave={handleSaveSubmissionReview}
          />
        )}

        {/* Link to Couple Modal */}
        {showLinkCoupleModal && (
          <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4">
            <div className="bg-zinc-800 rounded-2xl w-full max-w-md border border-zinc-700 p-6">
              <div className="flex justify-between items-center mb-6">
                <h2 className="text-xl font-bold text-white flex items-center gap-2">
                  <Heart className="w-5 h-5 text-pink-400" /> Link to a Couple
                </h2>
                <button
                  onClick={() => { setShowLinkCoupleModal(false); setLinkCoupleCode(''); setLinkCoupleError(''); }}
                  className="text-zinc-400 hover:text-white"
                >
                  <X className="w-6 h-6" />
                </button>
              </div>

              <p className="text-zinc-400 text-sm mb-4">
                Enter a couple code to access their calendar and help them plan their date nights.
              </p>

              <input
                type="text"
                value={linkCoupleCode}
                onChange={e => { setLinkCoupleCode(e.target.value.toUpperCase()); setLinkCoupleError(''); }}
                onKeyDown={e => e.key === 'Enter' && linkToCouple()}
                placeholder="VBC-XXXXXX"
                className="w-full bg-zinc-700 border border-zinc-600 rounded-xl px-4 py-3 text-white font-mono text-center text-lg tracking-wider mb-4"
                autoFocus
              />

              {linkCoupleError && (
                <p className="text-red-400 text-sm mb-4 text-center">{linkCoupleError}</p>
              )}

              <PrimaryButton onClick={linkToCouple} className="w-full">
                Link to Couple
              </PrimaryButton>

              <p className="text-zinc-500 text-xs mt-4 text-center">
                Ask your friends for their couple code, or check the admin panel for registered couples.
              </p>
            </div>
          </div>
        )}

        {/* Unlink Couple Confirmation Modal */}
        {showUnlinkConfirm && linkedCoupleData && (
          <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4">
            <div className="bg-zinc-800 rounded-2xl w-full max-w-sm border border-zinc-700 p-6">
              <div className="text-center mb-6">
                <div className="w-16 h-16 rounded-full bg-red-500/20 flex items-center justify-center mx-auto mb-4">
                  <AlertCircle className="w-8 h-8 text-red-400" />
                </div>
                <h2 className="text-xl font-bold text-white mb-2">Unlink from Couple?</h2>
                <p className="text-zinc-400 text-sm">
                  You will be unlinked from <span className="text-white font-medium">{linkedCoupleData.hisName} & {linkedCoupleData.herName}</span>.
                  You can always re-link using their couple code.
                </p>
              </div>

              <div className="flex gap-3">
                <button
                  onClick={() => setShowUnlinkConfirm(false)}
                  className="flex-1 px-4 py-3 rounded-xl bg-zinc-700 text-zinc-300 hover:bg-zinc-600"
                >
                  Cancel
                </button>
                <button
                  onClick={unlinkFromCouple}
                  className="flex-1 px-4 py-3 rounded-xl bg-red-500/20 text-red-400 border border-red-500/50 hover:bg-red-500/30"
                >
                  Unlink
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Couple Onboarding Wizard */}
        {showCoupleOnboardingWizard && onboardingCoupleData && (
          <div className="fixed inset-0 bg-black/90 flex items-center justify-center z-50 p-4 overflow-auto">
            <div className="bg-zinc-800 rounded-2xl w-full max-w-2xl border border-zinc-700 my-8 max-h-[90vh] overflow-y-auto">
              {/* Progress Indicator */}
              <div className="sticky top-0 bg-zinc-800 border-b border-zinc-700 p-4">
                <div className="flex items-center justify-between mb-3">
                  <h2 className="text-xl font-bold text-white">
                    Meet {onboardingCoupleData.hisName} & {onboardingCoupleData.herName}
                  </h2>
                  <button
                    onClick={() => {
                      setShowCoupleOnboardingWizard(false);
                      setTab('calendar');
                    }}
                    className="text-zinc-400 hover:text-white"
                  >
                    <X className="w-6 h-6" />
                  </button>
                </div>
                <div className="flex gap-1">
                  {[0, 1, 2, 3, 4].map(step => (
                    <div
                      key={step}
                      className={`flex-1 h-1 rounded-full ${
                        step <= onboardingWizardStep ? 'bg-lime-500' : 'bg-zinc-700'
                      }`}
                    />
                  ))}
                </div>
                <div className="flex justify-between mt-2 text-xs text-zinc-500">
                  <span className={onboardingWizardStep === 0 ? 'text-lime-400' : ''}>About</span>
                  <span className={onboardingWizardStep === 1 ? 'text-lime-400' : ''}>Preferences</span>
                  <span className={onboardingWizardStep === 2 ? 'text-lime-400' : ''}>His Profile</span>
                  <span className={onboardingWizardStep === 3 ? 'text-lime-400' : ''}>Feedback</span>
                  <span className={onboardingWizardStep === 4 ? 'text-lime-400' : ''}>Next Steps</span>
                </div>
              </div>

              <div className="p-6">
                {/* Step 0: About the Couple */}
                {onboardingWizardStep === 0 && (
                  <div className="space-y-6">
                    <div className="text-center">
                      <div className="w-20 h-20 rounded-full bg-gradient-to-r from-pink-500 to-purple-500 flex items-center justify-center mx-auto mb-4">
                        <Heart className="w-10 h-10 text-white" />
                      </div>
                      <h3 className="text-2xl font-bold text-white mb-2">
                        {onboardingCoupleData.hisName} & {onboardingCoupleData.herName}
                      </h3>
                      <p className="text-zinc-400">Couple Code: <span className="font-mono text-pink-400">{onboardingCoupleData.coupleId}</span></p>
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                      <div className="bg-zinc-700/50 rounded-xl p-4 text-center">
                        <p className="text-zinc-400 text-sm mb-1">Started</p>
                        <p className="text-white font-medium">
                          {new Date(onboardingCoupleData.setupAt).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}
                        </p>
                      </div>
                      <div className="bg-zinc-700/50 rounded-xl p-4 text-center">
                        <p className="text-zinc-400 text-sm mb-1">Managed By</p>
                        <p className="text-white font-medium">
                          {onboardingCoupleData.managedBy === 'him' ? `👨 ${onboardingCoupleData.hisName}` :
                           onboardingCoupleData.managedBy === 'her' ? `👩 ${onboardingCoupleData.herName}` :
                           '👫 Both'}
                        </p>
                      </div>
                    </div>

                    {onboardingCoupleData.lastActivityAt && (
                      <div className="bg-lime-500/10 border border-lime-500/30 rounded-xl p-4">
                        <p className="text-lime-400 text-sm">
                          Last active: {new Date(onboardingCoupleData.lastActivityAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} at {new Date(onboardingCoupleData.lastActivityAt).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })}
                        </p>
                      </div>
                    )}

                    {onboardingCoupleData.profileNotes && (
                      <div className="bg-zinc-700/50 rounded-xl p-4">
                        <p className="text-zinc-400 text-sm mb-2">📝 Their Notes</p>
                        <p className="text-white">{onboardingCoupleData.profileNotes}</p>
                      </div>
                    )}

                    {/* Available Days */}
                    {onboardingCoupleData.availableDays && onboardingCoupleData.availableDays.length > 0 && (
                      <div className="bg-blue-500/10 border border-blue-500/30 rounded-xl p-4">
                        <p className="text-blue-400 text-sm mb-2">📅 Available Days for Activities</p>
                        <div className="flex gap-2">
                          {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map((day, i) => (
                            <span
                              key={day}
                              className={`px-3 py-1 rounded-lg text-sm ${
                                onboardingCoupleData.availableDays?.includes(i)
                                  ? 'bg-blue-500/30 text-blue-300'
                                  : 'bg-zinc-700 text-zinc-500'
                              }`}
                            >
                              {day}
                            </span>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                )}

                {/* Step 2: His Profile Stats (moved down, with manager note) */}
                {onboardingWizardStep === 2 && (
                  <div className="space-y-6">
                    <div className="text-center mb-6">
                      <h3 className="text-xl font-bold text-white mb-2">🍆 {onboardingCoupleData.hisName}'s Profile</h3>
                      <p className="text-zinc-400">Stats and characteristics</p>
                    </div>

                    {/* Manager sharing note */}
                    <div className="bg-pink-500/10 border border-pink-500/30 rounded-xl p-4">
                      <p className="text-pink-300 text-sm">
                        💝 {onboardingCoupleData.managedBy === 'him'
                          ? `${onboardingCoupleData.hisName} (him)`
                          : onboardingCoupleData.managedBy === 'her'
                            ? `${onboardingCoupleData.herName} (her)`
                            : 'They'} chose to share these details about {onboardingCoupleData.hisName} to help you manage things and choose the right clips for them.
                      </p>
                    </div>

                    {onboardingCoupleData.hisProfile && (onboardingCoupleData.hisProfile.size || onboardingCoupleData.hisProfile.girth || onboardingCoupleData.hisProfile.cumIntensity) ? (
                      <div className="space-y-4">
                        {/* Size */}
                        {onboardingCoupleData.hisProfile.size && (() => {
                          const sizeData = HIS_SIZE_SCALE.find(s => s.value === onboardingCoupleData.hisProfile?.size);
                          return (
                            <div className="bg-zinc-700/50 rounded-xl p-4">
                              <p className="text-zinc-400 text-sm mb-2">Size</p>
                              <div className="flex items-center justify-between">
                                <div className="flex items-center gap-3">
                                  <span className="text-3xl">{sizeData?.emoji}</span>
                                  <span className="text-white text-lg">{sizeData?.label}</span>
                                </div>
                                <span className="text-purple-400 text-sm px-3 py-1 bg-purple-500/20 rounded-full">{sizeData?.percentile}</span>
                              </div>
                            </div>
                          );
                        })()}

                        {/* Girth */}
                        {onboardingCoupleData.hisProfile.girth && (() => {
                          const girthData = HIS_GIRTH_SCALE.find(s => s.value === onboardingCoupleData.hisProfile?.girth);
                          return (
                            <div className="bg-zinc-700/50 rounded-xl p-4">
                              <p className="text-zinc-400 text-sm mb-2">Girth</p>
                              <div className="flex items-center justify-between">
                                <div className="flex items-center gap-3">
                                  <span className="text-3xl">{girthData?.emoji}</span>
                                  <span className="text-white text-lg">{girthData?.size}</span>
                                </div>
                                <span className="text-purple-400 text-sm px-3 py-1 bg-purple-500/20 rounded-full">{girthData?.percentile}</span>
                              </div>
                            </div>
                          );
                        })()}

                        {/* Volume */}
                        {onboardingCoupleData.hisProfile.cumIntensity && (() => {
                          const cumData = HIS_CUM_SCALE.find(s => s.value === onboardingCoupleData.hisProfile?.cumIntensity);
                          return (
                            <div className="bg-zinc-700/50 rounded-xl p-4">
                              <p className="text-zinc-400 text-sm mb-2">Volume</p>
                              <div className="flex items-center justify-between">
                                <div className="flex items-center gap-3">
                                  <span className="text-3xl">{cumData?.emoji}</span>
                                  <span className="text-white text-lg">{cumData?.label}</span>
                                </div>
                                <span className="text-purple-400 text-sm px-3 py-1 bg-purple-500/20 rounded-full">{cumData?.percentile}</span>
                              </div>
                            </div>
                          );
                        })()}

                        {/* Consistency */}
                        {onboardingCoupleData.hisProfile.cumConsistency && (
                          <div className="bg-zinc-700/50 rounded-xl p-4">
                            <p className="text-zinc-400 text-sm mb-2">Consistency</p>
                            <div className="flex items-center gap-3">
                              <span className="text-3xl">
                                {HIS_CONSISTENCY_SCALE.find(s => s.value === onboardingCoupleData.hisProfile?.cumConsistency)?.emoji}
                              </span>
                              <span className="text-white text-lg">
                                {HIS_CONSISTENCY_SCALE.find(s => s.value === onboardingCoupleData.hisProfile?.cumConsistency)?.label}
                              </span>
                            </div>
                            {onboardingCoupleData.hisProfile.consistencyNotes && (
                              <p className="text-zinc-400 text-sm mt-2 italic">"{onboardingCoupleData.hisProfile.consistencyNotes}"</p>
                            )}
                          </div>
                        )}

                        {/* Taste */}
                        {onboardingCoupleData.hisProfile.cumTaste && (
                          <div className="bg-zinc-700/50 rounded-xl p-4">
                            <p className="text-zinc-400 text-sm mb-2">Taste</p>
                            <div className="flex items-center gap-3">
                              <span className="text-3xl">
                                {HIS_TASTE_SCALE.find(s => s.value === onboardingCoupleData.hisProfile?.cumTaste)?.emoji}
                              </span>
                              <span className="text-white text-lg">
                                {HIS_TASTE_SCALE.find(s => s.value === onboardingCoupleData.hisProfile?.cumTaste)?.label}
                              </span>
                            </div>
                            {onboardingCoupleData.hisProfile.tasteNotes && (
                              <p className="text-zinc-400 text-sm mt-2 italic">"{onboardingCoupleData.hisProfile.tasteNotes}"</p>
                            )}
                          </div>
                        )}

                        {/* Precum */}
                        {onboardingCoupleData.hisProfile.precumProduction && (
                          <div className="bg-zinc-700/50 rounded-xl p-4">
                            <p className="text-zinc-400 text-sm mb-2">Precum</p>
                            <div className="flex items-center gap-3">
                              <span className="text-3xl">
                                {HIS_PRECUM_SCALE.find(s => s.value === onboardingCoupleData.hisProfile?.precumProduction)?.emoji}
                              </span>
                              <span className="text-white text-lg">
                                {HIS_PRECUM_SCALE.find(s => s.value === onboardingCoupleData.hisProfile?.precumProduction)?.label}
                              </span>
                            </div>
                            {onboardingCoupleData.hisProfile.precumNotes && (
                              <p className="text-zinc-400 text-sm mt-2 italic">"{onboardingCoupleData.hisProfile.precumNotes}"</p>
                            )}
                          </div>
                        )}
                      </div>
                    ) : (
                      <div className="text-center py-8 bg-zinc-700/30 rounded-xl">
                        <p className="text-zinc-500">No profile stats shared yet</p>
                      </div>
                    )}
                  </div>
                )}

                {/* Step 1: Preferences (moved up) */}
                {onboardingWizardStep === 1 && (
                  <div className="space-y-6">
                    <div className="text-center mb-6">
                      <h3 className="text-xl font-bold text-white mb-2">🎯 Their Preferences</h3>
                      <p className="text-zinc-400">Categories and clips they're interested in</p>
                    </div>

                    {/* Category Preferences */}
                    {onboardingCoupleData.preferences && Object.keys(onboardingCoupleData.preferences).length > 0 ? (
                      <div className="space-y-4">
                        <h4 className="text-white font-medium">Category Preferences</h4>
                        <div className="grid grid-cols-2 gap-2">
                          {Object.entries(onboardingCoupleData.preferences).map(([catId, pref]) => {
                            const cat = CATEGORIES.find(c => c.id === catId);
                            const prefConfig: Record<string, { bg: string; text: string; label: string }> = {
                              'love': { bg: 'bg-pink-500/20', text: 'text-pink-400', label: '❤️ Love' },
                              'like': { bg: 'bg-green-500/20', text: 'text-green-400', label: '👍 Like' },
                              'curious': { bg: 'bg-amber-500/20', text: 'text-amber-400', label: '🤔 Curious' },
                              'nope': { bg: 'bg-red-500/20', text: 'text-red-400', label: '❌ Nope' },
                              'neutral': { bg: 'bg-zinc-700', text: 'text-zinc-400', label: '😐 Neutral' }
                            };
                            const config = prefConfig[pref] ?? { bg: 'bg-zinc-700', text: 'text-zinc-400', label: '😐 Neutral' };
                            return (
                              <div key={catId} className={`${config.bg} rounded-lg p-3 flex items-center justify-between`}>
                                <span className="text-white flex items-center gap-2">
                                  <span>{cat?.emoji}</span>
                                  <span className="text-sm">{cat?.name}</span>
                                </span>
                                <span className={`text-xs ${config.text}`}>{config.label}</span>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    ) : (
                      <div className="text-center py-4 bg-zinc-700/30 rounded-xl">
                        <p className="text-zinc-500">No category preferences set yet</p>
                      </div>
                    )}

                    {/* Tried Clips */}
                    {(onboardingCoupleData.tried || []).length > 0 && (
                      <div className="space-y-3">
                        <h4 className="text-white font-medium">Clips They've Tried ({(onboardingCoupleData.tried || []).length})</h4>
                        <div className="space-y-2 max-h-48 overflow-y-auto">
                          {(onboardingCoupleData.tried || []).slice(-5).reverse().map((t, i) => {
                            const clip = dateNiteClips.find(c => c.id === t.clipId);
                            const cat = clip ? CATEGORIES.find(c => c.id === clip.categoryId) : null;
                            const ratingEmoji: Record<string, string> = { 'hot': '🔥', 'good': '👍', 'ok': '😐', 'meh': '😕' };
                            return (
                              <div key={i} className="bg-zinc-700/50 rounded-lg p-3">
                                <div className="flex items-center justify-between mb-1">
                                  <span className="text-white flex items-center gap-2">
                                    <span>{cat?.emoji || '📹'}</span>
                                    <span className="text-sm">{clip?.title || 'Unknown'}</span>
                                  </span>
                                  <div className="flex items-center gap-2">
                                    <span className="text-lg">{ratingEmoji[t.rating] || t.rating}</span>
                                    {clip && (
                                      <button
                                        onClick={() => setWizardPreviewClip(clip)}
                                        className="px-2 py-1 bg-blue-500/20 text-blue-400 text-xs rounded hover:bg-blue-500/30 flex items-center gap-1"
                                      >
                                        <Eye className="w-3 h-3" /> View
                                      </button>
                                    )}
                                  </div>
                                </div>
                                {(t.hisThoughts || t.herThoughts) && (
                                  <div className="text-xs text-zinc-400 mt-2 space-y-1">
                                    {t.hisThoughts && <p>👨 {t.hisThoughts}</p>}
                                    {t.herThoughts && <p>👩 {t.herThoughts}</p>}
                                  </div>
                                )}
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    )}

                    {/* Favorites with Notes */}
                    {(onboardingCoupleData.favorites || []).length > 0 && (
                      <div className="space-y-3">
                        <h4 className="text-white font-medium">⭐ Their Favorites</h4>
                        <div className="space-y-2">
                          {(onboardingCoupleData.favorites || []).map(clipId => {
                            const clip = dateNiteClips.find(c => c.id === clipId);
                            const cat = clip ? CATEGORIES.find(c => c.id === clip.categoryId) : null;
                            const note = onboardingCoupleData.favoriteNotes?.[clipId];
                            return clip && (
                              <div key={clipId} className="bg-amber-500/10 border border-amber-500/20 rounded-lg p-3">
                                <div className="flex items-center gap-2">
                                  <span className="text-lg">{cat?.emoji}</span>
                                  <span className="text-amber-400 font-medium">{clip.title}</span>
                                </div>
                                {note && (
                                  <p className="text-zinc-300 text-sm mt-1 italic">"{note}"</p>
                                )}
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    )}
                  </div>
                )}

                {/* Step 3: Feedback & Requests */}
                {onboardingWizardStep === 3 && (
                  <div className="space-y-6">
                    <div className="text-center mb-6">
                      <h3 className="text-xl font-bold text-white mb-2">💬 Feedback & Requests</h3>
                      <p className="text-zinc-400">What they've shared and what they want</p>
                    </div>

                    {/* Would Submit Clips */}
                    <div className="bg-zinc-700/50 rounded-xl p-4">
                      <p className="text-zinc-400 text-sm mb-2">Would they submit their own clips?</p>
                      <p className="text-white text-lg">
                        {onboardingCoupleData.wouldSubmitClips === 'yes' ? '✅ Yes, they would!' :
                         onboardingCoupleData.wouldSubmitClips === 'maybe' ? '🤔 Maybe' :
                         onboardingCoupleData.wouldSubmitClips === 'no' ? '❌ No' :
                         '—  Not answered yet'}
                      </p>
                    </div>

                    {/* Product Excitement */}
                    <div className="bg-zinc-700/50 rounded-xl p-4">
                      <p className="text-zinc-400 text-sm mb-2">Product Excitement</p>
                      {onboardingCoupleData.productExcitement ? (
                        <div className="flex items-center gap-1">
                          {[1, 2, 3, 4, 5].map(star => (
                            <span key={star} className={`text-2xl ${star <= onboardingCoupleData.productExcitement! ? 'text-amber-400' : 'text-zinc-600'}`}>
                              ★
                            </span>
                          ))}
                          <span className="text-zinc-400 ml-2">({onboardingCoupleData.productExcitement}/5)</span>
                        </div>
                      ) : (
                        <p className="text-zinc-500">Not rated yet</p>
                      )}
                    </div>

                    {/* General Feedback */}
                    {onboardingCoupleData.generalFeedback && (
                      <div className="bg-purple-500/10 border border-purple-500/30 rounded-xl p-4">
                        <p className="text-purple-400 text-sm mb-2">Their Feedback</p>
                        <p className="text-white italic">"{onboardingCoupleData.generalFeedback}"</p>
                      </div>
                    )}

                    {/* Content Requests */}
                    {coupleRequests.length > 0 && (
                      <div className="space-y-3">
                        <h4 className="text-white font-medium flex items-center gap-2">
                          <Lightbulb className="w-5 h-5 text-purple-400" />
                          Content Requests ({coupleRequests.filter(r => r.status === 'pending').length} pending)
                        </h4>
                        <div className="space-y-3 max-h-[400px] overflow-y-auto">
                          {coupleRequests.slice(-5).reverse().map(req => (
                            <div key={req.id} className={`bg-zinc-700/50 rounded-lg p-3 ${req.status === 'pending' ? 'border border-purple-500/30' : ''}`}>
                              <div className="flex items-center gap-2 mb-2">
                                <span className="text-xs px-2 py-0.5 rounded bg-zinc-600 text-zinc-300">
                                  {req.type === 'clip' ? '🎬 Clip Request' : '📁 Category Suggestion'}
                                </span>
                                {req.categoryName && <span className="text-purple-400 text-xs font-medium">"{req.categoryName}"</span>}
                              </div>
                              <p className="text-white text-sm">{req.description}</p>
                              {req.whyWanted && <p className="text-zinc-400 text-xs mt-2 italic">💭 "{req.whyWanted}"</p>}

                              {/* RedGifs Embed */}
                              {req.exampleUrl && req.exampleUrl.includes('redgifs.com') && (
                                <div className="mt-3">
                                  <p className="text-zinc-500 text-xs mb-2">📎 Example they want more of:</p>
                                  <VideoPlayer url={req.exampleUrl} />
                                  <a
                                    href={req.exampleUrl}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="text-blue-400 text-xs hover:underline mt-1 inline-block"
                                  >
                                    Open in new tab ↗
                                  </a>
                                </div>
                              )}
                              {req.exampleUrl && !req.exampleUrl.includes('redgifs.com') && (
                                <a href={req.exampleUrl} target="_blank" rel="noopener noreferrer" className="text-blue-400 text-sm hover:underline mt-2 inline-flex items-center gap-1">
                                  📎 View example ↗
                                </a>
                              )}
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {!onboardingCoupleData.wouldSubmitClips && !onboardingCoupleData.productExcitement && !onboardingCoupleData.generalFeedback && coupleRequests.length === 0 && (
                      <div className="text-center py-8 bg-zinc-700/30 rounded-xl">
                        <p className="text-zinc-500">No feedback shared yet</p>
                      </div>
                    )}
                  </div>
                )}

                {/* Step 4: What's Next */}
                {onboardingWizardStep === 4 && (
                  <div className="space-y-6">
                    <div className="text-center mb-6">
                      <div className="w-16 h-16 rounded-full bg-gradient-to-r from-lime-500 to-emerald-500 flex items-center justify-center mx-auto mb-4">
                        <Check className="w-8 h-8 text-white" />
                      </div>
                      <h3 className="text-xl font-bold text-white mb-2">You're All Set!</h3>
                      <p className="text-zinc-400">Here's what you can do next</p>
                    </div>

                    <div className="space-y-3">
                      <button
                        onClick={() => {
                          setShowCoupleOnboardingWizard(false);
                          setTab('calendar');
                        }}
                        className="w-full bg-gradient-to-r from-lime-500 to-emerald-500 text-zinc-900 rounded-xl p-4 flex items-center gap-4 hover:opacity-90 transition-opacity"
                      >
                        <Calendar className="w-8 h-8" />
                        <div className="text-left">
                          <p className="font-bold">Schedule Their First Activity</p>
                          <p className="text-sm opacity-80">Plan something for them to try</p>
                        </div>
                        <ArrowRight className="w-6 h-6 ml-auto" />
                      </button>

                      <button
                        onClick={() => {
                          setShowCoupleOnboardingWizard(false);
                          setTab('couple');
                        }}
                        className="w-full bg-pink-500/20 text-pink-400 border border-pink-500/30 rounded-xl p-4 flex items-center gap-4 hover:bg-pink-500/30 transition-colors"
                      >
                        <Heart className="w-8 h-8" />
                        <div className="text-left">
                          <p className="font-bold">View Couple Dashboard</p>
                          <p className="text-sm opacity-80">See their full profile and activity</p>
                        </div>
                        <ArrowRight className="w-6 h-6 ml-auto" />
                      </button>

                      <button
                        onClick={() => {
                          setShowCoupleOnboardingWizard(false);
                          setTab('datenite');
                        }}
                        className="w-full bg-purple-500/20 text-purple-400 border border-purple-500/30 rounded-xl p-4 flex items-center gap-4 hover:bg-purple-500/30 transition-colors"
                      >
                        <Play className="w-8 h-8" />
                        <div className="text-left">
                          <p className="font-bold">Browse Clips to Recommend</p>
                          <p className="text-sm opacity-80">Find content they'll love</p>
                        </div>
                        <ArrowRight className="w-6 h-6 ml-auto" />
                      </button>

                      {coupleRequests.filter(r => r.status === 'pending').length > 0 && (
                        <button
                          onClick={() => {
                            setShowCoupleOnboardingWizard(false);
                            setTab('inbox');
                          }}
                          className="w-full bg-amber-500/20 text-amber-400 border border-amber-500/30 rounded-xl p-4 flex items-center gap-4 hover:bg-amber-500/30 transition-colors"
                        >
                          <Lightbulb className="w-8 h-8" />
                          <div className="text-left">
                            <p className="font-bold">Review Their Requests</p>
                            <p className="text-sm opacity-80">{coupleRequests.filter(r => r.status === 'pending').length} pending requests to review</p>
                          </div>
                          <ArrowRight className="w-6 h-6 ml-auto" />
                        </button>
                      )}
                    </div>
                  </div>
                )}
              </div>

              {/* Navigation Buttons */}
              <div className="sticky bottom-0 bg-zinc-800 border-t border-zinc-700 p-4 flex justify-between">
                {onboardingWizardStep > 0 ? (
                  <button
                    onClick={() => setOnboardingWizardStep(prev => prev - 1)}
                    className="px-6 py-3 rounded-xl font-medium bg-zinc-700 text-white hover:bg-zinc-600"
                  >
                    Back
                  </button>
                ) : (
                  <div />
                )}
                {onboardingWizardStep < 4 ? (
                  <button
                    onClick={() => setOnboardingWizardStep(prev => prev + 1)}
                    className="px-6 py-3 rounded-xl font-medium text-zinc-900"
                    style={{ backgroundColor: COLORS.lime }}
                  >
                    Next
                  </button>
                ) : (
                  <button
                    onClick={() => {
                      setShowCoupleOnboardingWizard(false);
                      setTab('calendar');
                    }}
                    className="px-6 py-3 rounded-xl font-medium text-zinc-900"
                    style={{ backgroundColor: COLORS.lime }}
                  >
                    Get Started
                  </button>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Wizard Clip Preview Modal */}
        <VideoModal
          isOpen={!!wizardPreviewClip}
          onClose={() => setWizardPreviewClip(null)}
          url={wizardPreviewClip?.link}
          title={wizardPreviewClip?.title}
          description={wizardPreviewClip?.description}
          clipId={wizardPreviewClip?.id}
        />

        {/* Add New Clip Modal */}
        <Modal
          isOpen={showAddClipModal}
          onClose={() => {
            setShowAddClipModal(false);
            setEditingClipData({ title: '', description: '', tags: [], categoryIds: [], link: '', reason: '' });
            setNewClipUrlError(null);
          }}
          title="Add New Clip"
        >
          <div className="p-6 space-y-4">
            {/* URL */}
            <div>
              <label className="block text-xs font-semibold text-zinc-500 uppercase tracking-wide mb-1">
                <Link className="w-3 h-3 inline mr-1" />RedGifs URL
              </label>
              <input
                type="url"
                value={editingClipData.link}
                onChange={e => {
                  setEditingClipData({ ...editingClipData, link: e.target.value });
                  setNewClipUrlError(null);
                }}
                className={`w-full bg-zinc-700 border rounded-lg px-3 py-2 text-white placeholder-zinc-500 ${
                  newClipUrlError ? 'border-red-500' : 'border-zinc-600'
                }`}
                placeholder="https://redgifs.com/watch/..."
              />
            </div>

            {/* Title */}
            <div>
              <label className="block text-xs font-semibold text-zinc-500 uppercase tracking-wide mb-1">Title</label>
              <input
                type="text"
                value={editingClipData.title}
                onChange={e => setEditingClipData({ ...editingClipData, title: e.target.value })}
                className="w-full bg-zinc-700 border border-zinc-600 rounded-lg px-3 py-2 text-white"
                placeholder="Give it a catchy name..."
              />
            </div>

            {/* Category - Multi-select */}
            <div>
              <label className="block text-xs font-semibold text-zinc-500 uppercase tracking-wide mb-1">
                Categories <span className="text-zinc-600 font-normal">(tap multiple, 1st = primary)</span>
              </label>
              <div className="grid grid-cols-3 gap-2">
                {CATEGORIES.map(cat => {
                  const isSelected = editingClipData.categoryIds.includes(cat.id);
                  const isPrimary = editingClipData.categoryIds[0] === cat.id;
                  return (
                    <button
                      key={cat.id}
                      onClick={() => {
                        if (isSelected) {
                          if (editingClipData.categoryIds.length > 1) {
                            setEditingClipData({
                              ...editingClipData,
                              categoryIds: editingClipData.categoryIds.filter(id => id !== cat.id)
                            });
                          }
                        } else {
                          setEditingClipData({
                            ...editingClipData,
                            categoryIds: [...editingClipData.categoryIds, cat.id]
                          });
                        }
                      }}
                      className={`p-2 rounded-lg text-center transition-all relative ${
                        isSelected
                          ? isPrimary
                            ? 'bg-emerald-500/20 border-2 border-emerald-500 text-emerald-400'
                            : 'bg-blue-500/20 border-2 border-blue-500 text-blue-400'
                          : 'bg-zinc-700 border border-zinc-600 text-zinc-400 hover:border-zinc-500'
                      }`}
                    >
                      {isPrimary && (
                        <span className="absolute -top-1 -right-1 text-[8px] bg-emerald-500 text-white px-1 rounded">1st</span>
                      )}
                      <span className="text-lg block">{cat.emoji}</span>
                      <span className="text-xs">{cat.name}</span>
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Description */}
            <div>
              <label className="block text-xs font-semibold text-zinc-500 uppercase tracking-wide mb-1">Description (optional)</label>
              <textarea
                value={editingClipData.description}
                onChange={e => setEditingClipData({ ...editingClipData, description: e.target.value })}
                className="w-full bg-zinc-700 border border-zinc-600 rounded-lg px-3 py-2 text-white placeholder-zinc-500 h-20 resize-none"
                placeholder="What makes this clip special..."
              />
            </div>

            {/* Tags */}
            <div>
              <label className="block text-xs font-semibold text-zinc-500 uppercase tracking-wide mb-1">Tags (optional)</label>
              <div className="flex flex-wrap gap-1.5">
                {TAGS.map(tag => (
                  <button
                    key={tag.id}
                    onClick={() => toggleEditTag(tag.id)}
                    className={`px-2 py-1 rounded-full text-xs font-medium transition-all ${
                      editingClipData.tags.includes(tag.id)
                        ? `${tag.color} border`
                        : 'bg-zinc-700 text-zinc-400 border border-zinc-600 hover:border-zinc-500'
                    }`}
                  >
                    {tag.label}
                  </button>
                ))}
              </div>
            </div>

            {/* Validation Error */}
            {newClipUrlError && (
              <div className="flex items-center gap-2 p-3 bg-red-500/10 border border-red-500/30 rounded-lg">
                <AlertCircle className="w-4 h-4 text-red-400 flex-shrink-0" />
                <p className="text-red-400 text-sm">{newClipUrlError}</p>
              </div>
            )}

            {/* Action Buttons */}
            <div className="flex gap-3 pt-2">
              <button
                onClick={() => {
                  setShowAddClipModal(false);
                  setEditingClipData({ title: '', description: '', tags: [], categoryIds: [], link: '', reason: '' });
                  setNewClipUrlError(null);
                }}
                className="flex-1 px-4 py-3 rounded-lg text-zinc-300 bg-zinc-700 hover:bg-zinc-600"
              >
                Cancel
              </button>
              <button
                onClick={addNewClip}
                className="flex-1 flex items-center justify-center gap-2 px-4 py-3 rounded-lg text-zinc-900 font-medium"
                style={{ backgroundColor: COLORS.lime }}
              >
                <Plus className="w-4 h-4" /> Add Clip
              </button>
            </div>
          </div>
        </Modal>

        {/* Couple Setup Modal - for creating couple profile from reviewer */}
        {currentReviewer && (
          <CoupleSetupModal
            isOpen={showCoupleSetupModal}
            onClose={() => setShowCoupleSetupModal(false)}
            reviewer={currentReviewer}
            onCoupleCreated={async (coupleId, _coupleData) => {
              // Update reviewer with couple link and switch to couple mode
              const updatedReviewer = {
                ...currentReviewer,
                coupleId,
                mode: 'couple' as const
              };
              const updated = reviewers.map(r => r.id === currentReviewer.id ? updatedReviewer : r);
              await saveReviewers(updated);
              setCurrentReviewer(updatedReviewer);
              // Backup couple link locally
              localStorage.setItem(`vb-reviewer-couple:${currentReviewer.id}`, coupleId);
              setShowCoupleSetupModal(false);
            }}
          />
        )}

        {/* Mode Switch Confirmation Modal */}
        {showModeConfirm && currentReviewer && (
          <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4">
            <div className="bg-zinc-900 rounded-2xl border border-zinc-700 w-full max-w-md p-6">
              <div className="text-center mb-6">
                <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-gradient-to-r from-pink-500 to-rose-500 flex items-center justify-center">
                  <Heart className="w-8 h-8 text-white" />
                </div>
                <h3 className="text-xl font-bold text-white mb-2">Switch to Date Night?</h3>
                <p className="text-zinc-400 text-sm">
                  This will take you to the full Date Night experience - browse categories, watch clips together with {currentReviewer.partnerName || 'your partner'}, and plan your next adventure.
                </p>
              </div>

              <div className="bg-zinc-800/50 rounded-xl p-4 mb-6">
                <p className="text-zinc-300 text-sm">
                  <span className="text-pink-400 font-medium">Couple Mode</span> is for enjoying content together. Switch back to <span className="text-lime-400 font-medium">Reviewer Mode</span> anytime to continue reviewing clips.
                </p>
              </div>

              <div className="flex gap-3">
                <button
                  onClick={() => setShowModeConfirm(false)}
                  className="flex-1 px-4 py-3 bg-zinc-700 hover:bg-zinc-600 text-zinc-300 rounded-xl text-sm font-medium transition-colors"
                >
                  Stay in Reviewer
                </button>
                <button
                  onClick={async () => {
                    if (currentReviewer.coupleId && onSwitchToCoupleMode) {
                      // Navigate to full DateNite couple experience
                      await onSwitchToCoupleMode(currentReviewer.coupleId);
                    }
                    setShowModeConfirm(false);
                  }}
                  className="flex-1 px-4 py-3 bg-gradient-to-r from-pink-500 to-rose-500 hover:from-pink-600 hover:to-rose-600 text-white rounded-xl text-sm font-medium transition-colors flex items-center justify-center gap-2"
                >
                  <Heart className="w-4 h-4" /> Enter Date Night
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Question Confirmation Popup */}
        {pendingQuestion && managedCoupleData && (
          <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
            <div className="bg-zinc-800 rounded-2xl p-6 w-full max-w-md border border-purple-500/30 shadow-xl">
              <h3 className="text-lg font-bold text-white mb-2 flex items-center gap-2">
                ❓ Confirm Question
              </h3>
              <p className="text-zinc-400 text-sm mb-4">
                This will be sent to {managedCoupleData.hisName} & {managedCoupleData.herName}
              </p>

              <div className="space-y-3">
                <div>
                  <label className="text-xs text-zinc-500 block mb-1">Question (edit if needed)</label>
                  <textarea
                    value={pendingQuestion.question}
                    onChange={(e) => setPendingQuestion({ ...pendingQuestion, question: e.target.value })}
                    className="w-full bg-zinc-700 border border-zinc-600 rounded-lg px-3 py-2 text-white text-sm resize-none h-24"
                  />
                </div>

                {pendingQuestion.context && (
                  <div>
                    <label className="text-xs text-zinc-500 block mb-1">Context (shown to them)</label>
                    <input
                      type="text"
                      value={pendingQuestion.context}
                      onChange={(e) => setPendingQuestion({ ...pendingQuestion, context: e.target.value })}
                      className="w-full bg-zinc-700 border border-zinc-600 rounded-lg px-3 py-2 text-white text-sm"
                    />
                  </div>
                )}

                <div className="flex gap-2 pt-2">
                  <button
                    onClick={() => setPendingQuestion(null)}
                    className="flex-1 px-4 py-2 bg-zinc-700 text-zinc-300 rounded-lg text-sm hover:bg-zinc-600 transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={async () => {
                      if (!pendingQuestion.question.trim()) return;
                      const newQuestion: CoupleQuestion = {
                        id: generateId('cq'),
                        coupleId: managedCoupleData.coupleId,
                        fromReviewerId: currentReviewer!.id,
                        fromReviewerName: currentReviewer!.partnerName || 'Reviewer',
                        question: pendingQuestion.question.trim(),
                        category: pendingQuestion.category as CoupleQuestion['category'],
                        context: pendingQuestion.context,
                        sentAt: now(),
                        status: 'pending'
                      };
                      await saveCoupleQuestions([...coupleQuestions, newQuestion]);
                      setPendingQuestion(null);
                    }}
                    disabled={!pendingQuestion.question.trim()}
                    className="flex-1 px-4 py-2 bg-purple-500/20 text-purple-400 rounded-lg text-sm hover:bg-purple-500/30 transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
                  >
                    <Send className="w-4 h-4" /> Send Question
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Clip Suggestion Review Modal */}
        {reviewingSuggestionClip && (
          <div className="fixed inset-0 bg-black/90 flex items-center justify-center z-50 p-4">
            <div className="bg-zinc-900 rounded-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto border border-pink-500/30">
              {/* Header */}
              <div className="flex items-center justify-between p-4 border-b border-zinc-700">
                <h3 className="text-lg font-bold text-white">{reviewingSuggestionClip.clipItem.title}</h3>
                <button
                  onClick={() => setReviewingSuggestionClip(null)}
                  className="text-zinc-400 hover:text-white"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>

              {/* Embedded Video */}
              <VideoPlayer url={reviewingSuggestionClip.clipItem.url} />

              {/* Rating Form */}
              <div className="p-4 space-y-4">
                {/* Star Rating */}
                <div>
                  <label className="text-zinc-400 text-sm block mb-2">How would you rate this clip?</label>
                  <div className="flex gap-2">
                    {[1,2,3,4,5].map(star => (
                      <button
                        key={star}
                        onClick={() => setSuggestionClipRating(star)}
                        className="p-1"
                      >
                        <Star
                          className={`w-8 h-8 transition-colors ${
                            star <= suggestionClipRating
                              ? 'text-yellow-400 fill-yellow-400'
                              : 'text-zinc-600 hover:text-zinc-500'
                          }`}
                        />
                      </button>
                    ))}
                  </div>
                </div>

                {/* Would Try */}
                <div>
                  <label className="text-zinc-400 text-sm block mb-2">Would you/your partner want to try this?</label>
                  <div className="flex gap-2">
                    <button
                      onClick={() => setSuggestionClipInterested(true)}
                      className={`flex-1 px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                        suggestionClipInterested === true
                          ? 'bg-lime-500 text-zinc-900'
                          : 'bg-zinc-700 text-zinc-300 hover:bg-zinc-600'
                      }`}
                    >
                      👍 Yes, would try!
                    </button>
                    <button
                      onClick={() => setSuggestionClipInterested(false)}
                      className={`flex-1 px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                        suggestionClipInterested === false
                          ? 'bg-zinc-600 text-white'
                          : 'bg-zinc-700 text-zinc-300 hover:bg-zinc-600'
                      }`}
                    >
                      👎 Not for us
                    </button>
                  </div>
                </div>

                {/* Comment */}
                <div>
                  <label className="text-zinc-400 text-sm block mb-2">Any thoughts? (optional)</label>
                  <textarea
                    value={suggestionClipComment}
                    onChange={(e) => setSuggestionClipComment(e.target.value)}
                    placeholder="What did you think? What stood out?"
                    className="w-full bg-zinc-800 border border-zinc-700 rounded-xl px-4 py-3 text-white placeholder-zinc-500 text-sm h-20 resize-none"
                  />
                </div>

                {/* Submit */}
                <button
                  onClick={async () => {
                    if (suggestionClipRating === 0) return;

                    // Update the clip suggestion with the rating
                    const updated = clipSuggestions.map(cs => {
                      if (cs.id !== reviewingSuggestionClip.suggestionId) return cs;
                      return {
                        ...cs,
                        clips: (cs.clips || []).map(c => {
                          if (c.id !== reviewingSuggestionClip.clipItem.id) return c;
                          return {
                            ...c,
                            rating: suggestionClipRating,
                            comment: suggestionClipComment.trim() || undefined,
                            interested: suggestionClipInterested ?? undefined,
                            respondedAt: now()
                          };
                        }),
                        // Auto-update status based on how many clips are rated
                        status: (() => {
                          const thisCs = clipSuggestions.find(s => s.id === reviewingSuggestionClip.suggestionId);
                          if (!thisCs) return 'pending' as const;
                          const ratedCount = (thisCs.clips || []).filter(c =>
                            c.id === reviewingSuggestionClip.clipItem.id ? true : !!c.rating
                          ).length;
                          if (ratedCount >= (thisCs.clips || []).length) return 'completed' as const;
                          if (ratedCount > 0) return 'partial' as const;
                          return 'pending' as const;
                        })(),
                        completedAt: (() => {
                          const thisCs = clipSuggestions.find(s => s.id === reviewingSuggestionClip.suggestionId);
                          if (!thisCs) return undefined;
                          const ratedCount = (thisCs.clips || []).filter(c =>
                            c.id === reviewingSuggestionClip.clipItem.id ? true : !!c.rating
                          ).length;
                          if (ratedCount >= (thisCs.clips || []).length) return now();
                          return undefined;
                        })()
                      };
                    });

                    await saveClipSuggestions(updated);
                    setReviewingSuggestionClip(null);
                    setSuggestionClipRating(0);
                    setSuggestionClipComment('');
                    setSuggestionClipInterested(null);
                  }}
                  disabled={suggestionClipRating === 0}
                  className={`w-full px-4 py-3 rounded-xl text-sm font-medium transition-colors ${
                    suggestionClipRating > 0
                      ? 'bg-pink-500 hover:bg-pink-600 text-white'
                      : 'bg-zinc-700 text-zinc-500 cursor-not-allowed'
                  }`}
                >
                  <Check className="w-4 h-4 inline mr-2" />
                  Submit Review
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Floating Chat - with managed couple */}
        {currentReviewer?.managedCoupleId && managedCoupleData && (
          <FloatingChat
            coupleId={currentReviewer.managedCoupleId}
            userType="reviewer"
            userName={currentReviewer.name || currentReviewer.partnerName || 'Your Reviewer'}
            partnerLabel={`Chat with ${managedCoupleData.hisName} & ${managedCoupleData.herName}`}
            clips={dateNiteClips}
            accentColor="lime"
          />
        )}
      </div>
    </div>
  );
};
